/*
 * kit-room.js — "The Bulk Desk" (formerly "The Kit Room"): self-contained
 * bulk quote-builder overlay.
 *
 * THEME ASSET (moved out of the app extension — the Liquid block hit the
 * 100KB theme-extension wall). Loaded by sections/bulk-landing.liquid, which
 * also injects window.PB_KITROOM. Deployed via
 * `shopify theme push --only assets/kit-room.js`.
 *
 * REDESIGN PASS (this update, KIT-ROOM-REDESIGN-SPEC.md owner decisions +
 * design-lead spec): renamed "The Bulk Desk" — topbar wordmark, segmented
 * Chat|Browse toggle, subtitle/online-dot killed. SURFACE INVERTED (Stripe
 * split): the room canvas is now ONE light warm surface throughout (the old
 * `.kr-in-room` dark-gradient takeover is gone); the order rail is now the
 * ONE deliberately dark panel (deep-purple gradient, mint for savings,
 * orange reserved for the primary CTA + qty/send hovers only). Card layout
 * reworked: swatches moved off the thumbnail to a row under the name, price
 * hierarchy (20/800 hero number, demoted qty/GST line), ONE full-width
 * primary Add (purple, not orange) + Compare demoted to a quiet text link,
 * calm "added" state (purple border + tint + a small ✓ chip, not a hard
 * orange outline). Swatches solve the perf trade-off from the earlier PERF
 * PASS: fast-path cards show an INSTANT colour count (from product.js
 * options/variants — colourCountFromProductJs()), then hydrateSwatches()
 * lazy-upgrades to real hex via a non-blocking background getPreviewConfig
 * call once the price has already rendered — graceful fallback to
 * count-only on failure, never a fabricated hex. Motion "calm pass" per
 * KIT-ROOM-MOTION-REVIEW.md: killed every remaining perpetual loop (the
 * "Best value" pill shimmer, the empty-rail breathing float, the ambient
 * orb rotate+pulse) — the Ari orb is now calm at rest and only comes alive
 * via krBusy()'s ref-counted `.kr-thinking` state while a grid load or
 * checkout fetch is in flight. Responsive ≤820px: rail becomes a sticky
 * dark mini-bar + slide-up bottom sheet (transform/max-height, RM drops the
 * slide to an instant state-jump), grid drops to 2-up.
 *
 * SLICE 1 (see KIT-ROOM-BUILD-SPEC.md §5): modal shell rendering a grid of
 * REAL product cards — real search results, real per-unit price, real
 * product photo (the browse category itself was hardcoded this slice;
 * Slice 3 replaced it with Popular picks + real category chips below).
 *
 * SLICE 2: the live multi-line "Your bulk order" kit rail. "Add to quote"
 * pushes a real line (using the card's already-fetched quote — no refetch);
 * a per-line qty stepper re-quotes live and debounces rapid clicks; totals
 * are getQuote-SHAPED numbers summed verbatim, NEVER recomputed from a
 * different formula at the totals layer (as of the PERF PASS below, a
 * card's price may originate from the client-compute fast path rather than
 * a live getQuote call — see that note for why the two are cent-exact). The
 * rail's "Generate quote PDF" is still an honest stub (Slice 6).
 *
 * SLICE 3: "Popular picks" (curated best-sellers) as the default room view,
 * real category chips backed by searchProducts filters.productType (NOT
 * free-text — see CATEGORIES below for why), and a real compare (⇄) flow:
 * pick 2-3 cards, get a spec-diff + price comparison built from data the
 * cards already fetched (no refetch), "Add" from any column pushes into the
 * same rail. No real chat yet (describe door opens Popular picks + an
 * honest inline note — Slice 7 wires real Ari).
 *
 * SLICE 4 (this update): "Start your order →" — the rail's primary CTA now
 * POSTs the kit to the new `createKitDraft` Cloud Function (region
 * australia-southeast2, additive — see production-dashboard's index.ts) and
 * redirects to the real Shopify checkout (invoiceUrl) it returns. The
 * request carries {productId, quantity, printLocations} per line — NEVER a
 * price; the CF re-prices every line server-side via the exact same
 * getQuote engine before creating the draft, so the Shopify total matches
 * the rail to the cent by construction, not by trusting the browser.
 * "Generate quote PDF" is now the secondary button, still an honest stub
 * (Slice 6). No artwork upload yet (Slice 5).
 *
 * MOTION PASS (this update, KIT-ROOM-MOTION-SPEC.md): §0 primitives —
 * KR_RM (matchMedia reduced-motion flag), krAnimateNumber() (rAF count-up),
 * pulseClass() (restart a one-shot CSS animation), krFlipFly() (FLIP-style
 * thumb-to-rail clone) — plus the TOP 5: the add-to-quote moment (button
 * morph, FLIP-fly, spring-in rail line, count-up totals, rail-head breath),
 * the entry->room "step inside" transition, live price/tier-crossing
 * flashes, staggered card hydrate/reveal, and an ambient Ari-orb rotation.
 * Also: rail exit-collapse on remove, PDF/checkout pop-on-enable, compare
 * column stagger + best-value pill pop, and card/button hover/press
 * micro-states. Transform/opacity only, no layout thrash, all kr-namespaced.
 * The blanket `*{animation:none;transition:none}` reduced-motion kill is
 * GONE — replaced with PER-FEATURE fallbacks (see the RM media query near
 * the end of CSS): every transient pulse/flash/fly is already gated by the
 * KR_RM flag in JS (never even created under reduced motion), and every
 * purely-CSS entrance/ambient animation gets an explicit `animation:none`
 * override that lands the element in its normal resting state — no motion,
 * but every number/colour/label still updates correctly and instantly.
 *
 * PERF PASS (this update): a grid load previously fired ~3 Cloud Function
 * calls PER CARD (getQuote + getPreviewConfig, plus a searchProducts per
 * Popular pick) — cold-start-prone CF-to-CF round trips that made a 6-card
 * grid take 20s+. Replaced with computeClientQuote() — an EXACT client
 * replica of the server's quantity-only pricing math (see that function's
 * comment for the writer-side-truth verification and one important
 * correction found vs. a naive "discount the price directly" formula) — fed
 * by a single Shopify /products/{handle}.js fetch per card (CDN-cached, no
 * cold start) instead of getQuote+getPreviewConfig. Popular-pick handles
 * resolve via searchProducts once per code then cache for the page session
 * (repeat views fire zero Cloud Function calls); categories still need
 * searchProducts to FIND matching products, just no more per-card follow-up
 * calls. Any product.js failure falls back to the original live getQuote+
 * getPreviewConfig path for JUST that card (hydrateCard(), unchanged) — a
 * card can never end up broken, only slower on that one card. The qty
 * stepper recomputes instantly (no network call) using the same formula.
 * createKitDraft checkout is UNCHANGED — it always re-prices server-side
 * regardless of how a line's on-screen price was computed, so price
 * integrity at the point of payment never depends on the client's math.
 *
 * CONFIGURE DRAWER (this update) — "Configure your line": the whole card
 * (`.kr-card-hit`, wrapping thumb/name/price) opens a right-edge slide-out
 * (bottom-sheet <=820px, mirrors the rail's responsive pattern) instead of
 * every line silently sharing the one global QUOTE_PRINT_LOCATIONS. Per line
 * you now pick real print placements (multi-select chips, one per zone the
 * PRODUCT ACTUALLY HAS — read live from getPreviewConfig's `zones`, never a
 * hardcoded 8-key list, so a cap only ever shows its front-panel and a tote
 * never shows a sleeve), a colour (real hex swatches, out-of-stock disabled),
 * and an optional size run (reuses the request_details `size_grid` chip/
 * stepper markup). computeClientQuote() now takes printLocations[] instead
 * of a scalar printPrice and replicates quote.ts's surface pricing EXACTLY:
 * front+back is the combined `double` addon price (not 2x `single`), each
 * sleeve is additive on top — verified cent-for-cent against quote.ts's
 * computeQuote(), not just the print-price fetch. A line can never reach
 * zero placements (front-full pre-checked, rubber-banded — the LAST chip
 * can't be unchecked); a line that somehow has none (only reachable via a
 * legacy/cross-device resume that couldn't round-trip a placement) shows an
 * amber "Needs placement" chip in the rail and blocks "Start your order"
 * until fixed. Every rail line now carries {printLocations[], colour,
 * sizeRun} — railLinesPayload() sends printLocations/colour PER LINE (never
 * the old global) to saveKitQuote/checkout, a size run rides in
 * createKitDraft's `sizes` (per-line, sole source of truth for that line's
 * qty — matches index.ts's contract) and saveKitQuote's top-level
 * `details.sizeRun[productId]` (the shape request_details forms already
 * write), and the rail line's meta text is now the REAL config ("AS Colour ·
 * Front + Back · Black · 2XL×10 L×20") — the "1 front print" hardcode is
 * gone. buildQuoteContext() folds the same real placements/colour into
 * Ari's per-line description so the chat can never contradict the panel.
 *
 * WRITER-SIDE TRUTH for this slice (verified live against the real
 * endpoints, not assumed):
 *   - `{text:'tee'}` free-text search ranks by title/type keyword match
 *     ONLY (product-search.ts textScore) — it has NO popularity signal, so
 *     it surfaces hi-vis workwear and "DISCOUNTED GARMENTS" placeholder SKUs
 *     ahead of real best-sellers. Confirmed by curling searchProducts
 *     directly.
 *   - product_facts (what searchProducts reads) has no bestseller field —
 *     it's a strict whitelist projection (product-facts.ts) that never
 *     copied Shopify `tags`. A real `bestseller` tag signal DOES exist, but
 *     only on the STAFF reorder-portal endpoint (index.ts:59872,
 *     `reorderPortalProducts`, portal-JWT gated, reads a different
 *     `products` collection) — not reachable from here and out of scope to
 *     wire net-new. So "Popular picks" is a CURATED list (owner-named style
 *     codes), each resolved live via searchProducts + verified via getQuote
 *     — never a hardcoded price or photo.
 *   - `filters.productType` exact-match (+ inStockOnly) is CLEAN — curling
 *     it for "T-Shirt"/"Hoodie"/"Polo Shirt"/"Cap"/"Tote Bag" returns zero
 *     junk (no DISCOUNTED GARMENTS, no stray hi-vis under other types).
 *     That's what backs the category chips, not free text.
 *
 * Exposes exactly one global: window.openKitRoom({ mode? }). `mode` is
 * accepted now so a future landing-page CTA (data-kitroom-mode="browse" or
 * "describe") can jump straight into the room per the eventual architecture
 * (KIT-ROOM-BUILD-SPEC.md §1) — but since no landing page exists yet, calling
 * openKitRoom() with no mode shows the in-modal two-door entry screen first.
 * Both doors land on the same real "Popular picks" view; "describe" mode
 * additionally shows an honest inline note rather than faking a chat stream
 * that isn't built yet (no fake success, no placeholders).
 *
 * Structural clone of assets/gang-sheet-builder.js (the production overlay
 * pattern in this repo): idempotent guard, a single self-injected <style>
 * scoped under #kit-room-overlay, position:fixed inset:0 z-index max,
 * document.body scroll-lock, history.pushState + popstate so the OS/browser
 * back gesture closes it, sibling inert+aria-hidden while open, focus trap,
 * Escape closes. ALL ids/classes are `kr-`/`kit-room-` namespaced — the
 * approved prototype's bare ids (#room/#stream/#entry/#lines/#cats) would
 * collide with the live theme, so nothing here reuses them.
 *
 * Endpoints (window.PB_KITROOM, injected by blocks/kit-room.liquid) — all
 * EXISTING public, CORS-enabled Cloud Functions, called directly from the
 * browser (production-dashboard/functions/src/index.ts):
 *   searchUrl    -> searchProducts   :73690  POST {text,filters,limit} -> {ok,results:SearchHit[]}
 *   getQuoteUrl  -> getQuote         :73653  POST {productId,quantity,printLocations[]} -> {ok,quote}
 *   previewUrl   -> getPreviewConfig :73706  GET  ?productId= -> {ok,preview}
 * `productId` is the SearchHit.id (product_facts Firestore doc id) — NEVER
 * styleCode; getQuote 404s otherwise (quote-server.ts:116-117).
 *
 * No external deps. No bundler — plain <script defer>, ES5-safe.
 */
(function () {
  'use strict';
  if (window.openKitRoom) return; // idempotent — never double-define

  var PB = window.PB_KITROOM || {};
  var OVERLAY_ID = 'kit-room-overlay';
  var STYLE_ID = 'kit-room-style';

  var QUOTE_QTY = 100;
  // Session-scoped override of QUOTE_QTY for the Popular grid's pricing basis
  // + its "priced at N units" label ONLY (set from openKitRoom({qty}) —
  // e.g. a landing-page Board the shopper dragged to 200 units before
  // clicking through). Everything else (category grids, Compare, default
  // rail-line qty) intentionally keeps using QUOTE_QTY — untouched. Reset to
  // QUOTE_QTY at the top of every openKitRoom() call, so a later open with
  // no qty (or a fresh page) never leaks a previous session's override.
  var _krGridQty = QUOTE_QTY;
  // Item 2 — {productId: {printLocations, colour, quantity}}, last-write-wins
  // per productId, populated from every get_quote trace entry's INPUT (never
  // its output — see renderTraceCards) across the whole Chat session. Module
  // scope for the same reason _krGridQty is (loadCardFast/handleOpenConfigure/
  // openChatConfigCard are module-scope functions, defined once outside any
  // single overlay instance): so a Chat-quoted config can steer a Browse
  // card's price/Configure seed too, not just Chat's own cards. Session-
  // scoped only — reset per open below, never persisted/sent to the server.
  var _chatLastQuoteConfig = {};
  // Money-moment fetch timeout (checkout + PDF paths only — see fetchJSON's
  // opts.timeoutMs). KR_FETCH_SLOW_MS is a shorter, non-fatal notice so the
  // button's copy stays honest ("still trying…") well before the hard abort.
  var KR_FETCH_TIMEOUT_MS = 22000;
  var KR_FETCH_SLOW_MS = 8000;
  // DEFAULT placement only now (Configure drawer) — every browse/chat card's
  // displayed list price and every fast-add line still starts here (never an
  // implicit zero), but a real per-line choice made in the Configure drawer
  // overrides it. NOT a shared global charged to every line any more — see
  // railLine.printLocations / railLinesPayload().
  var QUOTE_PRINT_LOCATIONS = ['front-full'];
  var GRID_LIMIT = 8; // per-category card cap (Popular picks uses its own fixed curated count)

  // Chat mode — the real Ari brain (agentChat CF, production-dashboard's
  // agent-chat.ts). Hardcoded fallback so Chat still works even where the
  // section hasn't wired PB_KITROOM.agentChatUrl yet.
  var AGENT_CHAT_FALLBACK_URL = 'https://australia-southeast1-printibly-production.cloudfunctions.net/agentChat';
  function agentChatUrl() { return PB.agentChatUrl || AGENT_CHAT_FALLBACK_URL; }
  var AGENT_MAX_TURNS = 6;
  // Staff-visibility logging (bulkDeskLogTurn, deployed) — fire-and-forget
  // after each real agentChat exchange; see logChatTurn() inside
  // openKitRoom(). Never awaited, never allowed to affect the customer path.
  var BULK_LOG_FALLBACK_URL = 'https://australia-southeast2-printibly-production.cloudfunctions.net/bulkDeskLogTurn';
  function bulkLogUrl() { return PB.bulkLogUrl || BULK_LOG_FALLBACK_URL; }
  // bulkDeskPoll (deployed, australia-southeast2, GET) — live human-handoff
  // poll for the SAME conversation bulkDeskLogTurn writes to. See the
  // "Human handoff" block inside openKitRoom() for the actual poll loop.
  var BULK_POLL_FALLBACK_URL = 'https://australia-southeast2-printibly-production.cloudfunctions.net/bulkDeskPoll';
  function bulkPollUrl() { return PB.bulkPollUrl || BULK_POLL_FALLBACK_URL; }
  var ARI_GREETING = 'Hi — I’m Ari 👋 Tell me what you’re making and roughly how many, and I’ll pull the right products and start your quote. Or jump in with our popular picks below.';
  var ARI_ERROR_REPLY = 'Sorry — I hit a snag there, try again.';

  // Save/Resume (saveKitQuote / getKitQuote, deployed) — persists the rail
  // server-side as a real staff-visible `quotes` doc + gives a resumable
  // link, and mirrors a lightweight snapshot to localStorage for an instant
  // same-device restore. See restoreRailFromEntries()/scheduleAutosave()
  // inside openKitRoom() for the actual wiring.
  var SAVE_QUOTE_FALLBACK_URL = 'https://australia-southeast2-printibly-production.cloudfunctions.net/saveKitQuote';
  function saveQuoteUrl() { return PB.saveUrl || SAVE_QUOTE_FALLBACK_URL; }
  // customerQuotePdf (deployed) — makes a real Shopify draft + emails the ABN'd
  // GST tax-quote PDF link via Postmark. "Email me this quote" sends THIS; the
  // silent autosave (saveKitQuote) still provides the quoteId + resume link.
  var CUSTOMER_QUOTE_PDF_FALLBACK_URL = 'https://australia-southeast2-printibly-production.cloudfunctions.net/customerQuotePdf';
  function customerQuotePdfUrl() { return PB.pdfUrl || CUSTOMER_QUOTE_PDF_FALLBACK_URL; }
  var GET_QUOTE_RESUME_FALLBACK_URL = 'https://australia-southeast2-printibly-production.cloudfunctions.net/getKitQuote';
  function getQuoteResumeUrl() { return PB.getQuoteResumeUrl || GET_QUOTE_RESUME_FALLBACK_URL; }
  // The storefront Bulk Desk landing page — confirmed live path (matches the
  // `?quote=` auto-open script that lives on that page). Only used to render
  // the inline resume link after "Email me this quote" — never sent
  // anywhere, purely a display string built from a resumeToken the server
  // already gave us.
  var RESUME_LINK_BASE = 'https://printibly.com.au/pages/bulk-custom-apparel-and-merch';
  var LOCAL_QUOTE_KEY = 'pb_bulkdesk_quote';
  var AUTOSAVE_DEBOUNCE_MS = 1500;

  // Artwork upload ("Ari sees your artwork") — reuses the same R2-sign
  // pattern the gang-sheet builder uses (extensions/product-customiser/
  // assets/gang-sheet-builder.js): POST {folder,contentType,filename,
  // contentLength} -> {uploadUrl,publicUrl,key} -> PUT the bytes. Only the
  // two allowlisted folders below are wired for this app proxy route.
  var R2_SIGN_FALLBACK_URL = '/apps/printibly/api/r2-sign';
  function r2SignUrl() { return PB.r2SignUrl || R2_SIGN_FALLBACK_URL; }
  var ARTWORK_MASTER_FOLDER = 'customer-uploads';
  var ARTWORK_PREVIEW_FOLDER = 'customer-previews';
  var ARTWORK_MAX_BYTES = 25 * 1024 * 1024; // 25MB cap, matches r2-sign's allowlist
  var ARTWORK_ALLOWED_MIME = {
    'image/png': 1, 'image/jpeg': 1, 'image/webp': 1, 'image/gif': 1,
    'image/svg+xml': 1, 'application/pdf': 1
  };
  var ARTWORK_PREVIEW_MAX_PX = 1568; // vision copy long-edge cap
  // PDF rasterisation — same pinned build as the gang-sheet builder (never
  // bump past 3.11.174 without checking that repo's UMD/ESM note first).
  var PDFJS_SRC = PB.pdfjsSrc || 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
  var PDFJS_WORKER = PB.pdfjsWorker || 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

  // ---- localStorage snapshot (same-device instant resume) ----
  // Defensive on every call: private-browsing/storage-disabled throws on
  // access in some browsers, and a corrupt/foreign value must never crash
  // the room — worst case we just don't restore anything.
  function krLoadLocalQuote() {
    try {
      if (!window.localStorage) return null;
      var raw = window.localStorage.getItem(LOCAL_QUOTE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.rail)) return null;
      return parsed;
    } catch (e) { return null; }
  }
  function krSaveLocalQuote(data) {
    try { if (window.localStorage) window.localStorage.setItem(LOCAL_QUOTE_KEY, JSON.stringify(data)); } catch (e) {}
  }
  function krClearLocalQuote() {
    try { if (window.localStorage) window.localStorage.removeItem(LOCAL_QUOTE_KEY); } catch (e) {}
  }
  // ?quote=<token> (or &quote=) from the current URL — the cross-device /
  // emailed-resume path. Plain regex (not URLSearchParams) for the widest
  // browser support, matching this file's no-deps/ES5 posture.
  function krQuoteTokenFromLocation() {
    try {
      var m = String(window.location.search || '').match(/[?&]quote=([^&]+)/);
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  // "Popular picks" — curated best-sellers (owner-named + rounded out with a
  // polo/cap/tote). No bestseller SIGNAL exists on the public endpoints (see
  // header note) — each code below is resolved LIVE via searchProducts at
  // open-time (never a hardcoded id/price/photo) and priced via the normal
  // hydrateCard() getQuote path, same as every other card.
  var POPULAR_STYLE_CODES = ['5050T', '5001', '5101', '5402', '1108', 'Carrie Tote'];
  // AS Colour Block Tubular Tee 5050T, Staple Tee 5001, Supply Hoodie 5101,
  // Chad Polo 5402, Stock Trucker Cap 1108, Carrie Tote (styleCode is "tote",
  // so the code "1001" substring-matched a Biz tee — use the title instead) —
  // each verified live (searchProducts + getQuote) before this shipped.

  // Category chips -> searchProducts filters.productType (exact match,
  // inStockOnly) — NOT free text. Confirmed live: filters.productType is
  // clean (no DISCOUNTED GARMENTS junk, no stray hi-vis bleeding into other
  // types); free-text "tee" is not (ranks by keyword match only, no
  // popularity/relevance signal). A category maps to >1 productType where
  // the real catalog splits it that way (Workwear, Bags) — searchByTypes()
  // fires one filtered call per type and merges+dedupes.
  // Order matches the club/school/event B2B framing used everywhere else on
  // the funnel (kits, cohorts, teams) — Tees/Hoodies/Polos first since
  // that's what clubs and schools actually order in bulk; Caps/Bags round
  // out the kit; Workwear (Corporate & Staff's hi-vis/site gear) trails
  // last rather than leading, since it's the smallest slice of this
  // funnel's audience, not the front door to it.
  var CATEGORIES = [
    { key: 'popular', label: 'Popular' },
    { key: 'tees', label: 'Tees', types: ['T-Shirt'] },
    { key: 'hoodies', label: 'Hoodies', types: ['Hoodie'] },
    { key: 'polos', label: 'Polos', types: ['Polo Shirt'] },
    { key: 'caps', label: 'Caps', types: ['Cap'] },
    { key: 'bags', label: 'Bags', types: ['Tote Bag', 'Bag', 'Cotton Bags'] },
    { key: 'workwear', label: 'Workwear', types: ['Hi Vis Tee', 'Hi Vis Polo', 'Hi Vis Vest', 'Workwear Shirt', 'Workwear Long Sleeve'] }
  ];
  function categoryByKey(key) {
    for (var i = 0; i < CATEGORIES.length; i++) { if (CATEGORIES[i].key === key) return CATEGORIES[i]; }
    return null;
  }
  function chipsHTML() {
    return CATEGORIES.map(function (c) {
      return '<button type="button" class="kr-chip' + (c.key === 'popular' ? ' kr-chip-on' : '') +
        '" data-cat="' + c.key + '">' + escapeHtml(c.label) + '</button>';
    }).join('');
  }

  // ===========================================================================
  // Motion primitives (§0). KR_RM gates every JS-DRIVEN animation (count-ups
  // jump straight to the target, FLIP-fly skips the flying clone, stagger
  // delays are skipped) — CSS-driven animations/transitions get their own
  // per-feature reduced-motion overrides in the stylesheet (see the RM block
  // near the end of CSS), not a blanket `*{animation:none}` kill, so state
  // (opacity/colour) still lands correctly without any movement.
  // ===========================================================================
  var KR_RM = false;
  try { KR_RM = !!(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches); } catch (e) {}

  // rAF easeOutCubic count-up for any number that changes (rail totals,
  // per-line prices). Stores the last rendered value on el._krVal so a
  // second call fired mid-animation (e.g. a fast requote) starts counting
  // from wherever the number visually is, not from a stale target — and so
  // the very first call on a fresh element counts up from 0 (a "reveal").
  function krAnimateNumber(el, to, dur, fmt) {
    if (!el) return;
    fmt = fmt || function (n) { return String(Math.round(n)); };
    var startVal = el._krVal != null ? el._krVal : 0;
    if (el._krRAF) { cancelAnimationFrame(el._krRAF); el._krRAF = null; }
    if (KR_RM || !dur || startVal === to) {
      el.textContent = fmt(to);
      el._krVal = to;
      return;
    }
    var start = null;
    function tick(ts) {
      if (start === null) start = ts;
      var p = Math.min(1, (ts - start) / dur);
      var eased = 1 - Math.pow(1 - p, 3);
      el.textContent = fmt(startVal + (to - startVal) * eased);
      if (p < 1) { el._krRAF = requestAnimationFrame(tick); }
      else { el._krVal = to; el.textContent = fmt(to); el._krRAF = null; }
    }
    el._krRAF = requestAnimationFrame(tick);
  }

  // Remove+reflow+re-add a class so its CSS animation restarts even if the
  // class was already present (e.g. re-triggering a "just landed" flash).
  // Skips entirely under KR_RM — nothing to restart if the class has no
  // motion under reduced motion anyway.
  function pulseClass(elm, cls) {
    if (!elm || KR_RM) return;
    elm.classList.remove(cls);
    void elm.offsetWidth; // force reflow
    elm.classList.add(cls);
  }

  // FLIP-style "fly": clone srcEl's current visual, pin it at srcEl's exact
  // screen rect via position:fixed, then transform+fade it to destEl's rect
  // on the next frame — transform/opacity only, no layout thrash. Purely
  // decorative: the caller must NEVER gate real state changes on this
  // finishing (it can no-op instantly under KR_RM or a zero-size rect, e.g.
  // an element not yet laid out — such as in a non-visual test DOM).
  function krFlipFly(srcEl, destEl, mountRoot) {
    if (KR_RM || !srcEl || !destEl || typeof srcEl.getBoundingClientRect !== 'function') return;
    var sr = srcEl.getBoundingClientRect();
    var dr = destEl.getBoundingClientRect();
    if (!sr || !dr || !sr.width || !sr.height) return; // nothing meaningful to animate
    var clone = srcEl.cloneNode(true);
    clone.className = 'kr-flip-clone';
    clone.setAttribute('aria-hidden', 'true');
    clone.style.position = 'fixed';
    clone.style.left = sr.left + 'px';
    clone.style.top = sr.top + 'px';
    clone.style.width = sr.width + 'px';
    clone.style.height = sr.height + 'px';
    clone.style.margin = '0';
    clone.style.borderRadius = '10px';
    (mountRoot || document.body).appendChild(clone);
    var dx = (dr.left + dr.width / 2) - (sr.left + sr.width / 2);
    var dy = (dr.top + dr.height / 2) - (sr.top + sr.height / 2);
    var scale = Math.max(0.15, Math.min(0.55, (dr.width || sr.width * 0.3) / sr.width));
    requestAnimationFrame(function () {
      requestAnimationFrame(function () {
        clone.style.transform = 'translate(' + dx + 'px,' + dy + 'px) scale(' + scale + ')';
        clone.style.opacity = '0';
      });
    });
    setTimeout(function () { if (clone.parentNode) clone.parentNode.removeChild(clone); }, 560);
  }

  // ===========================================================================
  // Scoped CSS — injected once, everything prefixed so nothing collides with
  // the live theme.
  // ===========================================================================
  var CSS = [
    '#kit-room-overlay{position:fixed;inset:0;z-index:2147483647;display:flex;',
    'align-items:center;justify-content:center;padding:20px;',
    'background:rgba(15,10,32,.6);backdrop-filter:blur(6px);-webkit-backdrop-filter:blur(6px);',
    'font-family:Inter,-apple-system,BlinkMacSystemFont,"Segoe UI",Roboto,system-ui,sans-serif;',
    '-webkit-font-smoothing:antialiased;',
    '--kr-cream:#faf7f2;--kr-purple:#2d1b4e;--kr-deep:#1e1145;--kr-deeper:#170c31;',
    '--kr-orange:#f97316;--kr-orange-d:#ea580c;--kr-yellow:#facc15;--kr-mint:#5eead4;',
    '--kr-ink:#0f172a;--kr-ink-2:#5b5468;',
    '--kr-muted:#6b6480;--kr-lav:#b9a9e0;--kr-hair:#ece7df;--kr-hair-d:rgba(255,255,255,.09);',
    '--kr-ease:cubic-bezier(.2,.7,.2,1);--kr-spring:cubic-bezier(.34,1.56,.64,1);',
    '--kr-spring-soft:cubic-bezier(.22,1.15,.36,1);--kr-exit:cubic-bezier(.4,0,1,1);',
    // Light-canvas shadows (surface inversion — the room canvas is light now,
    // so the old heavy dark-on-dark shadow values read muddy; the rail keeps
    // its own deeper shadow separately, --kr-e-rail, since it's the one
    // deliberately dark panel sitting on top of the light canvas).
    '--kr-e1:0 2px 8px rgba(20,15,35,.06);--kr-e2:0 10px 24px rgba(20,15,35,.10);',
    '--kr-e3:0 20px 48px rgba(20,15,35,.14);--kr-e-rail:0 20px 50px -10px rgba(10,5,25,.45);',
    // 8pt-ish spacing scale (--kr-N = N*4px) — canvas/card/rail padding+gaps
    // below reference these tokens rather than one-off px values.
    '--kr-1:4px;--kr-2:8px;--kr-3:12px;--kr-4:16px;--kr-5:20px;--kr-6:24px;--kr-7:28px;',
    '--kr-8:32px;--kr-9:36px;--kr-10:40px;--kr-11:44px;--kr-12:48px;--kr-16:64px}',
    '#kit-room-overlay,#kit-room-overlay *{box-sizing:border-box}',
    '#kit-room-overlay [hidden]{display:none!important}',
    // Theme-isolation reset. The Hyper theme force-styles buttons (display:flex,
    // white-space:nowrap), headings (uppercase, letter-spacing), and generic
    // div/span (margin, colour) which mangled the overlay — the entry doors in
    // particular broke into a horizontal-overflow row. Neutralise at the bare
    // element level; every specific kr- rule below is a class selector (higher
    // specificity than an id+element-list reset), so intentional uppercase/
    // letter-spacing/nowrap (.kr-kick, .kr-vend, .kr-rail-title, etc.) and every
    // rail number row still render exactly as designed.
    '#kit-room-overlay button{-webkit-appearance:none;appearance:none;background:none;border:0;margin:0;padding:0;font:inherit;color:inherit;text-align:left;text-transform:none;letter-spacing:normal;white-space:normal;line-height:normal;display:block;width:auto;height:auto;min-height:0;max-height:none;position:static;overflow:visible;float:none;cursor:pointer}',
    '#kit-room-overlay h1,#kit-room-overlay h2,#kit-room-overlay h3,#kit-room-overlay p,#kit-room-overlay span,#kit-room-overlay div{margin:0;font-family:inherit;color:inherit;text-transform:none;letter-spacing:normal;white-space:normal}',
    '#kit-room-overlay .kr-door{display:block;width:100%}',
    // SURFACE (owner-locked): Stripe-split INVERT — the shell/canvas is ONE
    // light warm surface throughout (entry AND room — the old .kr-in-room
    // dark-gradient takeover is gone entirely); the order rail is the ONE
    // deliberately dark panel (see the rail block below).
    // SIZE pass: grown from max-width:1240/88vh — on a typical laptop that
    // left a lot of dead backdrop margin. min(1600px,95vw)/min(94vh,1000px)
    // uses roughly 95% of the screen; the #kit-room-overlay{padding:20px}
    // backdrop inset means `width:100%` is already viewport-clamped on
    // mobile BEFORE max-width ever binds, and the @media(max-width:820px)
    // block below still fully overrides height to 92vh there — so this is a
    // desktop/laptop-only change, mobile is untouched.
    '#kit-room-overlay .kr-shell{width:100%;max-width:min(1600px,95vw);height:min(94vh,1000px);',
    'background:radial-gradient(120% 80% at 50% -10%,#fff,var(--kr-cream) 60%);',
    'border-radius:20px;box-shadow:var(--kr-e3);overflow:hidden;',
    'display:flex;flex-direction:column;color:var(--kr-ink);animation:kr-pop .35s var(--kr-ease) both}',
    // top bar (persistent across entry + room) — wordmark left, segmented
    // Chat|Browse toggle right, 44px close. No subtitle, no online dot.
    '#kit-room-overlay .kr-topbar{display:flex;align-items:center;gap:var(--kr-3);padding:var(--kr-4) var(--kr-5);',
    'border-bottom:1px solid rgba(15,10,32,.08);flex:0 0 auto}',
    '#kit-room-overlay .kr-orb{width:32px;height:32px;border-radius:50%;flex:0 0 auto;',
    'background:conic-gradient(from 210deg,#f97316,#facc15,#a78bfa,#f97316);position:relative;',
    'box-shadow:0 0 0 4px rgba(167,139,250,.16)}',
    '#kit-room-overlay .kr-orb::after{content:"";position:absolute;inset:7px;border-radius:50%;background:var(--kr-cream)}',
    // display:block is load-bearing: .kr-orbmini is an empty leaf div (pure
    // gradient swatch, no content) and the live theme ships a global
    // `div:empty{display:none}` rule — our #id+class selector outweighs that
    // bare `div:empty` on specificity, but only for properties we actually
    // declare, so `display` has to be set explicitly here or the theme rule
    // wins by default and the orb renders as nothing.
    '#kit-room-overlay .kr-orbmini{width:20px;height:20px;border-radius:50%;flex:0 0 auto;display:block;',
    'background:conic-gradient(from 210deg,#f97316,#facc15,#a78bfa,#f97316)}',
    // min-width:0 lets this shrink instead of overflowing the topbar row —
    // needed now it carries a two-line title+subtitle (see kr-who-sub) next
    // to the fixed-size orb/segmented-toggle/close on a narrow viewport.
    '#kit-room-overlay .kr-who{display:flex;flex-direction:column;justify-content:center;gap:1px;min-width:0}',
    // white-space:nowrap so the title itself can never wrap to 2-3 lines
    // when the row gets tight (small phones) — it should shrink/ellipsize
    // like the subtitle below it, never break mid-phrase.
    '#kit-room-overlay .kr-who b{font-weight:900;font-size:16px;letter-spacing:-.015em;',
    'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%;display:block}',
    // Value-prop subtitle — this is the TRUST line ("no sales call, no
    // commitment"), so it needs to register at a glance, not read as fine
    // print. PROMINENCE pass: bumped size + weight and swapped the lighter
    // --kr-muted for the darker --kr-ink-2, while staying visually
    // subordinate to the wordmark above it (still regular case, no colour
    // accent — that's reserved for the CTA). Truncates with an ellipsis
    // rather than ever forcing the topbar to overflow/wrap.
    '#kit-room-overlay .kr-who-sub{font-size:13.5px;font-weight:700;color:var(--kr-ink-2);',
    'white-space:nowrap;overflow:hidden;text-overflow:ellipsis;max-width:100%}',
    // segmented Chat|Browse toggle — purple-fill active (structure colour,
    // never orange; orange stays reserved for the primary CTA + direct hovers)
    '#kit-room-overlay .kr-seg{margin-left:auto;display:flex;gap:2px;background:rgba(15,10,32,.06);',
    'padding:3px;border-radius:11px;flex:0 0 auto}',
    '#kit-room-overlay .kr-seg button{border:0;background:transparent;color:var(--kr-ink-2);font-weight:800;',
    'font-size:12.5px;padding:8px 14px;border-radius:8px;min-height:38px;display:flex;align-items:center;',
    'justify-content:center;transition:.15s;position:relative}',
    // Topbar is already tight on mobile (see the 390px note below) so this
    // grows the TAP area only (vertical ::before, left/right:0 keeps the
    // 2px Chat/Browse gap intact) rather than the visible 38px pill height.
    '#kit-room-overlay .kr-seg button::before{content:"";position:absolute;top:-3px;bottom:-3px;left:0;right:0}',
    '#kit-room-overlay .kr-seg button.kr-seg-on{background:var(--kr-purple);color:#fff}',
    '#kit-room-overlay .kr-seg button:not(.kr-seg-on):hover{background:rgba(45,27,78,.08)}',
    '#kit-room-overlay .kr-close{flex:0 0 auto;margin-left:var(--kr-2);width:44px;height:44px;border-radius:11px;border:0;',
    'background:rgba(15,10,32,.06);color:var(--kr-muted);display:grid;place-items:center;transition:.15s}',
    '#kit-room-overlay .kr-close:hover{background:var(--kr-orange);color:#fff}',
    '#kit-room-overlay .kr-close svg{width:16px;height:16px}',
    '#kit-room-overlay .kr-body{flex:1 1 auto;min-height:0;display:flex;flex-direction:column}',
    // entry (two doors)
    '#kit-room-overlay .kr-entry{flex:1 1 auto;min-height:0;overflow-y:auto;',
    'display:flex;align-items:center;justify-content:center;padding:28px}',
    '#kit-room-overlay .kr-entry-inner{width:100%;max-width:820px;text-align:center;animation:kr-rise .4s var(--kr-ease) both}',
    '#kit-room-overlay .kr-kick{font-size:12.5px;font-weight:800;letter-spacing:.16em;text-transform:uppercase;color:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-h1{font-weight:900;letter-spacing:-.035em;line-height:1.02;font-size:clamp(28px,5vw,48px);margin:12px 0 8px}',
    '#kit-room-overlay .kr-h1 em{font-style:normal;color:var(--kr-orange)}',
    '#kit-room-overlay .kr-entry-sub{font-size:15px;color:var(--kr-muted);max-width:520px;margin:0 auto 28px;line-height:1.5}',
    // FIX #16 — bad/expired/deleted resume-link notice, shown on the entry
    // screen when a ?quote= token fails to resolve. Same red/orange error
    // tint as the chat's .kr-cbub-err so the two surfaces (entry vs
    // already-in-chat) read as the same "something's wrong" language.
    '#kit-room-overlay .kr-resume-fail{max-width:520px;margin:0 auto 22px;padding:14px 18px;border-radius:14px;',
    'background:rgba(234,88,12,.08);border:1px solid rgba(234,88,12,.22);text-align:left}',
    '#kit-room-overlay .kr-resume-fail:focus{outline:2px solid var(--kr-orange);outline-offset:2px}',
    '#kit-room-overlay .kr-resume-fail b{display:block;font-size:14px;color:var(--kr-orange-d);margin-bottom:4px}',
    '#kit-room-overlay .kr-resume-fail span{display:block;font-size:13px;color:var(--kr-ink-2);line-height:1.5}',
    '#kit-room-overlay .kr-resume-fail a{color:var(--kr-purple);font-weight:700;text-decoration:underline}',
    '#kit-room-overlay .kr-doors{display:grid;grid-template-columns:1fr 1fr;gap:16px;text-align:left}',
    '#kit-room-overlay .kr-door{background:#fff;border:1px solid var(--kr-hair);border-radius:20px;',
    'padding:24px 22px;cursor:pointer;transition:transform .2s var(--kr-ease),box-shadow .2s,border-color .2s;',
    'min-height:44px}',
    '#kit-room-overlay .kr-door:hover,#kit-room-overlay .kr-door:focus-visible{transform:translateY(-4px);',
    'box-shadow:0 22px 46px rgba(45,27,78,.14);border-color:#dfd7ea}',
    '#kit-room-overlay .kr-door .kr-dic{width:46px;height:46px;border-radius:13px;display:grid;place-items:center;margin-bottom:14px}',
    '#kit-room-overlay .kr-door .kr-dic svg{width:22px;height:22px}',
    '#kit-room-overlay .kr-door.kr-browse .kr-dic{background:rgba(249,115,22,.12);color:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-door.kr-describe .kr-dic{background:var(--kr-deep)}',
    '#kit-room-overlay .kr-door h2{font-weight:900;font-size:19px;letter-spacing:-.02em;margin-bottom:5px}',
    '#kit-room-overlay .kr-door p{font-size:13.5px;color:var(--kr-muted);line-height:1.5;margin-bottom:14px}',
    '#kit-room-overlay .kr-door .kr-go{font-weight:800;font-size:13px;color:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-foot{margin-top:20px;font-size:12px;color:var(--kr-muted)}',
    // room (browse canvas)
    '#kit-room-overlay .kr-room{flex:1 1 auto;min-height:0;display:flex;flex-direction:column;position:relative}',
    '#kit-room-overlay .kr-room-body{flex:1 1 auto;min-height:0;display:flex}',
    '#kit-room-overlay .kr-canvas-col{flex:1 1 auto;min-height:0;display:flex;flex-direction:column;min-width:0;position:relative}',
    '#kit-room-overlay .kr-cats{display:flex;gap:var(--kr-2);padding:var(--kr-5) var(--kr-7) var(--kr-3);flex:0 0 auto;flex-wrap:wrap}',
    // Light-canvas chips — resting state is a quiet outline; the ACTIVE chip
    // is the purple-fill/white-text moment (colour discipline: purple =
    // structure/active state, orange stays reserved for the primary CTA).
    '#kit-room-overlay .kr-chip{border:1px solid var(--kr-hair);background:#fff;',
    'color:var(--kr-ink-2);font-weight:800;font-size:13px;padding:9px 16px;border-radius:999px;min-height:44px;',
    'display:inline-flex;align-items:center}',
    '#kit-room-overlay .kr-chip.kr-chip-on{background:var(--kr-purple);border-color:var(--kr-purple);color:#fff}',
    // SIZE pass: side/bottom padding grown a step (kr-7/kr-8 -> kr-8/kr-9) to
    // use the wider shell as real breathing room, not just passive stretch.
    '#kit-room-overlay .kr-canvas{flex:1 1 auto;min-height:0;overflow-y:auto;padding:var(--kr-2) var(--kr-8) var(--kr-9)}',
    '#kit-room-overlay .kr-ghead{font-weight:900;letter-spacing:-.02em;font-size:21px;margin:var(--kr-2) 0 2px}',
    '#kit-room-overlay .kr-gsub{font-size:13px;color:var(--kr-muted);margin-bottom:var(--kr-5)}',
    // SIZE pass: gap widened (kr-5->kr-6) — 3 columns still, but each one now
    // gets more of the shell's extra width via the existing `1fr` tracks, so
    // the garment photo itself reads bigger, not just a wider empty shell.
    '#kit-room-overlay .kr-grid{display:grid;grid-template-columns:repeat(3,minmax(220px,1fr));gap:var(--kr-6)}',
    // cards — light surface throughout; the deliberate "moment" of colour on
    // a card is the purple Add button, never the card chrome itself.
    '#kit-room-overlay .kr-card{background:#fff;border-radius:16px;overflow:hidden;color:var(--kr-ink);',
    'border:2px solid transparent;display:flex;flex-direction:column;box-shadow:var(--kr-e1);',
    'animation:kr-rise .35s var(--kr-ease) both}',
    // image floats on a neutral ground with breathing room, never crops
    // (object-fit:contain) — garments read as products, not lifestyle crops.
    // CARD STREAMLINE pass: padding trimmed from kr-3(12px)->kr-2(8px) so the
    // garment itself reads bigger — less dead ground, same never-crop contain.
    '#kit-room-overlay .kr-thumb{aspect-ratio:1/1;position:relative;display:grid;place-items:center;',
    'background:#f4f1ec;overflow:hidden;padding:var(--kr-2)}',
    '#kit-room-overlay .kr-thumb img{width:100%;height:100%;object-fit:contain}',
    '#kit-room-overlay .kr-thumb svg{width:46%;height:46%;opacity:.8}',
    // swatch row — moved OFF the thumbnail, sits under the product name
    // (see kr-pb below). --kr-sw-ph is the instant grey placeholder dot
    // (colour COUNT only, from product.js options) shown before the
    // background getPreviewConfig call lazy-hydrates real hex. CARD
    // STREAMLINE pass: dots shrunk + softened (hairline shadow, slight
    // opacity) so the swatch row reads as a quiet detail, not a second
    // focal point — count/range is still fully legible, just lighter.
    '#kit-room-overlay .kr-sw{display:flex;align-items:center;gap:4px;margin:2px 0;min-height:12px}',
    '#kit-room-overlay .kr-sw i{width:11px;height:11px;border-radius:50%;box-shadow:0 0 0 1px rgba(15,10,32,.08);display:block;opacity:.9}',
    '#kit-room-overlay .kr-sw i.kr-sw-ph{background:#e7e2d6}',
    '#kit-room-overlay .kr-sw-more{font-size:9.5px;font-weight:700;color:var(--kr-muted);margin-left:1px;opacity:.8}',
    '#kit-room-overlay .kr-pb{padding:var(--kr-4);display:flex;flex-direction:column;gap:2px}',
    '#kit-room-overlay .kr-vend{font-size:9.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;color:var(--kr-muted);',
    'display:flex;flex-wrap:wrap;align-items:baseline;column-gap:3px}',
    '#kit-room-overlay .kr-nm{font-weight:800;font-size:13.5px;letter-spacing:-.01em;line-height:1.2}',
    // Secondary spec (GSM &middot; Fit) folded INLINE into the vendor eyebrow
    // (CARD STREAMLINE pass) — proper case + lighter weight so it never
    // competes with the vendor label it now rides alongside; no longer its
    // own line. Deliberately un-inherits .kr-vend's uppercase/bold/tracking.
    '#kit-room-overlay .kr-spec{font-weight:600;letter-spacing:0;text-transform:none;opacity:.72}',
    // price hierarchy: the number is the hero (20/800); the qty/GST context
    // is demoted onto its own quieter line underneath, not crowded inline.
    '#kit-room-overlay .kr-prc{margin-top:4px}',
    '#kit-room-overlay .kr-prc b{font-size:20px;color:var(--kr-ink);font-weight:800;letter-spacing:-.01em;display:block}',
    '#kit-room-overlay .kr-prc .kr-prc-ctx{font-size:11px;color:var(--kr-muted);font-weight:600;display:block;margin-top:1px}',
    '#kit-room-overlay .kr-acts{display:flex;flex-direction:column;gap:6px;margin-top:11px}',
    // Configure drawer trigger — wraps thumb+name+price (everything except
    // Add/Compare below) in a REAL button so the whole visible card is both
    // click- and keyboard-activatable, without nesting interactive controls
    // inside a single ARIA role. Button reset already strips default chrome.
    '#kit-room-overlay .kr-card-hit{display:block;width:100%;text-align:left;cursor:pointer;border-radius:0}',
    '#kit-room-overlay .kr-card-hit:disabled{cursor:default}',
    '#kit-room-overlay .kr-card-hit:focus-visible{outline:2px solid var(--kr-purple);outline-offset:-2px;border-radius:14px}',
    // display:flex/inline-flex + centering on every button below is REQUIRED
    // after the theme-isolation reset (button{display:block;text-align:left})
    // — these previously relied on the browser's default centered <button>
    // text, which the reset intentionally removes theme-wide.
    // ONE full-width primary Add — deep-purple (never orange; orange is
    // reserved for the rail's "Start your order" moment). Compare below it
    // is a quiet tertiary text button, not a paired equal-weight action.
    '#kit-room-overlay .kr-add{width:100%;display:flex;align-items:center;justify-content:center;',
    'border:0;background:var(--kr-deep);color:#fff;font-weight:800;',
    'font-size:12.5px;padding:11px 8px;border-radius:9px;min-height:44px;transition:.15s}',
    // hover deepens toward the brand purple, never orange — orange stays a
    // one-place-only signal (see colour discipline note above the rail CTAs).
    '#kit-room-overlay .kr-add:hover{background:var(--kr-purple)}',
    '#kit-room-overlay .kr-add:disabled{background:#e7e0d5;color:#b3a99a;cursor:default}',
    '#kit-room-overlay .kr-cmp:disabled{color:#c9c2d0;cursor:default}',
    // Compare — a quiet tertiary TEXT button, not a paired action: no border/
    // fill at rest, just weight + colour; underline on hover/focus.
    '#kit-room-overlay .kr-cmp{display:inline-flex;align-items:center;justify-content:center;gap:5px;',
    'border:0;background:transparent;color:var(--kr-ink-2);position:relative;',
    'font-weight:800;font-size:12px;padding:6px 4px;border-radius:7px;min-height:32px}',
    // Tap-area-only expansion (vertical, left/right:0) up to 44px — .kr-cmp
    // sits paired with .kr-add in a tight row (.kr-acts), so widening the
    // visible pill would crowd/overlap that sibling; the invisible ::before
    // grows the hit area without touching the text-button's own footprint.
    '#kit-room-overlay .kr-cmp::before{content:"";position:absolute;top:-6px;bottom:-6px;left:0;right:0}',
    '#kit-room-overlay .kr-cmp:hover{color:var(--kr-purple);text-decoration:underline}',
    // Calm "added" state — purple border (reserving the base 2px transparent
    // border above from .kr-card so this never shifts layout) + tinted wash,
    // NOT a hard orange outline. The visible confirmation is the ✓ chip.
    '#kit-room-overlay .kr-card.kr-added{border-color:var(--kr-purple);background:rgba(45,27,78,.035)}',
    '#kit-room-overlay .kr-added-chip{position:absolute;top:var(--kr-2);left:var(--kr-2);display:flex;',
    'align-items:center;justify-content:center;width:22px;height:22px;border-radius:50%;',
    'background:var(--kr-purple);color:#fff;font-size:12px;font-weight:900;box-shadow:var(--kr-e1)}',
    // ===========================================================================
    // Configure ("Configure your line") — item 1 (2026-08 unify): ONE wide,
    // centered modal shell on desktop, shared by BOTH Configure surfaces
    // (Browse's drawer via handleOpenConfigure, Chat's inline card via
    // openChatConfigCard both render the SAME buildLineConfigUI() 3-column
    // grid — see .kr-lcu-grid below). This block is Shell A's (the drawer)
    // OWN chrome (backdrop + centered panel) — bottom-sheet <=820px, see the
    // shared responsive block below. Siblings of .kr-shell (not nested
    // inside it) so overflow:hidden/any transform on the shell can never
    // clip or reposition a position:fixed panel — see shellHTML().
    // ===========================================================================
    '#kit-room-overlay .kr-cfg-backdrop{position:fixed;inset:0;background:rgba(15,10,32,.55);',
    'opacity:0;transition:opacity .28s var(--kr-ease);z-index:40}',
    '#kit-room-overlay .kr-cfg-backdrop.kr-show{opacity:1}',
    // Centered overlay, not a right-edge drawer — width:min(960px,92vw) so a
    // 3-column layout (col1 ~32% + 2 content columns) actually has room to
    // breathe instead of stacking cramped in a 420px sliver. max-height:88vh
    // (same cap the old drawer + the mobile sheet both already used) + its
    // own internal flex column so .kr-cfg-body below can scroll ONLY where
    // buildLineConfigUI's own grid needs it (col2/col3), never the whole panel.
    // Centered via inset:0 + margin:auto — NOT transform:translate(-50%,-50%)
    // — deliberately, so the shared RM override further down (which forces
    // transform:none!important on this same class, to kill the entrance
    // animation) can never break the panel's actual POSITION. A translate-
    // based center would collapse to its unset top-left corner (off-screen,
    // bottom-right of true-center) the instant that override fires. Only
    // `transform:scale()` — pure animation, never load-bearing for layout —
    // is used below; RM cancelling it just skips the entrance zoom, the
    // panel is still exactly centered either way.
    '#kit-room-overlay .kr-cfg-panel{position:fixed;inset:0;margin:auto;',
    'width:min(960px,92vw);max-height:88vh;transform:scale(.97);opacity:0;',
    'background:#fff;border-radius:20px;box-shadow:0 30px 80px rgba(10,5,25,.35);z-index:41;',
    'display:flex;flex-direction:column;transition:transform .28s var(--kr-ease),opacity .22s var(--kr-ease);outline:none}',
    '#kit-room-overlay .kr-cfg-panel.kr-show{transform:scale(1);opacity:1}',
    '#kit-room-overlay .kr-cfg-close{position:absolute;top:var(--kr-4);right:var(--kr-4);width:44px;height:44px;',
    'border-radius:10px;background:rgba(255,255,255,.85);color:var(--kr-muted);display:grid;place-items:center;',
    'z-index:2;box-shadow:var(--kr-e1)}',
    '#kit-room-overlay .kr-cfg-close:hover{background:var(--kr-orange);color:#fff}',
    '#kit-room-overlay .kr-cfg-close svg{width:16px;height:16px}',
    '#kit-room-overlay .kr-cfg-body{display:flex;flex-direction:column;min-height:0;flex:1 1 auto;overflow:hidden}',
    // SQUARE, uncropped — matches the grid card's own .kr-thumb treatment
    // exactly (aspect-ratio:1/1, contain, breathing-room padding) rather
    // than the landscape 4/3 this started as, which squeezed a portrait
    // garment photo down small. Same padding token as .kr-thumb too.
    '#kit-room-overlay .kr-cfg-img{position:relative;flex:0 0 auto;aspect-ratio:1/1;background:var(--kr-cream);',
    'display:grid;place-items:center;overflow:hidden;padding:var(--kr-2)}',
    '#kit-room-overlay .kr-cfg-img img{width:100%;height:100%;object-fit:contain}',
    '#kit-room-overlay .kr-cfg-img svg{width:30%;height:30%;opacity:.8}',
    // FEATURE 2 — live logo mockup. .kr-cfg-imgwrap becomes the positioning
    // context for the overlay layer (it already renders exactly the same
    // size as the garment <img> above it — confirmed live, square masters,
    // no object-fit:contain letterboxing — see artworkOverlayHtml() in JS).
    // pointer-events:none top-to-bottom so the overlay layer never
    // intercepts the front/back toggle or a card-level click.
    // NO background/border/box-shadow "patch" here — buildArtworkPreviewBlob
    // now outputs PNG (not a white-flattened JPEG), so a transparent logo's
    // alpha survives straight through and this <img> sits directly on the
    // garment photo, nothing behind it. A prior version rendered a white
    // rectangle behind every overlay to hide the old JPEG's opaque white
    // background — on a black tee that read as a sticker/label, not a print;
    // removed along with the JPEG flatten it was compensating for. The only
    // effect left is a whisper-subtle drop-shadow (NOT a box-shadow — that
    // draws a shape around the image's rectangular bounds, which is exactly
    // the "white box" look this is fixing; drop-shadow follows the actual
    // non-transparent pixels) so a light logo doesn't optically vanish flat
    // against a light garment — reads as "printed", not "floating cutout".
    // An opaque (JPEG-sourced, or genuinely opaque PNG) upload still shows
    // its own real background here — that's the honest rendering of the
    // customer's own file, not something to fake-remove (see the tip in
    // artworkSlotHtml for the transparent-PNG nudge instead).
    '#kit-room-overlay .kr-cfg-imgwrap{position:relative}',
    '#kit-room-overlay .kr-cfg-artwork-overlays{position:absolute;inset:0;pointer-events:none}',
    '#kit-room-overlay .kr-cfg-artwork-overlay-img{position:absolute;object-fit:contain;pointer-events:none;',
    'filter:drop-shadow(0 1px 3px rgba(0,0,0,.35))}',
    '#kit-room-overlay .kr-cfg-imgtoggle{position:absolute;left:var(--kr-4);bottom:var(--kr-4);display:flex;gap:2px;',
    'background:rgba(255,255,255,.92);padding:3px;border-radius:9px;box-shadow:var(--kr-e1)}',
    '#kit-room-overlay .kr-cfg-imgtab{padding:6px 12px;border-radius:6px;font-weight:800;font-size:12px;',
    'color:var(--kr-ink-2);min-height:0;position:relative}',
    // ~27px tall visually (front/back pill sits right on the image, no room
    // to grow it without looking heavy) — expand the tap area vertically
    // only via ::before; left/right stay at 0 so it never encroaches on the
    // sibling Front/Back tab across their 2px gap.
    '#kit-room-overlay .kr-cfg-imgtab::before{content:"";position:absolute;top:-9px;bottom:-9px;left:0;right:0}',
    '#kit-room-overlay .kr-cfg-imgtab.kr-cfg-imgtab-on{background:var(--kr-purple);color:#fff}',
    // Base (mobile/stacked, <=820px or a narrow chat card) padding for col2/
    // col3's content — the desktop 3-column media query above narrows this
    // to top/bottom only (var(--kr-6) column-gap already separates columns
    // side-to-side there). col1's image stays edge-to-edge/unpadded in both
    // cases, matching the original single-scroll layout's own image-outside-
    // padding treatment.
    '#kit-room-overlay .kr-lcu-col2,#kit-room-overlay .kr-lcu-col3{padding:var(--kr-5)}',
    '#kit-room-overlay .kr-cfg-title{font-size:10.5px;font-weight:800;letter-spacing:.08em;text-transform:uppercase;',
    'color:var(--kr-muted)}',
    '#kit-room-overlay .kr-cfg-nm{font-weight:900;font-size:17px;letter-spacing:-.015em;margin:2px 0 var(--kr-5)}',
    '#kit-room-overlay .kr-cfg-section{margin-bottom:var(--kr-6)}',
    '#kit-room-overlay .kr-cfg-h{font-weight:800;font-size:13px;margin-bottom:var(--kr-2)}',
    '#kit-room-overlay .kr-cfg-opt{font-weight:600;color:var(--kr-muted);text-transform:none;letter-spacing:normal;font-size:11.5px}',
    '#kit-room-overlay .kr-cfg-loading{font-size:12.5px;color:var(--kr-muted)}',
    '#kit-room-overlay .kr-cfg-swatches{display:flex;flex-wrap:wrap;gap:8px}',
    '#kit-room-overlay .kr-cfg-sw{width:32px;height:32px;border-radius:50%;padding:2px;border:2px solid transparent;',
    'min-height:0;position:relative}',
    // 44px touch target without growing the visual dot — an invisible ::before
    // extends the actual hit area 6px past each edge (32px visual -> 44px tap),
    // same trick used below for the other under-44 controls.
    '#kit-room-overlay .kr-cfg-sw::before{content:"";position:absolute;inset:-6px}',
    '#kit-room-overlay .kr-cfg-sw span{display:block;width:100%;height:100%;border-radius:50%;background:var(--sw);',
    'box-shadow:0 0 0 1px rgba(15,10,32,.14) inset}',
    '#kit-room-overlay .kr-cfg-sw.kr-cfg-sw-on{border-color:var(--kr-purple)}',
    '#kit-room-overlay .kr-cfg-sw:disabled{opacity:.35;cursor:not-allowed}',
    // Live stock hints (item 3) — 'low' gets an amber ring (still tappable,
    // just a hint); 'unknown' (stock genuinely not resolvable from the
    // preview CF) gets a dashed ring — NEVER styled as confidently in-stock.
    // 'out' reuses the existing disabled/opacity treatment above; its own
    // amber ring would be pointless once opacity:.35 + disabled already read
    // as unavailable.
    '#kit-room-overlay .kr-cfg-sw-stock-low{border-color:#f59e0b}',
    '#kit-room-overlay .kr-cfg-sw-stock-low.kr-cfg-sw-on{border-color:var(--kr-purple);box-shadow:0 0 0 2px #f59e0b}',
    '#kit-room-overlay .kr-cfg-sw-stock-unknown{border-style:dashed;border-color:#c9c2b6}',
    '#kit-room-overlay .kr-cfg-stock-legend{font-size:10.5px;color:var(--kr-muted);font-weight:700;margin-top:8px}',
    '#kit-room-overlay .kr-cfg-chips{display:flex;flex-wrap:wrap;gap:8px}',
    '#kit-room-overlay .kr-cfg-chip{border:1px solid var(--kr-hair);background:#fff;border-radius:12px;',
    'padding:8px 12px;min-height:44px;display:flex;flex-direction:column;align-items:flex-start;',
    'justify-content:center;gap:1px;transition:.15s}',
    '#kit-room-overlay .kr-cfg-chip-lbl{font-weight:800;font-size:12.5px}',
    '#kit-room-overlay .kr-cfg-chip-delta{font-size:10.5px;color:var(--kr-muted);font-weight:700}',
    '#kit-room-overlay .kr-cfg-chip.kr-cfg-chip-on{background:var(--kr-purple);border-color:var(--kr-purple)}',
    '#kit-room-overlay .kr-cfg-chip.kr-cfg-chip-on .kr-cfg-chip-lbl,',
    '#kit-room-overlay .kr-cfg-chip.kr-cfg-chip-on .kr-cfg-chip-delta{color:#fff}',
    '@keyframes kr-shake{25%{transform:translateX(-4px)}75%{transform:translateX(4px)}}',
    '#kit-room-overlay .kr-cfg-warn{font-size:11.5px;color:#b45309;font-weight:700;margin-top:8px}',
    // Item 5 — the qty basis the placement deltas are computed at, right
    // next to the chips themselves (the footer already states it further
    // down; this closes the gap so the deltas never read as "computed at
    // qty 0" when the size run is still empty).
    '#kit-room-overlay .kr-cfg-chips-basis{font-weight:600;color:var(--kr-muted);text-transform:none;',
    'letter-spacing:normal;font-size:11px}',
    // Item 4 — compact bulk savings ladder, same tier breakpoints as
    // KR_TIERS/the Board (10/20/50/100/200/400), always cent-exact with the
    // footer since both call computeClientQuote().
    '#kit-room-overlay .kr-cfg-ladder-row{display:flex;flex-wrap:wrap;gap:6px}',
    '#kit-room-overlay .kr-cfg-ladder-step{flex:1 1 60px;min-width:60px;text-align:center;background:var(--kr-cream);',
    'border:1px solid var(--kr-hair);border-radius:10px;padding:8px 4px;transition:.15s}',
    '#kit-room-overlay .kr-cfg-ladder-step-on{border-color:var(--kr-purple);background:rgba(45,27,78,.06)}',
    '#kit-room-overlay .kr-cfg-ladder-step b{display:block;font-size:13.5px;font-weight:900;color:var(--kr-ink)}',
    '#kit-room-overlay .kr-cfg-ladder-step-on b{color:var(--kr-purple)}',
    '#kit-room-overlay .kr-cfg-ladder-step span{font-size:10px;color:var(--kr-muted);font-weight:700}',
    // Item 5 — smart size-run presets, one-click ratios above the size-chip
    // grid itself. Quiet outline pills (same tertiary weight as .kr-cfg-chip
    // at rest, purple on hover — never orange, which stays reserved for
    // direct-action CTAs elsewhere in this file) — a preset PREFILLS the
    // grid below, it's still fully editable afterwards, never a commitment.
    '#kit-room-overlay .kr-cfg-size-presets{display:flex;flex-wrap:wrap;gap:6px;margin-bottom:10px}',
    '#kit-room-overlay .kr-cfg-preset-chip{border:1px solid var(--kr-hair);background:#fff;color:var(--kr-ink-2);',
    'font-weight:700;font-size:11.5px;padding:7px 11px;border-radius:999px;min-height:32px}',
    '#kit-room-overlay .kr-cfg-preset-chip:hover{border-color:var(--kr-purple);color:var(--kr-purple)}',
    '#kit-room-overlay .kr-cfg-sizes{display:flex;flex-wrap:wrap;gap:8px}',
    '#kit-room-overlay .kr-cfg-size-total{font-size:12px;color:var(--kr-muted);font-weight:700;margin-top:8px}',
    // flex:0 0 auto (mobile) / its own grid row (desktop, see .kr-lcu-grid
    // above) — the "sticky price/Confirm foot" itself: it's OUTSIDE
    // .kr-lcu-mid's scrollable area at every breakpoint, so it never scrolls
    // away — Confirm is always reachable with zero scrolling required.
    '#kit-room-overlay .kr-cfg-foot{flex:0 0 auto;',
    'padding:var(--kr-4) var(--kr-5);border-top:1px solid var(--kr-hair);',
    'display:flex;align-items:center;justify-content:space-between;gap:var(--kr-3);background:#fff}',
    '#kit-room-overlay .kr-cfg-price b{font-size:18px;font-weight:900;display:block}',
    '#kit-room-overlay .kr-cfg-price span{font-size:11px;color:var(--kr-muted);font-weight:600}',
    '#kit-room-overlay .kr-cfg-confirm{background:var(--kr-purple);color:#fff;font-weight:800;font-size:13.5px;',
    'padding:12px 18px;border-radius:11px;min-height:44px;white-space:nowrap;flex:0 0 auto}',
    '#kit-room-overlay .kr-cfg-confirm:disabled{opacity:.45;cursor:not-allowed}',
    // Ari-voiced intro line (drawer shell only — the chat shell's intro is
    // its own separate bubble above the card instead) + the artwork seam —
    // a labelled next-step hook, honestly disabled-reading but still
    // clickable so the shopper gets an explanation, never a dead click and
    // never a fake "uploaded" success (the real R2 upload ships separately).
    '#kit-room-overlay .kr-cfg-intro{font-size:12.5px;font-weight:600;color:var(--kr-ink-2);line-height:1.45;',
    'margin-bottom:var(--kr-4)}',
    '#kit-room-overlay .kr-cfg-artwork{display:inline-flex;align-items:center;gap:6px;',
    'border:1px dashed var(--kr-hair);background:transparent;color:var(--kr-muted);font-weight:700;',
    'font-size:12px;padding:9px 12px;border-radius:10px;min-height:40px}',
    '#kit-room-overlay .kr-cfg-artwork svg{width:14px;height:14px}',
    '#kit-room-overlay .kr-cfg-artwork:hover{border-color:var(--kr-lav);color:var(--kr-purple)}',
    // Real per-line artwork upload zone (replaces the old "coming soon"
    // stub above — that class stays in case anything else references it,
    // this is a new sibling set for the live drop/click zone).
    '#kit-room-overlay .kr-cfg-artwork-zone{display:flex;flex-direction:column;align-items:center;',
    'justify-content:center;gap:6px;border:1.5px dashed var(--kr-hair);border-radius:12px;padding:16px 12px;',
    'cursor:pointer;color:var(--kr-muted);font-size:12px;font-weight:700;text-align:center;min-height:74px}',
    '#kit-room-overlay .kr-cfg-artwork-zone svg{width:20px;height:20px}',
    '#kit-room-overlay .kr-cfg-artwork-zone:hover,#kit-room-overlay .kr-cfg-artwork-zone.kr-cfg-artwork-drag{',
    'border-color:var(--kr-purple);color:var(--kr-purple);background:rgba(45,27,78,.03)}',
    '#kit-room-overlay .kr-cfg-artwork-busy{cursor:wait;opacity:.7}',
    '#kit-room-overlay .kr-cfg-artwork-attached{display:flex;align-items:center;gap:10px;padding:8px;',
    'border:1px solid var(--kr-hair);border-radius:12px;background:#fff}',
    '#kit-room-overlay .kr-cfg-artwork-attached img{width:44px;height:44px;border-radius:8px;object-fit:cover;flex:0 0 auto}',
    '#kit-room-overlay .kr-cfg-artwork-name{flex:1 1 auto;min-width:0;font-size:12px;font-weight:700;',
    'color:var(--kr-ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '#kit-room-overlay .kr-cfg-artwork-remove{flex:0 0 auto;width:32px;height:32px;border-radius:8px;border:0;',
    'background:transparent;color:var(--kr-muted);display:inline-flex;align-items:center;justify-content:center}',
    '#kit-room-overlay .kr-cfg-artwork-remove svg{width:12px;height:12px}',
    '#kit-room-overlay .kr-cfg-artwork-remove:hover{background:rgba(234,88,12,.08);color:var(--kr-orange)}',
    '#kit-room-overlay .kr-cfg-artwork-err{margin-top:6px;font-size:11.5px;font-weight:700;color:#b91c1c}',
    // Honesty-guard nudge (artworkSlotHtml) — same slot, quieter tone than
    // the error above (not a problem, just a tip), only shown for a source
    // type that can't carry transparency (krMayHaveTransparency).
    '#kit-room-overlay .kr-cfg-artwork-tip{margin-top:6px;font-size:11.5px;font-weight:600;color:var(--kr-muted)}',
    // Item 4 — one labelled slot PER print-placement side (front/back/
    // sleeves independently) — a thin label above the SAME zone/attached/
    // error markup each slot already had when there was only ever one.
    '#kit-room-overlay .kr-cfg-artwork-slot{margin-bottom:10px}',
    '#kit-room-overlay .kr-cfg-artwork-slot:last-child{margin-bottom:0}',
    '#kit-room-overlay .kr-cfg-artwork-slot-lbl{font-weight:700;font-size:11.5px;color:var(--kr-ink-2);margin-bottom:5px}',
    // buildLineConfigUI()'s root — the shared engine's own flex column,
    // filling whichever host it's mounted into (the drawer's .kr-cfg-body,
    // or a chat card's .kr-lcu-card wrapper below).
    '#kit-room-overlay .kr-lcu{display:flex;flex-direction:column;flex:1 1 auto;min-height:0}',
    // Chat host (Shell B) — the SAME .kr-lcu engine inside a .kr-form-card.
    // .kr-form-card's own padding/border-radius is zeroed/reused here so the
    // engine's own section padding + square image's rounded-corner clip
    // read identically to the drawer, just inline in the thread instead of
    // a fixed-height panel (no internal scroll host — the card just grows
    // naturally with the thread, unlike the drawer's fixed 88vh/desktop
    // height — see .kr-lcu-card overrides in the media block below).
    '#kit-room-overlay .kr-form-card.kr-lcu-card{padding:0;overflow:hidden}',
    // ===========================================================================
    // Item 1 (2026-08 unify) — buildLineConfigUI()'s render() now emits ONE
    // shared 3-column grid (col1 image+toggle ~32% | col2 colour+placement+
    // ladder | col3 size-run+artwork, with the price/Confirm foot as its OWN
    // item under col3) used by BOTH shells — the drawer (now a centered
    // ~960px modal, see .kr-cfg-panel above) and the chat card (.kr-lcu-card,
    // inline in the thread) — so Browse and Chat read as one consistent
    // premium surface instead of two different shapes.
    //
    // .kr-lcu-mid wraps col2+col3 (see render()'s innerHTML) for ONE reason:
    // at <=820px they collapse into a SINGLE scrolling region — same as the
    // OLD one-piece .kr-cfg-scroll this replaces — with col1 (image) and the
    // foot both OUTSIDE it, always visible, zero scrolling required to reach
    // Confirm no matter how long col2/col3's content runs. At >=821px
    // .kr-lcu-mid becomes display:contents (see media block below) — it
    // disappears from layout entirely, so col1/col2/col3/foot become FOUR
    // direct grid items and col2/col3 split back into independently
    // scrolling real columns, foot pinned under col3 alone via grid
    // placement (never a full-width bar).
    // ===========================================================================
    '#kit-room-overlay .kr-lcu-grid{display:flex;flex-direction:column;flex:1 1 auto;min-height:0}',
    '#kit-room-overlay .kr-lcu-col{min-width:0}',
    '#kit-room-overlay .kr-lcu-mid{flex:1 1 auto;min-height:0;overflow-y:auto}',
    '#kit-room-overlay .kr-cfg-foot{flex:0 0 auto}',
    // Size-run sub-grid — its own internal scroll cap (unconditional — a
    // worst-case product, S-5XL x many placements, is as real a risk on a
    // phone as on desktop) so it never dominates .kr-lcu-mid's shared scroll;
    // everything else in col2/col3 still reads in full around it.
    '#kit-room-overlay .kr-cfg-sizes{max-height:180px;overflow-y:auto;padding-right:2px}',
    // Chat host (Shell B) — the card grows with the thread and never clips/
    // scrolls internally at ANY width (unconditional, not just >=821px) —
    // same "no internal scroll host" contract the old single .kr-cfg-scroll
    // override had for this class.
    '#kit-room-overlay .kr-lcu-card .kr-lcu-mid{overflow-y:visible}',
    '#kit-room-overlay .kr-lcu-card .kr-cfg-sizes{max-height:none;overflow-y:visible}',
    '@media(min-width:821px){',
    '#kit-room-overlay .kr-lcu-grid{display:grid;',
    'grid-template-columns:minmax(220px,32%) minmax(0,1fr) minmax(240px,1fr);',
    'grid-template-rows:minmax(0,1fr) auto;column-gap:var(--kr-6);',
    'flex:1 1 auto;min-height:0}',
    // .kr-lcu-mid disappears from the box tree — col2/col3 (its children)
    // become direct grid items, placed explicitly below.
    '#kit-room-overlay .kr-lcu-mid{display:contents}',
    // col1 — image + front/back toggle, spanning both rows. align-self:start
    // so a tall col2/3 (many placements/sizes) never stretches the photo
    // well past its own aspect-ratio.
    '#kit-room-overlay .kr-lcu-col1{grid-column:1;grid-row:1/3;align-self:start}',
    // col2 — colour + print-placement + bulk-savings ladder. Its own scroll,
    // bounded by the row's minmax(0,1fr) track (below) — same "CSS grid
    // blowout" fix as col3 needs; the chat-card override above (unconditional
    // overflow-y:visible on .kr-lcu-mid) already covers that host, so this
    // never needs its own chat-card exception.
    '#kit-room-overlay .kr-lcu-col2{grid-column:2;grid-row:1;overflow-y:auto;padding:var(--kr-5) 0;min-height:0}',
    // col3 — size-run + artwork, its own scroll too, same row/bound as col2.
    '#kit-room-overlay .kr-lcu-col3{grid-column:3;grid-row:1;overflow-y:auto;padding:var(--kr-5) 0;min-height:0}',
    // foot — its OWN grid item, row 2, col3's column ONLY (never full-width)
    // — this is the "sticky price/Confirm foot" itself: a real grid row
    // sized to content (auto), always rendered, never part of the scrollable
    // row-1 track above it.
    '#kit-room-overlay .kr-cfg-foot{grid-column:3;grid-row:2}',
    '}',
    // Chat host (Shell B) desktop-only deltas — widened cap so a 3-column
    // grid actually has room, and col2/col3's OWN internal scroll (set just
    // above for the drawer) turned back off so the card still just grows.
    '@media(min-width:821px){',
    '#kit-room-overlay .kr-form-card.kr-lcu-card{max-width:880px}',
    '#kit-room-overlay .kr-lcu-card .kr-lcu-grid{max-height:none}',
    '#kit-room-overlay .kr-lcu-card .kr-lcu-col2,#kit-room-overlay .kr-lcu-card .kr-lcu-col3{overflow-y:visible}',
    '}',
    // Rail row's amber "Needs placement" chip (buildLineEl) — a real button
    // (reopens Configure to fix it), not a static label.
    '#kit-room-overlay .kr-rline-needsplacement{display:inline-flex;align-items:center;background:#fef3c7;',
    'color:#92400e;font-weight:800;font-size:10.5px;padding:3px 8px;border-radius:999px;min-height:0}',
    '#kit-room-overlay .kr-rline-needsplacement:hover{background:#fde68a}',
    // ---- Chat — real Ari conversation (agentChat CF). Same light canvas
    // surface as Browse; user bubbles use the structure colour (purple), Ari
    // bubbles a quiet tint; Send is one of the two allowed orange moments
    // (direct-action, alongside the rail's qty +/-).
    '#kit-room-overlay .kr-chat{flex:1 1 auto;min-height:0;display:flex;flex-direction:column}',
    '#kit-room-overlay .kr-chat-thread{flex:1 1 auto;min-height:0;overflow-y:auto;',
    'padding:var(--kr-5) var(--kr-7)}',
    '#kit-room-overlay .kr-cbub{max-width:82%;margin:0 0 var(--kr-3);animation:kr-rise .3s var(--kr-ease) both}',
    '#kit-room-overlay .kr-cbub-me{margin-left:auto}',
    '#kit-room-overlay .kr-cbub-txt{padding:12px 15px;border-radius:16px;font-size:14px;line-height:1.5;',
    'white-space:pre-wrap;word-break:break-word}',
    '#kit-room-overlay .kr-cbub-ari .kr-cbub-txt{background:rgba(45,27,78,.05);border:1px solid rgba(45,27,78,.08);',
    'color:var(--kr-ink);border-bottom-left-radius:5px}',
    '#kit-room-overlay .kr-cbub-ari .kr-cbub-txt a{color:var(--kr-purple);font-weight:700;text-decoration:underline}',
    '#kit-room-overlay .kr-cbub-me .kr-cbub-txt{background:var(--kr-purple);color:#fff;',
    'border-bottom-right-radius:5px;font-weight:600}',
    // Softened sync-turn variant (item 2) — the config-card/request_details
    // confirm flows already show a "✓ Added…"/"✓ Details added" done-card
    // right above this bubble, so a full-weight dark bubble repeating the
    // same sentence read as a visual duplicate. Stays a REAL .kr-cbub-me
    // element (still a genuine user turn for Ari + for the compare-test's
    // `.kr-cbub-me .kr-cbub-txt` check) — only quieter, smaller, and tinted
    // instead of solid, so it reads as a footnote, not a second bubble.
    '#kit-room-overlay .kr-cbub-me.kr-cbub-sync{max-width:70%}',
    '#kit-room-overlay .kr-cbub-me.kr-cbub-sync .kr-cbub-txt{background:rgba(45,27,78,.07);color:var(--kr-ink-2);',
    'font-size:12px;font-weight:600;padding:7px 12px}',
    '#kit-room-overlay .kr-cbub-err .kr-cbub-txt{background:rgba(234,88,12,.08);border:1px solid rgba(234,88,12,.2);',
    'color:var(--kr-orange-d)}',
    // Human-handoff (Bulk Desk live takeover) — a staff reply must never be
    // mistaken for Ari. Same bubble shell, green tint instead of the AI's
    // purple tint, plus a small sender-name label above the text.
    '#kit-room-overlay .kr-cbub-staff .kr-cbub-sender{font-size:11px;font-weight:800;',
    'color:#0f7a52;margin:0 2px 4px;letter-spacing:.01em}',
    '#kit-room-overlay .kr-cbub-staff .kr-cbub-txt{background:rgba(15,122,82,.08);',
    'border:1px solid rgba(15,122,82,.2);color:var(--kr-ink);border-bottom-left-radius:5px}',
    '#kit-room-overlay .kr-cbub-staff .kr-cbub-txt a{color:#0f7a52;font-weight:700;text-decoration:underline}',
    // Takeover/handback system notes — a quiet centered pill, deliberately
    // NOT shaped like a chat bubble so it never reads as something Ari or
    // the customer "said".
    '#kit-room-overlay .kr-cbub-system{max-width:100%;margin:var(--kr-4) auto var(--kr-3);',
    'text-align:center;animation:kr-rise .3s var(--kr-ease) both}',
    '#kit-room-overlay .kr-cbub-system-txt{display:inline-block;padding:6px 14px;',
    'border-radius:999px;font-size:12px;font-weight:700;background:rgba(45,27,78,.06);',
    'color:var(--kr-muted);border:1px solid rgba(45,27,78,.08)}',
    // artwork user bubble — same purple .kr-cbub-me shell, tighter padding
    // so the thumbnail sits flush; an optional typed note renders under it.
    '#kit-room-overlay .kr-cbub-artwork .kr-cbub-txt{padding:8px}',
    '#kit-room-overlay .kr-cbub-artwork img{display:block;max-width:220px;max-height:220px;width:100%;',
    'height:auto;border-radius:10px;object-fit:cover}',
    '#kit-room-overlay .kr-cbub-artwork-note{margin-top:8px;padding:0 4px 2px;font-size:14px;line-height:1.4}',
    // thinking dots — static (opacity only, no blink) at rest under RM.
    // .kr-typing-dots wraps just the <i> dots so an optional leading label
    // (e.g. "Reading your artwork…") never shifts the :nth-child stagger
    // below, which counts position among ITS immediate siblings.
    '#kit-room-overlay .kr-typing{display:inline-flex;align-items:center;gap:4px;padding:13px 15px}',
    '#kit-room-overlay .kr-typing-label{font-size:12.5px;font-weight:700;color:var(--kr-muted)}',
    '#kit-room-overlay .kr-typing-dots{display:inline-flex;gap:4px}',
    '#kit-room-overlay .kr-typing i{width:6px;height:6px;border-radius:50%;background:var(--kr-muted);',
    'display:block;opacity:.35}',
    '#kit-room-overlay .kr-typing i.kr-typing-blink{animation:kr-typing-blink 1.1s ease-in-out infinite}',
    '#kit-room-overlay .kr-typing i:nth-child(2).kr-typing-blink{animation-delay:.15s}',
    '#kit-room-overlay .kr-typing i:nth-child(3).kr-typing-blink{animation-delay:.3s}',
    '@keyframes kr-typing-blink{0%,60%,100%{opacity:.35}30%{opacity:1}}',
    // Product cards Ari surfaces inline in the thread — the SAME .kr-card
    // component/behaviour as Browse (fillCard()/loadCardFast() have no idea
    // which container they're in), just visually COMPACT here via scoped
    // descendant overrides — a full Browse-size card (~370px, square 1/1
    // thumb) buries the conversation above/below it. Landscape thumb
    // (~4/3, capped) + tighter type/spacing gets a chat card to roughly HALF
    // that height while staying the same premium card, just smaller.
    // object-fit:contain (already set on .kr-thumb img) still letterboxes
    // cleanly at the new ratio — no separate image handling needed.
    // Browse's `.kr-grid` cards are UNCHANGED — none of this touches them.
    '#kit-room-overlay .kr-chat-cards{display:grid;grid-template-columns:repeat(auto-fill,minmax(152px,1fr));',
    'gap:var(--kr-2);margin:2px 0 var(--kr-4)}',
    '#kit-room-overlay .kr-chat-cards .kr-card{border-radius:12px}',
    // CARD STREAMLINE pass: padding trimmed further here too (already the
    // tighter of the two contexts) — same "image fills the frame" intent.
    '#kit-room-overlay .kr-chat-cards .kr-thumb{aspect-ratio:4/3;max-height:118px;padding:6px}',
    '#kit-room-overlay .kr-chat-cards .kr-pb{padding:10px;gap:1px}',
    '#kit-room-overlay .kr-chat-cards .kr-vend{font-size:8.5px}',
    // 2 lines, not 1 — at this 152px column width a single line mid-word
    // ellipsized nearly every real product name ("AS Colour Block Tubula…");
    // the Browse grid's own .kr-nm (no clamp at all) never had this problem.
    '#kit-room-overlay .kr-chat-cards .kr-nm{font-size:12px;',
    'display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}',
    '#kit-room-overlay .kr-chat-cards .kr-sw{margin:1px 0 2px}',
    '#kit-room-overlay .kr-chat-cards .kr-sw i{width:9px;height:9px}',
    // .kr-spec has no font-size of its own (see base rule) — it inherits
    // .kr-vend's 8.5px here automatically, same pattern as the grid card.
    '#kit-room-overlay .kr-chat-cards .kr-prc{margin-top:2px}',
    '#kit-room-overlay .kr-chat-cards .kr-prc b{font-size:15px}',
    '#kit-room-overlay .kr-chat-cards .kr-prc .kr-prc-ctx{font-size:9px}',
    // .kr-acts keeps .kr-add at its base min-height:44px (tap-target rule) —
    // only the spacing around it and the secondary Compare link shrink.
    '#kit-room-overlay .kr-chat-cards .kr-acts{gap:4px;margin-top:7px}',
    '#kit-room-overlay .kr-chat-cards .kr-add{font-size:11px;padding:0 6px}',
    '#kit-room-overlay .kr-chat-cards .kr-cmp{font-size:10.5px;min-height:26px;padding:2px 4px}',
    // Chat card's Compare pill visually shrinks to 26px in this tight
    // 152px-wide card — override the base ::before's -6px inset with a
    // bigger one so the invisible tap area still reaches 44px here too.
    '#kit-room-overlay .kr-chat-cards .kr-cmp::before{top:-9px;bottom:-9px}',
    // ---- request_details form card (Ari-driven) — same light-card language
    // as .kr-card (white, 1px hair border, 16px radius, kr-e1 shadow), sits
    // inline in the thread at the same insertion point .kr-chat-cards uses.
    // Every interactive target here is >=44px (world-class-pages hard rule).
    '#kit-room-overlay .kr-form-card{background:#fff;border:1px solid var(--kr-hair);border-radius:16px;',
    'box-shadow:var(--kr-e1);padding:var(--kr-4);margin:2px 0 var(--kr-4);max-width:420px;',
    'animation:kr-rise .3s var(--kr-ease) both}',
    '#kit-room-overlay .kr-form-reason{font-weight:800;font-size:12.5px;color:var(--kr-ink);',
    'line-height:1.4;margin-bottom:var(--kr-3)}',
    // error summary — same red/orange error palette as .kr-cbub-err (an
    // established precedent in this file for "this needs attention" text,
    // not a CTA — orange stays reserved for direct actions elsewhere).
    '#kit-room-overlay .kr-form-err{font-size:12px;font-weight:700;color:var(--kr-orange-d);',
    'background:rgba(234,88,12,.08);border:1px solid rgba(234,88,12,.2);border-radius:10px;',
    'padding:8px 12px;margin-bottom:var(--kr-3)}',
    '#kit-room-overlay .kr-form-field{margin-bottom:var(--kr-3)}',
    '#kit-room-overlay .kr-form-field label,#kit-room-overlay .kr-form-sizes-lbl{display:block;font-weight:800;',
    'font-size:12px;color:var(--kr-ink);margin-bottom:4px}',
    // required-asterisk communicated via weight, not a new colour — keeps
    // the file's "orange is CTA/warn only" discipline intact.
    '#kit-room-overlay .kr-form-req{font-weight:900}',
    '#kit-room-overlay .kr-form-field input,#kit-room-overlay .kr-form-field select,',
    '#kit-room-overlay .kr-form-addr-part{width:100%;min-height:44px;border:1px solid var(--kr-hair);',
    'border-radius:11px;padding:0 12px;font:inherit;font-size:13.5px;color:var(--kr-ink);',
    'background:#fff;outline:none}',
    '#kit-room-overlay .kr-form-field input:focus-visible,#kit-room-overlay .kr-form-field select:focus-visible,',
    '#kit-room-overlay .kr-form-addr-part:focus-visible{border-color:var(--kr-purple);',
    'box-shadow:0 0 0 3px rgba(45,27,78,.12)}',
    '#kit-room-overlay .kr-form-hint{font-size:10.5px;color:var(--kr-muted);margin-top:3px}',
    '#kit-room-overlay .kr-form-warn{font-size:10.5px;color:var(--kr-orange-d);margin-top:3px}',
    '#kit-room-overlay .kr-form-addr-grid{display:grid;grid-template-columns:1fr 1fr;gap:6px}',
    '#kit-room-overlay .kr-form-addr-full{grid-column:1/-1}',
    '#kit-room-overlay .kr-form-sizes{display:flex;flex-wrap:wrap;gap:8px;margin-top:2px}',
    '#kit-room-overlay .kr-form-size-chip{display:flex;flex-direction:column;align-items:center;gap:4px;',
    'background:#f8f6f2;border:1px solid var(--kr-hair);border-radius:10px;padding:6px 8px;min-width:64px}',
    '#kit-room-overlay .kr-form-size-lbl{font-weight:800;font-size:11.5px;color:var(--kr-ink)}',
    '#kit-room-overlay .kr-fqty{display:inline-flex;align-items:center;border:1px solid var(--kr-hair);',
    'border-radius:9px;overflow:hidden}',
    '#kit-room-overlay .kr-fqty button{width:44px;height:44px;display:flex;align-items:center;',
    'justify-content:center;border:0;background:#f4f1ec;color:var(--kr-ink);font-size:15px;',
    'font-weight:800;transition:.15s}',
    '#kit-room-overlay .kr-fqty button:hover{background:var(--kr-purple);color:#fff}',
    '#kit-room-overlay .kr-fqty-n{min-width:28px;text-align:center;font-weight:800;font-size:12.5px}',
    '#kit-room-overlay .kr-form-size-total{font-size:11.5px;font-weight:800;color:var(--kr-ink-2);margin-top:6px}',
    // submit — same treatment as .kr-add (deep-purple, purple hover): the
    // primary action on a LIGHT card, never orange (orange stays the rail
    // CTA + direct-hover-only colour per this file's discipline).
    '#kit-room-overlay .kr-form-submit{width:100%;min-height:44px;border:0;border-radius:11px;',
    'background:var(--kr-deep);color:#fff;font-weight:800;font-size:13px;margin-top:var(--kr-2);',
    'display:flex;align-items:center;justify-content:center;transition:.15s}',
    '#kit-room-overlay .kr-form-submit:hover{background:var(--kr-purple)}',
    // collapsed confirmation state — purple ✓, same "added" language as the
    // rail's .kr-added-chip.
    '#kit-room-overlay .kr-form-card.kr-form-done{padding:var(--kr-3) var(--kr-4);text-align:center}',
    '#kit-room-overlay .kr-form-done-check{display:inline-flex;align-items:center;gap:6px;',
    'font-weight:800;font-size:12.5px;color:var(--kr-purple)}',
    '#kit-room-overlay .kr-chat-inputbar{flex:0 0 auto;padding:var(--kr-3) var(--kr-6) var(--kr-5);',
    'display:flex;gap:var(--kr-2);align-items:center;border-top:1px solid var(--kr-hair)}',
    '#kit-room-overlay .kr-chat-field{flex:1 1 auto;min-width:0;display:flex;align-items:center;background:#fff;',
    'border:1px solid var(--kr-hair);border-radius:14px;padding:2px 14px}',
    '#kit-room-overlay .kr-chat-field input{flex:1 1 auto;min-width:0;border:0;background:transparent;',
    'font:inherit;font-size:14px;color:var(--kr-ink);padding:11px 0;outline:none}',
    '#kit-room-overlay .kr-chat-field input::placeholder{color:var(--kr-muted)}',
    '#kit-room-overlay .kr-chat-send{width:44px;height:44px;flex:0 0 auto;border-radius:12px;border:0;',
    'background:var(--kr-orange);color:#fff;display:flex;align-items:center;justify-content:center;transition:.15s}',
    '#kit-room-overlay .kr-chat-send svg{width:18px;height:18px}',
    '#kit-room-overlay .kr-chat-send:hover{background:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-chat-send:disabled{background:#e7e0d5;color:#b3a99a;cursor:default}',
    // Attach (artwork) — neutral chrome like .kr-close, NOT orange (orange
    // stays reserved for Send/the rail CTA). Same 44px target as Send.
    '#kit-room-overlay .kr-chat-attach{width:44px;height:44px;flex:0 0 auto;border-radius:12px;border:0;',
    'background:rgba(15,10,32,.06);color:var(--kr-ink-2);display:flex;align-items:center;justify-content:center;transition:.15s}',
    '#kit-room-overlay .kr-chat-attach svg{width:18px;height:18px}',
    '#kit-room-overlay .kr-chat-attach:hover{background:rgba(45,27,78,.1);color:var(--kr-purple)}',
    '#kit-room-overlay .kr-chat-attach:disabled{opacity:.5;cursor:default}',
    '#kit-room-overlay .kr-chat-attach.kr-uploading{opacity:.65;cursor:wait}',
    // Pending-attachment chip — sits above the input row once an upload
    // succeeds, until the customer hits Send (or removes it). The bubble
    // rendering the actual image lives in the thread (.kr-cbub-artwork,
    // appended on send) — this chip is just "what's about to go out".
    '#kit-room-overlay .kr-chat-pending{display:flex;align-items:center;gap:8px;margin:0 var(--kr-7) 8px;',
    'padding:6px 8px;background:rgba(45,27,78,.05);border:1px solid rgba(45,27,78,.1);border-radius:10px}',
    '#kit-room-overlay .kr-chat-pending-thumb{width:32px;height:32px;border-radius:6px;object-fit:cover;flex:0 0 auto}',
    '#kit-room-overlay .kr-chat-pending-name{flex:1 1 auto;min-width:0;font-size:12px;font-weight:700;',
    'color:var(--kr-ink);overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '#kit-room-overlay .kr-chat-pending-x{flex:0 0 auto;width:28px;height:28px;border-radius:8px;border:0;',
    'background:transparent;color:var(--kr-muted);display:inline-flex;align-items:center;justify-content:center}',
    '#kit-room-overlay .kr-chat-pending-x svg{width:12px;height:12px}',
    '#kit-room-overlay .kr-chat-pending-x:hover{background:rgba(45,27,78,.08);color:var(--kr-orange)}',
    '#kit-room-overlay .kr-chat.kr-chat-dragover{outline:2px dashed var(--kr-purple);outline-offset:-8px}',
    // ---- browse / compare (Slice 3) ----
    '#kit-room-overlay .kr-cmp.kr-cmp-on{color:var(--kr-purple);text-decoration:underline}',
    '#kit-room-overlay .kr-cmpbar{position:sticky;bottom:0;left:0;right:0;margin:10px 20px 0;padding:10px 12px 10px 16px;',
    'border-radius:14px;background:var(--kr-deep);color:#fff;display:flex;align-items:center;gap:10px;',
    'box-shadow:0 12px 30px rgba(0,0,0,.35);z-index:3}',
    '#kit-room-overlay .kr-cmpbar-txt{flex:1 1 auto;font-weight:800;font-size:13px}',
    '#kit-room-overlay .kr-cmpbar-view{border:0;background:var(--kr-orange);color:#fff;font-weight:800;',
    'font-size:12.5px;padding:0 14px;border-radius:9px;min-height:44px;display:flex;align-items:center;',
    'justify-content:center;white-space:nowrap}',
    '#kit-room-overlay .kr-cmpbar-view:hover{background:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-cmpbar-clear{border:0;background:rgba(255,255,255,.1);color:#fff;',
    'width:44px;height:44px;flex:0 0 auto;border-radius:9px;display:flex;align-items:center;justify-content:center}',
    '#kit-room-overlay .kr-cmpbar-clear svg{width:14px;height:14px}',
    '#kit-room-overlay .kr-cmpbar-clear:hover{background:rgba(255,255,255,.2)}',
    // FIX 2 — full-canvas OVERLAY: positioned absolute over the whole
    // .kr-canvas-col (which is position:relative — see above), independent
    // of whichever surface (Browse chips+grid, or Chat) is currently active
    // underneath. This is what lets the comparison render identically no
    // matter where it was opened from, with zero dependency on
    // switchToBrowse()/switchToChat() — see showCompareView()/backToGrid().
    '#kit-room-overlay .kr-compare-view{position:absolute;inset:0;z-index:4;background:#fff;color:var(--kr-ink);',
    'border-radius:18px;padding:28px 32px 32px;box-shadow:0 16px 40px rgba(10,5,25,.3);overflow-y:auto}',
    // FIX 1 — de-squish: spacious head, generous cell padding, bigger photo.
    '#kit-room-overlay .kr-cmp-head{margin-bottom:20px}',
    '#kit-room-overlay .kr-cmp-title{font-weight:900;font-size:20px;letter-spacing:-.02em}',
    '#kit-room-overlay .kr-cmp-sub{font-size:13px;color:var(--kr-muted);margin-top:4px}',
    '#kit-room-overlay .kr-cmp-back{margin-top:20px;border:0;background:transparent;color:var(--kr-orange-d);',
    'font-weight:800;font-size:13px;display:inline-flex;align-items:center;justify-content:center;',
    'min-height:44px;padding:0 4px}',
    '#kit-room-overlay .kr-cmp-back:hover{text-decoration:underline}',
    '#kit-room-overlay .kr-ctab{display:grid;font-size:13.5px;overflow-x:auto;max-width:960px;margin:0 auto;',
    'row-gap:2px}',
    '#kit-room-overlay .kr-ctab-cell{padding:16px 18px;border-top:1px solid var(--kr-hair);',
    'display:flex;flex-direction:column;justify-content:center;min-width:0;gap:2px}',
    '#kit-room-overlay .kr-ctab-lbl{color:var(--kr-muted);font-weight:700;font-size:11.5px;',
    'text-transform:uppercase;letter-spacing:.04em;border-top:0;justify-content:flex-start;padding-top:16px}',
    '#kit-room-overlay .kr-ctab-hd{border-top:0;text-align:center;align-items:center;padding-top:0;padding-bottom:18px}',
    // photo — bumped from a 40px thumbnail to a real product shot (was the
    // owner-flagged "tiny image" complaint).
    '#kit-room-overlay .kr-ctab-hd .kr-ctab-mi{width:120px;height:120px;border-radius:14px;background:#f4f1ec;',
    'display:grid;place-items:center;margin:0 auto 12px;overflow:hidden}',
    '#kit-room-overlay .kr-ctab-hd .kr-ctab-mi img{width:100%;height:100%;object-fit:contain}',
    '#kit-room-overlay .kr-ctab-hd .kr-ctab-mi svg{width:56%;height:56%}',
    '#kit-room-overlay .kr-ctab-hd b{font-weight:900;font-size:14px;letter-spacing:-.01em}',
    '#kit-room-overlay .kr-ctab-hd .kr-ctab-cd{font-size:11px;color:var(--kr-muted);margin-top:3px}',
    '#kit-room-overlay .kr-ctab-cell.kr-best{background:rgba(249,115,22,.07)}',
    '#kit-room-overlay .kr-ctab-big{font-size:17px;font-weight:900;display:block}',
    '#kit-room-overlay .kr-pill{display:inline-block;font-size:9px;font-weight:800;text-transform:uppercase;',
    'letter-spacing:.05em;background:var(--kr-yellow);color:#5c4a00;padding:2px 7px;border-radius:6px;margin-top:4px}',
    // colour swatches — reuses the card's dot styling (.kr-sw) sizing but a
    // touch bigger, laid out above the honest count text (never a bare number).
    '#kit-room-overlay .kr-ctab-sw{display:flex;flex-direction:column;align-items:flex-start;gap:5px}',
    '#kit-room-overlay .kr-ctab-sw-dots{display:flex;align-items:center;gap:5px}',
    '#kit-room-overlay .kr-ctab-sw-dots i{width:15px;height:15px;border-radius:50%;',
    'box-shadow:0 0 0 1px rgba(15,10,32,.1);display:block}',
    '#kit-room-overlay .kr-ctab-sw-dots i.kr-sw-ph{background:#e7e2d6}',
    '#kit-room-overlay .kr-ctab-sw-dots .kr-sw-more{font-size:10.5px;font-weight:700;color:var(--kr-muted)}',
    '#kit-room-overlay .kr-ctab-sw-ct{font-size:11.5px;font-weight:700;color:var(--kr-ink-2)}',
    '#kit-room-overlay .kr-ctab-sizes{font-size:12.5px;color:var(--kr-ink-2);line-height:1.4}',
    '#kit-room-overlay .kr-ctab-add{border:0;background:var(--kr-deep);color:#fff;font-weight:800;font-size:12.5px;',
    'padding:0 10px;border-radius:9px;min-height:44px;width:100%;display:flex;align-items:center;',
    'justify-content:center;transition:.15s;margin-top:4px}',
    '#kit-room-overlay .kr-ctab-add:hover{background:var(--kr-purple)}',
    '#kit-room-overlay .kr-ctab-cell.kr-best .kr-ctab-add{background:var(--kr-purple)}',
    // FIX 2 — neutralize the Chat|Browse segmented toggle while the compare
    // overlay is open: it lives in the persistent topbar (never covered by
    // the canvas-col overlay), so without this a click would silently swap
    // the surface *underneath* the overlay and "Back" would land somewhere
    // the shopper didn't expect. Restored in backToGrid().
    '#kit-room-overlay .kr-seg.kr-seg-disabled{opacity:.4;pointer-events:none}',
    // ---- rail (Slice 2: "Your bulk order") — SURFACE (owner-locked): the
    // ONE deliberate dark panel. A Stripe-style receipt sitting on the light
    // canvas — deep-purple gradient, white/lavender text, mint for savings,
    // orange reserved for the primary CTA + qty/send direct-action hovers.
    // SIZE pass: rail widened a step (380->420px) to share in the extra room
    // too, not just the grid side. The @media(max-width:820px) override
    // below replaces `width` with `width:auto` (full-bleed bottom sheet) on
    // mobile regardless, so this only affects desktop/tablet.
    '#kit-room-overlay .kr-rail{width:420px;flex:0 0 auto;',
    'background:linear-gradient(180deg,var(--kr-purple),var(--kr-deep) 70%,var(--kr-deeper));color:#efeaf9;',
    'display:flex;flex-direction:column;min-height:0;box-shadow:var(--kr-e-rail);position:relative;z-index:1}',
    '#kit-room-overlay .kr-rail-head{width:100%;border:0;background:transparent;color:inherit;',
    'display:flex;align-items:center;gap:10px;padding:var(--kr-5) var(--kr-6) var(--kr-3);flex:0 0 auto;',
    'text-align:left;min-height:44px}',
    '#kit-room-overlay .kr-rail-headtext{flex:1 1 auto;min-width:0;display:flex;flex-direction:column}',
    '#kit-room-overlay .kr-rail-k{font-size:10.5px;font-weight:800;letter-spacing:.12em;text-transform:uppercase;color:var(--kr-lav)}',
    '#kit-room-overlay .kr-rail-title{font-weight:900;font-size:16px;letter-spacing:-.02em;color:#fff;',
    'overflow:hidden;text-overflow:ellipsis;white-space:nowrap}',
    '#kit-room-overlay .kr-rail-headtotal{display:none;font-weight:900;font-size:16px;flex:0 0 auto;color:#fff}',
    '#kit-room-overlay .kr-rail-chev{display:none;flex:0 0 auto;color:var(--kr-lav);transition:transform .2s var(--kr-ease)}',
    '#kit-room-overlay .kr-rail-chev svg{width:18px;height:18px}',
    '#kit-room-overlay .kr-rail-head[aria-expanded="true"] .kr-rail-chev{transform:rotate(180deg)}',
    '#kit-room-overlay .kr-rail-body{flex:1 1 auto;min-height:0;display:flex;flex-direction:column}',
    // SIZE pass: a touch more side padding (kr-4->kr-5) now the rail's wider.
    '#kit-room-overlay .kr-rail-lines{flex:1 1 auto;overflow-y:auto;padding:0 var(--kr-5);min-height:0}',
    // static empty state (no breathing float — see the "calm pass" note near
    // the keyframes below; motion review flags a perpetual float as toy-like)
    '#kit-room-overlay .kr-rail-empty{text-align:center;color:var(--kr-lav);font-size:13px;padding:36px 18px;line-height:1.6}',
    // translucent receipt line rows — sit ON the dark rail rather than as
    // opaque white cards, so the rail reads as one continuous surface.
    // SIZE pass: a little more inner padding/gap now the rail has the room.
    '#kit-room-overlay .kr-rline{background:rgba(255,255,255,.055);border:1px solid rgba(255,255,255,.09);',
    'border-radius:14px;padding:13px;margin-bottom:10px;display:flex;gap:12px;position:relative}',
    '#kit-room-overlay .kr-rline-thumb{width:44px;height:44px;border-radius:10px;flex:0 0 auto;background:rgba(255,255,255,.08);',
    'overflow:hidden;display:grid;place-items:center;position:relative}',
    '#kit-room-overlay .kr-rline-thumb img{width:100%;height:100%;object-fit:contain}',
    '#kit-room-overlay .kr-rline-thumb svg{width:60%;height:60%;stroke:#efeaf9}',
    // Small badge on a rail line's thumb once it carries attached artwork
    // (railLine.artwork — see buildLineEl). Purposely tiny/non-blocking —
    // just enough to signal "this line has a design attached" at a glance.
    '#kit-room-overlay .kr-rline-artwork-badge{position:absolute;bottom:-2px;right:-2px;width:16px;height:16px;',
    'border-radius:50%;background:var(--kr-purple);color:#fff;display:flex;align-items:center;justify-content:center;',
    'box-shadow:var(--kr-e1)}',
    '#kit-room-overlay .kr-rline-artwork-badge svg{width:9px;height:9px}',
    // Small "Ari added" chip on a rail line Ari added/edited via update_quote
    // (railLine.ariAdded — see buildLineEl + applyUpdateQuoteActions). Clears
    // the moment the customer touches the line themselves (qty/Configure/
    // remove) — it is otherwise a fully normal, reconfigurable rail line.
    '#kit-room-overlay .kr-rline-ari-badge{position:absolute;top:-2px;left:-2px;background:var(--kr-orange);',
    'color:var(--kr-deeper);font-size:8px;font-weight:900;letter-spacing:.02em;padding:2px 5px;border-radius:6px;',
    'box-shadow:var(--kr-e1);text-transform:uppercase;line-height:1.3;pointer-events:none}',
    '#kit-room-overlay .kr-rline-body{flex:1;min-width:0;padding-right:26px}',
    '#kit-room-overlay .kr-rline-nm{font-weight:800;font-size:12.5px;letter-spacing:-.01em;line-height:1.2;color:#fff}',
    '#kit-room-overlay .kr-rline-mt{font-size:10.5px;color:var(--kr-lav);margin-top:1px}',
    '#kit-room-overlay .kr-rline-pr{font-weight:900;font-size:13px;margin-top:5px;color:#fff;transition:opacity .15s}',
    '#kit-room-overlay .kr-rline-pr small{font-weight:600;color:var(--kr-lav);font-size:10.5px}',
    '#kit-room-overlay .kr-rline.kr-requoting .kr-rline-pr{opacity:.5}',
    '#kit-room-overlay .kr-rline-fail{font-size:11px;color:var(--kr-lav);display:inline-flex;gap:6px;align-items:center;flex-wrap:wrap}',
    '#kit-room-overlay .kr-rline-retry{display:inline-flex;align-items:center;justify-content:center;',
    'border:1px solid rgba(255,255,255,.2);background:transparent;color:#fff;',
    'font-weight:800;font-size:10.5px;padding:4px 10px;border-radius:7px;min-height:44px}',
    '#kit-room-overlay .kr-qty{display:inline-flex;align-items:center;border:1px solid rgba(255,255,255,.16);',
    'border-radius:9px;margin-top:7px;overflow:hidden}',
    '#kit-room-overlay .kr-qty button{width:44px;height:44px;display:flex;align-items:center;justify-content:center;',
    'border:0;background:rgba(255,255,255,.06);color:#fff;font-size:15px;font-weight:800}',
    // direct-action hover -> orange (one of the two allowed orange moments)
    '#kit-room-overlay .kr-qty button:hover:not(:disabled){background:var(--kr-orange);color:#fff}',
    '#kit-room-overlay .kr-qty button:disabled{color:rgba(255,255,255,.28);cursor:default}',
    '#kit-room-overlay .kr-qty-n{min-width:34px;text-align:center;font-weight:800;font-size:12.5px;color:#fff}',
    '#kit-room-overlay .kr-rline-rm{position:absolute;top:2px;right:2px;width:44px;height:44px;',
    'border:0;background:transparent;color:rgba(255,255,255,.4);display:grid;place-items:center}',
    '#kit-room-overlay .kr-rline-rm svg{width:12px;height:12px}',
    '#kit-room-overlay .kr-rline-rm:hover{color:var(--kr-orange)}',
    '#kit-room-overlay .kr-rail-foot{flex:0 0 auto;border-top:1px solid rgba(255,255,255,.1);',
    'padding:var(--kr-4) var(--kr-6) var(--kr-5);background:transparent}',
    '#kit-room-overlay .kr-rrow{display:flex;justify-content:space-between;font-size:12.5px;color:var(--kr-lav);margin-bottom:6px}',
    // the "you saved" moment — mint, not orange (orange is the CTA-only signal)
    '#kit-room-overlay .kr-rrow.kr-save{color:var(--kr-mint);font-weight:800}',
    '#kit-room-overlay .kr-rrow.kr-total{color:#fff;font-weight:800;font-size:22px;margin:10px 0 0;align-items:baseline}',
    '#kit-room-overlay .kr-rrow.kr-total small{font-size:11px;font-weight:600;color:var(--kr-lav)}',
    // primary CTA — THE orange moment, gradient, unmistakably the one action
    '#kit-room-overlay .kr-qbtn{width:100%;display:flex;align-items:center;justify-content:center;',
    'border:0;border-radius:13px;padding:13px;font-weight:900;font-size:14.5px;',
    'margin-top:15px;min-height:44px;background:linear-gradient(180deg,#fb8a3c,var(--kr-orange));color:#fff;',
    'box-shadow:0 10px 24px rgba(249,115,22,.38);transition:.15s}',
    '#kit-room-overlay .kr-qbtn:hover{background:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-qbtn:disabled{background:rgba(255,255,255,.08);color:rgba(255,255,255,.35);box-shadow:none;cursor:default}',
    // secondary CTA ("Email me this quote") — ghost-on-dark, quiet
    '#kit-room-overlay .kr-qbtn-secondary{background:transparent;border:1.5px solid rgba(255,255,255,.22);',
    'color:#fff;box-shadow:none;margin-top:8px}',
    '#kit-room-overlay .kr-qbtn-secondary:hover{background:rgba(255,255,255,.08);border-color:rgba(255,255,255,.4);color:#fff}',
    '#kit-room-overlay .kr-qbtn-secondary:disabled{background:transparent;border-color:rgba(255,255,255,.1);color:rgba(255,255,255,.3)}',
    // Checkout in-flight — dim + spinner-less "Creating your order…" state
    // (no extra animation asset; text change + disabled is enough and keeps
    // this reduced-motion-safe by construction).
    '#kit-room-overlay .kr-qbtn.kr-busy{opacity:.75;cursor:wait}',
    // Inline email capture (Save/Resume "Email me this quote") — sits on the
    // same dark rail panel, so it borrows the qty-stepper's translucent
    // on-rail input treatment rather than Chat's light-canvas field style.
    '#kit-room-overlay .kr-email-capture{display:flex;gap:6px;margin-top:8px}',
    '#kit-room-overlay .kr-email-capture .kr-email-input{flex:1 1 auto;min-width:0;background:rgba(255,255,255,.07);',
    'border:1px solid rgba(255,255,255,.18);border-radius:9px;padding:0 12px;min-height:44px;',
    'color:#fff;font:inherit;font-size:12.5px;outline:none}',
    '#kit-room-overlay .kr-email-capture .kr-email-input::placeholder{color:var(--kr-lav)}',
    '#kit-room-overlay .kr-email-capture .kr-email-input:focus-visible{border-color:var(--kr-orange)}',
    '#kit-room-overlay .kr-email-send{flex:0 0 auto;border:0;background:var(--kr-orange);color:#fff;font-weight:800;',
    'font-size:12px;padding:0 14px;border-radius:9px;min-height:44px;display:flex;align-items:center;justify-content:center;transition:.15s}',
    '#kit-room-overlay .kr-email-send:hover{background:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-email-send:disabled{opacity:.5;cursor:default}',
    // LANGUAGE pass — small reassurance directly under "Email me this quote",
    // same quiet on-rail treatment as .kr-rail-note below.
    '#kit-room-overlay .kr-email-hint{text-align:center;font-size:10.5px;color:var(--kr-lav);margin-top:6px}',
    // Resume link surfaced inline after a successful email send — never
    // email-only. A quiet on-rail note with an underlined link, same lavender
    // body colour as the rest of the rail's secondary text.
    '#kit-room-overlay .kr-resume-link{font-size:11px;color:var(--kr-lav);margin-top:8px;text-align:center;',
    'line-height:1.5;word-break:break-all}',
    '#kit-room-overlay .kr-resume-link a{color:#fff;font-weight:700;text-decoration:underline}',
    '#kit-room-overlay .kr-rail-note{text-align:center;font-size:10.5px;color:var(--kr-lav);margin-top:10px}',
    // shimmer / skeleton
    '#kit-room-overlay .kr-sk{position:relative;overflow:hidden;background:#e9e4da;border-radius:6px}',
    // §6 staggered hydrate: --kr-i (set inline per-card, capped at 6) staggers
    // the shimmer sweep across a freshly-loaded grid so skeletons don't all
    // pulse in lockstep. Falls back to 0 (no stagger) if never set.
    '#kit-room-overlay .kr-sk::after{content:"";position:absolute;inset:0;',
    'background:linear-gradient(90deg,transparent,rgba(255,255,255,.65),transparent);',
    'transform:translateX(-100%);animation:kr-shimmer 1.4s infinite;',
    'animation-delay:calc(var(--kr-i,0) * 120ms)}',
    '#kit-room-overlay .kr-sk-thumb{aspect-ratio:1/1}',
    '#kit-room-overlay .kr-sk-line{height:10px;margin:6px 0}',
    '#kit-room-overlay .kr-sk-line.kr-w60{width:60%}',
    '#kit-room-overlay .kr-sk-line.kr-w40{width:40%}',
    '#kit-room-overlay .kr-sk-line.kr-w80{width:80%}',
    // error / retry
    '#kit-room-overlay .kr-err{padding:16px 12px;text-align:center}',
    '#kit-room-overlay .kr-err p{font-size:12.5px;color:var(--kr-muted);margin-bottom:10px;line-height:1.4}',
    '#kit-room-overlay .kr-retry{display:inline-flex;align-items:center;justify-content:center;',
    'border:1.5px solid var(--kr-hair);background:#fff;color:var(--kr-ink);',
    'font-weight:800;font-size:12px;padding:9px 14px;border-radius:9px;min-height:44px}',
    '#kit-room-overlay .kr-retry:hover{border-color:var(--kr-orange);color:var(--kr-orange-d)}',
    '#kit-room-overlay .kr-empty,#kit-room-overlay .kr-gridfail{grid-column:1/-1;text-align:center;',
    'color:var(--kr-muted);font-size:13px;padding:40px 20px;line-height:1.6}',
    // toast
    '#kit-room-overlay .kr-toast{position:fixed;bottom:calc(26px + env(safe-area-inset-bottom));left:50%;',
    'transform:translateX(-50%) translateY(20px);background:var(--kr-ink);color:#fff;padding:12px 20px;',
    'border-radius:13px;font-weight:700;font-size:13px;opacity:0;pointer-events:none;transition:.3s var(--kr-ease);',
    'z-index:5;max-width:88vw;text-align:center;box-shadow:0 16px 40px rgba(0,0,0,.35);',
    'display:flex;align-items:center;justify-content:center;gap:12px;flex-wrap:wrap}',
    '#kit-room-overlay .kr-toast.kr-show{opacity:1;transform:translateX(-50%) translateY(0);pointer-events:auto}',
    '#kit-room-overlay .kr-toast-action{background:transparent;border:none;color:#fff;text-decoration:underline;',
    'text-underline-offset:2px;font-weight:800;font-size:13px;padding:4px 2px;min-height:32px;cursor:pointer;',
    'flex:0 0 auto}',
    '#kit-room-overlay .kr-toast-action:hover{color:var(--kr-orange)}',
    // focus
    '#kit-room-overlay a:focus-visible,#kit-room-overlay button:focus-visible{outline:2px solid var(--kr-orange);outline-offset:2px}',
    // ---------------------------------------------------------------------
    // Motion & delight pass (KIT-ROOM-MOTION-SPEC.md). §10: tabular-nums on
    // every element whose text is a changing/countable number.
    // ---------------------------------------------------------------------
    '#kit-room-overlay .kr-prc b,#kit-room-overlay .kr-rline-pr,#kit-room-overlay .kr-rrow span,',
    '#kit-room-overlay .kr-rail-headtotal,#kit-room-overlay .kr-rail-title,#kit-room-overlay .kr-qty-n,',
    '#kit-room-overlay .kr-ctab-big{font-variant-numeric:tabular-nums}',
    // §7 micro-states — hover lift + focus-within mirrors hover; thumb zoom;
    // press-scale on the main interactive controls. Colour/shadow easing
    // stays on transition (kept under RM too — only transform is movement).
    '#kit-room-overlay .kr-card{transition:transform .2s var(--kr-ease),box-shadow .2s var(--kr-ease)}',
    '#kit-room-overlay .kr-card:hover,#kit-room-overlay .kr-card:focus-within{transform:translateY(-2px);box-shadow:var(--kr-e2)}',
    '#kit-room-overlay .kr-thumb img{transition:transform .25s var(--kr-ease)}',
    '#kit-room-overlay .kr-card:hover .kr-thumb img,#kit-room-overlay .kr-card:focus-within .kr-thumb img{transform:scale(1.04)}',
    '#kit-room-overlay .kr-door{transition:transform .2s var(--kr-ease),box-shadow .2s,border-color .2s}',
    '#kit-room-overlay .kr-add,#kit-room-overlay .kr-cmp,#kit-room-overlay .kr-chip,#kit-room-overlay .kr-qty button,',
    '#kit-room-overlay .kr-qbtn,#kit-room-overlay .kr-rline-rm,#kit-room-overlay .kr-close,#kit-room-overlay .kr-ctab-add,',
    '#kit-room-overlay .kr-retry,#kit-room-overlay .kr-rline-retry{transition:transform .12s var(--kr-ease),background .15s,border-color .15s,color .15s}',
    '#kit-room-overlay .kr-add:active,#kit-room-overlay .kr-cmp:active,#kit-room-overlay .kr-chip:active,',
    '#kit-room-overlay .kr-qty button:active,#kit-room-overlay .kr-qbtn:active,#kit-room-overlay .kr-door:active,',
    '#kit-room-overlay .kr-rline-rm:active,#kit-room-overlay .kr-close:active,#kit-room-overlay .kr-ctab-add:active,',
    '#kit-room-overlay .kr-retry:active,#kit-room-overlay .kr-rline-retry:active{transform:scale(.94)}',
    // §2 add-to-quote moment: button morph pop (triggered via pulseClass —
    // already KR_RM-safe, no separate RM override needed) + the new rail
    // line's spring-in + orange left-border flash + rail-head breath.
    '#kit-room-overlay .kr-add.kr-add-pop{animation:kr-add-pop .16s var(--kr-spring) both}',
    '#kit-room-overlay .kr-rline{border-left:3px solid transparent}',
    '#kit-room-overlay .kr-rline.kr-rline-new{animation:kr-rline-in .32s var(--kr-spring) both,kr-flash-border 1s ease-out both}',
    '#kit-room-overlay .kr-rline.kr-rline-out{animation:kr-rline-out .26s var(--kr-ease) both;pointer-events:none}',
    '#kit-room-overlay .kr-rail-head.kr-breath{animation:kr-breath .5s var(--kr-ease) both}',
    // §3 price/tier crossings: save-row flash + tier % pill pop + per-line
    // price flash on a requote that crosses into a higher bulk band.
    '#kit-room-overlay .kr-rrow.kr-save.kr-flash{animation:kr-tier-flash 1s ease-out both}',
    // savings-family colour = mint, not orange (orange stays CTA-only)
    '#kit-room-overlay .kr-tier-pill{display:inline-block;background:rgba(94,234,212,.16);color:var(--kr-mint);',
    'font-weight:900;border-radius:999px;padding:1px 7px;margin-left:2px}',
    '#kit-room-overlay .kr-tier-pill.kr-pill-pop{animation:kr-pill-pop .35s var(--kr-spring) both}',
    '#kit-room-overlay .kr-rline-pr.kr-flash-price{animation:kr-price-flash 1s ease-out both}',
    // §4 rail lifecycle: PDF/checkout buttons pop once when they first become
    // enabled (0 -> 1 line). The empty-bag icon is now STATIC at rest — a
    // perpetual float reads toy-like on a Stripe-style receipt panel (calm
    // pass, KIT-ROOM-MOTION-REVIEW.md §2).
    '#kit-room-overlay .kr-qbtn.kr-pop-enable{animation:kr-pop-enable .3s var(--kr-spring) both}',
    // §1 entry -> room "step inside": pressed/receding doors, hero fade-out,
    // the room itself rising in. All applied (and removed) by JS only when
    // a real click drove the transition — see enterRoom(); KR_RM/no-button
    // callers take the instant path in JS and never touch these classes.
    '#kit-room-overlay .kr-door.kr-door-pressed{animation:kr-door-press-out .26s var(--kr-ease) both}',
    '#kit-room-overlay .kr-door-recede.kr-browse{animation:kr-door-fade-left .26s var(--kr-ease) both}',
    '#kit-room-overlay .kr-door-recede.kr-describe{animation:kr-door-fade-right .26s var(--kr-ease) both}',
    '#kit-room-overlay .kr-entry-inner.kr-entry-leaving{animation:kr-entry-out .24s var(--kr-ease) both}',
    '#kit-room-overlay .kr-room.kr-room-entering{animation:kr-room-rise .44s var(--kr-spring) both}',
    // §8 Ari orb — CALM at rest per KIT-ROOM-MOTION-REVIEW.md A1 (no
    // perpetual rotate/pulse). The orb only comes alive while a fetch is in
    // flight (.kr-thinking, toggled by krBusy() around loadPopularGrid /
    // loadCategoryGrid / checkout) — a composited glow pseudo, not the
    // box-shadow loop the old ambient version used.
    '#kit-room-overlay .kr-orb{position:relative}',
    '#kit-room-overlay .kr-orb::before{content:"";position:absolute;inset:0;border-radius:50%;',
    'box-shadow:0 0 0 4px rgba(167,139,250,.16);pointer-events:none}',
    '#kit-room-overlay .kr-orb.kr-thinking,#kit-room-overlay .kr-orbmini.kr-thinking{animation:kr-orb-rotate 2.4s linear infinite}',
    '#kit-room-overlay .kr-orb.kr-thinking::before{animation:kr-orb-glow-strong 1.4s ease-in-out infinite;',
    'box-shadow:0 0 0 6px rgba(249,115,22,.3)}',
    // §5 view transitions: compare card spring-in, columns stagger left->right
    // (delay set inline per-column in compareHTML()), best-value pill pop-in.
    '#kit-room-overlay .kr-compare-view{animation:kr-pop .3s var(--kr-spring) both}',
    '#kit-room-overlay .kr-ctab-cell:not(.kr-ctab-lbl){opacity:0;animation:kr-rise .3s var(--kr-ease) both}',
    // one-shot pop-in only — the perpetual shimmer tail is gone (calm pass)
    '#kit-room-overlay .kr-pill.kr-pill-pop-in{animation:kr-pill-pop .3s var(--kr-spring) .05s both}',
    // FLIP-fly clone (krFlipFly) — position/left/top/width/height/transform/
    // opacity are all set inline per-flight; this just gives it a surface.
    '#kit-room-overlay .kr-flip-clone{background:#eef2f7;overflow:hidden;box-shadow:var(--kr-e2);',
    'z-index:2147483647;pointer-events:none;transition:transform .5s var(--kr-spring),opacity .5s ease .18s}',
    '@keyframes kr-rise{from{opacity:0;transform:translateY(12px)}to{opacity:1;transform:none}}',
    '@keyframes kr-pop{from{opacity:0;transform:scale(.97)}to{opacity:1;transform:none}}',
    '@keyframes kr-shimmer{100%{transform:translateX(100%)}}',
    '@keyframes kr-add-pop{0%{transform:scale(1)}40%{transform:scale(.88)}70%{transform:scale(1.1)}100%{transform:scale(1)}}',
    '@keyframes kr-rline-in{from{opacity:0;transform:translateX(16px) scale(.96)}to{opacity:1;transform:none}}',
    '@keyframes kr-flash-border{0%{border-left-color:var(--kr-orange)}100%{border-left-color:transparent}}',
    '@keyframes kr-rline-out{to{opacity:0;transform:translateX(24px) scale(.97)}}',
    '@keyframes kr-breath{0%,100%{transform:scale(1)}50%{transform:scale(1.04)}}',
    // tier-unlock flash is mint-tinted (savings family), not orange
    '@keyframes kr-tier-flash{0%{background:rgba(94,234,212,.22)}100%{background:transparent}}',
    '@keyframes kr-pill-pop{0%{transform:scale(.6)}60%{transform:scale(1.06)}100%{transform:scale(1)}}',
    // per-line price flash on a tier-crossing requote — mint (savings), not
    // yellow (yellow is reserved for the compare "Best value" pill only)
    '@keyframes kr-price-flash{0%{color:var(--kr-mint)}100%{color:#fff}}',
    '@keyframes kr-pop-enable{0%{transform:scale(1)}50%{transform:scale(1.04)}100%{transform:scale(1)}}',
    '@keyframes kr-door-press-out{0%{transform:scale(1);opacity:1}30%{transform:scale(.97);opacity:1}100%{transform:scale(.94);opacity:0}}',
    '@keyframes kr-door-fade-left{to{opacity:0;transform:translateX(-24px)}}',
    '@keyframes kr-door-fade-right{to{opacity:0;transform:translateX(24px)}}',
    '@keyframes kr-entry-out{to{opacity:0;transform:translateY(-10px)}}',
    '@keyframes kr-room-rise{from{opacity:0;transform:scale(.94) translateY(12px)}to{opacity:1;transform:none}}',
    '@keyframes kr-orb-rotate{to{transform:rotate(360deg)}}',
    '@keyframes kr-orb-glow-strong{0%,100%{transform:scale(1);opacity:.7}50%{transform:scale(1.16);opacity:1}}',
    // ---------------------------------------------------------------------
    // Reduced motion — PER-FEATURE fallbacks, not a blanket kill. Every
    // entry/hydrate/ambient animation named above lands in its natural
    // resting CSS state the instant `animation:none` applies (none of them
    // rely on a separate "from" base style — see the comment on each JS
    // trigger). Colour/shadow/opacity transitions are left alone — only
    // transform-based MOVEMENT is removed, per the spec's "no movement,
    // instant opacity/colour" rule. Classes that are only ever applied via
    // pulseClass()/krFlipFly() (button morph, flashes, FLIP-fly, the whole
    // entry->room step-inside sequence, the rail exit-collapse) already
    // self-disable in JS via the KR_RM flag — they need no entry here.
    // ---------------------------------------------------------------------
    '@media (prefers-reduced-motion:reduce){',
    '#kit-room-overlay .kr-shell,#kit-room-overlay .kr-entry-inner,#kit-room-overlay .kr-card,',
    '#kit-room-overlay .kr-orb,#kit-room-overlay .kr-orbmini,#kit-room-overlay .kr-pill,',
    '#kit-room-overlay .kr-rail-empty svg,#kit-room-overlay .kr-compare-view,',
    '#kit-room-overlay .kr-cbub,#kit-room-overlay .kr-typing i,#kit-room-overlay .kr-form-card,',
    '#kit-room-overlay .kr-ctab-cell{animation:none!important;opacity:1!important}',
    '#kit-room-overlay .kr-sk::after{display:none}',
    '#kit-room-overlay .kr-card:hover,#kit-room-overlay .kr-card:focus-within,',
    '#kit-room-overlay .kr-door:hover,#kit-room-overlay .kr-door:focus-visible,',
    '#kit-room-overlay .kr-thumb img,#kit-room-overlay button:active,',
    // mobile bottom-sheet slide (transform+max-height) drops to an instant
    // state-jump under RM — same expanded/collapsed end state, no motion.
    // Configure drawer's slide-in (desktop translateX / mobile translateY —
    // see the shared responsive block below) gets the identical treatment.
    '#kit-room-overlay .kr-rail-body,#kit-room-overlay .kr-cfg-panel,',
    '#kit-room-overlay .kr-rail-chev{transform:none!important;transition:none!important}',
    '}',
    '@media(max-width:820px){#kit-room-overlay .kr-doors{grid-template-columns:1fr}',
    '#kit-room-overlay .kr-shell{height:92vh}',
    '#kit-room-overlay .kr-grid{grid-template-columns:repeat(2,1fr)}',
    // Rail becomes a sticky dark mini-bar + slide-up bottom sheet: total +
    // line-count always visible in the head bar; the line list + breakdown +
    // CTAs slide up (transform + max-height, see .kr-rail-body below) until
    // the shopper taps to expand. `hidden` on #kr-room already keeps this
    // whole thing out of the layout on the entry screen.
    '#kit-room-overlay .kr-room-body{flex-direction:column}',
    '#kit-room-overlay .kr-rail{position:absolute;left:0;right:0;bottom:0;width:auto;',
    'border-top:1px solid rgba(255,255,255,.14);',
    'border-radius:18px 18px 0 0;box-shadow:0 -20px 44px rgba(10,5,25,.4);z-index:2}',
    '#kit-room-overlay .kr-rail-headtotal,#kit-room-overlay .kr-rail-chev{display:block}',
    // slide-up sheet: transform + max-height only (RM drops the slide —
    // see the RM block above, which zeroes the transition so the final
    // expanded/collapsed state still lands correctly, just instantly).
    '#kit-room-overlay .kr-rail-body{max-height:0;overflow:hidden;transform:translateY(10px);',
    'transition:max-height .32s var(--kr-ease),transform .32s var(--kr-ease)}',
    '#kit-room-overlay .kr-rail.kr-expanded .kr-rail-body{max-height:64vh;transform:none}',
    // The rail sheet is position:absolute (out of flow) pinned to the
    // bottom of #kr-room, so nothing reserves space for it automatically.
    // Without this, the floating compare bar (a normal-flow last child of
    // .kr-canvas-col) would render underneath the collapsed rail header.
    '#kit-room-overlay .kr-cmpbar{margin-bottom:64px}',
    // Same collapsed-rail overlap, chat side: .kr-chat-inputbar is the last
    // normal-flow element in .kr-chat (a .kr-canvas-col sibling of .kr-canvas),
    // so without this reservation the collapsed rail bar sits on top of the
    // composer and swallows taps meant for #kr-chat-input/#kr-chat-send.
    '#kit-room-overlay .kr-chat-inputbar{margin-bottom:64px}',
    // Configure drawer becomes a bottom sheet — same slide-up pattern as the
    // rail's own mobile sheet (transform only, RM override above handles it).
    // margin:0 explicitly — the desktop base rule's margin:auto (its own
    // inset:0-based centering, see above) would otherwise cascade through
    // unchanged; this mobile override only re-specifies 2 of 4 insets
    // (top:auto/bottom:0) so it's worth being explicit rather than relying
    // on auto-margin's three-way-auto-resolves-to-0 fallback.
    '#kit-room-overlay .kr-cfg-panel{left:0;right:0;top:auto;bottom:0;width:auto;max-width:none;margin:0;',
    'height:auto;max-height:88vh;border-radius:18px 18px 0 0;transform:translateY(100%);',
    'box-shadow:0 -20px 50px rgba(10,5,25,.3)}',
    '#kit-room-overlay .kr-cfg-panel.kr-show{transform:translateY(0)}',
    // Mobile-only: the base 1:1 aspect-ratio makes the photo as tall as the
    // sheet is wide — on a 390px phone that's ~390px of a ~750px sheet
    // (max-height:88vh), ~45% of the viewport eaten by the photo alone with
    // no room left for the form below it. A shorter well (object-fit:contain
    // keeps the image itself uncropped) leaves the fields the space.
    '#kit-room-overlay .kr-cfg-img{aspect-ratio:16/9;max-height:220px}',
    '}',
    // Narrow phones only — measured: at 390px the topbar has ~58px free for
    // .kr-who once orb+seg+close+gaps+padding are accounted for, but "The
    // Bulk Desk" alone needs ~101px (a pre-existing tightness, not something
    // the subtitle caused) — so the subtitle is dropped entirely here (no
    // room for it either way), a few other elements trim down to claw back
    // width, and — even after that — the title is allowed to wrap onto a
    // second line (topbar has no fixed height) rather than lean on the
    // ellipsis fallback above: wrapping loses no words, ellipsis would.
    '@media(max-width:480px){#kit-room-overlay .kr-who-sub{display:none}',
    '#kit-room-overlay .kr-topbar{gap:8px}',
    '#kit-room-overlay .kr-orb{width:28px;height:28px}',
    '#kit-room-overlay .kr-seg button{padding:8px 10px}',
    '#kit-room-overlay .kr-who b{font-size:15px;white-space:normal;overflow:visible;text-overflow:clip;line-height:1.15}',
    '}'
  ].join('');

  function injectCSS() {
    if (document.getElementById(STYLE_ID)) return;
    var s = document.createElement('style');
    s.id = STYLE_ID;
    s.textContent = CSS;
    document.head.appendChild(s);
  }

  // ===========================================================================
  // Small helpers
  // ===========================================================================
  function qs(sel, root) { return (root || document).querySelector(sel); }
  function el(tag, cls, html) {
    var n = document.createElement(tag);
    if (cls) n.className = cls;
    if (html != null) n.innerHTML = html;
    return n;
  }
  function escapeHtml(s) {
    return String(s == null ? '' : s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function money(n) {
    var v = (typeof n === 'number' && isFinite(n)) ? n : 0;
    return '$' + v.toLocaleString('en-AU', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  }
  // Shallow own-props clone (Configure drawer state) — no Object.assign
  // (ES5-safe file, no bundler/polyfills assumed).
  function krCloneObj(o) {
    var out = {};
    if (o) { for (var k in o) { if (Object.prototype.hasOwnProperty.call(o, k)) out[k] = o[k]; } }
    return out;
  }
  // Item 4 — a rail line's `artwork` used to be ONE flat {printReadyUrl,
  // uploadedUrl,previewUrl,name} object; it's now a map keyed by side (front/
  // back/leftSleeve/rightSleeve — see krArtworkSideKey below). A live
  // customer's already-saved quote (localStorage OR a resumed getKitQuote
  // doc) may still carry the OLD flat shape — detect it (a real uploadedUrl
  // STRING living directly on the object; the new map's own values are
  // objects, never a string, at its top level) and migrate it onto 'front'
  // (the overwhelmingly common single-placement case) rather than lose it.
  // Returns null for nothing/empty, never a fabricated entry.
  function krNormalizeArtworkMap(raw) {
    if (!raw) return null;
    if (typeof raw.uploadedUrl === 'string') return { front: krCloneObj(raw) };
    var out = {};
    for (var k in raw) { if (Object.prototype.hasOwnProperty.call(raw, k) && raw[k]) out[k] = krCloneObj(raw[k]); }
    return Object.keys(out).length ? out : null;
  }
  // P1 fix #9 (2026-08) — CLIENT mirror of the server's canonical size table
  // (production-dashboard/functions/src/index.ts normalizeSize()/SIZE_NORMALIZE,
  // ~index.ts:31877). MUST stay byte-for-byte in sync with that table — this is a
  // DISPLAY/edit-consistency mirror only (see krRemapSizeRunToVocab below), never a
  // second source of truth for pricing; checkout already bills correctly via the two
  // server-side normalizeSize() call sites in createKitDraft/saveKitQuote.
  var KR_SIZE_NORMALIZE = {
    'XS': 'XSM', 'XSML': 'XSM', 'EXTRA SMALL': 'XSM',
    'S': 'SML', 'SML': 'SML', 'SMALL': 'SML',
    'M': 'MED', 'MED': 'MED', 'MEDIUM': 'MED',
    'L': 'LRG', 'LRG': 'LRG', 'LARGE': 'LRG',
    'XL': 'XLG', 'XLG': 'XLG', 'EXTRA LARGE': 'XLG',
    '2XL': '2XL', 'XXL': '2XL', '2X': '2XL',
    '3XL': '3XL', 'XXXL': '3XL', '3X': '3XL',
    '4XL': '4XL', 'XXXXL': '4XL', '4X': '4XL',
    '5XL': '5XL', '5X': '5XL'
  };
  function krNormalizeSize(size) {
    var upper = String(size == null ? '' : size).replace(/^\s+|\s+$/g, '').toUpperCase();
    return KR_SIZE_NORMALIZE[upper] || upper;
  }
  // A rail line's sizeRun can arrive keyed in a vocabulary that ISN'T this exact
  // product's own Shopify variant option values — e.g. Ari's update_quote sends short
  // codes ("S"/"M"/"L") or AS Colour's real codes ("SML"/"MED") depending on how it
  // read the product, while `sizes` (state.sizes, from sizeOptionFromProductJs) is
  // whatever THIS product's variants literally use. A strict `sizeRun[realSize]`
  // lookup then finds nothing for every size — the Configure drawer's chips all read
  // 0 despite a real size run existing (P1 #9). Fix: remap sizeRun's keys onto the
  // product's REAL vocabulary by normalized-size equivalence, ONCE, at the boundary
  // where the real vocabulary becomes known (buildLineConfigUI's setSizes()) — every
  // read/edit site downstream (the chips, sizeRunTotal, getConfig) then just works
  // unchanged, and an edit can never leave two vocabularies (e.g. both "S" and "SML")
  // sitting on one line, because there's only ever one canonical key per size after
  // this runs. Two source keys that both normalize onto the same real size (unlikely,
  // but a client could combine an Ari-added line with a hand-edited one) are SUMMED,
  // never silently dropped. A source key with no equivalent in `sizes` is left as-is
  // (defensive — never lose units the product genuinely doesn't have a size chip for).
  function krRemapSizeRunToVocab(sizeRun, sizes) {
    if (!sizeRun || !sizes || !sizes.length) return sizeRun;
    var normToReal = {};
    for (var i = 0; i < sizes.length; i++) normToReal[krNormalizeSize(sizes[i])] = sizes[i];
    var out = null, changed = false;
    for (var k in sizeRun) {
      if (!Object.prototype.hasOwnProperty.call(sizeRun, k)) continue;
      var v = sizeRun[k];
      if (!v) continue;
      var real = normToReal[krNormalizeSize(k)] || k;
      if (real !== k) changed = true;
      out = out || {};
      out[real] = (out[real] || 0) + v;
    }
    return changed ? out : sizeRun; // unchanged input returned verbatim — never a needless new reference
  }
  // Chat — Ari's reply is plain text from the model, never trusted markup.
  // ALWAYS escapeHtml() first, THEN linkify: run this regex over the
  // already-escaped string, so a URL can only ever come from text the model
  // wrote (never raw HTML injection) and any literal &/</> in a reply still
  // renders as text, not markup. Trailing sentence punctuation right after a
  // URL (".", ",", ")", etc.) is peeled off the link so "here's your link)."
  // doesn't swallow the closing paren/period into the href.
  function krLinkifyEscaped(safeHtml) {
    return safeHtml.replace(/(https?:\/\/[^\s<]+)/g, function (url) {
      var trail = '';
      var m = url.match(/[).,;:!?\]]+$/);
      if (m) { trail = m[0]; url = url.slice(0, -trail.length); }
      if (!url) return m ? trail : '';
      return '<a href="' + url + '" target="_blank" rel="noopener noreferrer">' + url + '</a>' + trail;
    });
  }
  // Chat — Ari's replies use light markdown (**bold**, "- " bullet lines)
  // that used to render as raw literal asterisks/dashes. SAME safety
  // contract as krLinkifyEscaped above: escapeHtml() FIRST, every token
  // regex below runs ONLY on the already-escaped string, so a crafted reply
  // like "**<img src=x onerror=alert(1)>**" can only ever produce more
  // escaped TEXT wrapped in <strong> — never live markup or an attribute
  // breakout (the replacement template has no attribute context at all).
  // Deliberately minimal (bold + bullets + the existing linkify), not a
  // full markdown parser. Line breaks need no handling here — .kr-cbub-txt
  // is already `white-space:pre-wrap`, so literal \n in the escaped string
  // renders as a real line break with zero extra markup.
  function krFormatChatText(raw) {
    var safe = escapeHtml(raw);
    // "- item" / "* item" at the START of a line only (never a mid-sentence
    // em-dash like "5050T - Block Tee") -> a real bullet glyph.
    safe = safe.replace(/(^|\n)[ \t]*[-*][ \t]+/g, '$1&bull;&nbsp;');
    // **bold** -> <strong>bold</strong>. Non-greedy, no nesting, plain text
    // content only between the tags.
    safe = safe.replace(/\*\*([^*]+?)\*\*/g, '<strong>$1</strong>');
    return krLinkifyEscaped(safe);
  }
  // getPreviewConfig returns the full-res master image (often 600KB+). Shopify's
  // CDN resizes on the fly via ?width= — request a thumbnail sized for where it's
  // shown so cards load ~instantly instead of pulling multi-MB masters.
  function krImg(url, w) {
    if (!url || (url.indexOf('cdn.shopify.com') === -1 && url.indexOf('/cdn/shop/') === -1)) return url;
    if (/[?&]width=/.test(url)) return url;
    return url + (url.indexOf('?') > -1 ? '&' : '?') + 'width=' + (w || 400);
  }
  // opts.timeoutMs (optional): aborts the request past that many ms and
  // rejects with an honest "taking longer than usual" error instead of
  // hanging forever — a cold-starting CF on flaky mobile otherwise leaves a
  // "Creating your order…"/"Building your PDF…" button stuck indefinitely.
  // Feature-detected (AbortController) so a very old browser just skips the
  // timeout rather than breaking the request; never sent if the caller
  // already supplied its own `signal`.
  function fetchJSON(url, opts) {
    opts = opts || {};
    var fetchOpts = opts, timer = null, timedOut = false;
    if (opts.timeoutMs && typeof AbortController === 'function' && !opts.signal) {
      var controller = new AbortController();
      fetchOpts = krCloneObj(opts);
      fetchOpts.signal = controller.signal;
      timer = setTimeout(function () { timedOut = true; controller.abort(); }, opts.timeoutMs);
    }
    return fetch(url, fetchOpts).then(function (res) {
      if (timer) clearTimeout(timer);
      return res.json().catch(function () {
        throw new Error('Unexpected response (' + res.status + ')');
      }).then(function (data) {
        if (!res.ok || (data && data.ok === false)) {
          throw new Error((data && data.error) || ('Request failed (' + res.status + ')'));
        }
        return data;
      });
    }).catch(function (err) {
      if (timer) clearTimeout(timer);
      if (timedOut || (err && err.name === 'AbortError')) {
        throw new Error('Taking longer than usual — please try again.');
      }
      throw err;
    });
  }

  // ===========================================================================
  // Artwork upload ("Ari sees your artwork") — pure, instance-agnostic
  // helpers: sign+PUT to R2 (mirrors gang-sheet-builder.js's r2Upload
  // verbatim), and building a downscaled JPEG "vision copy" from whatever
  // the customer attached (raster image / SVG / PDF page 1). None of this
  // touches `root`/rail/chat state — callers inside openKitRoom() do that.
  // ===========================================================================
  function guessMimeFromExt(name) {
    var m = /\.([a-z0-9]+)$/i.exec(name || '');
    var ext = m ? m[1].toLowerCase() : '';
    var map = { png: 'image/png', jpg: 'image/jpeg', jpeg: 'image/jpeg', webp: 'image/webp', gif: 'image/gif', svg: 'image/svg+xml', pdf: 'application/pdf' };
    return map[ext] || 'application/octet-stream';
  }
  function previewFilenameFor(name) {
    var base = String(name || 'artwork').replace(/\.[a-z0-9]+$/i, '');
    return (base || 'artwork') + '-preview.png';
  }
  // Cheap, source-mime-only guess at whether the preview PNG actually kept
  // an alpha channel worth compositing — NOT a pixel inspection (that would
  // mean decoding the full canvas' ImageData for every upload just to show
  // an optional tip). PNG/WEBP/SVG can carry real transparency; JPEG/GIF
  // uploads and rasterised PDF pages (loadPdfCanvas always renders onto an
  // opaque white background, see below) never do. False negatives are fine
  // here (a PNG that happens to be fully opaque just won't get the tip) —
  // false positives would be worse (implying transparency that isn't
  // there), and this heuristic never produces one.
  function krMayHaveTransparency(mime) {
    return mime === 'image/png' || mime === 'image/webp' || mime === 'image/svg+xml';
  }
  // POST {folder,contentType,filename,contentLength} -> {uploadUrl,publicUrl,key}
  // -> PUT bytes. Same shape/contract as gang-sheet-builder.js's r2Upload.
  function r2Upload(folder, contentType, filename, blob) {
    return fetch(r2SignUrl(), {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ folder: folder, contentType: contentType, filename: filename, contentLength: blob.size })
    }).then(function (signRes) {
      if (!signRes.ok) throw new Error('upload sign failed (' + signRes.status + ')');
      return signRes.json();
    }).then(function (signed) {
      return fetch(signed.uploadUrl, { method: 'PUT', headers: { 'Content-Type': contentType }, body: blob })
        .then(function (putRes) {
          if (!putRes.ok) throw new Error('upload failed (' + putRes.status + ')');
          return { uploadUrl: signed.uploadUrl, publicUrl: signed.publicUrl, key: signed.key };
        });
    });
  }
  // Raster image (png/jpeg/webp/gif) -> <img>, via a local blob: URL — never
  // remote, so this never taints the canvas (no crossOrigin needed).
  function loadRasterImage(file) {
    return new Promise(function (resolve, reject) {
      var img = new Image();
      img.onload = function () { resolve(img); };
      img.onerror = function () { reject(new Error('couldn’t read that image')); };
      img.src = URL.createObjectURL(file);
    });
  }
  // SVG -> <img>, via a data: URL built from the file's own text (same
  // approach gang-sheet-builder.js's rasterSvg() uses) — also same-origin.
  function loadSvgImage(file) {
    return new Promise(function (resolve, reject) {
      var rd = new FileReader();
      rd.onload = function () {
        var img = new Image();
        img.onload = function () { resolve(img); };
        img.onerror = function () { reject(new Error('couldn’t read that SVG')); };
        img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(String(rd.result || ''));
      };
      rd.onerror = function () { reject(new Error('couldn’t read that file')); };
      rd.readAsText(file);
    });
  }
  // pdf.js, loaded once and cached on window — same pinned 3.11.174 UMD
  // build as gang-sheet-builder.js (never the v4+ ESM-only build).
  var krPdfjsPromise = null;
  function loadPdfjs() {
    if (window.pdfjsLib) return Promise.resolve(window.pdfjsLib);
    if (!krPdfjsPromise) {
      krPdfjsPromise = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = PDFJS_SRC;
        s.onload = function () {
          if (window.pdfjsLib) {
            try { window.pdfjsLib.GlobalWorkerOptions.workerSrc = PDFJS_WORKER; } catch (e) {}
            resolve(window.pdfjsLib);
          } else {
            reject(new Error('pdf.js failed to load'));
          }
        };
        s.onerror = function () { reject(new Error('pdf.js failed to load')); };
        document.head.appendChild(s);
      });
    }
    return krPdfjsPromise;
  }
  // Render a PDF's page 1 to a <canvas> at ~targetPx wide — a preview only;
  // the untouched original bytes are what gets uploaded as the master.
  function loadPdfCanvas(file, targetPx) {
    return loadPdfjs().then(function (pdfjs) {
      return file.arrayBuffer().then(function (buf) {
        return pdfjs.getDocument({ data: buf }).promise
          .then(function (pdf) { return pdf.getPage(1); })
          .then(function (page) {
            var v0 = page.getViewport({ scale: 1 });
            var s = targetPx / Math.max(v0.width, v0.height, 1);
            var vp = page.getViewport({ scale: s });
            var cv = document.createElement('canvas');
            cv.width = Math.max(1, Math.round(vp.width));
            cv.height = Math.max(1, Math.round(vp.height));
            return page.render({ canvasContext: cv.getContext('2d'), viewport: vp, background: '#ffffff' }).promise
              .then(function () { return cv; });
          });
      });
    });
  }
  // Build the downscaled "vision copy" PNG blob for whatever was attached —
  // raster/SVG/PDF all funnel through the same final resize+encode step so
  // every source ends up a single, bounded (<=ARTWORK_PREVIEW_MAX_PX long
  // edge) PNG, never the full-res master (that stays untouched for
  // check_artwork's real DPI/size numbers). PNG (not JPEG) so a transparent
  // logo (PNG-with-alpha is the common case for club/business marks)
  // survives into both the vision copy Ari sees AND the live garment
  // mockup overlay (artworkOverlayHtml) — Anthropic's vision API accepts
  // PNG directly, no separate flatten step needed there. The canvas below
  // is left transparent (no fillStyle/fillRect) rather than flattened onto
  // white, so drawImage() carries over whatever alpha the source actually
  // has: real for an alpha PNG/WEBP/SVG, none for an opaque JPEG (nothing
  // to carry), always-opaque-white for a PDF page (loadPdfCanvas rasterises
  // page 1 onto a hardcoded white background — a PDF preview is never
  // transparent, honestly, since the source page isn't either).
  function buildArtworkPreviewBlob(file, mime) {
    var srcP;
    if (mime === 'application/pdf') {
      srcP = loadPdfCanvas(file, ARTWORK_PREVIEW_MAX_PX).then(function (cv) { return { src: cv, w: cv.width, h: cv.height }; });
    } else if (mime === 'image/svg+xml') {
      srcP = loadSvgImage(file).then(function (img) { return { src: img, w: img.naturalWidth || img.width || 1, h: img.naturalHeight || img.height || 1 }; });
    } else {
      srcP = loadRasterImage(file).then(function (img) { return { src: img, w: img.naturalWidth || img.width || 1, h: img.naturalHeight || img.height || 1 }; });
    }
    return srcP.then(function (src) {
      var longEdge = Math.max(src.w, src.h) || 1;
      var scale = Math.min(1, ARTWORK_PREVIEW_MAX_PX / longEdge);
      var w = Math.max(1, Math.round(src.w * scale));
      var h = Math.max(1, Math.round(src.h * scale));
      var cv = document.createElement('canvas');
      cv.width = w; cv.height = h;
      var ctx = cv.getContext('2d');
      // Deliberately no fillStyle/fillRect white flatten here — a fresh
      // canvas is already fully transparent, so drawImage() preserves
      // whatever alpha the source actually carries (see the function
      // comment above).
      ctx.drawImage(src.src, 0, 0, w, h);
      return new Promise(function (resolve, reject) {
        cv.toBlob(function (b) { b ? resolve(b) : reject(new Error('couldn’t prepare a preview')); }, 'image/png');
      });
    });
  }

  // krUploadArtwork(file) — the full attach flow: validate, then upload TWO
  // objects in parallel —
  //   1. the untouched original bytes as the MASTER, to customer-uploads.
  //      This is what check_artwork reads for real DPI/print-readiness, and
  //      what production eventually prints from — never re-encoded.
  //   2. a downscaled (<=ARTWORK_PREVIEW_MAX_PX long edge) PNG "vision
  //      copy" as the PREVIEW, to customer-previews. This is what the chat
  //      message's `content:[{type:'image',source:{type:'url',url:...}}]`
  //      block points Ari's vision at (PNG is a supported vision media type)
  //      AND what the live garment mockup overlay (artworkOverlayHtml) uses
  //      as its <img src> — PNG rather than JPEG specifically so a
  //      transparent logo's alpha survives into both.
  // Both r2Upload() calls return {uploadUrl (signed PUT, single-use, never
  // persisted), publicUrl (plain https://uploads.printibly.com.au/... CDN
  // URL — see r2.server.ts publicUrlFor(), NOT signed, publicly fetchable
  // with no auth — confirmed from the source, this is the field a vision
  // `source:{type:'url'}` block needs), key}. Resolves
  // {uploadedUrl, previewUrl, printReadyUrl, mayHaveTransparency} —
  // uploadedUrl/printReadyUrl are the SAME master publicUrl (two names for
  // two call-sites' vocabulary: "what got uploaded" vs "what production
  // prints from" — they're identical here, there's no separate print-ready
  // conversion step for a Bulk-Desk-attached reference file the way there
  // is for a gang-sheet design asset). mayHaveTransparency is the cheap
  // source-mime guess from krMayHaveTransparency — used only to decide
  // whether to show the "transparent PNG shows best" tip next to an
  // attached slot, never to alter the render itself.
  function krUploadArtwork(file) {
    if (!file) return Promise.reject(new Error('No file chosen.'));
    var mime = (file.type && ARTWORK_ALLOWED_MIME[file.type]) ? file.type : null;
    if (!mime) {
      var guessed = guessMimeFromExt(file.name);
      if (ARTWORK_ALLOWED_MIME[guessed]) mime = guessed;
    }
    if (!mime) return Promise.reject(new Error('That file type isn’t supported — try an image or a PDF.'));
    if (file.size > ARTWORK_MAX_BYTES) return Promise.reject(new Error('That file is over the 25MB limit.'));
    var masterP = r2Upload(ARTWORK_MASTER_FOLDER, mime, file.name, file);
    var previewP = buildArtworkPreviewBlob(file, mime).then(function (blob) {
      return r2Upload(ARTWORK_PREVIEW_FOLDER, 'image/png', previewFilenameFor(file.name), blob);
    });
    return Promise.all([masterP, previewP]).then(function (results) {
      var master = results[0], preview = results[1];
      return {
        uploadedUrl: master.publicUrl, previewUrl: preview.publicUrl, printReadyUrl: master.publicUrl,
        mayHaveTransparency: krMayHaveTransparency(mime)
      };
    });
  }

  // ===========================================================================
  // PERF: client-side pricing — replaces the per-card getQuote + getPreviewConfig
  // round-trips with a single Shopify /products/{handle}.js fetch (CDN-cached,
  // no Cloud Function cold start) + an EXACT client replica of the server's
  // quantity-only pricing math (quote.ts computeQuote in production-dashboard).
  //
  // WRITER-SIDE TRUTH verified live before building this (curled getQuote across
  // 4 different real products/vendors — AS Colour, DNC, Syzmik):
  //   - Garment price is FLAT across every variant of a product (Printibly
  //     doesn't per-size/colour price — same fact order-draft.ts's reorder
  //     endpoint comment states, confirmed empirically: MIN/MODAL/every variant
  //     all returned the identical price on every product tested). So
  //     product.js's `price_min` IS getQuote's `garmentUnitPrice`, safely.
  //   - The bulk tier ladder (10/20/50/100/200 -> 5/10/15/20/30%; 400+ -> 40%
  //     added 2026-10 to match config.bulkDiscounts) held
  //     identically across AS Colour, DNC and Syzmik — it's a site-wide
  //     convention, not per-product config, for every product checked.
  //   - IMPORTANT CORRECTION vs the literal spec formula: `floor(price*(1-rate)
  //     *100)/100` is NOT always identical to the server's actual math
  //     (floor EACH component's discount separately, THEN subtract) — floor
  //     doesn't distribute over subtraction. Concrete counter-example found
  //     while verifying: AS Colour Carrie Tote, qty 75 (15% tier), print
  //     $11.95 -> the literal formula gives perUnit $22.90, but live getQuote
  //     returns $22.91. computeClientQuote() below instead replicates
  //     quote.ts's actual per-component-floor-then-subtract order of
  //     operations exactly, and matched getQuote to the cent on every one of
  //     the 4 live products checked (DNC $27.96, Syzmik $27.92, Chad Polo
  //     $35.96, Carrie Tote $22.91 — all exact, including the case above).
  // ===========================================================================
  var KR_TIERS = [
    { minQuantity: 400, discountPercent: 40 },
    { minQuantity: 200, discountPercent: 30 },
    { minQuantity: 100, discountPercent: 20 },
    { minQuantity: 50, discountPercent: 15 },
    { minQuantity: 20, discountPercent: 10 },
    { minQuantity: 10, discountPercent: 5 }
  ];
  // DEFAULT_ADDON fallbacks (quote.ts) — used only if the live fetch below
  // fails; verified live against the real custom-printing-service product
  // (curled 2026-08-04): Single Surface Print $11.95 (PRINT-SINGLE), Two
  // Surface Print $23.95 (PRINT-DOUBLE), Sleeve Print $7.00 (PRINT-SLEEVE).
  var KR_DEFAULT_PRINT_PRICE = 11.95;
  var KR_DEFAULT_DOUBLE_PRICE = 23.95;
  var KR_DEFAULT_SLEEVE_PRICE = 7.00;
  function krR2(n) { return Math.round(n * 100) / 100; }
  function krFloorCents(n) { return Math.floor(n * 100) / 100; }

  // ===========================================================================
  // Multi-surface print pricing (Configure drawer) — mirrors quote.ts's
  // computeQuote() surface detection EXACTLY (production-dashboard/functions/
  // src/lib/quote.ts:66-79): front = any location containing "front" or
  // "chest" (chest counts as front — front-chest/left-chest/right-chest, and
  // a cap's own "front-panel" zone too, confirmed live); back = any location
  // containing "back"; sleeves = COUNT of locations containing "sleeve"
  // (left-sleeve + right-sleeve = 2). Base price is the COMBINED `double`
  // add-on when both front AND back are picked — never 2x the single price —
  // then each sleeve adds the flat sleeve price on top, r2-rounded once after
  // the sleeve add (same order of operations as quote.ts's
  // `printPricePerGarment = r2(printPricePerGarment + sleeveCount * a.sleeve)`).
  // ===========================================================================
  // Cross-device resume only: saveKitQuote's stored `rail[].printLocations`
  // is a collapsed human label ("Front,Back,Left Sleeve" — index.ts's
  // zoneLabel() output), not the real zone keys, so this is a best-effort
  // approximation back to a representative key per surface — good enough to
  // seed the Configure drawer with a sensible starting point, which the
  // shopper can still adjust. A same-device localStorage restore never needs
  // this — persistLocalQuote() stores the real printLocations[] verbatim.
  function krPrintLocationsFromLabel(label) {
    if (!label) return [];
    var parts = String(label).split(',').map(function (p) { return p.trim().toLowerCase(); }).filter(Boolean);
    var out = [];
    parts.forEach(function (l) {
      if (l === 'front') out.push('front-full');
      else if (l === 'back') out.push('back-full');
      else if (l === 'left sleeve') out.push('left-sleeve');
      else if (l === 'right sleeve') out.push('right-sleeve');
      else if (l === 'sleeve') out.push('left-sleeve');
    });
    return out;
  }

  // Configure drawer chip labels — known zone keys get a friendly name;
  // ANY other key (e.g. a cap's own "front-panel", confirmed live via
  // getPreviewConfig — it's not one of the 8 "standard" keys) still renders
  // honestly via the fallback title-case, never a raw slug. Chips are built
  // from whatever preview.zones ACTUALLY has for this product, never a
  // hardcoded list — see handleOpenConfigure.
  var KR_ZONE_LABELS = {
    'front-full': 'Front (Full)', 'front-chest': 'Front (Chest)',
    'left-chest': 'Left Chest', 'right-chest': 'Right Chest',
    'back-full': 'Back (Full)', 'back-shoulder': 'Back (Shoulder)',
    'left-sleeve': 'Left Sleeve', 'right-sleeve': 'Right Sleeve',
    'front-panel': 'Front Panel'
  };
  function zoneDisplayLabel(z) {
    if (KR_ZONE_LABELS[z]) return KR_ZONE_LABELS[z];
    var words = String(z || '').split('-');
    for (var i = 0; i < words.length; i++) { words[i] = words[i].charAt(0).toUpperCase() + words[i].slice(1); }
    return words.join(' ') || 'Placement';
  }
  var KR_ZONE_ORDER = ['front-full', 'front-chest', 'front-panel', 'left-chest', 'right-chest', 'back-full', 'back-shoulder', 'left-sleeve', 'right-sleeve'];
  function orderZoneKeys(keys) {
    var known = [], rest = [];
    keys.forEach(function (k) { (KR_ZONE_ORDER.indexOf(k) > -1 ? known : rest).push(k); });
    known.sort(function (a, b) { return KR_ZONE_ORDER.indexOf(a) - KR_ZONE_ORDER.indexOf(b); });
    rest.sort();
    return known.concat(rest);
  }

  // Human-readable "Front + Back + Left Sleeve" summary of a line's real
  // placements, for the rail row meta + Ari's quote context — replaces the
  // old hardcoded "1 front print". Same front/back/sleeve grouping as
  // krPrintPriceForLocations, so the label always agrees with the price.
  function zoneGroupLabel(printLocations) {
    var locs = (printLocations || []).map(function (l) { return String(l || '').toLowerCase(); });
    if (!locs.length) return 'No placement';
    var front = locs.filter(function (l) { return l.indexOf('front') > -1 || l.indexOf('chest') > -1; }).length > 0;
    var back = locs.filter(function (l) { return l.indexOf('back') > -1; }).length > 0;
    var leftSleeve = locs.filter(function (l) { return l.indexOf('sleeve') > -1 && l.indexOf('left') > -1; }).length > 0;
    var rightSleeve = locs.filter(function (l) { return l.indexOf('sleeve') > -1 && l.indexOf('right') > -1; }).length > 0;
    var otherSleeve = locs.filter(function (l) { return l.indexOf('sleeve') > -1 && l.indexOf('left') === -1 && l.indexOf('right') === -1; }).length > 0;
    var parts = [];
    if (front && back) parts.push('Front + Back');
    else if (front) parts.push('Front');
    else if (back) parts.push('Back');
    if (leftSleeve) parts.push('Left Sleeve');
    if (rightSleeve) parts.push('Right Sleeve');
    if (otherSleeve) parts.push('Sleeve');
    return parts.length ? parts.join(' + ') : 'No placement';
  }
  // Item 4 — per-ZONE (not per-line) side grouping, SAME front/back/sleeve
  // taxonomy as zoneGroupLabel above (never a second one) but for a single
  // print-location key rather than a whole line's list — this is the key
  // state.artwork is keyed by, so "front artwork" always means the exact
  // same thing zoneGroupLabel's "Front" already reads as everywhere else in
  // this file (rail meta, Ari's quote context).
  function krArtworkSideKey(loc) {
    var l = String(loc || '').toLowerCase();
    if (l.indexOf('sleeve') > -1 && l.indexOf('left') > -1) return 'leftSleeve';
    if (l.indexOf('sleeve') > -1 && l.indexOf('right') > -1) return 'rightSleeve';
    if (l.indexOf('sleeve') > -1) return 'sleeve';
    if (l.indexOf('back') > -1) return 'back';
    if (l.indexOf('front') > -1 || l.indexOf('chest') > -1) return 'front';
    return 'other';
  }
  var KR_ARTWORK_SIDE_LABELS = { front: 'Front', back: 'Back', leftSleeve: 'Left Sleeve', rightSleeve: 'Right Sleeve', sleeve: 'Sleeve', other: 'Placement' };
  // Every side key the line's CURRENT print placements touch, in a stable
  // display order — dedup'd (2 back zones both map to 'back', one slot).
  var KR_ARTWORK_SIDE_ORDER = ['front', 'back', 'leftSleeve', 'rightSleeve', 'sleeve', 'other'];
  function krArtworkSidesForLocations(printLocations) {
    var seen = {}, out = [];
    (printLocations || []).forEach(function (l) {
      var side = krArtworkSideKey(l);
      if (!seen[side]) { seen[side] = true; out.push(side); }
    });
    out.sort(function (a, b) { return KR_ARTWORK_SIDE_ORDER.indexOf(a) - KR_ARTWORK_SIDE_ORDER.indexOf(b); });
    return out;
  }
  // ALL CAPS catalogue colour names ("BLACK") -> "Black" for display only —
  // never changes what's sent to the CFs (colour is sent verbatim).
  function krTitleCase(s) {
    return String(s || '').toLowerCase().replace(/(^|\s)([a-z])/g, function (m, sp, c) { return sp + c.toUpperCase(); });
  }
  // Item 3 — live stock, normalized to the real getPreviewConfig shape:
  // c.stock is "in"|"low"|"out"|"unknown" (production-dashboard
  // functions/src/lib/product-facts.ts colourStock() — a per-colour
  // roll-up of the BEST size-level signal; per-SIZE stock is computed
  // server-side but never returned to the client, so it can't be shown
  // here without fabricating it — colour-level only, honestly). Any value
  // outside the known enum (missing field, a future addition) is treated
  // as 'unknown', never silently as 'in' — "stock unavailable" beats a
  // fake in-stock claim.
  function krStockValue(c) {
    var v = String(c && c.stock || '').toLowerCase();
    return (v === 'in' || v === 'low' || v === 'out') ? v : 'unknown';
  }
  function krStockSuffix(v) {
    if (v === 'out') return ' — out of stock';
    if (v === 'low') return ' — low stock';
    if (v === 'unknown') return ' — stock unavailable';
    return '';
  }
  // Selected-colour label suffix (" — Low stock" etc.) — looked up fresh
  // against the live availableColours list every render/click so an
  // editing line whose saved colour has since sold out still tells the
  // truth instead of silently showing it as available.
  function krColourStockLabelSuffix(colours, name) {
    if (!name || !colours) return '';
    for (var i = 0; i < colours.length; i++) {
      if (String(colours[i].name).toLowerCase() === String(name).toLowerCase()) {
        var v = krStockValue(colours[i]);
        if (v === 'low') return ' (Low stock)';
        if (v === 'out') return ' (Now out of stock)';
        if (v === 'unknown') return ' (Stock unavailable)';
        return '';
      }
    }
    return '';
  }
  // "2XL×10 L×20…" — capped so a big size run never overruns the rail row.
  function sizeRunTotal(sizeRun) {
    var total = 0;
    for (var k in sizeRun) { if (Object.prototype.hasOwnProperty.call(sizeRun, k)) total += sizeRun[k] || 0; }
    return total;
  }
  // P1 fix #6 — Ari's update_quote "add" on an (productId,colour) identity ALREADY on
  // the rail must MERGE quantities into that line (never overwrite — see
  // applyAriUpsert), including a per-size merge when both sides carry a size run.
  // Plain per-key sum; a size present on only one side just carries through.
  function mergeSizeRuns(a, b) {
    if (!a && !b) return null;
    var out = {};
    if (a) { for (var k1 in a) { if (Object.prototype.hasOwnProperty.call(a, k1)) out[k1] = (out[k1] || 0) + (a[k1] || 0); } }
    if (b) { for (var k2 in b) { if (Object.prototype.hasOwnProperty.call(b, k2)) out[k2] = (out[k2] || 0) + (b[k2] || 0); } }
    return Object.keys(out).length ? out : null;
  }
  function sizeRunSummary(sizeRun) {
    if (!sizeRun) return '';
    var keys = Object.keys(sizeRun).filter(function (k) { return sizeRun[k] > 0; });
    if (!keys.length) return '';
    var shown = keys.slice(0, 3).map(function (k) { return k + '×' + sizeRun[k]; }).join(' ');
    return shown + (keys.length > 3 ? '…' : '');
  }

  // ===========================================================================
  // Item 5 — smart size-run presets. Pure client, one-click ratios that
  // PREFILL state.sizeRun (still fully editable afterwards, same +/- chips).
  // krSizePresetKinds() decides which chips make sense for THIS product's
  // real size list (from product.js — never a hardcoded S-5XL); Youth+Adult
  // only appears when the list genuinely has youth-labelled sizes.
  // ===========================================================================
  var KR_YOUTH_SIZE_RE = /^(y|youth|kids?|junior)\b|^y[-\s]?(xs|s|m|l|xl|2xl)$/i;
  var KR_SIZE_PRESETS = [
    { kind: 'standard', label: 'Standard spread' },
    { kind: 'youth', label: 'Youth + Adult mix' },
    { kind: 'even', label: 'Even split' }
  ];
  function krSizePresetKinds(sizes) {
    var hasYouth = (sizes || []).some(function (s) { return KR_YOUTH_SIZE_RE.test(String(s || '').trim()); });
    return KR_SIZE_PRESETS.filter(function (p) { return p.kind !== 'youth' || hasYouth; });
  }
  // Bell-curve weights over an ordered size list — peak at the middle index,
  // tapering toward the ends. Mirrors the real-world shape of a bulk apparel
  // order (most units land in the middle sizes, fewer at the extremes) far
  // better than a flat split, without needing per-product sales data we
  // don't have.
  function krBellWeights(list) {
    if (!list.length) return {};
    var mid = (list.length - 1) / 2;
    var raw = list.map(function (s, i) { return 1 / (1 + Math.abs(i - mid)); });
    var sum = raw.reduce(function (a, b) { return a + b; }, 0);
    var out = {};
    list.forEach(function (s, i) { out[s] = raw[i] / sum; });
    return out;
  }
  function krEvenWeights(list) {
    var out = {};
    list.forEach(function (s) { out[s] = list.length ? 1 / list.length : 0; });
    return out;
  }
  // {size: weight} fractions summing to ~1 across ALL of `sizes` for the
  // given preset kind — 'youth' splits ~30% across youth sizes (even) and
  // ~70% across the rest (bell curve), falling back to a plain bell curve
  // over everything if the product genuinely has no adult sizes to weight
  // separately (shouldn't happen — krSizePresetKinds() already gates this
  // chip on hasYouth, but the fallback keeps this pure function honest on
  // its own regardless of caller).
  function krSizePresetWeights(kind, sizes) {
    if (kind === 'even') return krEvenWeights(sizes);
    if (kind === 'youth') {
      var youth = sizes.filter(function (s) { return KR_YOUTH_SIZE_RE.test(String(s || '').trim()); });
      var adult = sizes.filter(function (s) { return !KR_YOUTH_SIZE_RE.test(String(s || '').trim()); });
      if (!youth.length || !adult.length) return krBellWeights(sizes);
      var yW = krEvenWeights(youth), aW = krBellWeights(adult);
      var out = {};
      Object.keys(yW).forEach(function (s) { out[s] = yW[s] * 0.3; });
      Object.keys(aW).forEach(function (s) { out[s] = aW[s] * 0.7; });
      return out;
    }
    return krBellWeights(sizes); // 'standard' (and any unknown kind — never blows up)
  }
  // Allocates `qty` whole units across `sizes` per krSizePresetWeights(),
  // rounding to the nearest unit then correcting any rounding drift onto the
  // single heaviest-weighted size — so the preset's total always matches the
  // requested qty exactly, never off by the 1-3 units naive rounding leaves.
  function krSizePresetAllocate(kind, sizes, qty) {
    var weights = krSizePresetWeights(kind, sizes);
    var out = {}, allocated = 0, heaviestSize = sizes[0];
    sizes.forEach(function (s) {
      var v = Math.round((weights[s] || 0) * qty);
      out[s] = v; allocated += v;
      if ((weights[s] || 0) > (weights[heaviestSize] || 0)) heaviestSize = s;
    });
    var drift = qty - allocated;
    if (drift && heaviestSize) out[heaviestSize] = Math.max(0, (out[heaviestSize] || 0) + drift);
    // Never leave a real size at 0 in a "spread" preset when qty comfortably
    // covers every size — 0-unit chips in a "Standard spread" read as a bug,
    // not a deliberate choice (the customer can always zero one out by hand).
    Object.keys(out).forEach(function (s) { if (out[s] <= 0) delete out[s]; });
    return out;
  }
  // Real per-line meta ("AS Colour · Front + Back · Black · 2XL×10 L×20") —
  // replaces the old hardcoded "1 front print" everywhere a rail line meta
  // renders (buildLineEl + Ari's quote context, so the two can never disagree).
  function railLineMetaText(line) {
    var bits = [];
    if (line.hit.vendor) bits.push(line.hit.vendor);
    bits.push(zoneGroupLabel(line.printLocations));
    if (line.colour) bits.push(krTitleCase(line.colour));
    var sr = sizeRunSummary(line.sizeRun);
    if (sr) bits.push(sr);
    return bits.join(' · ');
  }

  function krPrintPriceForLocations(printLocations) {
    var locs = (printLocations || []).map(function (l) { return String(l || '').toLowerCase(); });
    var front = locs.filter(function (l) { return l.indexOf('front') > -1 || l.indexOf('chest') > -1; }).length > 0;
    var back = locs.filter(function (l) { return l.indexOf('back') > -1; }).length > 0;
    var sleeveCount = locs.filter(function (l) { return l.indexOf('sleeve') > -1; }).length;
    var single = _krPrintPriceValue != null ? _krPrintPriceValue : KR_DEFAULT_PRINT_PRICE;
    var double = _krPrintPriceDoubleValue != null ? _krPrintPriceDoubleValue : KR_DEFAULT_DOUBLE_PRICE;
    var sleevePrice = _krPrintPriceSleeveValue != null ? _krPrintPriceSleeveValue : KR_DEFAULT_SLEEVE_PRICE;
    var price = 0;
    if (front && back) price = double;
    else if (front || back) price = single;
    if (sleeveCount > 0) price = krR2(price + sleeveCount * sleevePrice);
    return price;
  }

  // Same shape as getQuote's response (the subset the rail/compare/checkout
  // actually read: quantity, garmentUnitPrice, subtotal, discountAmount,
  // total, perUnit, appliedTier) — computeClientQuote()'s output is a
  // drop-in replacement for handleQuoteRequest()'s in every caller here.
  // `printLocations` replaces the old scalar `printPrice` param (PERF PASS
  // era) — every caller now passes the line's REAL per-line placements
  // (Configure drawer) rather than a single price already baked in.
  function computeClientQuote(garmentUnit, printLocations, qty) {
    var printPrice = krPrintPriceForLocations(printLocations);
    var tier = null;
    for (var i = 0; i < KR_TIERS.length; i++) { if (qty >= KR_TIERS[i].minQuantity) { tier = KR_TIERS[i]; break; } }
    var garmentSubtotal = krR2(garmentUnit * qty);
    var printSubtotal = krR2(printPrice * qty);
    var subtotal = krR2(garmentSubtotal + printSubtotal);
    var discountAmount = 0;
    if (tier) {
      var rate = tier.discountPercent / 100;
      discountAmount = krR2((krFloorCents(garmentUnit * rate) + krFloorCents(printPrice * rate)) * qty);
    }
    var total = krR2(subtotal - discountAmount);
    var perUnit = qty > 0 ? krR2(total / qty) : 0;
    return {
      quantity: qty, garmentUnitPrice: garmentUnit, printPrice: printPrice,
      printLocations: (printLocations || []).slice(),
      subtotal: subtotal, discountAmount: discountAmount, total: total, perUnit: perUnit, appliedTier: tier
    };
  }

  // Print prices are GLOBAL constants (same $11.95/$23.95/$7.00 across every
  // product checked) — fetched ONCE per page session from the live Custom
  // Printing Service product, then reused for every card/requote/Configure
  // computation. _krPrintPrice*Value are set the moment the fetch resolves so
  // later synchronous callers (the qty stepper, computeClientQuote itself)
  // never have to await anything.
  var _krPrintPricePromise = null;
  var _krPrintPriceValue = null;        // single-surface
  var _krPrintPriceDoubleValue = null;  // front+back combined
  var _krPrintPriceSleeveValue = null;  // per sleeve
  function getPrintPrices() {
    if (_krPrintPricePromise) return _krPrintPricePromise;
    _krPrintPricePromise = fetch('/products/custom-printing-service.js')
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
      .then(function (pj) {
        var variants = pj && Array.isArray(pj.variants) ? pj.variants : [];
        var single = null, double = null, sleeve = null;
        for (var i = 0; i < variants.length; i++) {
          var v = variants[i];
          var hay = String(v.sku || '') + ' ' + String(v.title || '');
          if (v.sku === 'PRINT-SLEEVE' || /sleeve/i.test(hay)) { if (sleeve == null && typeof v.price === 'number') sleeve = v.price / 100; }
          else if (v.sku === 'PRINT-DOUBLE' || /two surface/i.test(hay)) { if (double == null && typeof v.price === 'number') double = v.price / 100; }
          else if (v.sku === 'PRINT-SINGLE' || /single surface/i.test(hay)) { if (single == null && typeof v.price === 'number') single = v.price / 100; }
        }
        _krPrintPriceValue = single != null ? single : KR_DEFAULT_PRINT_PRICE;
        _krPrintPriceDoubleValue = double != null ? double : KR_DEFAULT_DOUBLE_PRICE;
        _krPrintPriceSleeveValue = sleeve != null ? sleeve : KR_DEFAULT_SLEEVE_PRICE;
        return { single: _krPrintPriceValue, double: _krPrintPriceDoubleValue, sleeve: _krPrintPriceSleeveValue };
      })
      .catch(function () {
        _krPrintPriceValue = KR_DEFAULT_PRINT_PRICE;
        _krPrintPriceDoubleValue = KR_DEFAULT_DOUBLE_PRICE;
        _krPrintPriceSleeveValue = KR_DEFAULT_SLEEVE_PRICE;
        return { single: _krPrintPriceValue, double: _krPrintPriceDoubleValue, sleeve: _krPrintPriceSleeveValue };
      });
    return _krPrintPricePromise;
  }

  // Popular-pick style-code -> SearchHit resolution, cached PER CODE (not as
  // one big batch) so a transient failure on one code doesn't poison the
  // whole set — a failed lookup un-caches itself so a later call (retry, or
  // just reopening the room) tries again; a SUCCESSFUL resolution (including
  // "genuinely not found", i.e. hit === null) is cached for the rest of the
  // page session, so repeat Popular-picks views fire ZERO searchProducts
  // calls and go straight to product.js.
  var _krPopularHitCache = {};
  function resolvePopularHit(code) {
    if (_krPopularHitCache[code]) return _krPopularHitCache[code];
    var p = fetchJSON(PB.searchUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ text: code, limit: 5 })
    }).then(function (data) {
      var results = data.results || [];
      var hit = null;
      for (var i = 0; i < results.length; i++) {
        if (results[i].styleCode && results[i].styleCode.toLowerCase() === code.toLowerCase()) { hit = results[i]; break; }
      }
      if (!hit) hit = results[0] || null;
      return hit; // may legitimately be null ("not available") — still cached
    });
    _krPopularHitCache[code] = p.catch(function (err) {
      delete _krPopularHitCache[code]; // network failure — don't poison the cache, allow retry
      throw err;
    });
    return _krPopularHitCache[code];
  }

  // Colour COUNT from product.js's own `options`/`variants` — no extra
  // request, works the instant product.js resolves. Finds the Color/Colour
  // option's index, then counts distinct values across variants. Returns 0
  // (not an error) if the product has no colour option — some items (a
  // single-colour tote, a cap) legitimately don't.
  function colourCountFromProductJs(pj) {
    var opts = pj && Array.isArray(pj.options) ? pj.options : [];
    var idx = -1;
    for (var i = 0; i < opts.length; i++) {
      var name = String((opts[i] && opts[i].name) || opts[i] || '').toLowerCase();
      if (name === 'color' || name === 'colour') { idx = i; break; }
    }
    if (idx === -1) return 0;
    var key = 'option' + (idx + 1);
    var seen = {}, n = 0;
    var variants = pj && Array.isArray(pj.variants) ? pj.variants : [];
    for (var j = 0; j < variants.length; j++) {
      var v = variants[j][key];
      if (v && !seen[v]) { seen[v] = true; n++; }
    }
    return n;
  }

  // Lazy hex hydration (redesign — solves the perf/swatch trade-off): fires
  // AFTER the card has already painted its instant price, never blocks it.
  // On success, swaps the grey count-only placeholder dots for real hex; on
  // failure (or if the card's grid was switched away in the meantime) it
  // just leaves the honest count-only placeholder in place — NEVER fabricates
  // a colour. One extra getPreviewConfig call per card, off the critical path.
  function hydrateSwatches(card, hit) {
    if (!PB.previewUrl) return;
    fetchJSON(PB.previewUrl + '?productId=' + encodeURIComponent(hit.id))
      .then(function (data) {
        if (!card.isConnected) return; // grid moved on — nothing to update
        var colours = (data.preview && Array.isArray(data.preview.availableColours)) ? data.preview.availableColours : [];
        var dots = swatchDotsHtml(colours);
        if (!dots) return; // graceful fallback — keep the count-only placeholder
        var swEl = qs('.kr-sw', card);
        if (swEl) swEl.innerHTML = dots;
      })
      .catch(function () {}); // graceful fallback — placeholder stands, never fabricate hex
  }

  // Fast path for one card: one product.js fetch (CDN, no cold start) +
  // instant client compute. ROBUSTNESS: any failure here (network, missing
  // price, bad handle) falls back to the ORIGINAL live path (hydrateCard —
  // real getQuote + getPreviewConfig, unchanged) for just this card, so a
  // card can never end up broken — it's just slower on that one card.
  // `context` ('browse'|'chat', default 'browse') decides what a card's
  // whole-card click / "Add to Quote" click DO (see fillCard) — Browse opens
  // the slide-out drawer / fast-adds; Chat opens the in-chat Ari config card
  // for both, since chat mode has no drawer host. Purely a wiring flag —
  // the card's own fetched price/photo/quote never differ by context.
  function loadCardFast(card, hit, qty, context) {
    fetch('/products/' + hit.handle + '.js')
      .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
      .then(function (pj) {
        var garmentUnit = (typeof pj.price_min === 'number' ? pj.price_min : 0) / 100;
        if (!(garmentUnit > 0)) throw new Error('no price on product.js');
        // Item 2 — a card for a product Ari already ran get_quote on prices
        // at THAT real configuration (module-scope _chatLastQuoteConfig, see
        // renderTraceCards) instead of the generic default, so the number on
        // the card never contradicts what Ari just told the customer. Falls
        // back to the default front-full/qty basis for everything else —
        // real per-line placements are still chosen in Configure once the
        // shopper opens the card either way, never fabricated here.
        var lastQuoteCfg = _chatLastQuoteConfig[hit.id];
        var priceLocations = (lastQuoteCfg && lastQuoteCfg.printLocations) ? lastQuoteCfg.printLocations : QUOTE_PRINT_LOCATIONS;
        var priceQty = (lastQuoteCfg && lastQuoteCfg.quantity) ? lastQuoteCfg.quantity : qty;
        var quote = computeClientQuote(garmentUnit, priceLocations, priceQty);
        // Shaped exactly like getPreviewConfig's `preview` — fillCard()/
        // buildLineEl() only ever read .images.front and .availableColours,
        // so this is a drop-in substitute. product.js has no hex data (that's
        // a getPreviewConfig-only capability) — availableColours stays empty
        // here; fillCard renders an instant COUNT-only placeholder instead
        // (from colourCountFromProductJs below), then hydrateSwatches() below
        // upgrades it to real hex in the background, off the price's critical path.
        // product.js `featured_image` is a STRING URL (NOT an object with .src —
        // that's the AJAX-cart shape). Reading .src returned undefined, so every
        // fast-path card fell to the placeholder and only got its real photo once
        // the background getPreviewConfig landed — THAT was the "slow images".
        var fi = pj.featured_image;
        var frontImg = (typeof fi === 'string' ? fi : (fi && fi.src)) || (pj.images && pj.images[0]) || null;
        var preview = { images: { front: frontImg }, availableColours: [] };
        var colourCount = colourCountFromProductJs(pj) || hit.colourCount || 0;
        // Persist the freshly-computed count back onto the shared hit object
        // (compareSelection/chatKnownHits/railLines all hold this SAME
        // reference) — without this, anything reading hit.colourCount later
        // (compareHTML's Colours row, before its own lazy hex fetch lands)
        // sees 0/undefined even though the count was known instantly here.
        // Real computed data from product.js, never fabricated.
        if (colourCount) hit.colourCount = colourCount;
        fillCard(card, hit, quote, preview, colourCount, context);
        if (colourCount) hydrateSwatches(card, hit);
      })
      .catch(function () {
        hydrateCard(card, hit, context);
      });
  }

  var GENERIC_TEE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="#0f172a" stroke-width="1.4" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M7 4 4 7l2 2 1-1v11h10V8l1 1 2-2-3-3-3 1a3 3 0 0 1-6 0Z"/></svg>';
  var BROWSE_DOOR_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<rect x="3" y="3" width="7" height="7" rx="1.5"/><rect x="14" y="3" width="7" height="7" rx="1.5"/>' +
    '<rect x="3" y="14" width="7" height="7" rx="1.5"/><rect x="14" y="14" width="7" height="7" rx="1.5"/></svg>';
  var CLOSE_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" aria-hidden="true"><path d="M6 6l12 12M18 6 6 18"/></svg>';
  var CHEVRON_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M6 9l6 6 6-6"/></svg>';
  var BAG_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.4" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M6 8h12l-1 12H7L6 8Z"/><path d="M9 8a3 3 0 0 1 6 0"/></svg>';
  var SEND_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true"><path d="M12 19V5M5 12l7-7 7 7"/></svg>';
  var CLIP_SVG = '<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" ' +
    'stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<path d="M21 11.5 12.5 20a4.5 4.5 0 0 1-6.36-6.36L15 4.78a3 3 0 0 1 4.24 4.24L10.4 17.87a1.5 1.5 0 0 1-2.12-2.12l7.78-7.78"/></svg>';

  // ===========================================================================
  // Overlay shell markup
  // ===========================================================================
  function shellHTML() {
    return (
      '<div class="kr-shell">' +
        '<div class="kr-topbar">' +
          '<div class="kr-orb" id="kr-orb" aria-hidden="true"></div>' +
          '<div class="kr-who"><b>The Bulk Desk</b><span class="kr-who-sub">Obligation-free instant quote &mdash; no sales call, no commitment.</span></div>' +
          '<div class="kr-seg" id="kr-seg" role="tablist" aria-label="Chat or browse">' +
            '<button type="button" class="kr-seg-describe" data-kr-seg-mode="describe" role="tab">Chat</button>' +
            '<button type="button" class="kr-seg-browse kr-seg-on" data-kr-seg-mode="browse" role="tab">Browse</button>' +
          '</div>' +
          '<button type="button" class="kr-close" aria-label="Close The Bulk Desk">' + CLOSE_SVG + '</button>' +
        '</div>' +
        '<div class="kr-body">' +
          '<section class="kr-entry" id="kr-entry">' +
            '<div class="kr-entry-inner">' +
              '<div class="kr-kick">Printibly &middot; The Bulk Desk</div>' +
              '<h1 class="kr-h1">Let&rsquo;s build your <em>bulk order.</em></h1>' +
              '<p class="kr-entry-sub">Real products, live pricing, GST-inclusive. Two ways in:</p>' +
              '<div class="kr-doors">' +
                '<button type="button" class="kr-door kr-browse" data-kr-mode="browse">' +
                  '<div class="kr-dic">' + BROWSE_DOOR_SVG + '</div>' +
                  '<h2>Browse at your own pace</h2>' +
                  '<p>Jump into the range by category and see live, real pricing on every card.</p>' +
                  '<div class="kr-go">Start browsing &rarr;</div>' +
                '</button>' +
                '<button type="button" class="kr-door kr-describe" data-kr-mode="describe">' +
                  '<div class="kr-dic"><div class="kr-orbmini"></div></div>' +
                  '<h2>Describe what you need</h2>' +
                  '<p>Tell Ari &mdash; &ldquo;100 tees + 50 hoodies for our footy club&rdquo; &mdash; and it finds the right gear, prices it live, and builds your order.</p>' +
                  '<div class="kr-go">Talk to Ari &rarr;</div>' +
                '</button>' +
              '</div>' +
              '<div class="kr-foot">Live pricing, straight from our quote engine &mdash; nothing estimated.</div>' +
            '</div>' +
          '</section>' +
          '<section class="kr-room" id="kr-room" hidden>' +
            '<div class="kr-room-body">' +
              '<div class="kr-canvas-col">' +
                '<div class="kr-cats" id="kr-cats">' + chipsHTML() + '</div>' +
                '<div class="kr-canvas" id="kr-canvas">' +
                  '<div class="kr-grid-wrap" id="kr-grid-wrap">' +
                    '<div class="kr-ghead" id="kr-ghead">Popular picks</div>' +
                    '<div class="kr-gsub" id="kr-gsub">Our best-sellers to get you started &middot; priced at ' + _krGridQty + ' units, one front print &middot; inc GST</div>' +
                    '<div class="kr-grid" id="kr-grid"></div>' +
                  '</div>' +
                '</div>' +
                // ---- Chat ("Describe what you need") — a real Ari-powered
                // conversation (agentChat CF), not a stub. Hidden by default;
                // switchToChat()/switchToBrowse() toggle this vs .kr-cats+.kr-canvas.
                '<div class="kr-chat" id="kr-chat" hidden>' +
                  '<div class="kr-chat-thread" id="kr-chat-thread" aria-live="polite"></div>' +
                  '<div class="kr-chat-pending" id="kr-chat-pending" hidden>' +
                    '<img class="kr-chat-pending-thumb" id="kr-chat-pending-thumb" alt="">' +
                    '<span class="kr-chat-pending-name" id="kr-chat-pending-name"></span>' +
                    '<button type="button" class="kr-chat-pending-x" id="kr-chat-pending-x" aria-label="Remove attachment">' + CLOSE_SVG + '</button>' +
                  '</div>' +
                  '<div class="kr-chat-inputbar">' +
                    '<input type="file" id="kr-chat-file" class="kr-chat-file-input" accept="image/*,application/pdf" hidden>' +
                    '<button type="button" class="kr-chat-attach" id="kr-chat-attach" aria-label="Attach artwork">' + CLIP_SVG + '</button>' +
                    '<div class="kr-chat-field">' +
                      '<input type="text" id="kr-chat-input" placeholder="Describe what you need&hellip; (e.g. 50 hoodies for a footy club)" aria-label="Message Ari">' +
                    '</div>' +
                    '<button type="button" class="kr-chat-send" id="kr-chat-send" aria-label="Send">' + SEND_SVG + '</button>' +
                  '</div>' +
                '</div>' +
                // ---- Compare — deliberately NOT nested inside #kr-canvas
                // (unlike the old layout). It's a direct child of
                // .kr-canvas-col (which is position:relative, see CSS) so it
                // can render as a full-canvas overlay ABOVE whichever of
                // #kr-cats+#kr-canvas / #kr-chat is currently active,
                // independent of the Browse/Chat toggle — see
                // showCompareView()/backToGrid() for the FIX 2 mode handling.
                '<div class="kr-compare-view" id="kr-compare-view" hidden></div>' +
              '</div>' +
              // ---- rail ("Your bulk order") — Slice 2. Static empty-state
              // defaults baked in here; renderRail() rewrites all of this
              // once a line is added. Reset per open (fresh shellHTML each
              // openKitRoom() call).
              '<aside class="kr-rail" id="kr-rail">' +
                '<button type="button" class="kr-rail-head" id="kr-rail-toggle" aria-expanded="false" aria-controls="kr-rail-body">' +
                  '<span class="kr-rail-headtext">' +
                    '<span class="kr-rail-k">Your bulk quote</span>' +
                    '<b class="kr-rail-title" id="kr-rail-title">Empty for now</b>' +
                  '</span>' +
                  '<span class="kr-rail-headtotal" id="kr-rail-headtotal">' + money(0) + '</span>' +
                  '<span class="kr-rail-chev">' + CHEVRON_SVG + '</span>' +
                '</button>' +
                '<div class="kr-rail-body" id="kr-rail-body">' +
                  '<div class="kr-rail-lines" id="kr-rail-lines">' +
                    '<div class="kr-rail-empty">' + BAG_SVG.replace('<svg ', '<svg style="width:40px;height:40px;opacity:.4;margin-bottom:10px" ') +
                      '<div>Add gear from the grid &mdash; your live quote builds here.</div></div>' +
                  '</div>' +
                  '<div class="kr-rail-foot">' +
                    // Live totals — announced to screen readers whenever a
                    // line/qty change updates them (add, remove, re-quote).
                    '<div class="kr-rail-totals" aria-live="polite" aria-atomic="true">' +
                      '<div class="kr-rrow"><span>Subtotal (before bulk discount)</span><span id="kr-f-sub">' + money(0) + '</span></div>' +
                      '<div class="kr-rrow kr-save"><span id="kr-f-savelbl">Bulk discount</span><span id="kr-f-save">' + money(0) + '</span></div>' +
                      '<div class="kr-rrow"><span>GST (10%)</span><span id="kr-f-gst">' + money(0) + '</span></div>' +
                      '<div class="kr-rrow kr-total"><span>Total <small>inc GST</small></span><span id="kr-f-tot">' + money(0) + '</span></div>' +
                    '</div>' +
                    '<button type="button" class="kr-qbtn" id="kr-checkout-btn" disabled>Start your order &rarr;</button>' +
                    '<button type="button" class="kr-qbtn kr-qbtn-secondary" id="kr-pdf-btn" disabled>Email me this quote</button>' +
                    '<div class="kr-email-hint">Share it with your treasurer or committee &mdash; no login required.</div>' +
                    // Inline email capture — shown in place of asking for a
                    // separate modal; hidden until "Email me this quote" is
                    // clicked with no email known yet (see beginEmailCapture()).
                    '<div class="kr-email-capture" id="kr-email-capture" hidden>' +
                      '<input type="email" class="kr-email-input" id="kr-email-input" placeholder="you@example.com" aria-label="Your email, to send the quote">' +
                      '<button type="button" class="kr-email-send" id="kr-email-send">Send</button>' +
                    '</div>' +
                    '<div class="kr-resume-link" id="kr-resume-link" hidden></div>' +
                    '<div class="kr-rail-note" id="kr-rail-checkout-note">Live pricing from our quote engine &middot; no obligation, no sales call</div>' +
                  '</div>' +
                '</div>' +
              '</aside>' +
            '</div>' +
          '</section>' +
        '</div>' +
      '</div>' +
      // Configure drawer — siblings of .kr-shell (NOT nested inside it), so
      // .kr-shell's overflow:hidden/animation can never clip or reposition a
      // position:fixed drawer. Content is built entirely in JS
      // (renderConfigureDrawer) since it depends on the specific product.
      '<div class="kr-cfg-backdrop" id="kr-cfg-backdrop" hidden></div>' +
      '<div class="kr-cfg-panel" id="kr-cfg-panel" role="dialog" aria-modal="true" aria-label="Configure this style" tabindex="-1" hidden>' +
        '<button type="button" class="kr-cfg-close" id="kr-cfg-close" aria-label="Close">' + CLOSE_SVG + '</button>' +
        '<div class="kr-cfg-body" id="kr-cfg-body"></div>' +
      '</div>'
    );
  }

  // ===========================================================================
  // Product cards — skeleton first (shimmer), then hydrated from live data.
  // Each card resolves independently so one slow/failed fetch never blocks
  // the rest of the grid.
  // ===========================================================================
  function skeletonCardHTML() {
    return (
      '<div class="kr-thumb kr-sk kr-sk-thumb"></div>' +
      '<div class="kr-pb">' +
        '<div class="kr-sk kr-sk-line kr-w40"></div>' +
        '<div class="kr-sk kr-sk-line kr-w80"></div>' +
        '<div class="kr-sk kr-sk-line kr-w60"></div>' +
      '</div>'
    );
  }
  // `i` (0-based position in the current grid) staggers the shimmer sweep
  // via the --kr-i custom prop (see .kr-sk::after) — capped at 6 so a big
  // grid doesn't leave the last cards waiting a long time to even start.
  function buildSkeletonCard(i) {
    var card = el('div', 'kr-card kr-loading', skeletonCardHTML());
    if (typeof i === 'number') card.style.setProperty('--kr-i', String(Math.min(i, 6)));
    return card;
  }

  // Force the card's existing `.kr-card{animation:kr-rise}` to restart —
  // reused every time a card's CONTENT changes (skeleton -> real, error ->
  // retry, error -> "not available") so each reveal gets its own rise
  // rather than only ever playing once at the original (empty) insertion.
  // No-op under KR_RM — the CSS rule already disables the animation there,
  // so restarting it would be wasted work.
  function krRevealCard(card) {
    if (KR_RM || !card) return;
    card.style.animation = 'none';
    void card.offsetWidth; // force reflow
    card.style.animation = '';
  }

  function renderCardError(card, hit, onRetry) {
    card.className = 'kr-card kr-card-error';
    card.innerHTML = '<div class="kr-err"><p>Couldn&rsquo;t load a price for this one right now.</p>' +
      '<button type="button" class="kr-retry">Retry</button></div>';
    krRevealCard(card);
    qs('.kr-retry', card).addEventListener('click', function () {
      card.className = 'kr-card kr-loading';
      card.innerHTML = skeletonCardHTML();
      onRetry();
    });
  }

  // Real hex dots (getPreviewConfig-backed) — capped at 4 + "+N more". Used
  // both by fillCard's slow path (preview already has colours) and by
  // hydrateSwatches() below (lazy background upgrade of a fast-path card).
  function swatchDotsHtml(colours) {
    var real = colours.filter(function (c) { return c && c.hex; });
    var dots = real.slice(0, 4).map(function (c) {
      return '<i style="background:' + escapeHtml(c.hex) + '" title="' + escapeHtml(c.name || '') + '"></i>';
    }).join('');
    if (real.length > 4) dots += '<span class="kr-sw-more">+' + (real.length - 4) + '</span>';
    return dots;
  }
  // Instant placeholder — colour COUNT only (no hex yet), neutral grey dots.
  // Never fabricates a colour; this is purely "there are N of them".
  function swatchPlaceholderHtml(count) {
    if (!count) return '';
    var shown = Math.min(count, 4);
    var dots = '';
    for (var i = 0; i < shown; i++) dots += '<i class="kr-sw-ph"></i>';
    if (count > 4) dots += '<span class="kr-sw-more">+' + (count - 4) + '</span>';
    return dots;
  }

  // `context` ('browse'|'chat', default 'browse') — see loadCardFast's note.
  function fillCard(card, hit, quote, preview, colourCountHint, context) {
    card.className = 'kr-card';
    var colours = (preview && Array.isArray(preview.availableColours)) ? preview.availableColours : [];
    // Real hex if we already have it (the slow/full path); otherwise an
    // instant count-only placeholder (fast path — see loadCardFast, which
    // lazy-hydrates the real hex in the background right after this paints).
    var swHtml = colours.length ? swatchDotsHtml(colours) : swatchPlaceholderHtml(colourCountHint || hit.colourCount || 0);
    var photo = preview && preview.images && preview.images.front;
    var thumbInner = photo
      ? '<img src="' + escapeHtml(krImg(photo, 400)) + '" alt="" decoding="async" fetchpriority="high">'
      : GENERIC_TEE_SVG;

    // Escape each bit individually — NOT the joined string, which contains a
    // literal "&middot;" separator that escapeHtml() would otherwise mangle
    // into "&amp;middot;" (renders as literal text instead of the glyph).
    var specBits = [];
    if (hit.gsm) specBits.push(escapeHtml(hit.gsm + ' GSM'));
    if (hit.fit) specBits.push(escapeHtml(hit.fit));

    var qty = (quote && quote.quantity) || QUOTE_QTY;
    var perUnit = quote ? quote.perUnit : null;
    // Chat can surface a card from get_preview_config alone (Ari named/showed
    // a product before pricing it) — no quote yet. Never invent a price:
    // show an honest placeholder and disable Add/Compare (both need a real
    // quote.perUnit) rather than crash or add an unpriced line to the rail.
    var hasQuote = !!quote;

    // Price hierarchy: the number is the hero (20/800); qty+GST context is
    // demoted onto its own quieter line underneath, not crowded inline.
    // Configure drawer: thumb+name+price live inside a real `<button
    // class="kr-card-hit">` (not the bare div) — the whole visible card is
    // clickable/keyboard-activatable, opening Configure, while Add/Compare
    // stay separate sibling buttons below (never nested interactive
    // controls). CSS reset already neutralises the button chrome.
    card.innerHTML =
      '<button type="button" class="kr-card-hit"' + (hasQuote ? '' : ' disabled') + ' aria-label="Configure ' + escapeHtml(hit.title || 'this product') + '">' +
        '<div class="kr-thumb">' + thumbInner + '</div>' +
        '<div class="kr-pb">' +
          // Vendor eyebrow now carries the secondary spec (GSM/Fit) folded in,
          // lighter weight — CARD STREAMLINE pass: one fewer competing line,
          // image gets the reclaimed room (see tightened .kr-thumb padding).
          '<div class="kr-vend">' + escapeHtml(hit.vendor || '') +
            (specBits.length ? ' <span class="kr-spec">&middot; ' + specBits.join(' &middot; ') + '</span>' : '') + '</div>' +
          '<div class="kr-nm">' + escapeHtml(hit.title || '') + (hit.styleCode ? ' ' + escapeHtml(hit.styleCode) : '') + '</div>' +
          (swHtml ? '<div class="kr-sw">' + swHtml + '</div>' : '') +
          '<div class="kr-prc">' + (perUnit != null
            ? '<b>' + money(perUnit) + '</b><span class="kr-prc-ctx">per unit &middot; qty ' + qty + ' &middot; inc GST</span>'
            : '<span class="kr-prc-ctx">Ask Ari for a price to add this</span>') + '</div>' +
        '</div>' +
      '</button>' +
      '<div class="kr-acts">' +
        '<button type="button" class="kr-add"' + (hasQuote ? '' : ' disabled') + '>' + (hasQuote ? 'Configure &amp; Add' : 'No price yet') + '</button>' +
        '<button type="button" class="kr-cmp" aria-pressed="false"' + (hasQuote ? '' : ' disabled') + ' aria-label="Compare ' + escapeHtml(hit.title || 'this product') + '">Compare</button>' +
      '</div>';
    // De-HTML-entity the "middot" placeholders written above via textContent-safe markup —
    // these are static separators, not user data, so literal HTML entities are fine as authored.

    // Whole-card click -> Configure UI, and "Add to Quote" is now the SAME
    // action, not a silent fast-add — the owner flagged that shoppers click
    // the obvious primary CTA ("Add to Quote") expecting the colour/size-
    // run/print-zone picker, not a bare front-full/rail-qty line with no
    // choices made. In Browse (default) both open the slide-out drawer; Chat
    // has no drawer host, so both open the in-chat Ari config card instead
    // (unchanged — that path was already correct). The rail line is only
    // ever created once the shopper hits Confirm inside that picker
    // (handleOpenConfigure/openChatConfigCard's onConfirm -> handleAddToRail
    // with the real cfg) — addToRail() is no longer called directly from a
    // card at all. Compare toggles this card into the comparison selection
    // using the SAME already-fetched quote+preview — Slice 3, no second
    // round-trip either. All guarded by `hasQuote` (disabled attribute); the
    // handlers themselves also short-circuit defensively.
    var isChat = context === 'chat';
    qs('.kr-card-hit', card).addEventListener('click', function () {
      if (!quote) return;
      if (isChat) openChatConfigCard(hit, quote, preview, card);
      else openConfigureDrawer(hit, quote, preview, card);
    });
    qs('.kr-add', card).addEventListener('click', function () {
      if (!quote) return;
      if (isChat) openChatConfigCard(hit, quote, preview, card);
      else openConfigureDrawer(hit, quote, preview, card);
    });
    qs('.kr-cmp', card).addEventListener('click', function () {
      if (!quote) return;
      toggleCompare(hit, quote, preview, card, qs('.kr-cmp', card));
    });
    krRevealCard(card); // staggered hydrate — restart the rise for THIS reveal, not just the original skeleton insert
  }

  // ===========================================================================
  // Compare table — pure HTML builder over an already-resolved selection
  // (each entry is a card's already-fetched {hit, quote, preview}; no
  // second round of getQuote/searchProducts calls). "Best value" = lowest
  // quote.perUnit among the selection — a real quoted number, not a client
  // recompute. Event wiring happens where this is inserted (showCompareView,
  // inside openKitRoom, since the "Add" buttons need handleAddToRail).
  // ===========================================================================
  function compareHTML(selection, origin) {
    var n = selection.length;
    var bestIdx = 0;
    for (var i = 1; i < n; i++) { if (selection[i].quote.perUnit < selection[bestIdx].quote.perUnit) bestIdx = i; }
    // §5 columns stagger left->right: every cell in column `i` (header, each
    // row, the Add button) shares the same delay, so the WHOLE column reads
    // as one reveal rather than the grid popping in cell-by-cell.
    function colDelay(i) { return KR_RM ? '' : ' style="animation-delay:' + (i * 60) + 'ms"'; }

    var head =
      '<div class="kr-cmp-head">' +
        '<div class="kr-cmp-title">Comparing ' + n + ' product' + (n === 1 ? '' : 's') + '</div>' +
        '<div class="kr-cmp-sub">at ' + QUOTE_QTY + ' units &middot; your print included &middot; inc GST</div>' +
      '</div>';

    var headRow = '<div class="kr-ctab-cell kr-ctab-lbl"></div>';
    selection.forEach(function (s, i) {
      var photo = s.preview && s.preview.images && s.preview.images.front;
      // De-squish: a real product shot, not the old 40px thumbnail — see
      // .kr-ctab-hd .kr-ctab-mi (120px) above.
      var thumb = photo ? '<img src="' + escapeHtml(krImg(photo, 320)) + '" alt="" decoding="async">' : GENERIC_TEE_SVG;
      headRow += '<div class="kr-ctab-cell kr-ctab-hd' + (i === bestIdx ? ' kr-best' : '') + '"' + colDelay(i) + '>' +
        '<div class="kr-ctab-mi">' + thumb + '</div>' +
        '<b>' + escapeHtml(s.hit.title || '') + '</b>' +
        '<div class="kr-ctab-cd">' + escapeHtml(s.hit.vendor || '') + (s.hit.styleCode ? ' ' + escapeHtml(s.hit.styleCode) : '') + '</div>' +
      '</div>';
    });

    var rows = [
      { label: 'Price / unit', render: function (s, best) {
          return '<span class="kr-ctab-big">' + money(s.quote.perUnit) + '</span>' +
            (best ? '<span class="kr-pill kr-pill-pop-in">Best value</span>' : '');
        } },
      { label: 'Weight', render: function (s) { return s.hit.gsm ? s.hit.gsm + ' GSM' : '&mdash;'; } },
      { label: 'Fit', render: function (s) { return escapeHtml(s.hit.fit || '—'); } },
      { label: 'Fabric', render: function (s) { return escapeHtml(s.hit.composition || '—'); } },
      // Real hex SWATCHES (same dots as the cards — swatchDotsHtml/
      // swatchPlaceholderHtml), never a bare number. If this card was
      // hydrated via the fast path, s.preview.availableColours is still
      // empty at compare-time (loadCardFast never mutates that closure) —
      // render the honest count-only placeholder now and lazy-upgrade to
      // real hex via hydrateCompareSwatches() right after insert, same
      // "never fabricate a colour" contract as hydrateSwatches() on cards.
      { label: 'Colours', render: function (s, best, i) {
          var colours = (s.preview && Array.isArray(s.preview.availableColours)) ? s.preview.availableColours : [];
          var count = colours.length || s.hit.colourCount || 0;
          var dots = colours.length ? swatchDotsHtml(colours) : swatchPlaceholderHtml(count);
          var countTxt = count ? (count + ' colour' + (count === 1 ? '' : 's')) : '&mdash;';
          return '<div class="kr-ctab-sw" data-cmp-sw="' + i + '">' +
            (dots ? '<span class="kr-ctab-sw-dots">' + dots + '</span>' : '') +
            '<span class="kr-ctab-sw-ct">' + countTxt + '</span></div>';
        } },
      // Size availability — not on SearchHit/product.js's fast-path shape,
      // so it's lazy-fetched per product.js (same handle-keyed fetch
      // loadCardFast already does) via hydrateCompareSizes() below. Honest
      // "Size data unavailable" if the card has no handle to fetch with
      // (e.g. some Chat-sourced hits) — never fabricated.
      { label: 'Sizes', render: function (s, best, i) {
          return '<span class="kr-ctab-sizes" data-cmp-sizes="' + i + '">' +
            (s.hit.handle ? 'Checking sizes&hellip;' : 'Size data unavailable') + '</span>';
        } }
    ];
    var bodyRows = rows.map(function (r) {
      var cells = '<div class="kr-ctab-cell kr-ctab-lbl">' + escapeHtml(r.label) + '</div>';
      selection.forEach(function (s, i) {
        cells += '<div class="kr-ctab-cell' + (i === bestIdx ? ' kr-best' : '') + '"' + colDelay(i) + '>' + r.render(s, i === bestIdx, i) + '</div>';
      });
      return cells;
    }).join('');

    var addRow = '<div class="kr-ctab-cell kr-ctab-lbl"></div>';
    selection.forEach(function (s, i) {
      addRow += '<div class="kr-ctab-cell' + (i === bestIdx ? ' kr-best' : '') + '"' + colDelay(i) + '>' +
        '<button type="button" class="kr-ctab-add" data-idx="' + i + '">Add ' + escapeHtml(s.hit.styleCode || s.hit.title || '') + '</button></div>';
    });

    // De-squish: wider label column + comfortable minimum per product
    // column (was 110px/100px) so a 2-3 way comparison reads like a real
    // spec sheet — .kr-ctab caps overall width (max-width:960px) so it
    // doesn't stretch edge-to-edge on a wide overlay either.
    var cols = 'grid-template-columns:130px repeat(' + n + ',minmax(160px,1fr))';
    // FIX 2: "Back" returns to wherever compare was opened FROM (origin is
    // captured by showCompareView() before it hides anything) — Browse or
    // Chat — never a hardcoded "browsing" when the shopper came from a
    // live Ari conversation.
    var backLabel = origin === 'chat' ? '&larr; Back to chat' : '&larr; Back to browsing';
    return head +
      '<div class="kr-ctab" style="' + cols + '">' + headRow + bodyRows + addRow + '</div>' +
      '<button type="button" class="kr-cmp-back">' + backLabel + '</button>';
  }

  // Lazy hex hydration for the compare view — mirrors hydrateSwatches()
  // above exactly (same "paint instantly, upgrade in the background, never
  // fabricate" contract), just targeting the compare table's swatch cells
  // (data-cmp-sw="i") instead of a card's .kr-sw. Skipped entirely for a
  // selection entry that already has real hex (the slow/full hydrateCard
  // path) — nothing to upgrade there.
  function hydrateCompareSwatches(selection, containerEl) {
    if (!PB.previewUrl) return;
    selection.forEach(function (s, i) {
      var already = s.preview && Array.isArray(s.preview.availableColours) && s.preview.availableColours.length;
      if (already) return;
      fetchJSON(PB.previewUrl + '?productId=' + encodeURIComponent(s.hit.id))
        .then(function (data) {
          if (!containerEl.isConnected) return; // compare view closed/replaced meanwhile
          var colours = (data.preview && Array.isArray(data.preview.availableColours)) ? data.preview.availableColours : [];
          var dots = swatchDotsHtml(colours);
          if (!dots) return; // graceful fallback — keep the count-only placeholder
          var wrap = qs('[data-cmp-sw="' + i + '"]', containerEl);
          if (!wrap) return;
          var dotsEl = qs('.kr-ctab-sw-dots', wrap);
          if (dotsEl) dotsEl.innerHTML = dots;
          else wrap.insertAdjacentHTML('afterbegin', '<span class="kr-ctab-sw-dots">' + dots + '</span>');
        })
        .catch(function () {}); // placeholder stands — never fabricate hex
    });
  }

  // Size option values from product.js's own `options`/`variants` — same
  // shape/approach as colourCountFromProductJs() above, just for the
  // "Size"/"Sizes" option instead of Colour. Returns null (not []) when the
  // product has no size option at all, so callers can tell "genuinely one
  // size" apart from "the fetch just hasn't landed yet".
  function sizeOptionFromProductJs(pj) {
    var opts = pj && Array.isArray(pj.options) ? pj.options : [];
    var idx = -1;
    for (var i = 0; i < opts.length; i++) {
      var name = String((opts[i] && opts[i].name) || opts[i] || '').toLowerCase();
      if (name === 'size' || name === 'sizes') { idx = i; break; }
    }
    if (idx === -1) return null;
    var key = 'option' + (idx + 1);
    var seen = {}, sizes = [];
    var variants = pj && Array.isArray(pj.variants) ? pj.variants : [];
    for (var j = 0; j < variants.length; j++) {
      var v = variants[j][key];
      if (v && !seen[v]) { seen[v] = true; sizes.push(v); }
    }
    return sizes;
  }

  // ===========================================================================
  // buildLineConfigUI(hit, preview, opts) — the SHARED "configure this line"
  // engine's UI half. ONE factory, used by BOTH shells (Browse's slide-out
  // drawer, Chat's in-chat Ari card) so placement/colour/size-run logic,
  // live pricing, and valid-quote enforcement can never drift between them.
  // Pure module-scope function — no dependency on any single overlay
  // instance's `root`/rail state; each call is a fully independent closure,
  // so N simultaneous instances (an open drawer + several chat cards down
  // the thread) never collide. Every element lookup is CLASS-based, scoped
  // to THIS instance's own `root` via qs(sel, root) — never a global id —
  // which is what makes multiple simultaneous instances safe.
  //
  // opts: { garmentUnit (required, >0), qty (initial, default QUOTE_QTY),
  //   printLocations (initial, default QUOTE_PRINT_LOCATIONS), colour,
  //   sizeRun, editing (bool — swaps Confirm's label to "Update line"),
  //   introHtml (optional — an Ari-voiced line rendered above the product
  //   name; the drawer shell uses this, the chat shell puts its intro in a
  //   separate bubble instead and leaves this empty), confirmLabel
  //   (optional — overrides the default Confirm button label entirely;
  //   Feature 1's pre-built chat card uses this for "✓ Looks right — Add
  //   to Quote"), onConfirm(cfg) }
  // cfg passed to onConfirm: { printLocations[], colour, sizeRun|null, qty,
  //   quote } — the factory NEVER touches railLines/rail state itself; both
  // shells own that via their own onConfirm.
  //
  // Returns { el, setPreview(p), setSizes(sizes), getConfig(), getPreview(),
  //   focusFirst(), destroy() }. setPreview/setSizes are how a shell feeds in
  // lazily-hydrated data (see krHydrateLineConfigUI() below, which both
  // shells call identically) — destroy() stops any further re-render after
  // the host removes `el` from the DOM (a drawer close mid-fetch).
  // ===========================================================================
  function buildLineConfigUI(hit, preview, opts) {
    opts = opts || {};
    // Item 1 (2026-08 unify) — ONE shared root class for both shells; the
    // 3-column grid (.kr-lcu-grid, built in render() below) is what used to
    // be opts.layout:'horizontal' chat-only special-casing. Both the drawer
    // (Shell A) and the chat card (Shell B) get the identical structure now
    // — see the .kr-lcu-grid CSS above for the per-host width/scroll deltas.
    var root = el('div', 'kr-lcu');
    var destroyed = false;
    var state = {
      hit: hit,
      preview: {
        images: (preview && preview.images) || {},
        availableColours: (preview && Array.isArray(preview.availableColours) && preview.availableColours.length) ? preview.availableColours.slice() : null,
        zones: (preview && preview.zones) ? preview.zones : null
      },
      garmentUnit: opts.garmentUnit,
      printLocations: (opts.printLocations && opts.printLocations.length) ? opts.printLocations.slice() : QUOTE_PRINT_LOCATIONS.slice(),
      colour: opts.colour || null,
      sizes: null,
      sizeRun: opts.sizeRun ? krCloneObj(opts.sizeRun) : {},
      imageSide: 'front',
      editing: !!opts.editing,
      initialQty: opts.qty || QUOTE_QTY,
      // Item 4 — keyed by SIDE (front/back/leftSleeve/rightSleeve — see
      // krArtworkSideKey), each value a real {printReadyUrl,uploadedUrl,
      // previewUrl,name} once that side's R2 upload lands, or an existing
      // line's already-attached artwork when editing. krNormalizeArtworkMap
      // migrates an old single-object shape (a live customer's already-saved
      // quote) onto 'front'. Never a fake success — a side stays absent
      // through an in-flight/failed upload for THAT side only.
      artwork: krNormalizeArtworkMap(opts.artwork),
      // {side: true} while that side's upload is in flight; {side: 'msg'}
      // for that side's last upload error — both per-slot now, never global.
      artworkUploading: {},
      artworkError: {}
    };

    // A fast-add/browse default of "front-full" may not correspond to any
    // REAL zone on this specific product (a cap's only zone is
    // "front-panel", confirmed live) — once zones are known, re-point the
    // default at an actual chip. Never touches an already-editing line —
    // its saved choice is authoritative even if the product's zones changed.
    function normalizeDefaultPlacement() {
      if (state.editing || !state.preview.zones) return;
      var zk = Object.keys(state.preview.zones);
      if (!zk.length) return;
      var matches = state.printLocations.filter(function (l) { return zk.indexOf(l) > -1; }).length > 0;
      if (matches) return;
      var frontish = zk.filter(function (k) { return k.toLowerCase().indexOf('front') > -1; });
      state.printLocations = [frontish[0] || zk[0]];
    }
    normalizeDefaultPlacement(); // no-op unless zones were already known synchronously

    function qty() {
      var total = sizeRunTotal(state.sizeRun);
      return total > 0 ? total : state.initialQty;
    }
    function recomputeQuote() {
      return computeClientQuote(state.garmentUnit, state.printLocations, qty());
    }

    function updateImage() {
      if (destroyed) return;
      var images = state.preview.images || {};
      var hasFront = !!images.front, hasBack = !!images.back;
      var side = (state.imageSide === 'back' && hasBack) ? 'back' : 'front';
      state.imageSide = side;
      var imgUrl = images[side];
      var imgWrap = qs('.kr-cfg-imgwrap', root);
      // Feature 2 — live logo mockup overlay travels with the base photo
      // (front/back toggle here, colour swap via hydrateColourImage below —
      // both funnel through updateImage()) — see artworkOverlayHtml(). Only
      // ever composited onto a REAL photo, never the GENERIC_TEE_SVG
      // fallback.
      if (imgWrap) {
        imgWrap.innerHTML = (imgUrl ? ('<img src="' + escapeHtml(krImg(imgUrl, 700)) + '" alt="" decoding="async">') : GENERIC_TEE_SVG) +
          '<div class="kr-cfg-artwork-overlays">' + (imgUrl ? artworkOverlayHtml() : '') + '</div>';
      }
      var toggleWrap = qs('.kr-cfg-imgtoggle', root);
      if (toggleWrap) {
        toggleWrap.hidden = !(hasFront && hasBack);
        Array.prototype.forEach.call(toggleWrap.querySelectorAll('.kr-cfg-imgtab'), function (btn) {
          var on = btn.getAttribute('data-side') === side;
          btn.classList.toggle('kr-cfg-imgtab-on', on);
          btn.setAttribute('aria-pressed', on ? 'true' : 'false');
        });
      }
    }

    // Real colour hex for the chosen colour, lazy — mirrors hydrateSwatches'
    // "paint instantly, upgrade in the background, never fabricate" contract.
    // Item 3 — same fetch/apply the click handler below uses AND the one
    // fired once from construction (see the end of buildLineConfigUI) when
    // a colour arrives pre-selected (Ari's inherited config, editing an
    // existing line) rather than from a swatch tap, so the photo is never
    // stuck on the default White shot just because nothing was clicked.
    function hydrateColourImage() {
      if (!PB.previewUrl || !state.colour) return;
      var hitId = state.hit.id, colour = state.colour;
      fetchJSON(PB.previewUrl + '?productId=' + encodeURIComponent(hitId) + '&colour=' + encodeURIComponent(colour))
        .then(function (data) {
          if (destroyed || state.hit.id !== hitId || state.colour !== colour) return; // stale — moved on
          var p = data.preview;
          if (p && p.images && (p.images.front || p.images.back)) { state.preview.images = p.images; updateImage(); }
        })
        .catch(function () {}); // graceful fallback — keep showing the current photo
    }

    function renderFooter() {
      if (destroyed) return;
      var quote = recomputeQuote();
      var q = qty();
      var unitEl = qs('.kr-cfg-price-unit', root), ctxEl = qs('.kr-cfg-price-ctx', root);
      if (unitEl) unitEl.textContent = money(quote.perUnit);
      if (ctxEl) ctxEl.textContent = '/unit · qty ' + q + ' · ' + money(quote.total) + ' total inc GST';
      var confirmBtn = qs('.kr-cfg-confirm', root);
      if (confirmBtn) {
        // Blocked while zones are still loading too, not just on an empty
        // selection — the seeded default may not match any real chip until
        // normalizeDefaultPlacement() has run against the real zone set.
        var zonesReady = state.preview.zones !== null;
        var ok = state.printLocations.length > 0 && zonesReady;
        confirmBtn.disabled = !ok;
        confirmBtn.title = ok ? '' : (zonesReady ? 'Pick at least one print placement' : 'Loading placements…');
      }
    }

    // Rebuilds ONLY the placement chips (+ footer) — every chip's "+$X/unit"
    // delta is the REAL per-unit total impact of toggling it (computed via
    // computeClientQuote at the current qty/tier, not the raw undiscounted
    // add-on price difference) — so it matches EXACTLY what the footer price
    // does the instant you tap the chip. Front/back deltas correctly shift
    // between the single and combined-double price as the other one
    // toggles; sleeve deltas never do (additive, independent of front/back)
    // — this is why the whole chip set re-renders together rather than each
    // chip toggling in isolation.
    function renderChips() {
      if (destroyed) return;
      var wrap = qs('.kr-cfg-chips', root);
      if (!wrap || !state.preview.zones) return;
      var q = qty();
      var orderedKeys = orderZoneKeys(Object.keys(state.preview.zones));
      wrap.innerHTML = orderedKeys.map(function (z) {
        var isOn = state.printLocations.indexOf(z) > -1;
        var without = state.printLocations.filter(function (l) { return l !== z; });
        var delta = krR2(
          computeClientQuote(state.garmentUnit, without.concat([z]), q).perUnit -
          computeClientQuote(state.garmentUnit, without, q).perUnit
        );
        return '<button type="button" class="kr-cfg-chip' + (isOn ? ' kr-cfg-chip-on' : '') + '" data-zone="' + escapeHtml(z) + '" aria-pressed="' + (isOn ? 'true' : 'false') + '">' +
          '<span class="kr-cfg-chip-lbl">' + escapeHtml(zoneDisplayLabel(z)) + '</span>' +
          '<span class="kr-cfg-chip-delta">' + (delta > 0 ? '+' : '') + money(delta) + '/unit</span>' +
        '</button>';
      }).join('');
      var warnEl = qs('.kr-cfg-chips-warn', root);
      if (warnEl) warnEl.hidden = state.printLocations.length > 0;
      Array.prototype.forEach.call(wrap.querySelectorAll('.kr-cfg-chip'), function (btn) {
        btn.addEventListener('click', function () {
          var z = btn.getAttribute('data-zone');
          var idx = state.printLocations.indexOf(z);
          if (idx > -1) {
            // Rubber-band — a line can never reach zero placements. Shake
            // the chip rather than silently ignore the tap.
            if (state.printLocations.length === 1) { pulseClass(btn, 'kr-shake'); return; }
            state.printLocations.splice(idx, 1);
          } else {
            state.printLocations.push(z);
          }
          renderChips();
        });
      });
      // Item 5 — state the qty basis right next to the deltas themselves
      // (the footer already says "qty N" further down; this closes the gap
      // at the point of confusion). Always the real current qty() — never
      // 0/1, since qty() itself already falls back to a sensible default
      // (initialQty, e.g. the 100-unit ladder base) whenever the size run
      // is empty.
      var basisEl = qs('.kr-cfg-chips-basis', root);
      if (basisEl) basisEl.textContent = '· at qty ' + q;
      renderFooter();
      renderLadder();
      // Item 4 — a placement toggle can gain/lose a whole artwork slot (e.g.
      // adding Back means a NEW back drop zone appears) — keep the slot set
      // live with state.printLocations on every chip tap, not just at
      // construction. Idempotent/harmless when printLocations didn't
      // actually change (e.g. the qty-only callers of renderChips()).
      renderArtworkSection();
    }

    // Item 4 — bulk savings ladder body. Reuses KR_TIERS' own breakpoints
    // (10/20/50/100/200/400 — the SAME tiers the bulk-landing Board's _tqty list
    // uses), ascending. Doesn't require `wrap`/zones the way renderChips()
    // does — printLocations always has a sensible default — so it renders
    // as soon as garmentUnit is known, never flickers while placements
    // hydrate. Cent-exact with the footer: both go through
    // computeClientQuote()/krPrintPriceForLocations().
    function renderLadder() {
      if (destroyed) return;
      var wrap = qs('.kr-cfg-ladder-row', root);
      if (!wrap) return;
      var q = qty();
      var tiersAsc = KR_TIERS.slice().reverse();
      wrap.innerHTML = tiersAsc.map(function (t, i) {
        var next = tiersAsc[i + 1];
        var active = q >= t.minQuantity && (!next || q < next.minQuantity);
        var stepQuote = computeClientQuote(state.garmentUnit, state.printLocations, t.minQuantity);
        return '<div class="kr-cfg-ladder-step' + (active ? ' kr-cfg-ladder-step-on' : '') + '">' +
          '<b>' + money(stepQuote.perUnit) + '</b><span>' + t.minQuantity + '+</span></div>';
      }).join('');
    }

    // getConfig()/onConfirm's artwork output — PRUNED to only the sides the
    // line's CURRENT placements actually touch, so toggling a placement off
    // (its slot disappears but the upload stays in state.artwork in case the
    // customer toggles it back on before confirming — a deliberate, undo-
    // friendly in-session convenience) never ships an orphaned side's file
    // on a line that no longer has that placement. Never a fabricated {}
    // either — null when nothing real survives the prune.
    function artworkOut() {
      if (!state.artwork) return null;
      var activeSides = krArtworkSidesForLocations(state.printLocations);
      var out = {};
      activeSides.forEach(function (side) { if (state.artwork[side]) out[side] = krCloneObj(state.artwork[side]); });
      return Object.keys(out).length ? out : null;
    }
    // ---- Item 4 — PER-ZONE artwork (front file != back file). One labelled
    // drop slot per side the line's CURRENT placements touch
    // (krArtworkSidesForLocations, module scope — SAME front/back/sleeve
    // taxonomy as zoneGroupLabel, never a second one), re-rendered via
    // renderArtworkSection() both on its own upload/remove AND whenever
    // renderChips() toggles a placement (a side gaining/losing its slot).
    // Pure targeted re-render: only the .kr-cfg-artwork-section node gets
    // replaced, so an in-progress edit elsewhere in the panel (colour, size
    // run) never loses state to a full render().
    function artworkSlotHtml(side) {
      var label = KR_ARTWORK_SIDE_LABELS[side] || 'Placement';
      if (state.artworkUploading[side]) {
        return '<div class="kr-cfg-artwork-slot" data-side="' + escapeHtml(side) + '">' +
          '<div class="kr-cfg-artwork-slot-lbl">' + escapeHtml(label) + '</div>' +
          '<div class="kr-cfg-artwork-zone kr-cfg-artwork-busy">' + CLIP_SVG + '<span>Uploading&hellip;</span></div></div>';
      }
      var art = state.artwork && state.artwork[side];
      if (art) {
        // Honesty guard — a cheap source-mime guess (krMayHaveTransparency),
        // never a pixel scan. Shown only for a source type that CAN'T carry
        // alpha (JPEG, or a rasterised PDF page) — the mockup above is
        // correctly showing that file's own real background, this just
        // tells the customer why, in case a coloured garment surprised
        // them. Never shown for PNG/WEBP/SVG (may genuinely be transparent
        // already) and never implies today's render is wrong.
        var tipHtml = art.mayHaveTransparency ? '' :
          '<div class="kr-cfg-artwork-tip">Tip: a transparent PNG shows best on coloured garments.</div>';
        return '<div class="kr-cfg-artwork-slot" data-side="' + escapeHtml(side) + '">' +
          '<div class="kr-cfg-artwork-slot-lbl">' + escapeHtml(label) + '</div>' +
          '<div class="kr-cfg-artwork-attached">' +
            '<img src="' + escapeHtml(art.previewUrl) + '" alt="' + escapeHtml(art.name || 'Attached artwork') + '">' +
            '<span class="kr-cfg-artwork-name">' + escapeHtml(art.name || 'Artwork attached') + '</span>' +
            '<button type="button" class="kr-cfg-artwork-remove" data-side="' + escapeHtml(side) + '" aria-label="Remove ' + escapeHtml(label) + ' artwork">' + CLOSE_SVG + '</button>' +
          '</div>' + tipHtml + '</div>';
      }
      var err = state.artworkError[side];
      var errHtml = err ? ('<div class="kr-cfg-artwork-err">' + escapeHtml(err) + '</div>') : '';
      return '<div class="kr-cfg-artwork-slot" data-side="' + escapeHtml(side) + '">' +
        '<div class="kr-cfg-artwork-slot-lbl">' + escapeHtml(label) + '</div>' +
        '<label class="kr-cfg-artwork-zone">' + CLIP_SVG + '<span>Drop ' + escapeHtml(label.toLowerCase()) + ' artwork here or click to upload</span>' +
          '<input type="file" class="kr-cfg-artwork-input" data-side="' + escapeHtml(side) + '" accept="image/*,application/pdf" hidden></label>' +
        errHtml + '</div>';
    }
    function artworkSectionHtml() {
      var sides = krArtworkSidesForLocations(state.printLocations);
      if (!sides.length) return '<div class="kr-cfg-section kr-cfg-artwork-section"></div>'; // rubber-band (renderChips) keeps this unreachable in practice
      return '<div class="kr-cfg-section kr-cfg-artwork-section"><div class="kr-cfg-h">Artwork <span class="kr-cfg-opt">(optional — or send it to Ari in chat)</span></div>' +
        sides.map(artworkSlotHtml).join('') +
      '</div>';
    }
    function handleArtworkFile(side, file) {
      if (!file || destroyed) return;
      state.artworkUploading[side] = true;
      delete state.artworkError[side];
      renderArtworkSection();
      krUploadArtwork(file).then(function (artwork) {
        if (destroyed) return;
        artwork.name = file.name;
        if (!state.artwork) state.artwork = {};
        state.artwork[side] = artwork;
        delete state.artworkUploading[side];
        renderArtworkSection();
      }).catch(function (e) {
        if (destroyed) return;
        delete state.artworkUploading[side];
        state.artworkError[side] = (e && e.message) || 'That upload didn’t work — try again.';
        renderArtworkSection();
      });
    }
    function wireArtworkEvents() {
      if (destroyed) return;
      Array.prototype.forEach.call(root.querySelectorAll('.kr-cfg-artwork-zone'), function (zone) {
        var input = qs('.kr-cfg-artwork-input', zone);
        var side = input && input.getAttribute('data-side');
        if (!side) return;
        input.addEventListener('change', function () {
          var f = input.files && input.files[0];
          if (f) handleArtworkFile(side, f);
        });
        zone.addEventListener('dragover', function (e) { e.preventDefault(); zone.classList.add('kr-cfg-artwork-drag'); });
        zone.addEventListener('dragleave', function () { zone.classList.remove('kr-cfg-artwork-drag'); });
        zone.addEventListener('drop', function (e) {
          e.preventDefault();
          zone.classList.remove('kr-cfg-artwork-drag');
          var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
          if (f) handleArtworkFile(side, f);
        });
      });
      Array.prototype.forEach.call(root.querySelectorAll('.kr-cfg-artwork-remove'), function (btn) {
        btn.addEventListener('click', function () {
          var side = btn.getAttribute('data-side');
          if (state.artwork) delete state.artwork[side];
          renderArtworkSection();
        });
      });
    }
    function renderArtworkSection() {
      if (destroyed) return;
      var wrap = qs('.kr-cfg-artwork-section', root);
      if (!wrap || !wrap.parentNode) return;
      var tmp = document.createElement('div');
      tmp.innerHTML = artworkSectionHtml();
      wrap.parentNode.replaceChild(tmp.firstChild, wrap);
      wireArtworkEvents();
      renderImageOverlays(); // Feature 2 — an upload/remove can add/drop a slot's overlay
    }

    // ===========================================================================
    // FEATURE 2 — live logo mockup. Composites each currently-selected
    // placement's uploaded artwork (state.artwork, keyed by SIDE — see
    // krArtworkSideKey) onto the garment photo currently shown, using
    // preview.zones' real per-zone {left,top,widthPercent,heightPercent}
    // (percentages of the product photo — buildPreviewConfig in
    // production-dashboard's product-search.ts, reached verbatim through
    // getPreviewConfig) as an absolutely-positioned <img> overlay. Pure CSS
    // positioning, no canvas/compositing — WRITER-SIDE TRUTH confirmed live
    // (curled getPreviewConfig + inspected the rendered DOM): every garment
    // master photo here is a SQUARE crop (e.g. 700x700), and .kr-cfg-imgwrap
    // ends up sized to exactly match the rendered <img> with no letterboxing
    // (object-fit:contain on an already-square box), so a zone's percentages
    // map straight onto the wrap with zero extra transform.
    //
    // The zone's own `side` field ("front"/"back", NOT a guess from the zone
    // key's name) decides which garment photo a placement's overlay belongs
    // on — confirmed live this also correctly puts sleeve zones on the FRONT
    // photo (a mannequin's sleeves are visible from the front).
    //
    // krUploadArtwork's previewUrl is a PNG (buildArtworkPreviewBlob — no
    // white flatten, alpha survives from a transparent source), so a real
    // club/business logo composites straight onto the garment photo with no
    // backing behind it (see .kr-cfg-artwork-overlay-img — no background/
    // border/box-shadow "patch", just a whisper drop-shadow for print
    // realism). A PREVIOUS version flattened the preview to an opaque white
    // JPEG and rendered a crisp white rectangle behind every overlay to
    // "honestly" show that white backing — on a black tee (Printibly's
    // single most common colour) that read as a white sticker/label, not a
    // print, and was the actual bug this rewrite fixes. mix-blend-
    // mode:multiply was tried and rejected earlier (still true with PNG: on
    // a genuinely opaque source it'd multiply a dark garment's own colour
    // into the mark's ink, muddying it) — plain alpha compositing is
    // correct instead. An opaque-source upload (JPEG, or a PDF page — those
    // are always rasterised onto white, see loadPdfCanvas) still shows its
    // own real background here, honestly — that's the customer's actual
    // file, not something to fake-remove. Silently renders nothing when
    // zones haven't hydrated yet, a placement has no real zone entry, or
    // nothing's been uploaded for that side — never a placeholder/broken
    // overlay.
    // ===========================================================================
    function artworkOverlayHtml() {
      if (!state.preview.zones || !state.artwork) return '';
      var side = state.imageSide;
      var html = '';
      state.printLocations.forEach(function (loc) {
        var zone = state.preview.zones[loc];
        if (!zone || zone.side !== side) return; // no real zone data for this placement, or it belongs on the other side's photo
        var art = state.artwork[krArtworkSideKey(loc)];
        if (!art || !art.previewUrl) return;
        var left = parseFloat(zone.left), top = parseFloat(zone.top),
            w = parseFloat(zone.widthPercent), h = parseFloat(zone.heightPercent);
        if (!(w > 0) || !(h > 0) || isNaN(left) || isNaN(top)) return; // never a 0/NaN-size overlay
        html += '<img class="kr-cfg-artwork-overlay-img" src="' + escapeHtml(art.previewUrl) + '" alt=""' +
          ' style="left:' + left + '%;top:' + top + '%;width:' + w + '%;height:' + h + '%" aria-hidden="true">';
      });
      return html;
    }
    // Targeted refresh for a placement toggle (renderChips) or an
    // upload/remove (renderArtworkSection) — neither touches the base
    // garment photo, so this only replaces the overlay layer's own content.
    // A front/back toggle or colour swap goes through updateImage()/
    // render() instead, which already inline the same markup fresh. Gated
    // on a REAL photo actually being shown (never composited over the
    // GENERIC_TEE_SVG fallback) by checking for the base <img> sibling,
    // same guard updateImage()/render() apply at construction time.
    function renderImageOverlays() {
      if (destroyed) return;
      var ov = qs('.kr-cfg-artwork-overlays', root);
      if (!ov) return;
      var hasRealPhoto = !!qs('.kr-cfg-imgwrap > img', root);
      ov.innerHTML = hasRealPhoto ? artworkOverlayHtml() : '';
    }

    function wireEvents() {
      if (destroyed) return;
      Array.prototype.forEach.call(root.querySelectorAll('.kr-cfg-imgtab'), function (btn) {
        btn.addEventListener('click', function () { state.imageSide = btn.getAttribute('data-side'); updateImage(); });
      });
      Array.prototype.forEach.call(root.querySelectorAll('.kr-cfg-sw'), function (btn) {
        if (btn.disabled) return;
        btn.addEventListener('click', function () {
          var c = btn.getAttribute('data-colour');
          state.colour = (state.colour && state.colour.toLowerCase() === String(c).toLowerCase()) ? null : c;
          var lblEl = qs('.kr-cfg-colour-lbl', root);
          if (lblEl) lblEl.textContent = state.colour ? (' — ' + krTitleCase(state.colour) + krColourStockLabelSuffix(state.preview.availableColours, state.colour)) : '';
          Array.prototype.forEach.call(root.querySelectorAll('.kr-cfg-sw'), function (sw) {
            var on = !!state.colour && sw.getAttribute('data-colour').toLowerCase() === state.colour.toLowerCase();
            sw.classList.toggle('kr-cfg-sw-on', on);
            sw.setAttribute('aria-pressed', on ? 'true' : 'false');
          });
          hydrateColourImage();
        });
      });
      renderChips(); // wires its own chip buttons + footer
      // Shared tail for every size-run edit (a single +/- tap OR a preset
      // applying several sizes at once) — pulled out of the per-chip loop
      // below so item 5's preset handler can reuse it verbatim instead of
      // re-deriving the same total/chip-delta/ladder refresh.
      function updateSizeTotal() {
        var totalEl = qs('.kr-cfg-size-total', root);
        var total = sizeRunTotal(state.sizeRun);
        if (totalEl) totalEl.textContent = 'Size run total: ' + total + ' unit' + (total === 1 ? '' : 's');
        // qty follows the size-run total once any size is set — item 5:
        // keep the chip deltas' basis label + the ladder + the footer ALL
        // live with the real qty, not just the footer (renderChips() only
        // rebuilds when zones are known; renderFooter/renderLadder cover
        // the rare race where sizes hydrate before zones do).
        if (state.preview.zones) renderChips(); else renderFooter();
        renderLadder();
      }
      Array.prototype.forEach.call(root.querySelectorAll('.kr-form-size-chip'), function (chip) {
        var size = chip.getAttribute('data-size');
        var nEl = qs('.kr-fqty-n', chip);
        qs('.kr-fqty-dec', chip).addEventListener('click', function () {
          var v = Math.max(0, (state.sizeRun[size] || 0) - 1);
          if (v) state.sizeRun[size] = v; else delete state.sizeRun[size];
          nEl.textContent = String(v);
          updateSizeTotal();
        });
        qs('.kr-fqty-inc', chip).addEventListener('click', function () {
          var v = Math.min(99999, (state.sizeRun[size] || 0) + 1);
          state.sizeRun[size] = v;
          nEl.textContent = String(v);
          updateSizeTotal();
        });
      });
      // Item 5 — size-run presets. Allocates krSizePresetAllocate()'s output
      // straight into state.sizeRun at the CURRENT qty() basis (the same
      // fallback-to-initialQty basis the chip deltas/ladder already use), so
      // clicking a preset on an empty size run still lands a sensible spread
      // rather than 0 units everywhere. Still just a prefill — every chip
      // stays fully editable with the same +/- controls straight after.
      Array.prototype.forEach.call(root.querySelectorAll('.kr-cfg-preset-chip'), function (btn) {
        btn.addEventListener('click', function () {
          var kind = btn.getAttribute('data-preset');
          state.sizeRun = krSizePresetAllocate(kind, state.sizes || [], qty());
          Array.prototype.forEach.call(root.querySelectorAll('.kr-form-size-chip'), function (chip) {
            var size = chip.getAttribute('data-size');
            var nEl = qs('.kr-fqty-n', chip);
            if (nEl) nEl.textContent = String(state.sizeRun[size] || 0);
          });
          updateSizeTotal();
        });
      });
      var confirmBtn = qs('.kr-cfg-confirm', root);
      if (confirmBtn) {
        confirmBtn.addEventListener('click', function () {
          if (!state.printLocations.length || state.preview.zones === null) return; // defensive — disabled in this state anyway
          var q = qty();
          var quote = computeClientQuote(state.garmentUnit, state.printLocations, q);
          var sizeRunOut = Object.keys(state.sizeRun || {}).length ? krCloneObj(state.sizeRun) : null;
          if (typeof opts.onConfirm === 'function') {
            opts.onConfirm({
              printLocations: state.printLocations.slice(), colour: state.colour || null, sizeRun: sizeRunOut, qty: q, quote: quote,
              artwork: artworkOut()
            });
          }
        });
      }
      wireArtworkEvents();
      // renderChips() already calls this when the chips container exists;
      // called again (harmless/idempotent) so the FIRST paint — while zones
      // are still loading and .kr-cfg-chips doesn't exist yet — still starts
      // Confirm correctly disabled, never enabled-by-omission.
      renderFooter();
      renderLadder(); // independent of zones — always safe to (re)paint
    }

    // Full rebuild — called on construction and once each time zones/
    // colours/sizes hydration lands (at most 3x per instance; every
    // interaction after that uses the lightweight updates above so typing/
    // tapping never loses focus mid-edit).
    function render() {
      if (destroyed) return;
      var preview = state.preview;
      var images = preview.images || {};
      var hasFront = !!images.front, hasBack = !!images.back;
      var side = (state.imageSide === 'back' && hasBack) ? 'back' : 'front';
      state.imageSide = side;
      var imgUrl = images[side];
      // Feature 2 — same overlay markup updateImage() paints on a toggle/
      // colour swap, inlined here for the initial/full rebuild paint.
      var imgHtml = (imgUrl ? ('<img src="' + escapeHtml(krImg(imgUrl, 700)) + '" alt="" decoding="async">') : GENERIC_TEE_SVG) +
        '<div class="kr-cfg-artwork-overlays">' + (imgUrl ? artworkOverlayHtml() : '') + '</div>';
      var toggleHtml = '<div class="kr-cfg-imgtoggle"' + ((hasFront && hasBack) ? '' : ' hidden') + '>' +
        '<button type="button" class="kr-cfg-imgtab' + (side === 'front' ? ' kr-cfg-imgtab-on' : '') + '" data-side="front" aria-pressed="' + (side === 'front' ? 'true' : 'false') + '">Front</button>' +
        '<button type="button" class="kr-cfg-imgtab' + (side === 'back' ? ' kr-cfg-imgtab-on' : '') + '" data-side="back" aria-pressed="' + (side === 'back' ? 'true' : 'false') + '">Back</button>' +
      '</div>';

      var colours = preview.availableColours; // null = not hydrated yet
      var swatchesHtml = '';
      if (colours === null) {
        swatchesHtml = '<div class="kr-cfg-section"><div class="kr-cfg-h">Colour</div><div class="kr-cfg-loading">Loading colours&hellip;</div></div>';
      } else if (colours.length) {
        var lowCount = 0, unknownCount = 0;
        swatchesHtml = '<div class="kr-cfg-section"><div class="kr-cfg-h">Colour<span class="kr-cfg-colour-lbl">' + (state.colour ? ' — ' + escapeHtml(krTitleCase(state.colour)) + krColourStockLabelSuffix(colours, state.colour) : '') + '</span> <span class="kr-cfg-opt">(optional)</span></div>' +
          '<div class="kr-cfg-swatches">' + colours.map(function (c) {
            var stockVal = krStockValue(c);
            var oos = stockVal === 'out';
            if (stockVal === 'low') lowCount++; else if (stockVal === 'unknown') unknownCount++;
            var isOn = !!state.colour && String(state.colour).toLowerCase() === String(c.name).toLowerCase();
            var suffix = krStockSuffix(stockVal);
            return '<button type="button" class="kr-cfg-sw kr-cfg-sw-stock-' + stockVal + (isOn ? ' kr-cfg-sw-on' : '') + '" data-colour="' + escapeHtml(c.name) + '"' + (oos ? ' disabled' : '') +
              ' style="--sw:' + escapeHtml(c.hex || '#ccc') + '" title="' + escapeHtml(c.name) + suffix + '" aria-label="' + escapeHtml(c.name) + suffix + '" aria-pressed="' + (isOn ? 'true' : 'false') + '"><span></span></button>';
          }).join('') + '</div>' +
          ((lowCount || unknownCount) ? '<div class="kr-cfg-stock-legend">' +
            [lowCount ? (lowCount + ' low stock') : null, unknownCount ? (unknownCount + ' stock unavailable') : null].filter(Boolean).join(' · ') +
          '</div>' : '') +
        '</div>';
      } // colours.length === 0 (hydrated, genuinely none) — render nothing, never fabricate

      var zoneKeys = preview.zones ? Object.keys(preview.zones) : null; // null = not hydrated yet
      var placementHtml;
      if (zoneKeys === null) {
        placementHtml = '<div class="kr-cfg-section"><div class="kr-cfg-h">Print placement</div><div class="kr-cfg-loading">Loading placements&hellip;</div></div>';
      } else {
        // .kr-cfg-chips-basis (item 5) — states the qty the "+$X/unit" chip
        // deltas below are computed at, so an empty size run (which falls
        // back to a sensible qty, never 0/1 — see qty()) never reads as
        // broken. renderChips() keeps this text in sync on every toggle AND
        // every size-run edit (see updateSizeTotal()).
        placementHtml = '<div class="kr-cfg-section"><div class="kr-cfg-h">Print placement <span class="kr-cfg-chips-basis"></span></div>' +
          '<div class="kr-cfg-chips"></div>' +
          '<div class="kr-cfg-warn kr-cfg-chips-warn"' + (state.printLocations.length ? ' hidden' : '') + '>Pick at least one placement.</div>' +
        '</div>';
      }

      // Item 4 — bulk savings ladder. Same KR_TIERS breakpoints the Board on
      // bulk-landing.liquid uses (10/20/50/100/200/400 — its own _tqty list),
      // reused rather than invented; populated by renderLadder() below so it
      // stays cent-exact with the footer (both call computeClientQuote()).
      // Rendered here (not gated on zoneKeys) so it never disappears/
      // flickers while placements are still hydrating.
      var ladderHtml = '<div class="kr-cfg-section kr-cfg-ladder"><div class="kr-cfg-h">Bulk savings <span class="kr-cfg-opt">at this placement</span></div>' +
        '<div class="kr-cfg-ladder-row"></div></div>';

      var sizesHtml = '';
      if (state.sizes === null) {
        sizesHtml = '<div class="kr-cfg-section"><div class="kr-cfg-h">Size run <span class="kr-cfg-opt">(optional)</span></div><div class="kr-cfg-loading">Loading sizes&hellip;</div></div>';
      } else if (state.sizes.length) {
        // Item 5 — one-click presets, only worth showing on a real multi-size
        // spread (a 1-size product has nothing to distribute a ratio across).
        var presetsHtml = state.sizes.length > 1
          ? '<div class="kr-cfg-size-presets">' + krSizePresetKinds(state.sizes).map(function (p) {
              return '<button type="button" class="kr-cfg-preset-chip" data-preset="' + escapeHtml(p.kind) + '">' + escapeHtml(p.label) + '</button>';
            }).join('') + '</div>'
          : '';
        sizesHtml = '<div class="kr-cfg-section"><div class="kr-cfg-h">Size run <span class="kr-cfg-opt">(optional — leave blank for one quantity)</span></div>' +
          presetsHtml +
          '<div class="kr-cfg-sizes">' + state.sizes.map(function (s) {
            var v = state.sizeRun[s] || 0;
            return '<div class="kr-form-size-chip" data-size="' + escapeHtml(s) + '">' +
              '<span class="kr-form-size-lbl">' + escapeHtml(s) + '</span>' +
              '<div class="kr-fqty">' +
                '<button type="button" class="kr-fqty-dec" aria-label="Decrease ' + escapeHtml(s) + '">−</button>' +
                '<span class="kr-fqty-n">' + v + '</span>' +
                '<button type="button" class="kr-fqty-inc" aria-label="Increase ' + escapeHtml(s) + '">+</button>' +
              '</div></div>';
          }).join('') + '</div>' +
          '<div class="kr-cfg-size-total">Size run total: ' + sizeRunTotal(state.sizeRun) + ' unit' + (sizeRunTotal(state.sizeRun) === 1 ? '' : 's') + '</div>' +
        '</div>';
      } // state.sizes.length === 0 (hydrated, one-size product) — nothing to render

      var introHtml = opts.introHtml ? ('<div class="kr-cfg-intro">' + opts.introHtml + '</div>') : '';
      // Real upload zone / attached thumbnail / uploading / error state —
      // see artworkSectionHtml() above. Rendered here on full rebuilds;
      // renderArtworkSection() handles every subsequent state change
      // in-place so the rest of the panel never re-renders around it.
      var artworkHtml = artworkSectionHtml();

      // Item 1 (2026-08 unify) — ONE shared 3-column grid, used identically
      // by both shells: col1 image+toggle (~32%) | col2 name/colour/
      // placement/ladder | col3 size-run+artwork, with the price/Confirm
      // foot as its OWN grid item (grid-column:3 at desktop, so it visually
      // sits under col3 only — never a full-width bar) rather than nested
      // inside col3. .kr-lcu-mid wraps col2+col3 purely so <=820px (where
      // they collapse to a single scrolling flow, same as the OLD
      // .kr-cfg-scroll region) can scroll them TOGETHER as one area while
      // the foot stays pinned outside it — display:contents at desktop (see
      // CSS above) makes this wrapper disappear from layout there, so col2/
      // col3 become direct grid children for real column placement. Replaces
      // the old flat .kr-cfg-img + single .kr-cfg-scroll sibling list.
      root.innerHTML =
        '<div class="kr-lcu-grid">' +
          '<div class="kr-lcu-col kr-lcu-col1">' +
            '<div class="kr-cfg-img"><div class="kr-cfg-imgwrap">' + imgHtml + '</div>' + toggleHtml + '</div>' +
          '</div>' +
          '<div class="kr-lcu-mid">' +
            '<div class="kr-lcu-col kr-lcu-col2">' +
              introHtml +
              '<div class="kr-cfg-title">' + escapeHtml(hit.vendor || '') + '</div>' +
              '<div class="kr-cfg-nm">' + escapeHtml(hit.title || '') + (hit.styleCode ? ' ' + escapeHtml(hit.styleCode) : '') + '</div>' +
              swatchesHtml + placementHtml + ladderHtml +
            '</div>' +
            '<div class="kr-lcu-col kr-lcu-col3">' + sizesHtml + artworkHtml + '</div>' +
          '</div>' +
          '<div class="kr-cfg-foot">' +
            '<div class="kr-cfg-price"><b class="kr-cfg-price-unit">' + money(0) + '</b><span class="kr-cfg-price-ctx">/unit</span></div>' +
            '<button type="button" class="kr-cfg-confirm">' + escapeHtml(opts.confirmLabel || (state.editing ? 'Update line' : 'Add to Quote')) + '</button>' +
          '</div>' +
        '</div>';

      wireEvents();
    }
    render();
    // Item 3(b) — a colour arriving pre-selected at construction (an
    // existing line reopened, or Ari's inherited config seeding opts.colour
    // — see handleOpenConfigure/openChatConfigCard) never fired a CLICK, so
    // the swatch handler's hydrateColourImage() call never ran for it. Fire
    // it once here too — idempotent/stale-guarded, and harmless even on the
    // (today, always-taken) path where krHydrateLineConfigUI's own
    // colour-aware fetch already lands the right photo first.
    if (opts.colour) hydrateColourImage();

    return {
      el: root,
      setPreview: function (p) {
        if (destroyed || !p) return;
        if (p.images && (p.images.front || p.images.back)) state.preview.images = p.images;
        if (Array.isArray(p.availableColours)) state.preview.availableColours = p.availableColours;
        state.preview.zones = p.zones || {};
        normalizeDefaultPlacement();
        render();
      },
      setSizes: function (sizes) {
        if (destroyed) return;
        state.sizes = sizes || [];
        // P1 fix #9 — the real vocabulary just landed; remap any sizeRun seeded at
        // construction (an existing/Ari-added line's raw sizeRun, possibly in a
        // different size vocabulary — see krRemapSizeRunToVocab above) onto it BEFORE
        // the size chips ever paint, so they read real counts on first render, never 0.
        state.sizeRun = krRemapSizeRunToVocab(state.sizeRun, state.sizes) || state.sizeRun;
        render();
      },
      getConfig: function () {
        var q = qty();
        return {
          printLocations: state.printLocations.slice(), colour: state.colour || null,
          sizeRun: Object.keys(state.sizeRun).length ? krCloneObj(state.sizeRun) : null, qty: q,
          quote: computeClientQuote(state.garmentUnit, state.printLocations, q),
          artwork: artworkOut()
        };
      },
      getPreview: function () { return { images: krCloneObj(state.preview.images), availableColours: (state.preview.availableColours || []).slice() }; },
      focusFirst: function () { try { var f = root.querySelector('button:not([disabled]), input, select'); if (f) f.focus(); } catch (e) {} },
      destroy: function () { destroyed = true; }
    };
  }

  // Fires the SAME lazy zones/colours + sizes hydration a config UI instance
  // needs, regardless of which shell owns it — a fast-path preview never
  // carries zones (getPreviewConfig-only data), and sizes always need a
  // fresh product.js fetch. `ui` is a buildLineConfigUI() return value;
  // `hit` must be the SAME object passed to buildLineConfigUI (identity
  // check guards a fetch landing after the caller moved on to a different
  // product — see handleOpenConfigure's original comment this mirrors).
  function krHydrateLineConfigUI(ui, hit, preview, colour) {
    var needsZones = !(preview && preview.zones);
    var needsColours = !(preview && Array.isArray(preview.availableColours) && preview.availableColours.length);
    if (needsZones || needsColours) {
      if (PB.previewUrl) {
        // Item 3(a) — carry the already-selected colour (an existing line,
        // or Ari's inherited config) onto this SAME hydration fetch, so the
        // photo that lands with the real zones/colours is already the right
        // one — never a default White shot that then has to be replaced.
        var url = PB.previewUrl + '?productId=' + encodeURIComponent(hit.id) + (colour ? '&colour=' + encodeURIComponent(colour) : '');
        fetchJSON(url)
          .then(function (data) { ui.setPreview(data.preview || { zones: {}, availableColours: [] }); })
          .catch(function () { ui.setPreview({ zones: {}, availableColours: [] }); }); // graceful fallback — never fabricate, never blocks Confirm
      } else {
        ui.setPreview({ zones: {}, availableColours: [] });
      }
    }
    if (hit.handle) {
      fetch('/products/' + hit.handle + '.js')
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
        .then(function (pj) { ui.setSizes(sizeOptionFromProductJs(pj) || []); })
        .catch(function () { ui.setSizes([]); });
    } else {
      ui.setSizes([]);
    }
  }

  // Deterministic (zero-LLM) Ari-voiced intro for a line's config UI — used
  // by BOTH shells (the drawer's header line, the chat card's leading Ari
  // bubble) so the two read as the same voice. Deliberately generic/honest
  // rather than inventing product-specific flavour text ("a workhorse") this
  // file has no data to back up — never fabricate a claim about the product.
  function krAriLineIntro(hit) {
    var name = escapeHtml(hit.title || 'this one');
    // Item 2 — a real Unicode apostrophe (U+2019 ’), NOT the &rsquo; HTML
    // entity. This string is used two ways: (a) the drawer's opts.introHtml,
    // injected as raw innerHTML (an entity would have rendered fine there);
    // (b) the chat shell's appendAriBubble(), which runs it through
    // krFormatChatText() -> escapeHtml() FIRST — that turns the entity's own
    // "&" into "&amp;", so "&rsquo;" rendered as the literal text "&rsquo;"
    // in the bubble instead of an apostrophe. A real ’ character survives
    // escapeHtml() untouched (it only escapes & < > " ') and renders
    // correctly through BOTH paths — see ARI_GREETING above, which already
    // uses real ’ characters for exactly this reason.
    return 'Great choice — here’s the ' + name + '. Set your placements, colour, and run below.';
  }

  // Feature 1 (2026-08 pre-build) — deterministic (zero-LLM) confirm-framed
  // sibling to krAriLineIntro above, used ONLY by openChatConfigCard when
  // this session's get_quote for `hit` already carried a COMPLETE config
  // (colour + placements + qty — see renderTraceCards' configComplete
  // flag). Same real-’-character / escapeHtml-through-krFormatChatText
  // contract as krAriLineIntro (this one is never used as the drawer's raw-
  // innerHTML opts.introHtml, only the chat bubble, but keeps the same
  // convention for consistency). States only what Ari actually quoted —
  // straight from the real _chatLastQuoteConfig entry — never a fabricated
  // detail.
  function krAriLineConfirmIntro(hit, inherited) {
    var colourPrefix = inherited.colour ? (krTitleCase(inherited.colour) + ' ') : '';
    var name = escapeHtml(colourPrefix + (hit.title || 'this one'));
    var placementBit = zoneGroupLabel(inherited.printLocations);
    var qtyBit = inherited.quantity ? (inherited.quantity + ' units') : '';
    var summary = [placementBit, qtyBit].filter(Boolean).join(', ');
    return 'Here’s the ' + name + ' — ' + summary + '. Looks right? Add it below, or tweak anything.';
  }

  // Lazy size hydration for the compare view — one product.js fetch per
  // selection entry (same CDN-cached, no-cold-start fetch loadCardFast
  // already does for pricing), off the critical path of opening compare.
  // A card with no handle (some Chat-sourced hits — see chatKnownHits doc
  // above hydrateCard) can't be fetched at all; that's already rendered
  // honestly by compareHTML() and this function leaves it alone.
  function hydrateCompareSizes(selection, containerEl) {
    selection.forEach(function (s, i) {
      if (!s.hit.handle) return;
      var target = qs('[data-cmp-sizes="' + i + '"]', containerEl);
      if (!target) return;
      fetch('/products/' + s.hit.handle + '.js')
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
        .then(function (pj) {
          if (!containerEl.isConnected) return; // compare view closed/replaced meanwhile
          var sizes = sizeOptionFromProductJs(pj);
          target.textContent = (sizes && sizes.length) ? sizes.join(', ') : (sizes ? 'Size data unavailable' : 'One size');
        })
        .catch(function () { target.textContent = 'Size data unavailable'; });
    });
  }

  function hydrateCard(card, hit, context) {
    var quoteReq = fetchJSON(PB.getQuoteUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ productId: hit.id, quantity: QUOTE_QTY, printLocations: QUOTE_PRINT_LOCATIONS })
    }).then(function (data) { return data.quote; });

    var previewReq = fetchJSON(PB.previewUrl + '?productId=' + encodeURIComponent(hit.id))
      .then(function (data) { return data.preview; })
      .catch(function () { return null; }); // photo is nice-to-have — never blocks the price

    quoteReq.then(function (quote) {
      previewReq.then(function (preview) {
        fillCard(card, hit, quote, preview, null, context);
      });
    }).catch(function () {
      // Price is the core value of a card — without it, retry rather than
      // showing a broken/blank card.
      renderCardError(card, hit, function () { hydrateCard(card, hit, context); });
    });
  }

  // ===========================================================================
  // Category grid — live searchProducts, one call per productType in the
  // category (merged + deduped), inStockOnly. See CATEGORIES above for why
  // this is filters.productType and not free text.
  // ===========================================================================
  function searchByTypes(types, limit) {
    return Promise.all(types.map(function (t) {
      return fetchJSON(PB.searchUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ filters: { productType: t, inStockOnly: true }, limit: limit })
      }).then(function (data) {
        return { ok: true, results: data.results || [] };
      }).catch(function () {
        return { ok: false, results: [] };
      });
    })).then(function (outcomes) {
      var seen = {}, merged = [], anyOk = false;
      outcomes.forEach(function (o) {
        if (o.ok) anyOk = true;
        o.results.forEach(function (hit) {
          if (seen[hit.id]) return;
          seen[hit.id] = true;
          merged.push(hit);
        });
      });
      return { ok: anyOk, hits: merged.slice(0, limit) };
    });
  }

  // PERF: product resolution still needs searchProducts (1 call per productType
  // in the category — see searchByTypes above) to FIND matching products, but
  // every per-card getQuote+getPreviewConfig round-trip is gone — loadCardFast()
  // does one CDN-cached product.js fetch + instant client compute per card,
  // falling back to the original hydrateCard() path only if that fails.
  function loadCategoryGrid(grid, types) {
    grid.innerHTML = '';
    for (var i = 0; i < GRID_LIMIT; i++) grid.appendChild(buildSkeletonCard(i));

    krBusy(true); // Ari orb "thinking" — see KIT-ROOM-MOTION-REVIEW.md A1
    Promise.all([searchByTypes(types, GRID_LIMIT), getPrintPrices()]).then(function (results) {
      krBusy(false);
      var res = results[0];
      grid.innerHTML = '';
      if (!res.ok) {
        var fail = el('div', 'kr-gridfail', 'Couldn&rsquo;t load this category right now.<br><button type="button" class="kr-retry">Retry</button>');
        grid.appendChild(fail);
        qs('.kr-retry', fail).addEventListener('click', function () { loadCategoryGrid(grid, types); });
        return;
      }
      if (!res.hits.length) {
        grid.appendChild(el('div', 'kr-empty', 'Nothing in this category right now &mdash; check back soon.'));
        return;
      }
      res.hits.forEach(function (hit, i) {
        var card = buildSkeletonCard(i);
        grid.appendChild(card);
        loadCardFast(card, hit, QUOTE_QTY);
      });
    });
  }

  // ===========================================================================
  // "Popular picks" grid — each curated style code resolved via
  // resolvePopularHit() (cached per-code, see above — a repeat view of
  // Popular picks fires ZERO searchProducts calls), then priced via the
  // client-compute fast path (loadCardFast). Each pick still resolves
  // independently (one failing never blocks the rest); a code that
  // genuinely isn't in the catalog anymore shows an honest "not available"
  // card, not a broken/blank one.
  // ===========================================================================
  function loadPopularCard(card, code, context) {
    krBusy(true); // Ari orb "thinking" — ref-counted, safe across N concurrent cards
    Promise.all([resolvePopularHit(code), getPrintPrices()]).then(function (results) {
      krBusy(false);
      var hit = results[0];
      if (!hit) {
        card.className = 'kr-card kr-card-error';
        card.innerHTML = '<div class="kr-err"><p>This pick isn&rsquo;t available right now.</p></div>';
        krRevealCard(card);
        return;
      }
      loadCardFast(card, hit, _krGridQty, context); // session basis (may be Board-carried) — Popular grid only
    }).catch(function () {
      krBusy(false);
      // Only resolvePopularHit() can reject here (getPrintPrices() never
      // does — it falls back to the KR_DEFAULT_* prices internally) — a
      // genuine network failure resolving the code, not a "not found".
      // renderCardError() already resets card to the loading/skeleton state
      // before invoking the retry callback — no need to duplicate that here.
      renderCardError(card, null, function () { loadPopularCard(card, code, context); });
    });
  }

  function loadPopularGrid(grid) {
    grid.innerHTML = '';
    POPULAR_STYLE_CODES.forEach(function (code, i) {
      var card = buildSkeletonCard(i);
      grid.appendChild(card);
      loadPopularCard(card, code);
    });
  }

  // ===========================================================================
  // window.openKitRoom({ mode?, qty? }) — overlay open/close mechanics
  // ===========================================================================
  window.openKitRoom = function (opts) {
    opts = opts || {};
    if (document.getElementById(OVERLAY_ID)) return; // already open — idempotent

    // A landing-page Board (or any future caller) can carry the quantity a
    // shopper already saw priced (e.g. dragged to 200 units) straight into
    // the Popular grid, so the price they clicked through on doesn't
    // downgrade the instant the modal opens. Any other positive qty falls
    // back to the standard QUOTE_QTY basis — same as passing nothing.
    var qtyOpt = parseInt(opts.qty, 10);
    _krGridQty = (qtyOpt > 0) ? qtyOpt : QUOTE_QTY;
    _chatLastQuoteConfig = {}; // item 2 — a fresh open never inherits a previous session's Ari config

    injectCSS();

    var prevFocus = document.activeElement;
    var root = document.createElement('div');
    root.id = OVERLAY_ID;
    root.setAttribute('role', 'dialog');
    root.setAttribute('aria-modal', 'true');
    root.setAttribute('aria-label', 'The Bulk Desk — bulk quote builder');
    root.innerHTML = shellHTML();
    document.body.appendChild(root);
    document.body.style.overflow = 'hidden';

    // Inert + aria-hide every other top-level node while open (same pattern
    // as gang-sheet-builder.js) so the page underneath can't be tabbed into
    // or read by assistive tech.
    var inertNodes = [];
    Array.prototype.slice.call(document.body.children).forEach(function (n) {
      if (n === root) return;
      inertNodes.push({ n: n, ariaWas: n.getAttribute('aria-hidden'), inertWas: n.hasAttribute('inert') });
      n.setAttribute('aria-hidden', 'true');
      try { n.setAttribute('inert', ''); } catch (e) {}
    });
    function restoreInert() {
      inertNodes.forEach(function (o) {
        if (o.ariaWas === null) o.n.removeAttribute('aria-hidden'); else o.n.setAttribute('aria-hidden', o.ariaWas);
        if (!o.inertWas) { try { o.n.removeAttribute('inert'); } catch (e) {} }
      });
    }

    var isOpen = true;
    function teardown() {
      if (!isOpen) return;
      isOpen = false;
      document.removeEventListener('keydown', escHandler);
      root.removeEventListener('keydown', trapHandler);
      window.removeEventListener('popstate', popHandler);
      document.body.style.overflow = '';
      // Cancel any pending qty-stepper re-quote debounces so a closed
      // overlay never fires a late getQuote call. `railLines` is declared
      // further down this same function (hoisted var) — always populated
      // ([] at minimum) by the time a close can happen.
      railLines.forEach(function (l) { clearTimeout(l._debounceT); });
      // Stop the human-handoff poll the instant the room closes — never
      // poll a conversation nobody is looking at. `stopBulkDeskPoll`/
      // `onBulkPollVisibilityChange` are declared further down this same
      // function (hoisted vars/fns), same pattern as `railLines` above.
      stopBulkDeskPoll();
      document.removeEventListener('visibilitychange', onBulkPollVisibilityChange);
      restoreInert();
      if (root.parentNode) root.parentNode.removeChild(root);
      try { if (prevFocus && typeof prevFocus.focus === 'function') prevFocus.focus(); } catch (e) {}
    }
    // Programmatic close (X button / Esc / backdrop click): pop the history
    // entry we pushed on open, which triggers popHandler -> teardown. A
    // back-gesture close skips straight to popHandler (browser already
    // navigated), so it must never double-pop.
    function closeKitRoom() {
      if (!isOpen) return;
      try {
        if (history.state && history.state.kitRoom) { history.back(); return; }
      } catch (e) {}
      teardown();
    }
    var popHandler = function () { if (isOpen) teardown(); };
    try { history.pushState({ kitRoom: true }, '', location.href); } catch (e) {}
    window.addEventListener('popstate', popHandler);

    // The Configure drawer (declared further down — hoisted like railLines
    // above) is a nested overlay: Escape closes JUST the drawer when it's
    // open, never the whole Bulk Desk underneath it.
    var escHandler = function (e) {
      if (e.key !== 'Escape') return;
      if (cfgOpen) { closeConfigureDrawer(); return; }
      closeKitRoom();
    };
    document.addEventListener('keydown', escHandler);

    // Focus trap — Tab cycles within the overlay only.
    function focusables() {
      return Array.prototype.slice.call(
        root.querySelectorAll('a[href],button:not([disabled]),input:not([disabled]),[tabindex]:not([tabindex="-1"])')
      ).filter(function (n) { return n.offsetWidth > 0 || n.offsetHeight > 0; });
    }
    var trapHandler = function (e) {
      if (e.key !== 'Tab') return;
      var f = focusables(); if (!f.length) return;
      var first = f[0], last = f[f.length - 1];
      if (!root.contains(document.activeElement)) { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    root.addEventListener('keydown', trapHandler);

    qs('.kr-close', root).addEventListener('click', closeKitRoom);
    // Click on the backdrop itself (not the shell) closes.
    root.addEventListener('click', function (e) { if (e.target === root) closeKitRoom(); });

    // ---------- toast (scoped to this overlay instance) ----------
    // Signature widened (2nd arg optional) to carry an action button — e.g. Ari's
    // update_quote "remove" needs an inline Undo. Backward-compatible: every
    // existing call site passes only `msg`, so `opts` is simply undefined there
    // and behaviour is byte-identical to before (grepped every toast( call site
    // in this file first to confirm none passed a 2nd positional arg).
    var toastEl = null, toastT;
    function toast(msg, opts) {
      if (!toastEl) {
        toastEl = el('div', 'kr-toast');
        toastEl.setAttribute('role', 'status');
        toastEl.setAttribute('aria-live', 'polite');
        toastEl.setAttribute('aria-atomic', 'true');
        root.appendChild(toastEl);
      }
      toastEl.innerHTML = ''; // clear any previous message + action button
      toastEl.appendChild(document.createTextNode(msg));
      if (opts && opts.actionLabel && typeof opts.onAction === 'function') {
        var actionBtn = el('button', 'kr-toast-action');
        actionBtn.type = 'button';
        actionBtn.textContent = opts.actionLabel;
        actionBtn.addEventListener('click', function () {
          clearTimeout(toastT);
          toastEl.classList.remove('kr-show');
          opts.onAction();
        });
        toastEl.appendChild(actionBtn);
      }
      requestAnimationFrame(function () { toastEl.classList.add('kr-show'); });
      clearTimeout(toastT);
      toastT = setTimeout(function () { toastEl.classList.remove('kr-show'); }, (opts && opts.durationMs) || 2600);
    }

    // ---------- Ari orb "thinking" state (KIT-ROOM-MOTION-REVIEW.md A1) ----------
    // Ref-counted so overlapping fetches (N popular cards resolving at once)
    // never turn the orb off early while a sibling fetch is still in flight.
    // Calm at rest (no perpetual loop) — the orb only comes alive here.
    var krBusyN = 0;
    function krBusy(on) {
      if (KR_RM) return;
      krBusyN = Math.max(0, krBusyN + (on ? 1 : -1));
      var orb = qs('#kr-orb', root), mini = qs('.kr-orbmini', root);
      if (orb) orb.classList.toggle('kr-thinking', krBusyN > 0);
      if (mini) mini.classList.toggle('kr-thinking', krBusyN > 0);
    }
    _krBusy = krBusy;
    // Point the module-scoped toast() (used by fillCard's Add/Compare stub
    // handlers, defined once outside openKitRoom — see `_toast` below) at
    // THIS overlay instance's toast element.
    _toast = toast;

    // ===========================================================================
    // Rail — "Your bulk order" (Slice 2). State lives on THIS overlay
    // instance only (`railLines`), reset every time openKitRoom() runs since
    // a fresh shellHTML() is built per open. Each line carries its own last
    // RESOLVED getQuote() response (`quote`) — totals are always a sum of
    // real quote fields, never a client-side recompute.
    // ===========================================================================
    var railLines = []; // [{ hit, quote, preview, qty, card, requoting, requoteFailed, _debounceT }]
    var QTY_STEP = 10, QTY_MIN = 10, REQUOTE_DEBOUNCE_MS = 250;
    // Mobile-only auto-expand (see handleAddToRail below): on the FIRST line
    // add, briefly pop the collapsed bottom-sheet open so "Start your order"
    // is visible without the extra chevron tap, then settle it back closed.
    // A manual toggle (railToggleBtn's own click handler) always cancels the
    // pending auto-collapse and is never fought. No-op on desktop, where the
    // rail body is always visible regardless of .kr-expanded.
    var railEl = qs('#kr-rail', root);
    var railAutoExpanded = false;
    var railAutoCollapseTimer = null;
    function cancelRailAutoCollapse() {
      if (railAutoCollapseTimer) { clearTimeout(railAutoCollapseTimer); railAutoCollapseTimer = null; }
    }

    var railLinesEl = qs('#kr-rail-lines', root);
    var railTitleEl = qs('#kr-rail-title', root);
    var railHeadTotalEl = qs('#kr-rail-headtotal', root);
    var pdfBtn = qs('#kr-pdf-btn', root);
    var checkoutBtn = qs('#kr-checkout-btn', root);
    var checkoutNoteEl = qs('#kr-rail-checkout-note', root);
    var CHECKOUT_NOTE_DEFAULT = checkoutNoteEl.textContent;
    var emailCaptureEl = qs('#kr-email-capture', root);
    var emailInputEl = qs('#kr-email-input', root);
    var emailSendBtn = qs('#kr-email-send', root);
    var resumeLinkEl = qs('#kr-resume-link', root);
    var PDF_BTN_DEFAULT_TEXT = pdfBtn.textContent;
    var fSubEl = qs('#kr-f-sub', root), fSaveLblEl = qs('#kr-f-savelbl', root), fSaveEl = qs('#kr-f-save', root),
      fGstEl = qs('#kr-f-gst', root), fTotEl = qs('#kr-f-tot', root);

    // ===========================================================================
    // Save/Resume state — this overlay instance's saveKitQuote/getKitQuote
    // session, reset per open like everything else here. `quoteId` (the
    // Firestore doc id — lets a re-save MERGE instead of forking a new quote)
    // and `resumeToken`/`quoteNumber` are populated the first time we hear
    // back from saveKitQuote (autosave OR "Email me this quote"), or from a
    // resumed quote's own response. `customerEmailCaptured`/`...Name` seed
    // bulkDeskLogTurn's identity fields the moment we learn them.
    // `restoringRail` suppresses autosave WHILE we're programmatically
    // rebuilding the rail from local/resume data (that's not a user edit).
    // ===========================================================================
    var quoteId = null;
    var resumeToken = null;
    var quoteNumber = null;
    var customerEmailCaptured = null;
    var customerNameCaptured = null;
    // FIX #17 — company/ABN/PO/contact/delivery details rehydrated from a
    // resumed quote's `businessDetails` (getKitQuote), if it has one. null
    // for a fresh session / an old quote saved before this shipped — see
    // applyBusinessDetailsPrefill() (Chat block) for how this pre-fills a
    // fresh request_details form instead of asking the customer again.
    var krBusinessDetails = null;
    // FIX #12 — monotonic autosave revision. Incremented on every rail
    // mutation (add/remove/qty/recolour/size-edit/artwork-attach — every
    // scheduleAutosave() call site, see each `railRev++` above its call) and
    // sent as `rev` on every saveKitQuote POST so a slow/out-of-order
    // autosave response can never roll the rail back over a newer one — the
    // server rejects/skips a save whose rev isn't ahead of what it already
    // has stored. Initialized from the resumed/local quote's own last-saved
    // rev below (else starts at 0 for a genuinely fresh quote).
    var railRev = 0;
    var autosaveTimer = null;
    var restoringRail = false;

    function persistLocalQuote() {
      if (!railLines.length) { krClearLocalQuote(); return; }
      krSaveLocalQuote({
        rail: railLines.map(function (l) {
          return { hit: l.hit, qty: l.qty, printLocations: l.printLocations, colour: l.colour, sizeRun: l.sizeRun, artwork: l.artwork };
        }),
        quoteId: quoteId, resumeToken: resumeToken,
        rev: railRev // FIX #12 — carried forward on the next same-device load, see the resume block below
      });
    }

    // Shared {productId,quantity,printLocations,colour?,sizes?[,artwork]} line
    // shape for saveKitQuote callers (autosave + the request_details form
    // submit) AND customerQuotePdf (sendQuoteEmail) — same doc, same
    // discipline: never a price. `sizes` (mirrors the checkoutBtn builder)
    // so the emailed PDF keeps the per-size split instead of falling back to
    // one arbitrary variant for the full qty. `artwork` (set by
    // storeArtworkOnActiveLine() in the Chat block once Ari's been shown a
    // design) rides along whenever a line carries one.
    function railLinesPayload() {
      return railLines.map(function (l) {
        // REAL per-line placements now — never the old shared global. Falls
        // back to the default only for the (should-be-unreachable) case of a
        // line with none, so a save/checkout call is never sent an empty array.
        var p = {
          productId: l.hit.id, quantity: l.qty,
          printLocations: (l.printLocations && l.printLocations.length) ? l.printLocations : QUOTE_PRINT_LOCATIONS
        };
        if (l.colour) p.colour = l.colour;
        if (l.sizeRun && Object.keys(l.sizeRun).length) p.sizes = l.sizeRun;
        if (l.artwork) p.artwork = l.artwork;
        if (l.hit && l.hit.handle) p.handle = l.hit.handle; // R1 (2026-08-05): persist handle → lossless getKitQuote resume, no per-line preview refetch (#23)
        return p;
      });
    }

    // {lineIndex(as string): {size:qty}} across every line that has a size run —
    // saveKitQuote's sizeRunForLine() (index.ts) reads details.sizeRun keyed by
    // productId, by 0-based line index, OR (single-line quotes only) a bare flat map —
    // this uses the INDEX form, not productId. P1 fix #6 (2026-08): a rail line's
    // identity is no longer productId alone — two lines can now share a productId with
    // different colours (see findLine()) — so a productId-keyed map here would
    // silently collide: the SECOND same-product line's sizeRun would clobber the
    // first's in this object, and the server's sizeRunForLine() would then hand BOTH
    // lines whichever one won. Index is always unique per rail line regardless of
    // colourway, and the server already supports it as a fallback key (no server
    // change needed). Returns null when no line has one, so callers never send an
    // empty/fabricated details.sizeRun.
    function railSizeRunDetails() {
      var out = {}, any = false;
      railLines.forEach(function (l, idx) {
        if (l.sizeRun && Object.keys(l.sizeRun).length) { out[String(idx)] = l.sizeRun; any = true; }
      });
      return any ? out : null;
    }

    // Server autosave — debounced, silent-fail (never disrupts the build).
    // Sends {productId,quantity,printLocations,colour?} per line — NEVER a
    // price (saveKitQuote re-prices every line itself, same discipline as
    // createKitDraft) — plus details.sizeRun when any line carries one.
    // Passes quoteId back so re-saves MERGE one doc, and conversationId so
    // the quote↔conversation link is set once known.
    function doAutosave() {
      if (!railLines.length) return; // nothing to persist server-side
      try {
        var payload = { lines: railLinesPayload(), rev: railRev }; // FIX #12 — lets the server reject/skip this if a newer save already landed
        var sizeRun = railSizeRunDetails();
        if (sizeRun) payload.details = { sizeRun: sizeRun };
        if (quoteId) payload.quoteId = quoteId;
        if (chatConversationId) payload.conversationId = chatConversationId;
        if (customerEmailCaptured) payload.customer = { email: customerEmailCaptured, name: customerNameCaptured || undefined };
        fetchJSON(saveQuoteUrl(), {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        }).then(function (data) {
          if (data && data.quoteId) quoteId = data.quoteId;
          if (data && data.resumeToken) resumeToken = data.resumeToken;
          if (data && data.quoteNumber) quoteNumber = data.quoteNumber;
          // FIX #12(c) (2026-08-05) — learn the server's true rev from every save response, not
          // just the resume fetch. Without this, a same-device reload carrying a stale localStorage
          // rev has no way to learn the server is already ahead until its NEXT autosave gets
          // rejected as stale — a slow, silent window where saves are dropped. Only ever moves
          // railRev FORWARD (never backward — a stale response's `rev` is the server's stored rev,
          // still >= our own).
          if (data && typeof data.rev === 'number' && data.rev > railRev) railRev = data.rev;
          persistLocalQuote(); // refresh the local snapshot now the ids are known
        }).catch(function () {}); // silent-fail — autosave must never disrupt the build
      } catch (e) { /* same guarantee for a synchronous throw */ }
    }

    // Ari's request_details form card (see the Chat block's
    // renderRequestDetailsCards/buildFormCard) submits here — an additive
    // `details` object alongside the SAME lines/quoteId/conversationId
    // shape autosave sends, so it merges into the same quote doc. NEVER a
    // price. saveKitQuote requires >=1 line, so if the rail is still empty
    // when a form is submitted (Ari collected company/PO details before any
    // product was added) there's nothing to attach it to yet — skip the
    // call rather than send a request guaranteed to 400. Same silent-fail
    // discipline as autosave: this must never block the chat.
    function submitDetailsForm(details) {
      if (!railLines.length) return;
      try {
        // Ari's request_details size_grid field reads back as a FLAT
        // {size:qty} map (buildFormField's size_grid controller — see
        // read() above), no productId/line reference — it has no notion of
        // "which line". railSizeRunDetails() (the drawer's snapshot) is keyed BY
        // LINE INDEX (P1 fix #6, 2026-08 — was productId, but productId is no longer
        // a unique rail-line identity once two colourway lines can share one, which
        // would collide in a productId-keyed map here; index is always unique). Naively
        // merging the two (old behaviour) left BOTH shapes sitting in the same object —
        // the flat size keys alongside an index key the drawer inserted — so the
        // server, which reads details.sizeRun[productId] / [lineIndex] in that order,
        // saw the STALE drawer entry and never the customer's fresh form answer.
        // Reshape the flat answer onto the line it's actually about (whichever rail
        // line is currently open in Configure, or the sole line when there's only one)
        // BEFORE folding in the drawer's per-OTHER-line fallbacks, so the customer's
        // own answer always wins for its own line.
        if (details.sizeRun && Object.keys(details.sizeRun).length) {
          var srKeys = Object.keys(details.sizeRun);
          // No key looks like a real line index -> it's size labels (a flat answer).
          // Size labels are never bare digit strings ("2XL"/"3XL" fail this test), so
          // this can't false-positive on a real size run.
          var looksFlat = !srKeys.some(function (k) { return /^\d+$/.test(k) && Number(k) < railLines.length; });
          if (looksFlat) {
            // P1 #6 defect fix (2026-08) — with 2+ colourways on the rail, an "any
            // colourway" lookup here could tag the WRONG line's index in the save
            // payload (whichever colourway happened to be added first, not the one
            // Ari's size-grid form is actually about). krOpenConfig.line carries the
            // EXACT EDIT-intent target when Configure was opened against a known rail
            // line (see FINDING #2 near handleOpenConfigure) — prefer it; only fall
            // back to the "any colourway" lookup for ADD intent (krOpenConfig.line is
            // null there by construction, so this can't silently override a real
            // EDIT target).
            var targetLine = krOpenConfig
              ? (krOpenConfig.line || (krOpenConfig.hit ? findLine(krOpenConfig.hit.id) : null))
              : null;
            if (!targetLine && railLines.length === 1) targetLine = railLines[0];
            var targetIdx = targetLine ? railLines.indexOf(targetLine) : -1;
            if (targetIdx !== -1) {
              var flatRun = details.sizeRun;
              details.sizeRun = {};
              details.sizeRun[String(targetIdx)] = flatRun;
            }
            // else: multiple lines open and nothing in Configure — genuinely
            // ambiguous which line this answers for; never guess, leave
            // details.sizeRun as Ari sent it rather than fabricate a target.
          }
        }
        // Fold in the Configure drawer's per-line size runs only where Ari's
        // own form (details.sizeRun, from a request_details size_grid field)
        // didn't already set one for that key — never clobber what the
        // shopper just typed into the form they're actively submitting.
        var drawerSizeRun = railSizeRunDetails();
        if (drawerSizeRun) {
          if (!details.sizeRun) details.sizeRun = {};
          for (var lineIdx in drawerSizeRun) {
            if (Object.prototype.hasOwnProperty.call(drawerSizeRun, lineIdx) && !(lineIdx in details.sizeRun)) details.sizeRun[lineIdx] = drawerSizeRun[lineIdx];
          }
        }
        var payload = {
          lines: railLinesPayload(),
          details: details,
          rev: railRev // FIX #12 — same staleness guard as the autosave POST (this merges into the same doc)
        };
        if (quoteId) payload.quoteId = quoteId;
        if (chatConversationId) payload.conversationId = chatConversationId;
        if (customerEmailCaptured) payload.customer = { email: customerEmailCaptured, name: customerNameCaptured || undefined };
        fetchJSON(saveQuoteUrl(), {
          method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload)
        }).then(function (data) {
          if (data && data.quoteId) quoteId = data.quoteId;
          if (data && data.resumeToken) resumeToken = data.resumeToken;
          if (data && data.quoteNumber) quoteNumber = data.quoteNumber;
          // FIX #12(c) (2026-08-05) — same self-heal as doAutosave's handler above: learn the
          // server's true rev from this save response so a stale localStorage rev doesn't sit
          // unresolved until the next round-trip gets rejected as stale.
          if (data && typeof data.rev === 'number' && data.rev > railRev) railRev = data.rev;
          persistLocalQuote();
        }).catch(function () {}); // silent-fail — a form submit must never break the chat
      } catch (e) { /* same guarantee for a synchronous throw */ }
    }

    // Called on every rail-changing action (add/remove/qty-step). Writes the
    // instant local snapshot right away (cheap, same-device only) and
    // debounces the server round-trip ~1.5s so a burst of qty clicks doesn't
    // fire a save per click.
    function scheduleAutosave() {
      if (restoringRail) return; // a restore is writing state, not the customer — nothing new to persist
      persistLocalQuote();
      clearTimeout(autosaveTimer);
      autosaveTimer = setTimeout(doAutosave, AUTOSAVE_DEBOUNCE_MS);
    }

    // Resolve a restore-time entry into a full, handle-bearing hit:
    //   - localStorage entries already carry the full hit (incl. handle) we
    //     stored ourselves — used as-is.
    //   - getKitQuote's `rail[]` items only ever carry {productId,title,
    //     vendor,...} — no handle (quote-server.ts's QuoteResponse shape has
    //     none either) — so those resolve via getPreviewConfig, the only
    //     endpoint that maps a productId back to a real handle.
    // Returns null (never a fabricated card) if nothing resolves.
    function resolveHitForRestore(hit) {
      if (hit && hit.handle) return Promise.resolve(hit);
      var id = hit && hit.id;
      if (!id || !PB.previewUrl) return Promise.resolve(null);
      return fetchJSON(PB.previewUrl + '?productId=' + encodeURIComponent(id))
        .then(function (data) {
          var p = data && data.preview && data.preview.product;
          if (!p || !p.handle) return null;
          return {
            id: p.id || id, title: (hit && hit.title) || p.title || '', styleCode: p.styleCode || null,
            vendor: (hit && hit.vendor) || null, handle: p.handle, gsm: null, fit: null, composition: null, colourCount: 0
          };
        })
        .catch(function () { return null; });
    }

    // Rebuild the rail from a set of {hit, qty} entries (local snapshot OR a
    // resumed quote), RE-PRICING every line live via the same fast
    // product.js + computeClientQuote path Browse/Chat cards use — never
    // trusting a stored price. A line whose product can't be resolved/priced
    // any more is just dropped (never a fabricated line); `_entered:true` on
    // each pushed line skips the "just added" spring-in — this is a restore,
    // not a fresh add.
    function restoreRailFromEntries(entries) {
      if (!entries || !entries.length) return;
      restoringRail = true;
      krBusy(true);
      getPrintPrices().then(function () {
        return Promise.all(entries.map(function (entry) {
          return resolveHitForRestore(entry.hit).then(function (hit) {
            if (!hit || !hit.handle) return null;
            return fetch('/products/' + hit.handle + '.js')
              .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); })
              .then(function (pj) {
                var garmentUnit = (typeof pj.price_min === 'number' ? pj.price_min : 0) / 100;
                if (!(garmentUnit > 0)) throw new Error('no price on product.js');
                var qty = Math.max(QTY_MIN, Math.floor(entry.qty) || QUOTE_QTY);
                // printLocations/colour/sizeRun round-trip verbatim from a
                // same-device localStorage snapshot (persistLocalQuote stores
                // the real arrays/objects). A cross-device resume (getKitQuote)
                // only ever carries a collapsed human-readable label string —
                // krPrintLocationsFromLabel() below approximates real zone
                // keys from it; never fabricates a size run it never had.
                var printLocations = (entry.printLocations && entry.printLocations.length) ? entry.printLocations : QUOTE_PRINT_LOCATIONS.slice();
                var quote = computeClientQuote(garmentUnit, printLocations, qty);
                var fi = pj.featured_image;
                var frontImg = (typeof fi === 'string' ? fi : (fi && fi.src)) || (pj.images && pj.images[0]) || null;
                return {
                  hit: hit, quote: quote, preview: { images: { front: frontImg }, availableColours: [] },
                  printLocations: printLocations, colour: entry.colour || null, sizeRun: entry.sizeRun || null,
                  // Item 4 — krNormalizeArtworkMap migrates a same-device
                  // localStorage snapshot saved BEFORE per-zone artwork
                  // shipped (still the old flat {uploadedUrl,...} shape) onto
                  // 'front', so a resumed quote never silently loses it.
                  artwork: krNormalizeArtworkMap(entry.artwork)
                };
              })
              .catch(function () { return null; });
          });
        }));
      }).then(function (results) {
        // FIX #18 — a null result means that saved line's product couldn't be
        // re-resolved (discontinued/removed since the quote was saved — see
        // resolveHitForRestore/the product.js fetch above, both silent-fail
        // to null). Count them BEFORE the forEach below folds them away, so
        // the welcome-back toast can tell the customer honestly instead of
        // claiming "right where you left it" when it silently isn't.
        var droppedCount = 0;
        results.forEach(function (r) {
          if (!r) { droppedCount++; return; }
          // Exact (productId, colour) match — P1 fix #6: a saved/resumed quote can now
          // legitimately carry TWO lines for the same productId (different colourways);
          // an "any colourway" check here would wrongly treat the second as a dupe and
          // drop it. Still blocks a genuine re-restore of the exact same line.
          if (findLine(r.hit.id, r.colour)) return; // never fabricate, never duplicate
          railLines.push({
            hit: r.hit, quote: r.quote, preview: r.preview, qty: r.quote.quantity,
            printLocations: r.printLocations, colour: r.colour, sizeRun: r.sizeRun,
            card: null, requoting: false, requoteFailed: false, _debounceT: null, _entered: true, artwork: r.artwork || null
          });
        });
        renderRail();
        restoringRail = false;
        krBusy(false);
        if (droppedCount) {
          var itemWord = droppedCount === 1 ? 'item' : 'items';
          var toastMsg = railLines.length
            ? ('Welcome back — ' + droppedCount + ' ' + itemWord + ' from your quote ' + (droppedCount === 1 ? 'is' : 'are') +
               ' no longer available and ' + (droppedCount === 1 ? 'was' : 'were') + ' removed.')
            : ('Welcome back — but the ' + itemWord + ' from your quote ' + (droppedCount === 1 ? 'is' : 'are') + ' no longer available.');
          toast(toastMsg, { durationMs: 5200 }); // longer than the default 2.6s — this one matters more than a routine confirmation
        } else if (railLines.length) {
          toast('Welcome back — your quote is right where you left it.');
        }
      }).catch(function () {
        restoringRail = false;
        krBusy(false);
      });
    }

    // P1 fix #6 (2026-08) — a rail line's identity used to be productId ALONE, so a
    // second colourway of the same style (e.g. "30 × Navy" added after "50 × Black")
    // silently REPLACED the first line instead of adding a second — and autosave then
    // persisted the loss. Identity is now (productId, colour), colour
    // case/whitespace-normalized so "Navy"/"navy "/"NAVY" are one slot and a line with
    // no colour set ('' ) never collides with one that has a real colour. print
    // placements are deliberately NOT part of identity — see the banner above
    // applyAriUpsert for why.
    //
    // Contract: call with ONE argument (just productId) to match ANY colourway of this
    // product — informational "is this product already on the rail" checks, and any
    // call site applying the ≤1-vs-2+ rule below when it's already established there's
    // at most one colourway to match. Call with TWO arguments (productId, colour —
    // colour may be null/'' for "no colour") to require an EXACT match, which every
    // add/remove/dedupe decision that must never cross-match two different colourways
    // of the same product uses once 2+ colourways genuinely exist.
    //
    // THE ≤1-VS-2+ RULE (P1 #6 defect fix, 2026-08) — the Browse/Chat-card single-line
    // edit flows (handleOpenConfigure/openChatConfigCard onConfirm) and Ari's
    // update_quote 'update' action (applyAriUpsert) all resolve their mutation target
    // via countLinesForProduct(hit.id): with 0-1 colourways on the rail, an exact
    // (productId, colour) match is the WRONG tool — recolouring the sole line (Blue ->
    // Black) has no "Black" line to exact-match yet, so exact-match misses it and a
    // brand-new line gets pushed alongside the untouched original (double-add, and for
    // Ari's 'update' path the new line also loses the original's qty to the QUOTE_QTY
    // default). Below that threshold, fall back to the pre-#6 "any" match — genuinely
    // unambiguous with at most one line, and it recolours that line in place, keeping
    // its qty/sizeRun/artwork (a design is garment-colour-independent, so carrying
    // artwork across a recolour is correct — and matches the "Update line" label).
    // With 2+ colourways already on the rail, keep the exact match — that's the actual
    // #6 fix (a new colour must resolve precisely / add, never clobber one of the two).
    function railColourKey(c) { return String(c || '').replace(/^\s+|\s+$/g, '').toLowerCase(); }
    function findLine(productId, colour) {
      var exact = arguments.length > 1;
      var wantColour = exact ? railColourKey(colour) : null;
      for (var i = 0; i < railLines.length; i++) {
        if (railLines[i].hit.id !== productId) continue;
        if (!exact) return railLines[i];
        if (railColourKey(railLines[i].colour) === wantColour) return railLines[i];
      }
      return null;
    }

    // Valid-quote enforcement — every line the Configure drawer or fast-add
    // creates always carries >=1 placement (front-full default, rubber-
    // banded). The only way a line can reach here with none is a legacy path
    // (a cross-device resume whose label didn't parse to a real zone — see
    // krPrintLocationsFromLabel) — surfaced honestly rather than silently
    // charging $0 print or crashing computeClientQuote.
    function lineNeedsPlacement(line) { return !line.printLocations || !line.printLocations.length; }
    function anyLineNeedsPlacement() {
      for (var i = 0; i < railLines.length; i++) { if (lineNeedsPlacement(railLines[i])) return true; }
      return false;
    }

    function buildLineEl(line) {
      var q = line.quote;
      var photo = line.preview && line.preview.images && line.preview.images.front;
      var thumbInner = photo ? '<img src="' + escapeHtml(krImg(photo, 120)) + '" alt="" loading="lazy" decoding="async">' : GENERIC_TEE_SVG;
      var priceHtml;
      if (line.requoteFailed) {
        priceHtml = '<span class="kr-rline-fail">Couldn&rsquo;t update the price.<button type="button" class="kr-rline-retry">Retry</button></span>';
      } else {
        // q always exists here — a line is only ever created from an
        // already-resolved quote, and a failed re-quote leaves the last
        // good one in place rather than clearing it.
        priceHtml = money(q.total) + ' <small>&middot; ' + money(q.perUnit) + '/unit</small>';
      }
      // Spring-in + orange flash plays ONCE per line's lifetime, on its
      // first render — renderRail() rebuilds every line's DOM node on every
      // call (qty change on line B re-renders line A too), so without this
      // guard every existing line would replay its entrance animation any
      // time ANY line in the rail changed.
      var isNew = !line._entered;
      line._entered = true;
      var needsPlacement = lineNeedsPlacement(line);
      var lineCls = 'kr-rline' + (line.requoting ? ' kr-requoting' : '') + (isNew ? ' kr-rline-new' : '');
      var lineEl = el('div', lineCls);
      // Real per-line config, not the old hardcoded "1 front print" — see
      // railLineMetaText(). A line that somehow has zero placements (legacy
      // resume path — see krPrintLocationsFromLabel) gets an amber "Needs
      // placement" chip instead, which reopens the Configure drawer to fix it.
      var metaHtml = needsPlacement
        ? '<button type="button" class="kr-rline-needsplacement">Needs placement</button>'
        : escapeHtml(railLineMetaText(line));
      var artworkBadge = line.artwork ? ('<div class="kr-rline-artwork-badge" title="Artwork attached" aria-hidden="true">' + CLIP_SVG + '</div>') : '';
      // "Ari added" chip — set by applyUpdateQuoteActions() when Ari added/edited this
      // line via update_quote. Clears (line.ariAdded = false) the moment the customer
      // touches qty/Configure/remove on it below — an Ari-added line is otherwise a
      // fully normal, reconfigurable/removable rail line, no parallel UI.
      var ariBadge = line.ariAdded ? '<div class="kr-rline-ari-badge" title="Ari added this to your quote">Ari added</div>' : '';
      lineEl.innerHTML =
        '<div class="kr-rline-thumb">' + thumbInner + artworkBadge + ariBadge + '</div>' +
        '<div class="kr-rline-body">' +
          '<div class="kr-rline-nm">' + escapeHtml(line.hit.title || '') + (line.hit.styleCode ? ' ' + escapeHtml(line.hit.styleCode) : '') + '</div>' +
          '<div class="kr-rline-mt">' + metaHtml + '</div>' +
          '<div class="kr-rline-pr">' + priceHtml + '</div>' +
          '<div class="kr-qty">' +
            '<button type="button" class="kr-qty-dec" aria-label="Decrease quantity"' + (line.qty <= QTY_MIN ? ' disabled' : '') + '>&minus;</button>' +
            '<span class="kr-qty-n">' + line.qty + '</span>' +
            '<button type="button" class="kr-qty-inc" aria-label="Increase quantity">+</button>' +
          '</div>' +
        '</div>' +
        '<button type="button" class="kr-rline-rm" aria-label="Remove ' + escapeHtml(line.hit.title || 'item') + ' from your order">' + CLOSE_SVG + '</button>';

      qs('.kr-qty-dec', lineEl).addEventListener('click', function () { line.ariAdded = false; stepQty(line, -QTY_STEP); });
      qs('.kr-qty-inc', lineEl).addEventListener('click', function () { line.ariAdded = false; stepQty(line, QTY_STEP); });
      qs('.kr-rline-rm', lineEl).addEventListener('click', function () { line.ariAdded = false; removeLine(line); });
      if (needsPlacement) {
        qs('.kr-rline-needsplacement', lineEl).addEventListener('click', function () {
          line.ariAdded = false;
          // FINDING #3 (P1 #6 follow-up) — pass THIS exact line as the 5th
          // (targetLine) arg so handleOpenConfigure edits it specifically,
          // never whichever colourway findLine(hit.id)'s "any" match happens
          // to resolve to first. Without this, clicking a 2nd colourway's
          // chip silently reopened the 1st, leaving the 2nd stuck un-placeable
          // forever (anyLineNeedsPlacement() blocks checkout).
          handleOpenConfigure(line.hit, line.quote, line.preview, line.card, line);
        });
      }
      if (line.requoteFailed) {
        qs('.kr-rline-retry', lineEl).addEventListener('click', function () {
          line.requoteFailed = false; line.requoting = true; renderRail(); requoteLine(line);
        });
      }
      return lineEl;
    }

    var checkoutInFlight = false; // guards against re-enabling the checkout button mid-request
    var railHadItems = false;     // 0 -> 1 line transition pops the PDF/checkout buttons once
    var lastTopTierPct = 0;       // a NEW higher bulk band flashes the save row + pops the % pill
    var negMoneyFmt = function (v) { return '– ' + money(v); };

    // renderRail() fully tears down + rebuilds every line's DOM (railLinesEl.innerHTML='')
    // on every call, including a plain qty +/- tick — without this, focus silently jumps
    // to <body> after every stepQty() click, killing keyboard use of the stepper.
    // openConfigureDrawer/closeConfigureDrawer already save/restore focus across their
    // own open/close; this mirrors that discipline across a rail re-render instead.
    function captureRailFocus() {
      var af = document.activeElement;
      if (!af || !railLinesEl.contains(af)) return null;
      var lineEl = af.closest ? af.closest('.kr-rline') : null;
      if (!lineEl) return null;
      var idx = Array.prototype.indexOf.call(railLinesEl.children, lineEl);
      if (idx === -1) return null;
      var btnClass = af.classList.contains('kr-qty-dec') ? '.kr-qty-dec' :
        af.classList.contains('kr-qty-inc') ? '.kr-qty-inc' :
        af.classList.contains('kr-rline-rm') ? '.kr-rline-rm' :
        af.classList.contains('kr-rline-needsplacement') ? '.kr-rline-needsplacement' :
        af.classList.contains('kr-rline-retry') ? '.kr-rline-retry' : null;
      if (!btnClass) return null;
      return { idx: idx, btnClass: btnClass };
    }
    function restoreRailFocus(saved) {
      if (!saved) return;
      var lineEl = railLinesEl.children[saved.idx];
      if (!lineEl) return;
      // Prefer the exact same button; if a qty tick disabled it (hit QTY_MIN),
      // fall back to its sibling qty button rather than dropping focus to body.
      var target = qs(saved.btnClass, lineEl);
      if (!target || target.disabled) target = qs('.kr-qty-inc', lineEl) || qs('.kr-qty-dec', lineEl);
      if (target && !target.disabled && typeof target.focus === 'function') target.focus();
    }

    function renderRail() {
      var savedFocus = captureRailFocus();
      var hasItems = !!railLines.length;
      if (!hasItems) {
        railLinesEl.innerHTML = '<div class="kr-rail-empty">' +
          BAG_SVG.replace('<svg ', '<svg style="width:40px;height:40px;opacity:.4;margin-bottom:10px" ') +
          '<div>Add gear from the grid &mdash; your live quote builds here.</div></div>';
        railTitleEl.textContent = 'Empty for now';
        pdfBtn.disabled = true;
        checkoutBtn.disabled = true;
        // Never leave the inline email-capture field open over an emptied rail.
        if (emailCaptureEl && !emailCaptureEl.hidden) cancelEmailCapture();
      } else {
        var totalQty = 0;
        railLines.forEach(function (l) { totalQty += l.qty; });
        railTitleEl.textContent = totalQty + ' unit' + (totalQty === 1 ? '' : 's') + ' · ' +
          railLines.length + ' style' + (railLines.length === 1 ? '' : 's');
        pdfBtn.disabled = false;
        // Valid-quote enforcement — a line with no print placement (the
        // amber "Needs placement" chip in buildLineEl) blocks checkout,
        // same discipline as the checkoutInFlight guard already had.
        var blockedByPlacement = anyLineNeedsPlacement();
        checkoutBtn.disabled = checkoutInFlight || blockedByPlacement;
        if (checkoutNoteEl && !checkoutInFlight) {
          checkoutNoteEl.textContent = blockedByPlacement ? 'Pick at least one print placement for every style above.' : CHECKOUT_NOTE_DEFAULT;
        }

        railLinesEl.innerHTML = '';
        railLines.forEach(function (line) { railLinesEl.appendChild(buildLineEl(line)); });
        restoreRailFocus(savedFocus);
      }
      // §4 rail lifecycle: pop the CTAs the moment they first become usable.
      if (hasItems && !railHadItems) {
        pulseClass(pdfBtn, 'kr-pop-enable');
        pulseClass(checkoutBtn, 'kr-pop-enable');
      }
      railHadItems = hasItems;

      // ---- totals: getQuote fields summed VERBATIM, never recomputed ----
      var sub = 0, discount = 0, total = 0, topTierPct = 0;
      railLines.forEach(function (l) {
        var q = l.quote;
        sub += q.subtotal;
        discount += q.discountAmount;
        total += q.total;
        var pct = q.appliedTier ? q.appliedTier.discountPercent : 0;
        if (pct > topTierPct) topTierPct = pct;
      });
      // GST is not a getQuote field (site convention: `total` is GST-inclusive) —
      // this is the one place a number is derived, and it's a pure display
      // split of the verbatim total, never a repriced/recomputed discount.
      var gst = total - total / 1.1;

      // §3 live count-ups — every number here is still the exact verbatim
      // getQuote figure above; krAnimateNumber only controls HOW the digits
      // get there (instant under KR_RM), never WHAT the final value is.
      krAnimateNumber(fSubEl, sub, 400, money);
      krAnimateNumber(fSaveEl, discount, 400, negMoneyFmt);
      krAnimateNumber(fGstEl, gst, 400, money);
      krAnimateNumber(fTotEl, total, 400, money);
      krAnimateNumber(railHeadTotalEl, total, 400, money);

      fSaveLblEl.innerHTML = topTierPct
        ? ('Bulk discount <span class="kr-tier-pill">' + topTierPct + '%</span>')
        : 'Bulk discount';
      if (topTierPct > lastTopTierPct) {
        pulseClass(qs('.kr-rrow.kr-save', root), 'kr-flash');
        pulseClass(qs('.kr-tier-pill', fSaveLblEl), 'kr-pill-pop');
      }
      lastTopTierPct = topTierPct;
    }

    // PERF: instant client-side recompute — no getQuote call. line.quote.
    // garmentUnitPrice is always present regardless of which path originally
    // priced the line (the fast client-compute path or the hydrateCard()
    // fallback both return that field), so this works uniformly for every
    // line. Fully synchronous now, so the old "stale response" staleness
    // guard (a later qty click landing before an earlier network response)
    // is structurally impossible — there's no gap for a second click to land
    // in. requoteFailed/kr-rline-retry stay wired in buildLineEl() as a
    // safety net (pure math can't fail here, but nothing breaks if it's
    // never triggered).
    function requoteLine(line) {
      if (!isOpen) return;
      var oldPct = line.quote.appliedTier ? line.quote.appliedTier.discountPercent : 0;
      var newQuote = computeClientQuote(line.quote.garmentUnitPrice, line.printLocations, line.qty);
      var newPct = newQuote.appliedTier ? newQuote.appliedTier.discountPercent : 0;
      line.quote = newQuote;
      line.requoting = false;
      renderRail();
      // §3 tier crossing, attributed to the specific line that crossed it —
      // renderRail() rebuilds every line's DOM in lockstep with railLines,
      // so this line's index still finds the right element post-render.
      if (newPct > oldPct) {
        var idx = railLines.indexOf(line);
        var lineEl = idx > -1 ? railLinesEl.children[idx] : null;
        if (lineEl) pulseClass(qs('.kr-rline-pr', lineEl), 'kr-flash-price');
      }
    }

    function stepQty(line, delta) {
      var newQty = Math.max(QTY_MIN, line.qty + delta);
      if (newQty === line.qty) return;
      line.qty = newQty;
      line.requoting = true;
      line.requoteFailed = false;
      renderRail();
      railRev++; // FIX #12 — qty step
      scheduleAutosave();
      clearTimeout(line._debounceT);
      line._debounceT = setTimeout(function () { requoteLine(line); }, REQUOTE_DEBOUNCE_MS);
    }

    function removeLine(line) {
      var idx = railLines.indexOf(line);
      if (idx === -1) return;
      clearTimeout(line._debounceT);

      function finishRemove() {
        if (!isOpen) return; // overlay closed during the 260ms collapse — nothing left to update
        railLines.splice(idx, 1);
        // Revert the source card's "added" state if it's still in the grid —
        // it may not be: Slice 3 lets the shopper switch category/Popular
        // chips, which rebuilds the grid and detaches old card nodes. The rail
        // line itself is independent of that (kept across chip switches), so
        // this guard just skips the now-pointless DOM write rather than assume.
        if (line.card && line.card.isConnected) {
          line.card.classList.remove('kr-added');
          var addBtn = qs('.kr-add', line.card);
          if (addBtn) addBtn.textContent = 'Configure & Add';
          var chip = qs('.kr-added-chip', line.card);
          if (chip && chip.parentNode) chip.parentNode.removeChild(chip);
        }
        renderRail();
        railRev++; // FIX #12 — line removed
        scheduleAutosave();
      }

      // §4 rail lifecycle: collapse the line out before actually removing it
      // from state. `idx` is captured above (from THIS render's railLines,
      // which buildLineEl() populates in lockstep with railLinesEl.children)
      // so it still points at the right DOM node here.
      var lineEl = railLinesEl.children[idx];
      if (KR_RM || !lineEl) { finishRemove(); return; }
      lineEl.classList.add('kr-rline-out');
      setTimeout(finishRemove, 260);
    }

    // `cfg` (optional) — {printLocations[],colour,sizeRun} from the Configure
    // drawer's "Add to Quote". Omitted entirely for a fast-add (the card's
    // own "Add to Quote" button, or Compare's "Add") — defaults to
    // QUOTE_PRINT_LOCATIONS (front-full) so even a fast buyer always gets a
    // VALID line (>=1 placement), never the old implicit/shared global.
    function handleAddToRail(hit, quote, preview, card, cfg) {
      if (!quote) { toast('Ask Ari for a price on this one first.'); return; } // defensive — see fillCard's hasQuote guard
      // FINDING #1 follow-through (P1 #6 hardening, 2026-08) — a Configure-
      // drawer confirm (cfg present) already resolved an EXACT
      // (productId, colour) non-match before calling here (see
      // handleOpenConfigure/openChatConfigCard's onConfirm) — that's what
      // makes it a legitimate NEW colourway. Re-checking with the OLD 1-arg
      // "any colourway" match would wrongly block it every time a DIFFERENT
      // colourway of the same product is already on the rail ("Already in
      // your kit" even though the colour picked here isn't). A colourless
      // fast-add (Compare's "Add" button, cfg omitted — no colour picker
      // there) keeps the original "any" dedupe, since it has no colour to
      // disambiguate by.
      if (cfg ? findLine(hit.id, cfg.colour) : findLine(hit.id)) { toast('Already in your kit'); return; }
      var line = {
        hit: hit, quote: quote, preview: preview, qty: quote.quantity || QUOTE_QTY,
        printLocations: (cfg && cfg.printLocations && cfg.printLocations.length) ? cfg.printLocations.slice() : QUOTE_PRINT_LOCATIONS.slice(),
        colour: (cfg && cfg.colour) || null,
        sizeRun: (cfg && cfg.sizeRun) || null,
        card: card, requoting: false, requoteFailed: false, _debounceT: null,
        // Item 4 — {side: {printReadyUrl,uploadedUrl,previewUrl,name}}, keyed
        // by print-zone side (front/back/leftSleeve/rightSleeve) — from the
        // Configure panel's own per-slot upload zones (cfg.artwork) if the
        // customer attached one there, else null until
        // storeArtworkOnActiveLine() attaches one from a chat-vision turn
        // once Ari's actually seen a design and resolved which zone it's for.
        artwork: (cfg && cfg.artwork) || null
      };
      railLines.push(line);
      card.classList.add('kr-added');
      var addBtn = qs('.kr-add', card);
      if (addBtn) { addBtn.textContent = '✓ In your order'; pulseClass(addBtn, 'kr-add-pop'); }
      // Calm added-state signal (purple border + tint from .kr-card.kr-added
      // above) plus a small ✓ chip on the thumb — replaces the old hard
      // orange outline per the redesign's colour discipline.
      var thumbEl = qs('.kr-thumb', card);
      if (thumbEl && !qs('.kr-added-chip', card)) {
        var chip = el('div', 'kr-added-chip', '&#10003;');
        chip.setAttribute('aria-hidden', 'true');
        thumbEl.appendChild(chip);
      }
      // Decorative FLIP-fly from the card's thumb to the rail — purely
      // visual, fires and forgets; the actual state change below never
      // waits on it (no-ops instantly under KR_RM/zero-size rects).
      krFlipFly(qs('.kr-thumb', card), railTitleEl, root);
      renderRail();
      railRev++; // FIX #12 — line added
      scheduleAutosave();
      pulseClass(railToggleBtn, 'kr-breath');
      // First add only: on mobile the rail starts collapsed to a mini-bar —
      // "Start your order" lives inside the sheet, a chevron tap away. Pop
      // it open now so the primary CTA is visible without that extra tap,
      // then let it settle back closed a couple of seconds later. Harmless
      // on desktop (.kr-expanded only affects layout under the ≤820px media
      // query) and never fights a shopper who's already toggled it herself
      // (cancelRailAutoCollapse() below on any manual toggle).
      if (railLines.length === 1 && !railAutoExpanded) {
        railAutoExpanded = true;
        if (railToggleBtn.getAttribute('aria-expanded') !== 'true') {
          railToggleBtn.setAttribute('aria-expanded', 'true');
          railEl.classList.add('kr-expanded');
          railAutoCollapseTimer = setTimeout(function () {
            railAutoCollapseTimer = null;
            if (!isOpen) return; // overlay closed in the meantime
            railToggleBtn.setAttribute('aria-expanded', 'false');
            railEl.classList.remove('kr-expanded');
          }, 3200);
        }
      }
      // The fly + spring-in line + button morph above ARE the confirmation
      // under normal motion — the toast would be redundant noise. Under
      // reduced motion none of that plays, so the toast is the only
      // confirmation a shopper gets and stays exactly as before.
      if (KR_RM) toast((hit.title || 'Item') + ' added to your kit — ' + money(quote.total) + ' total.');
    }
    _addToRail = handleAddToRail;

    // ===========================================================================
    // Configure drawer ("Configure your line") — Shell A (Browse). Hosts the
    // SHARED buildLineConfigUI() engine (module scope, above) in a
    // right-edge slide-out (bottom-sheet <=820px, same responsive pattern as
    // the rail's own sheet). Opened by clicking a card's `.kr-card-hit`
    // (thumb/name/price — see fillCard) or the rail's amber "Needs
    // placement" chip. Confirm either pushes a NEW valid rail line (via
    // handleAddToRail, cfg param above) or updates the line already in the
    // rail for this product. See openChatConfigCard() further down for
    // Shell B (Chat) — same engine, different host chrome, identical result.
    // ===========================================================================
    var cfgBackdropEl = qs('#kr-cfg-backdrop', root);
    var cfgPanelEl = qs('#kr-cfg-panel', root);
    var cfgCloseBtn = qs('#kr-cfg-close', root);
    var cfgBodyEl = qs('#kr-cfg-body', root);
    var cfgOpen = false;
    var cfgUI = null;       // current buildLineConfigUI() instance, null while closed
    var krOpenConfig = null; // {hit, ui, line} the customer currently has OPEN (drawer or chat card) but has NOT added yet — surfaced to Ari via buildQuoteContext so "this"/"is this good?" resolves to what's on their screen. `line` (P1 #6 follow-up, FINDING #2) is the exact rail line being EDITed, or null for ADD intent — see storeArtworkOnActiveLine. Identity-guarded on clear so a stale card is never reported.
    var cfgOpenerEl = null; // focus-return target on close

    function closeConfigureDrawer() {
      if (!cfgOpen) return;
      cfgOpen = false;
      if (krOpenConfig && krOpenConfig.ui === cfgUI) krOpenConfig = null; // no longer open on screen
      cfgPanelEl.classList.remove('kr-show');
      cfgBackdropEl.classList.remove('kr-show');
      if (cfgUI) { cfgUI.destroy(); cfgUI = null; } // stop any in-flight hydration from re-rendering a closed drawer
      var opener = cfgOpenerEl;
      setTimeout(function () {
        if (cfgOpen) return; // a fresh open beat this timeout — never hide a drawer that reopened in the meantime
        cfgPanelEl.hidden = true;
        cfgBackdropEl.hidden = true;
      }, KR_RM ? 0 : 300);
      try { if (opener && typeof opener.focus === 'function') opener.focus(); } catch (e) {}
    }

    // hit/quote/preview/card — same 4-tuple every card's fillCard() already
    // holds; `card` is the originating grid card (thumb used for openerEl
    // focus-return; may be a rail row's stale/absent card when reopened via
    // the amber "Needs placement" chip — never required for correctness).
    //
    // targetLine (optional, P1 #6 follow-up hardening, 2026-08) — the EXACT
    // rail line to edit, by object identity. Passed ONLY by call sites that
    // know precisely which colourway the customer clicked (buildLineEl's
    // "Needs placement" chip). Omitted -> this open is ADD intent (a Browse
    // product card — see fillCard's kr-card-hit/kr-add handlers): the
    // customer is adding a colourway, not editing a specific known line.
    // FINDING #1: it used to be that ANY open here (drawer or chip) resolved
    // its mutation target via findLine(hit.id) — a 1-arg "any colourway of
    // this product" match. With 2+ colourways possible, "Browse this
    // product again and pick a different colour" would silently relabel an
    // unrelated existing line in place (wrong colour, wrong qty, stolen
    // artwork) instead of adding a NEW line. Now: EDIT intent (targetLine
    // given) always mutates that exact object; ADD intent NEVER pre-commits
    // to a target — it's resolved at confirm time by an EXACT
    // (productId, colour) match against whatever colour the customer
    // actually picked (see onConfirm below), same discipline handleAddToRail/
    // applyAriUpsert already use — UNLESS this product has ≤1 colourway on
    // the rail (countLinesForProduct), in which case that exact match is
    // relaxed to an "any" match so a plain recolour of the sole line lands on
    // that line instead of double-adding (P1 #6 defect fix, 2026-08 — see the
    // ≤1-vs-2+ rule at findLine's contract banner).
    function handleOpenConfigure(hit, quote, preview, card, targetLine) {
      var editIntent = !!targetLine;
      // Pre-fill seed only — NEVER the confirm-time mutation target for ADD
      // intent. Safe to seed from "any" colourway here because this is only
      // used to populate the drawer's initial fields for convenience; the
      // real identity decision happens fresh in onConfirm.
      var seed = targetLine || findLine(hit.id);
      var garmentUnit = quote ? quote.garmentUnitPrice : (seed && seed.quote ? seed.quote.garmentUnitPrice : 0);
      if (!(garmentUnit > 0)) { toast('Ask Ari for a price on this one first.'); return; }

      if (cfgUI) { cfgUI.destroy(); cfgUI = null; } // clean up any previous instance before opening a new one

      cfgOpenerEl = (card && qs('.kr-card-hit', card)) || document.activeElement;

      // Item 2 — no seed line yet? Seed from whatever Ari most recently ran
      // get_quote with (module-scope _chatLastQuoteConfig, see
      // renderTraceCards) instead of the bare defaults, so opening Configure
      // from Browse right after Ari quoted this product in Chat lands on the
      // SAME config Ari described — never overrides a seed line's own saved
      // choice.
      var inherited = (!seed && _chatLastQuoteConfig[hit.id]) || null;

      cfgUI = buildLineConfigUI(hit, preview, {
        garmentUnit: garmentUnit,
        printLocations: seed ? seed.printLocations : (inherited ? inherited.printLocations : null),
        colour: seed ? seed.colour : (inherited ? inherited.colour : null),
        sizeRun: seed ? seed.sizeRun : null,
        qty: seed ? seed.qty : ((inherited && inherited.quantity) ? inherited.quantity : QUOTE_QTY),
        artwork: seed ? seed.artwork : null,
        editing: !!seed,
        introHtml: krAriLineIntro(hit), // Ari-voiced header line so the drawer doesn't feel like a bare form
        onConfirm: function (cfg) {
          // Resolve the REAL mutation target NOW, never trust `seed` above.
          // EDIT intent always targets the exact line the caller identified
          // (still validated still-on-rail, by object identity — not just
          // "still has a line for this productId", which with 2 colourways
          // could be a DIFFERENT line than the one the customer clicked).
          // ADD intent only ever treats an EXACT (productId, colour) match
          // as "edit this line" — anything else is a brand-new colourway.
          // P1 #6 defect fix (2026-08): that exact match ONLY makes sense once
          // 2+ colourways genuinely exist to disambiguate between — with 0-1
          // colourways on the rail it's unambiguous, and an exact match would
          // wrongly miss a plain recolour (Blue -> Black on the SOLE line has
          // no "Black" line to exact-match yet), silently double-adding a
          // second line instead of recolouring the one that's there. See the
          // ≤1-vs-2+ rule at findLine's contract banner above.
          // P1 #6 defect #3 fix (2026-08): ADD intent's mutation target is
          // ALWAYS the line the drawer was seeded from (`seed`, captured
          // above at open time) — never a fresh findLine(hit.id, cfg.colour)
          // re-derivation. With 2+ colourways, re-deriving by the CONFIRMED
          // colour could resolve to a DIFFERENT existing line than the one
          // the drawer prefilled from (e.g. seeded from Black, customer
          // flips the picker to Navy) and silently clobber that other
          // line's qty/sizeRun/printLocations/artwork with the seed line's
          // data. `seed` IS findLine(hit.id) for the ≤1-colourway case, so
          // plain single-colourway recolour is unchanged.
          // #8 fix: liveness-check `seed` too (mirrors the editIntent indexOf guard). If the seeded
          // line was removed (e.g. the customer told Ari "remove that" via chat) while this drawer
          // was open, `seed` is a detached object no longer in railLines; fall through to a fresh
          // add rather than mutating an orphan (silent edit-loss + a false "Updated" toast).
          var target = editIntent
            ? (railLines.indexOf(targetLine) > -1 ? targetLine : null)
            : ((seed && railLines.indexOf(seed) > -1) ? seed : null);
          if (editIntent && !target) { closeConfigureDrawer(); return; } // defensive — this exact line was removed from the rail while the drawer was open

          if (target) {
            // Collision guard — now unified across BOTH intents. Changing
            // colour to one that collides with a DIFFERENT rail line must
            // never silently overwrite/orphan it. EDIT intent: customer
            // picked a colour that collides with an unrelated line. ADD
            // intent (P1 #6 #3 fix): `target` is the seed line, which can
            // diverge from cfg.colour once 2+ colourways exist — if that
            // confirmed colour belongs to a DIFFERENT existing line, block
            // instead of clobbering it. For a plain recolour to a colour
            // nobody else has, findLine(hit.id, cfg.colour) is either null
            // or resolves back to `target` itself, so this never blocks the
            // safe cases.
            var cfgCollision = findLine(hit.id, cfg.colour);
            if (cfgCollision && cfgCollision !== target) {
              toast('You already have a ' + (cfg.colour ? krTitleCase(cfg.colour) + ' ' : '') + 'line for this item — remove it first, or choose a different colour here.');
              return;
            }
            target.printLocations = cfg.printLocations;
            target.colour = cfg.colour;
            target.sizeRun = cfg.sizeRun;
            target.qty = cfg.qty;
            target.quote = cfg.quote;
            target.artwork = cfg.artwork;
            // Keep the line's preview in sync if the drawer hydrated real
            // colours/zones/images it didn't have before (fast-path lines
            // start with availableColours:[] and no back image).
            var hydratedPreview = cfgUI.getPreview();
            target.preview = {
              images: (hydratedPreview.images.front || hydratedPreview.images.back) ? hydratedPreview.images : target.preview.images,
              availableColours: hydratedPreview.availableColours.length ? hydratedPreview.availableColours : target.preview.availableColours
            };
            renderRail();
            railRev++; // FIX #12 — line updated (Configure drawer)
            scheduleAutosave();
            toast('Updated ' + (target.hit.title || 'your line') + '.');
          } else {
            handleAddToRail(hit, cfg.quote, cfgUI.getPreview(), card, cfg);
          }
          closeConfigureDrawer();
        }
      });

      // FINDING #2 (P1 #6 follow-up) — `line` carries the EXACT EDIT-intent
      // target (or null for ADD intent) so storeArtworkOnActiveLine below can
      // resolve identity-precisely instead of re-deriving via findLine(hit.id)'s
      // "any colourway" fallback.
      krOpenConfig = { hit: hit, ui: cfgUI, line: editIntent ? targetLine : null }; // Ari sees what they have open (buildQuoteContext) [drawer]
      cfgBodyEl.innerHTML = '';
      cfgBodyEl.appendChild(cfgUI.el);

      cfgBackdropEl.hidden = false;
      cfgPanelEl.hidden = false;
      cfgOpen = true;
      requestAnimationFrame(function () {
        cfgBackdropEl.classList.add('kr-show');
        cfgPanelEl.classList.add('kr-show');
      });
      try { cfgCloseBtn.focus(); } catch (e) {}

      krHydrateLineConfigUI(cfgUI, hit, preview, cfgUI.getConfig().colour);
    }
    _openConfigure = handleOpenConfigure;

    cfgCloseBtn.addEventListener('click', closeConfigureDrawer);
    cfgBackdropEl.addEventListener('click', closeConfigureDrawer);

    // ===========================================================================
    // "Email me this quote" (Save/Resume) — real saveKitQuote send, replacing
    // the old honest stub. First click with no known email opens a small
    // inline capture (never a separate modal); a known email (already
    // captured this session, or carried in from a resumed quote's customer)
    // sends straight away. On success: toast + surface the resume link
    // inline (never email-only) — and from then on customerEmailCaptured
    // flows into every subsequent autosave + bulkDeskLogTurn call.
    // ===========================================================================
    var emailSendInFlight = false;
    var EMAIL_RE = /^[^@\s]+@[^@\s]+\.[^@\s]+$/;

    function showResumeLinkInline() {
      if (!resumeToken || !resumeLinkEl) return;
      var url = RESUME_LINK_BASE + '?quote=' + encodeURIComponent(resumeToken);
      resumeLinkEl.innerHTML = 'Resume link: <a href="' + escapeHtml(url) + '" target="_blank" rel="noopener noreferrer">' + escapeHtml(url) + '</a>';
      resumeLinkEl.hidden = false;
    }

    function sendQuoteEmail(email, name) {
      if (!railLines.length || emailSendInFlight) return;
      emailSendInFlight = true;
      pdfBtn.disabled = true;
      pdfBtn.textContent = 'Building your PDF…';
      krBusy(true);
      // Money-moment fetch: a cold-starting CF on flaky mobile must never
      // hang this button forever. fetchJSON aborts + rejects past
      // FETCH_TIMEOUT_MS; the slow-notice timer (shorter) keeps the shopper
      // honestly informed *before* that happens rather than leaving a bare
      // "Building your PDF…" sitting there with no signal anything's wrong.
      var pdfSlowTimer = setTimeout(function () {
        pdfBtn.textContent = 'Still building — taking longer than usual…';
      }, KR_FETCH_SLOW_MS);
      // "Email me this quote" now sends the ABN'd GST tax-quote PDF
      // (customerQuotePdf makes a real Shopify draft + emails the PDF link).
      // The silent autosave (saveKitQuote, doAutosave) already persisted the
      // quote and captured quoteId + resumeToken, so we pass quoteId to link
      // the PDF to the saved quote and still surface the resume link inline.
      var payload = {
        lines: railLinesPayload(),
        customer: { email: email, name: name || undefined }
      };
      if (quoteId) payload.quoteId = quoteId;
      fetchJSON(customerQuotePdfUrl(), {
        method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(payload),
        timeoutMs: KR_FETCH_TIMEOUT_MS
      }).then(function (data) {
        customerEmailCaptured = email; // flows into every later autosave + bulkDeskLogTurn call
        toast(data && data.ok && data.emailSent
          ? ('Your PDF tax quote is on its way to ' + email + ' — check your inbox.')
          : 'Couldn’t email the PDF just now — your quote is saved; try again shortly.');
        showResumeLinkInline(); // from the autosaved resumeToken, if we have one
      }).catch(function (err) {
        // Retry affordance: the final `.then` below re-enables pdfBtn with
        // its default label, so "try again" IS just clicking it again.
        var reason = (err && err.message) || 'try again shortly.';
        toast('Couldn’t send that just now — ' + reason);
      }).then(function () {
        clearTimeout(pdfSlowTimer);
        emailSendInFlight = false;
        krBusy(false);
        pdfBtn.disabled = !railLines.length;
        pdfBtn.textContent = PDF_BTN_DEFAULT_TEXT;
      });
    }

    function cancelEmailCapture() {
      emailCaptureEl.hidden = true;
      pdfBtn.hidden = false;
    }
    function submitEmailCapture() {
      var val = (emailInputEl.value || '').trim();
      if (!EMAIL_RE.test(val)) { toast('Enter a valid email to send your quote.'); return; }
      customerEmailCaptured = val; // flows into every later autosave + bulkDeskLogTurn call from here on
      cancelEmailCapture();
      sendQuoteEmail(val, customerNameCaptured);
    }
    emailSendBtn.addEventListener('click', submitEmailCapture);
    emailInputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); submitEmailCapture(); }
      else if (e.key === 'Escape') { e.preventDefault(); cancelEmailCapture(); }
    });

    pdfBtn.addEventListener('click', function () {
      if (!railLines.length) return; // defensive — button is disabled empty anyway
      if (customerEmailCaptured) { sendQuoteEmail(customerEmailCaptured, customerNameCaptured); return; }
      emailCaptureEl.hidden = false;
      pdfBtn.hidden = true;
      try { emailInputEl.focus(); } catch (e) {}
    });

    // ===========================================================================
    // Checkout (Slice 4) — "Start your order →" posts the rail to createKitDraft
    // (region australia-southeast2), which re-prices every line server-side
    // (this button never sends a price, only {productId, quantity,
    // printLocations}) and returns a real Shopify draft invoiceUrl. Kit Room
    // doesn't collect a colour yet, so `colour` is simply omitted per line —
    // the CF treats it as optional. On success we hand off to Shopify
    // checkout; on failure the rail is untouched and the shopper can retry.
    // ===========================================================================
    checkoutBtn.addEventListener('click', function () {
      if (checkoutInFlight || !railLines.length) return;
      // Defensive — checkoutBtn.disabled already gates this in renderRail()
      // whenever any line has no print placement (the amber "Needs
      // placement" chip), but never trust only the DOM attribute for a
      // real-money action.
      if (anyLineNeedsPlacement()) {
        toast('Pick a print placement for every style before checkout.');
        return;
      }
      if (!PB.kitDraftUrl) {
        // Defensive — the section hasn't wired the endpoint yet. Never send a
        // request into the void; say so plainly instead of hanging/failing silently.
        toast('Checkout isn’t connected yet — try again shortly.');
        return;
      }
      checkoutInFlight = true;
      checkoutBtn.disabled = true;
      pdfBtn.disabled = true;
      checkoutBtn.classList.add('kr-busy');
      checkoutBtn.textContent = 'Creating your order…';
      if (checkoutNoteEl) checkoutNoteEl.textContent = 'Talking to Shopify — this takes a few seconds.';
      krBusy(true); // Ari orb "thinking" while we talk to Shopify

      // Real-money moment: a cold-starting CF on flaky mobile must never
      // leave "Creating your order…" hanging forever. fetchJSON aborts +
      // rejects past KR_FETCH_TIMEOUT_MS (below); this shorter, non-fatal
      // notice keeps the note honest before that happens.
      var checkoutSlowTimer = setTimeout(function () {
        if (checkoutNoteEl) checkoutNoteEl.textContent = 'Still talking to Shopify — taking longer than usual, still trying…';
      }, KR_FETCH_SLOW_MS);

      // createKitDraft's contract (index.ts ~75050): {productId,quantity,
      // printLocations,colour?,sizes?,artwork?} — `sizes` (not `sizeRun`) is
      // the SOLE source of truth for that line's quantity when given, so
      // it's sent ONLY when the drawer actually captured a size run.
      // `artwork` (item 4 — {side: {printReadyUrl,uploadedUrl,previewUrl}},
      // keyed by zone) rides along so the order the customer just paid for
      // keeps every file Ari already saw, on the right side. Backend accepts
      // this keyed shape OR the legacy flat one.
      var payload = {
        lines: railLines.map(function (l) {
          var p = {
            productId: l.hit.id, quantity: l.qty,
            printLocations: (l.printLocations && l.printLocations.length) ? l.printLocations : QUOTE_PRINT_LOCATIONS
          };
          if (l.colour) p.colour = l.colour;
          if (l.sizeRun && Object.keys(l.sizeRun).length) p.sizes = l.sizeRun;
          if (l.artwork) p.artwork = l.artwork;
          return p;
        })
      };

      fetchJSON(PB.kitDraftUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
        timeoutMs: KR_FETCH_TIMEOUT_MS
      }).then(function (data) {
        clearTimeout(checkoutSlowTimer);
        if (!data.invoiceUrl) throw new Error('No checkout link returned.');
        checkoutNoteEl && (checkoutNoteEl.textContent = 'Redirecting you to checkout…');
        window.location = data.invoiceUrl;
        // Deliberately NOT resetting checkoutInFlight/button state here — the
        // page is navigating away; leaving the button disabled avoids a
        // double-submit flash while that happens.
      }).catch(function (err) {
        clearTimeout(checkoutSlowTimer);
        krBusy(false);
        checkoutInFlight = false;
        checkoutBtn.classList.remove('kr-busy');
        checkoutBtn.textContent = 'Start your order →';
        // Retry affordance: the button above is re-enabled right here, so
        // "try again" is simply clicking Start your order once more.
        checkoutBtn.disabled = !railLines.length;
        pdfBtn.disabled = !railLines.length;
        if (checkoutNoteEl) checkoutNoteEl.textContent = CHECKOUT_NOTE_DEFAULT;
        toast('Couldn’t start your order — ' + (err && err.message ? err.message : 'please try again.'));
      });
    });

    // Mobile bottom-sheet expand/collapse. Harmless on desktop — the rail
    // body is always visible there regardless of this class/attribute.
    var railToggleBtn = qs('#kr-rail-toggle', root);
    railToggleBtn.addEventListener('click', function () {
      cancelRailAutoCollapse(); // a manual toggle always wins over the first-add auto-expand/collapse
      var expanded = railToggleBtn.getAttribute('aria-expanded') === 'true';
      railToggleBtn.setAttribute('aria-expanded', String(!expanded));
      railEl.classList.toggle('kr-expanded', !expanded);
    });

    // ===========================================================================
    // Compare (Slice 3) — state lives on THIS overlay instance, reset per
    // open, and is ALSO cleared on every category/Popular switch (its card
    // DOM references belong to whichever grid is currently rendered).
    // ===========================================================================
    var compareSelection = []; // [{hit, quote, preview, card, btn}], max 3
    var compareBarEl = null;
    var gridWrapEl = qs('#kr-grid-wrap', root);
    var compareViewEl = qs('#kr-compare-view', root);
    // FIX 2 — which surface compare was opened FROM ('browse' | 'chat'),
    // captured by showCompareView() before it touches anything. backToGrid()
    // returns the shopper there instead of always landing on Browse — see
    // showCompareView()/backToGrid() below.
    var compareOrigin = 'browse';

    function findCompareIndex(productId) {
      for (var i = 0; i < compareSelection.length; i++) { if (compareSelection[i].hit.id === productId) return i; }
      return -1;
    }

    function renderCompareBar() {
      if (compareSelection.length < 2) {
        if (compareBarEl) compareBarEl.hidden = true;
        return;
      }
      if (!compareBarEl) {
        compareBarEl = el('div', 'kr-cmpbar',
          '<span class="kr-cmpbar-txt" aria-live="polite"></span>' +
          '<button type="button" class="kr-cmpbar-view">View comparison &rarr;</button>' +
          '<button type="button" class="kr-cmpbar-clear" aria-label="Clear comparison">' + CLOSE_SVG + '</button>');
        qs('.kr-canvas-col', root).appendChild(compareBarEl);
        qs('.kr-cmpbar-view', compareBarEl).addEventListener('click', showCompareView);
        qs('.kr-cmpbar-clear', compareBarEl).addEventListener('click', clearCompare);
      }
      compareBarEl.hidden = false;
      qs('.kr-cmpbar-txt', compareBarEl).textContent =
        'Comparing ' + compareSelection.length + ' product' + (compareSelection.length === 1 ? '' : 's');
    }

    function handleToggleCompare(hit, quote, preview, card, btn) {
      var idx = findCompareIndex(hit.id);
      if (idx !== -1) {
        compareSelection.splice(idx, 1);
        btn.classList.remove('kr-cmp-on');
        btn.setAttribute('aria-pressed', 'false');
      } else {
        if (compareSelection.length >= 3) { toast('Compare up to 3 at a time — remove one first.'); return; }
        compareSelection.push({ hit: hit, quote: quote, preview: preview, card: card, btn: btn });
        btn.classList.add('kr-cmp-on');
        btn.setAttribute('aria-pressed', 'true');
      }
      renderCompareBar();
    }
    _toggleCompare = handleToggleCompare;

    function clearCompare() {
      compareSelection.forEach(function (s) {
        s.btn.classList.remove('kr-cmp-on');
        s.btn.setAttribute('aria-pressed', 'false');
      });
      compareSelection = [];
      renderCompareBar();
      if (!compareViewEl.hidden) backToGrid();
    }

    // FIX 2 — neutralize the Chat|Browse segmented toggle while compare is
    // open (it lives in the persistent topbar, outside the canvas-col the
    // overlay covers, so it stays clickable otherwise — see .kr-seg-disabled
    // CSS). `.disabled` on each button also blocks keyboard activation,
    // which pointer-events:none alone would not.
    function setSegToggleDisabled(disabled) {
      var segEl = qs('#kr-seg', root);
      if (!segEl) return;
      segEl.classList.toggle('kr-seg-disabled', disabled);
      Array.prototype.forEach.call(segEl.querySelectorAll('button'), function (b) { b.disabled = disabled; });
    }

    function backToGrid() {
      compareViewEl.hidden = true;
      gridWrapEl.hidden = false;
      setSegToggleDisabled(false);
      // FIX 2 — "Back" returns to the ORIGIN surface (wherever compare was
      // opened from), not unconditionally to Browse: switchToChat()/
      // switchToBrowse() restore the correct #kr-cats/#kr-canvas/#kr-chat
      // hidden-state, and syncSegClasses matches the topbar toggle to it —
      // opening compare from Chat and hitting Back lands back in the SAME
      // conversation (switchToChat() only ever seeds the thread once, via
      // its own chatSeeded guard), never Browse.
      if (compareOrigin === 'chat') { switchToChat(); syncSegClasses('describe'); }
      else { switchToBrowse(); syncSegClasses('browse'); }
      // COMPARE polish: restore the sticky bar (still hidden if the
      // selection dropped below 2, e.g. clearCompare() got here first).
      renderCompareBar();
    }

    function showCompareView() {
      // FIX 2 — the compare view (#kr-compare-view) is now a direct child of
      // .kr-canvas-col (see shellHTML), positioned as a full-canvas overlay
      // (position:absolute;inset:0 — see CSS) that sits ABOVE whichever of
      // #kr-cats+#kr-canvas (Browse) or #kr-chat (Chat) is currently active.
      // Unlike the old implementation, this NEVER calls switchToBrowse() —
      // doing that used to force Browse active and reveal the category
      // pills even when compare was opened from a live Chat thread, which
      // silently dropped the shopper out of the conversation the moment
      // they clicked a pill. Capture which surface we're covering (for
      // backToGrid() to restore) and leave the underlying mode untouched.
      compareOrigin = chatEl.hidden ? 'browse' : 'chat';
      setSegToggleDisabled(true);
      // A11y: the overlay visually covers #kr-cats/#kr-canvas/#kr-chat
      // either way (it's position:absolute;inset:0 over the whole
      // .kr-canvas-col), but without this a keyboard/screen-reader user
      // could still tab into the category chips, grid cards, or chat
      // input sitting invisibly underneath it. `hidden` here is purely a
      // focus/AT concern — it does NOT touch chat's seeded thread state,
      // and backToGrid()'s switchToChat()/switchToBrowse() call is what
      // un-hides the correct one(s) again.
      catsEl.hidden = true;
      canvasEl.hidden = true;
      chatEl.hidden = true;
      compareViewEl.innerHTML = compareHTML(compareSelection, compareOrigin);
      qs('.kr-cmp-back', compareViewEl).addEventListener('click', backToGrid);
      Array.prototype.forEach.call(compareViewEl.querySelectorAll('.kr-ctab-add'), function (btn) {
        btn.addEventListener('click', function () {
          var idx = Number(btn.getAttribute('data-idx'));
          var s = compareSelection[idx];
          if (!s) return;
          handleAddToRail(s.hit, s.quote, s.preview, s.card);
          btn.textContent = '✓ Added';
        });
      });
      // FIX 1 — lazy-hydrate real colour hex + size availability now that
      // the table is in the DOM (both no-op gracefully on failure/no data —
      // see hydrateCompareSwatches/hydrateCompareSizes above compareHTML).
      hydrateCompareSwatches(compareSelection, compareViewEl);
      hydrateCompareSizes(compareSelection, compareViewEl);
      gridWrapEl.hidden = true;
      compareViewEl.hidden = false;
      // COMPARE polish: hide the sticky "Comparing N / View comparison" bar
      // while the comparison itself is already open — it was floating
      // redundantly over the comparison view before this fix. backToGrid()
      // brings it back.
      if (compareBarEl) compareBarEl.hidden = true;
    }

    // ===========================================================================
    // Chat ("Describe what you need") — a real Ari conversation over the
    // agentChat CF (production-dashboard's agent-chat.ts: search_products /
    // get_quote / get_preview_config / etc. tool loop). State lives on THIS
    // overlay instance only, reset per open:
    //   chatConvo         — [{role,content}] sent verbatim to agentChat each
    //                        turn, so multi-turn context accumulates exactly
    //                        as the CF expects.
    //   chatKnownHits      — id -> a SearchHit-shaped (or SearchHit-compatible)
    //                        object, harvested from search_products /
    //                        get_preview_config trace outputs. This is the
    //                        ONLY source of a product's `handle` — get_quote's
    //                        output never carries one (quote-server.ts's
    //                        QuoteResponse.product is {id,title,styleCode,
    //                        vendor} only) — so a get_quote for a product this
    //                        overlay has never seen searched/previewed simply
    //                        can't render a card. That's by design: never
    //                        fabricate a handle to force a card into being.
    //   chatShownProductIds — dedup set so the same product never gets a
    //                        second card lower in the thread.
    // Cards reuse the EXACT same pipeline as Browse (buildSkeletonCard +
    // loadCardFast, which itself falls back to hydrateCard()) at the SAME
    // QUOTE_QTY, so a Chat card's price can never drift from its Browse
    // twin — and "Add to order" reuses handleAddToRail via the same
    // addToRail() module indirection every other card already goes through,
    // so it lands in the SAME rail.
    // ===========================================================================
    var chatConvo = [];
    // Vision — "Ari genuinely sees the artwork". Set the moment a chat
    // upload succeeds, cleared the instant that turn is actually sent (so a
    // normal follow-up message goes back to byte-identical plain-string
    // `content` — only the ONE turn carrying a fresh attachment ever uses
    // the content-array shape). {uploadedUrl,previewUrl,printReadyUrl,name}.
    var pendingArtwork = null;
    // The last artwork Ari has actually been SHOWN via chat (set right
    // after a successful vision turn, by storeArtworkOnActiveLine below) —
    // folded into buildQuoteContext() so Ari can call check_artwork
    // (imageUrl + productId) on it even on a later turn, and so a second
    // attachment naturally supersedes the first. {uploadedUrl,productTitle}.
    var chatArtworkContext = null;
    var chatKnownHits = {};
    var chatShownProductIds = {};
    // Dedup for Ari's request_details form cards — same idiom as
    // chatShownProductIds: marked the moment a form is rendered (not only
    // once answered), so a repeated request_details call with the same
    // field spec never renders a second card lower in the thread.
    var chatShownFormIds = {};
    // Dedup for Ari's update_quote rail mutations — same idiom as
    // chatShownProductIds/chatShownFormIds: a trace can be re-sent/re-rendered
    // on a retry, so each individual update_quote call (action+input, keyed by
    // its JSON) is only ever APPLIED to the rail once, no matter how many times
    // applyUpdateQuoteActions() sees it across re-renders of the same turn.
    var chatAppliedQuoteActions = {};
    var FORM_UID_SEQ = 0;
    var chatSeeded = false;
    var chatBusy = false;
    // bulkDeskLogTurn's conversationId — returned on its first call, cached
    // here and sent on every subsequent call so a staff-visible thread stays
    // one conversation rather than a new row per turn. null until then (the
    // CF's contract: first call has no conversationId, returns one).
    var chatConversationId = null;
    // ---- Human-handoff (bulkDeskPoll) state — all inert until a real
    // chatConversationId exists (see startBulkDeskPoll(), called the moment
    // logChatTurn's response first learns one). Nothing here fires for a
    // customer who never sends a chat message.
    var chatPollTimerId = null;
    var chatPollInFlight = false;
    var chatPollSinceCursor = 0; // mirrors bulkDeskPoll's own `since` cursor contract
    var chatPollSeenKeys = {}; // belt-and-braces de-dupe on top of the since-cursor
    var chatHumanTakeover = false;
    var chatStaffTypingEl = null;
    var catsEl = qs('#kr-cats', root);
    var canvasEl = qs('#kr-canvas', root);
    var chatEl = qs('#kr-chat', root);
    var threadEl = qs('#kr-chat-thread', root);
    var chatInputEl = qs('#kr-chat-input', root);
    var chatSendBtn = qs('#kr-chat-send', root);
    var chatAttachBtn = qs('#kr-chat-attach', root);
    var chatFileEl = qs('#kr-chat-file', root);
    var chatPendingEl = qs('#kr-chat-pending', root);
    var chatPendingThumbEl = qs('#kr-chat-pending-thumb', root);
    var chatPendingNameEl = qs('#kr-chat-pending-name', root);
    var chatPendingXEl = qs('#kr-chat-pending-x', root);

    function scrollThreadToBottom() { threadEl.scrollTop = threadEl.scrollHeight; }

    function appendAriBubble(text, isError) {
      var b = el('div', 'kr-cbub kr-cbub-ari' + (isError ? ' kr-cbub-err' : ''));
      b.innerHTML = '<div class="kr-cbub-txt">' + krFormatChatText(text) + '</div>';
      threadEl.appendChild(b);
      scrollThreadToBottom();
      return b;
    }
    // `sync` (item 2 — soften the synthetic sync-turn bubble): the
    // request_details/config-card confirm flows already show a "✓ Added…"
    // done-card right above this bubble, so a full-weight dark .kr-cbub-me
    // repeating the exact same sentence read as a visual duplicate. Stays a
    // REAL .kr-cbub-me element either way (kr-chat-compare-test's
    // `.kr-cbub-me .kr-cbub-txt` check — and Ari's own context — both still
    // see it as a genuine user turn); only the extra kr-cbub-sync class
    // changes, which the CSS above dials back visually.
    function appendUserBubble(text, sync) {
      threadEl.appendChild(el('div', 'kr-cbub kr-cbub-me' + (sync ? ' kr-cbub-sync' : ''), '<div class="kr-cbub-txt">' + escapeHtml(text) + '</div>'));
      scrollThreadToBottom();
    }
    // The vision turn's user bubble — the attached image + (if the customer
    // typed anything alongside it) their note underneath, in ONE bubble
    // (.kr-cbub-artwork, styled above) rather than two separate bubbles.
    // Uses previewUrl (the downscaled vision copy) — same image Ari is
    // actually being shown this turn, so the bubble never shows something
    // different from what the model saw.
    function appendArtworkBubble(previewUrl, name, note) {
      var b = el('div', 'kr-cbub kr-cbub-me kr-cbub-artwork');
      b.innerHTML = '<div class="kr-cbub-txt"><img src="' + escapeHtml(previewUrl) + '" alt="' + escapeHtml(name || 'Attached artwork') + '">' +
        (note ? ('<div class="kr-cbub-artwork-note">' + escapeHtml(note) + '</div>') : '') + '</div>';
      threadEl.appendChild(b);
      scrollThreadToBottom();
    }
    // Thinking indicator — three dots, no bubble chrome (deliberately not
    // wrapped in .kr-cbub-txt, so it reads as a lightweight "..." rather than
    // a second bubble box). Static (no blink class) under KR_RM per the
    // reduced-motion contract — same rule .kr-typing's CSS already encodes.
    function appendThinkingBubble() {
      var blink = KR_RM ? '' : ' kr-typing-blink';
      var dot = '<i class="' + blink.trim() + '"></i>';
      var b = el('div', 'kr-cbub kr-cbub-ari kr-cbub-thinking', '<div class="kr-typing">' + dot + dot + dot + '</div>');
      threadEl.appendChild(b);
      scrollThreadToBottom();
      return b;
    }

    // ===========================================================================
    // Human handoff — live poll of bulkDeskPoll (production-dashboard
    // index.ts ~76206) for a staff takeover on THIS conversation. Additive:
    // entirely inert (no timer ever starts) until chatConversationId exists,
    // which itself only happens after a real logChatTurn round-trip — so a
    // customer who never sends a chat message, or whose bulkDeskLogTurn call
    // was skipped (greeting-only), never triggers a single poll request.
    // conversationId is the only auth bulkDeskPoll accepts — never enumerate,
    // never guess one.
    // ===========================================================================
    function appendStaffBubble(text, staffName) {
      var b = el('div', 'kr-cbub kr-cbub-staff');
      b.innerHTML = '<div class="kr-cbub-sender">' + escapeHtml(staffName || 'Printibly specialist') + '</div>' +
        '<div class="kr-cbub-txt">' + krFormatChatText(text) + '</div>';
      threadEl.appendChild(b);
      scrollThreadToBottom();
    }
    // System note for the takeover/handback moment — a quiet centered pill,
    // never a chat bubble (nobody "said" this). `kr-cbub` base class buys it
    // the same reduced-motion override every other bubble type already gets.
    function appendSystemNote(text) {
      threadEl.appendChild(el('div', 'kr-cbub kr-cbub-system', '<span class="kr-cbub-system-txt">' + escapeHtml(text) + '</span>'));
      scrollThreadToBottom();
    }
    // ---------- FIX #16 — bad/expired/deleted resume-quote notice ----------
    // getKitQuote can 200 with {ok:false} (deleted/expired quote), 404, or
    // just fail on the network. fetchJSON already turns {ok:false} into a
    // rejection (see its `data.ok === false` check), so ALL of these land in
    // the resume fetch's own .catch() below — previously an empty
    // .catch(function(){}) that swallowed it completely, leaving the shopper
    // in what reads as a brand-new-visitor chat with zero explanation
    // (bulk-landing.liquid's ?quote= auto-open forces mode:'describe'
    // straight past the entry screen, so there's no other surface that could
    // have told them). Shown on whichever surface is actually on screen the
    // moment the resume fetch fails — the entry screen (a bare resume with
    // no forced mode) or the chat thread (bulk-landing's forced describe
    // mode already entered the room by the time this fires).
    function showResumeFailedNotice() {
      var msg = 'We couldn’t find that quote — it may have expired or been removed.';
      var help = 'Start a fresh quote below, or email <a href="mailto:hello@printibly.com.au">hello@printibly.com.au</a> and we’ll help you track it down.';
      var entryEl = qs('#kr-entry', root);
      if (entryEl && !entryEl.hidden) {
        if (qs('#kr-resume-fail', root)) return; // never stack a second notice (e.g. a retry)
        var inner = qs('.kr-entry-inner', root);
        var sub = qs('.kr-entry-sub', root);
        if (!inner) return;
        var notice = el('div', 'kr-resume-fail', '<b>' + escapeHtml(msg) + '</b><span>' + help + '</span>');
        notice.id = 'kr-resume-fail';
        notice.setAttribute('role', 'alert');
        notice.setAttribute('tabindex', '-1');
        if (sub && sub.parentNode) sub.parentNode.insertBefore(notice, sub.nextSibling);
        else inner.insertBefore(notice, inner.firstChild);
        try { notice.focus(); } catch (e) {} // accessible focus — an alert nobody's told about isn't accessible
        return;
      }
      // Already past the entry screen — thread's own aria-live="polite"
      // (see shellHTML) announces this to screen readers; deliberately not
      // stealing focus out from under a shopper who may already be typing.
      if (threadEl && !qs('#kr-resume-fail-bub', root)) {
        var b = el('div', 'kr-cbub kr-cbub-ari kr-cbub-err', '<div class="kr-cbub-txt">' + escapeHtml(msg) + ' ' + help + '</div>');
        b.id = 'kr-resume-fail-bub';
        threadEl.appendChild(b);
        scrollThreadToBottom();
      }
    }
    // Staff-typing preview — same shell as appendThinkingBubble but held in
    // chatStaffTypingEl so repeated polls update/remove ONE element instead
    // of stacking a new indicator every ~4s.
    function showStaffTyping(name) {
      var label = escapeHtml(name || 'Printibly specialist') + ' is typing…';
      if (chatStaffTypingEl) {
        var lbl = chatStaffTypingEl.querySelector('.kr-typing-label');
        if (lbl) lbl.innerHTML = label;
        return;
      }
      var blink = KR_RM ? '' : ' kr-typing-blink';
      var dot = '<i class="' + blink.trim() + '"></i>';
      chatStaffTypingEl = el('div', 'kr-cbub kr-cbub-staff kr-cbub-thinking',
        '<div class="kr-typing"><span class="kr-typing-label">' + label + '</span>' +
        '<span class="kr-typing-dots">' + dot + dot + dot + '</span></div>');
      threadEl.appendChild(chatStaffTypingEl);
      scrollThreadToBottom();
    }
    function hideStaffTyping() {
      if (chatStaffTypingEl && chatStaffTypingEl.parentNode) chatStaffTypingEl.parentNode.removeChild(chatStaffTypingEl);
      chatStaffTypingEl = null;
    }

    // ~4s while this browser tab is focused/visible, backing off to ~60s
    // while backgrounded — mirrors Connect's open/idle cadence split
    // (ConnectChatWidget.tsx ~322, `open ? POLL_INTERVAL_MS : 60_000`)
    // adapted to Kit Room's single always-open overlay by keying off real
    // tab visibility instead (there's no separate "widget minimized" state
    // here — the room is either open or torn down).
    function bulkPollDelayMs() {
      try { return document.hidden ? 60000 : 4000; } catch (e) { return 4000; }
    }
    function scheduleBulkPoll(delayMs) {
      if (!isOpen || !chatConversationId) return;
      clearTimeout(chatPollTimerId);
      chatPollTimerId = setTimeout(pollBulkDesk, delayMs);
    }
    // Idempotent — safe to call from every place chatConversationId might
    // first become non-null (the normal reply path AND the handed-off
    // persist-only path in sendChatMessage).
    function startBulkDeskPoll() {
      if (chatPollTimerId || !isOpen || !chatConversationId) return;
      scheduleBulkPoll(1000); // small stagger after the logChatTurn call that just learned the id
    }
    function stopBulkDeskPoll() {
      clearTimeout(chatPollTimerId);
      chatPollTimerId = null;
    }
    // Tab regains focus while a longer 60s backoff is pending — don't make
    // the customer wait up to a minute to see a reply that's been sitting
    // there. Registered once below (chatSendBtn wiring) and torn down in
    // teardown(); a no-op whenever there's nothing to poll yet.
    function onBulkPollVisibilityChange() {
      if (document.hidden || !isOpen || !chatConversationId) return;
      scheduleBulkPoll(300);
    }
    function pollBulkDesk() {
      chatPollTimerId = null;
      if (!isOpen || !chatConversationId || chatPollInFlight) { scheduleBulkPoll(bulkPollDelayMs()); return; }
      chatPollInFlight = true;
      var url = bulkPollUrl() + '?conversationId=' + encodeURIComponent(chatConversationId) +
        (chatPollSinceCursor > 0 ? '&since=' + chatPollSinceCursor : '');
      fetchJSON(url).then(function (data) {
        if (isOpen) handleBulkDeskPoll(data);
      }).catch(function () {
        // A poll error must never break the chat — silently retry on the
        // next tick (same failure-invisible contract as logChatTurn).
      }).then(function () {
        chatPollInFlight = false;
        scheduleBulkPoll(bulkPollDelayMs());
      });
    }
    // bulkDeskPoll's response shape: {ok,conversationId,messages,needsHuman,
    // aiHandedOff,humanTakeover,staffTyping,staffTypingName} — messages are
    // {role,bodyText,createdAt,attachments}, no `id` (unlike Connect's
    // chat/poll). De-dupe therefore keys on role+createdAt+text rather than
    // an id, on top of the since-cursor which already prevents re-fetching
    // anything at/under the last seen createdAt.
    function handleBulkDeskPoll(data) {
      if (!data) return;
      var msgs = Array.isArray(data.messages) ? data.messages : [];
      var maxTs = chatPollSinceCursor;
      msgs.forEach(function (m) {
        if (typeof m.createdAt === 'number' && m.createdAt > maxTs) maxTs = m.createdAt;
        var key = (m.role || '') + '|' + (m.createdAt || 0) + '|' + String(m.bodyText || '').slice(0, 200);
        if (chatPollSeenKeys[key]) return;
        chatPollSeenKeys[key] = true;
        // Only role:"agent" (a staff reply — legacy/inbox's own outbound
        // convention, index.ts ~11868) renders here. role:"customer"/"ai"
        // are echoes of turns THIS tab already rendered locally the moment
        // they were sent/received — re-rendering them from the poll would
        // double the thread. role:"note" never reaches the client at all
        // (bulkDeskPoll filters it server-side).
        if (m.role === 'agent') {
          appendStaffBubble(m.bodyText, data.staffTypingName);
          // Fold the staff's turn into Ari's own context so a handback
          // resumes with full awareness of what was said while paused,
          // instead of picking up mid-conversation blind.
          chatConvo.push({ role: 'assistant', content: m.bodyText });
        }
      });
      if (maxTs > chatPollSinceCursor) chatPollSinceCursor = maxTs;

      var takeoverNow = data.humanTakeover === true;
      if (takeoverNow && !chatHumanTakeover) {
        chatHumanTakeover = true;
        appendSystemNote('A Printibly specialist has joined — Ari’s stepped back.');
      } else if (!takeoverNow && chatHumanTakeover) {
        chatHumanTakeover = false;
        hideStaffTyping();
        appendSystemNote('Ari’s back with you.');
      }
      if (chatHumanTakeover && data.staffTyping === true) {
        showStaffTyping(data.staffTypingName);
      } else {
        hideStaffTyping();
      }
    }

    // Render a set of already-resolved hits as real Bulk Desk cards inline in
    // the thread — identical skeleton -> loadCardFast pipeline Browse uses.
    function renderChatCards(hits) {
      if (!hits.length) return;
      var wrap = el('div', 'kr-chat-cards');
      threadEl.appendChild(wrap);
      scrollThreadToBottom();
      getPrintPrices().then(function () {
        hits.forEach(function (hit, i) {
          var card = buildSkeletonCard(i);
          wrap.appendChild(card);
          loadCardFast(card, hit, QUOTE_QTY, 'chat');
        });
        scrollThreadToBottom();
      });
    }

    // First open of Chat: Ari's greeting + the Popular picks, inline. Reuses
    // loadPopularCard() (resolvePopularHit + client-compute) verbatim for the
    // actual card render/skeleton — resolvePopularHit() is called a second
    // time alongside it purely to learn each pick's id (for dedup bookkeeping
    // below), which costs zero extra network calls (cached per code).
    function seedChatThread() {
      if (chatSeeded) return;
      chatSeeded = true;
      appendAriBubble(ARI_GREETING);
      var wrap = el('div', 'kr-chat-cards');
      threadEl.appendChild(wrap);
      POPULAR_STYLE_CODES.forEach(function (code, i) {
        var card = buildSkeletonCard(i);
        wrap.appendChild(card);
        loadPopularCard(card, code, 'chat');
        resolvePopularHit(code).then(function (hit) {
          if (hit) { chatKnownHits[hit.id] = hit; chatShownProductIds[hit.id] = true; }
        });
      });
      scrollThreadToBottom();
    }

    // ===========================================================================
    // Shell B (Chat) — the in-chat Ari config card. "Add to Quote" (or the
    // card click) on an Ari-recommended chat card does NOT silently add —
    // it renders a DETERMINISTIC (zero-LLM, no agentChat call, instant, no
    // cost) Ari bubble + the SAME buildLineConfigUI() engine inlined into
    // the thread, using the existing .kr-form-card visual language
    // (renderRequestDetailsCards/buildFormCard). On Confirm: updates the
    // rail exactly like Shell A (the drawer — see handleOpenConfigure
    // above), AND appends a synthetic user turn summarizing the line
    // (mirrors buildFormCard's own request_details submit) so Ari's server
    // context stays in sync and can comment on it on the next real turn —
    // same real pipeline (ack + cards + staff logging) request_details
    // already uses, just triggered by a click instead of a tool call.
    // ===========================================================================
    // openChatConfigCard is ALWAYS ADD intent — chat has no "reopen this
    // exact rail line" entry point (no Needs-placement chip host here); the
    // only way in is a product card click, same as Browse. See FINDING #1
    // (P1 #6 follow-up) / handleOpenConfigure's banner above for the shared
    // reasoning.
    function openChatConfigCard(hit, quote, preview, card) {
      var seed = findLine(hit.id); // pre-fill only — NEVER the confirm-time mutation target, see below
      var garmentUnit = quote ? quote.garmentUnitPrice : (seed && seed.quote ? seed.quote.garmentUnitPrice : 0);
      if (!(garmentUnit > 0)) { toast('Ask Ari for a price on this one first.'); return; }

      // Item 2 — same inheritance as handleOpenConfigure (Shell A): a fresh
      // product Ari already get_quote'd this session seeds from that real
      // config instead of the bare defaults. `seed` still wins when this
      // product is already a rail line.
      var inherited = (!seed && _chatLastQuoteConfig[hit.id]) || null;
      // Feature 1 (2026-08 pre-build) — ONLY a fresh (never-yet-configured)
      // line whose inherited get_quote was COMPLETE (colour + placements +
      // qty all real — see renderTraceCards) gets the confirm framing. An
      // already-existing rail line (editing) or a partial/exploratory
      // inherited config falls straight through to the normal ask, unchanged.
      var preBuilt = !!(inherited && inherited.configComplete);

      appendAriBubble(preBuilt ? krAriLineConfirmIntro(hit, inherited) : krAriLineIntro(hit)); // deterministic — zero-LLM, never a real agentChat call to OPEN the card

      var wrap = el('div', 'kr-form-card kr-lcu-card');
      threadEl.appendChild(wrap);
      scrollThreadToBottom();

      var ui = buildLineConfigUI(hit, preview, {
        garmentUnit: garmentUnit,
        printLocations: seed ? seed.printLocations : (inherited ? inherited.printLocations : null),
        colour: seed ? seed.colour : (inherited ? inherited.colour : null),
        sizeRun: seed ? seed.sizeRun : null,
        qty: seed ? seed.qty : ((inherited && inherited.quantity) ? inherited.quantity : QUOTE_QTY),
        artwork: seed ? seed.artwork : null,
        editing: !!seed,
        // Feature 1 (2026-08 pre-build) — swaps the confirm button's label
        // when the card opened already fully configured; every other case
        // (drawer, editing, partial inherited config) keeps buildLineConfigUI's
        // own default ('Update line' / 'Add to Quote') by passing nothing.
        confirmLabel: preBuilt ? '✓ Looks right — Add to Quote' : null,
        // item 1 (2026-08 unify) — no more layout:'horizontal' special-case;
        // buildLineConfigUI's root now always renders the shared 3-column
        // grid (.kr-lcu-grid) — the chat-card CSS override (see .kr-lcu-card
        // rules) is what keeps this host's own scroll/width behaviour.
        onConfirm: function (cfg) {
          var summaryText;
          var configBits = zoneGroupLabel(cfg.printLocations);
          if (cfg.colour) configBits += ', ' + krTitleCase(cfg.colour);
          var sr = sizeRunSummary(cfg.sizeRun);
          if (sr) configBits += ', sizes ' + sr;

          // P1 #6 defect #3 fix (2026-08) — the mutation target is ALWAYS
          // `seed` (captured above at open time, the line this card was
          // prefilled from), never a fresh findLine(hit.id, cfg.colour)
          // re-derivation. With 2+ colourways, re-deriving by the CONFIRMED
          // colour could resolve to a DIFFERENT existing line than the one
          // this card was seeded from (e.g. seeded from Black, customer
          // flips the picker to Navy) and silently clobber that other
          // line's qty/sizeRun/printLocations/artwork with the seed line's
          // data — same fix as handleOpenConfigure's onConfirm. `seed` IS
          // findLine(hit.id) for the ≤1-colourway case, so plain
          // single-colourway recolour is unchanged. openChatConfigCard is
          // ALWAYS ADD intent (see banner above), so there's no editIntent
          // branch here.
          // #8 fix: liveness-check `seed` (see handleOpenConfigure) — a chat "remove that" can
          // detach it between open and confirm; fall through to a fresh add, never mutate an orphan.
          var target = (seed && railLines.indexOf(seed) > -1) ? seed : null;

          if (target) {
            // Collision guard (P1 #6 #3 fix) — `target` is the seed line,
            // which can diverge from cfg.colour once 2+ colourways exist.
            // If the confirmed colour belongs to a DIFFERENT existing line,
            // block instead of clobbering it. For a plain recolour to a
            // colour nobody else has, findLine(hit.id, cfg.colour) is
            // either null or resolves back to `target` itself, so this
            // never blocks the safe cases.
            var cfgCollision = findLine(hit.id, cfg.colour);
            if (cfgCollision && cfgCollision !== target) {
              toast('You already have a ' + (cfg.colour ? krTitleCase(cfg.colour) + ' ' : '') + 'line for this item — remove it first, or choose a different colour here.');
              return;
            }
            target.printLocations = cfg.printLocations;
            target.colour = cfg.colour;
            target.sizeRun = cfg.sizeRun;
            target.qty = cfg.qty;
            target.quote = cfg.quote;
            target.artwork = cfg.artwork;
            var hydratedPreview = ui.getPreview();
            target.preview = {
              images: (hydratedPreview.images.front || hydratedPreview.images.back) ? hydratedPreview.images : target.preview.images,
              availableColours: hydratedPreview.availableColours.length ? hydratedPreview.availableColours : target.preview.availableColours
            };
            renderRail();
            railRev++; // FIX #12 — line updated (chat config card)
            scheduleAutosave();
            summaryText = 'Updated ' + (hit.title || 'that line') + ': ' + cfg.qty + ' units, ' + configBits + '.';
          } else {
            handleAddToRail(hit, cfg.quote, ui.getPreview(), card, cfg);
            summaryText = 'Added ' + cfg.qty + ' × ' + (hit.title || 'Item') + ', ' + configBits + '.';
          }

          // Collapse the card to a static confirmation — same visual
          // language as buildFormCard's own "✓ Details added" done-state.
          ui.destroy();
          wrap.className = 'kr-form-card kr-form-done';
          wrap.innerHTML = '<div class="kr-form-done-check">✓ ' + escapeHtml(summaryText) + '</div>';

          // Synthetic user turn — NEVER a client-stated price; this just
          // keeps Ari aware of what changed, reusing the EXACT real
          // sendChatMessage() pipeline (ack + cards + staff logging) the
          // request_details forms already use, so it costs one normal chat
          // turn — never an extra hidden agentChat call on top of that.
          chatInputEl.value = summaryText;
          if (krOpenConfig && krOpenConfig.ui === ui) krOpenConfig = null; // now a rail line — don’t also report it as "open" in the synthetic turn’s context
          sendChatMessage({ synthetic: true }); // softened bubble — the "✓ ..." card right above already shows this same summary
        }
      });
      krOpenConfig = { hit: hit, ui: ui, line: null }; // Ari sees what they have open (buildQuoteContext) [chat card]; line:null — always ADD intent here, see FINDING #2
      wrap.appendChild(ui.el);
      scrollThreadToBottom();

      krHydrateLineConfigUI(ui, hit, preview, ui.getConfig().colour);
    }
    _openChatConfig = openChatConfigCard;

    // Parse a turn's trace for products Ari actually surfaced (quoted or
    // previewed) and render cards for the ones we can resolve a real handle
    // for. Two passes: (1) harvest every handle-bearing hit this trace
    // mentions (search_products results + get_preview_config's product) into
    // chatKnownHits, since a later get_quote in the SAME trace may reference
    // an id search_products only just resolved; (2) decide which ids were
    // actually engaged with (get_quote / get_preview_config — never a raw,
    // un-engaged search_products hit, which is search noise, not something
    // Ari showed the customer — see the header note on free-text ranking).
    function renderTraceCards(trace) {
      if (!Array.isArray(trace)) return;
      trace.forEach(function (t) {
        if (!t || !t.output) return;
        if (t.tool === 'search_products' && Array.isArray(t.output)) {
          t.output.forEach(function (hit) { if (hit && hit.id) chatKnownHits[hit.id] = hit; });
        } else if (t.tool === 'get_preview_config' && t.output.product && t.output.product.id) {
          var p = t.output.product;
          if (!chatKnownHits[p.id]) {
            chatKnownHits[p.id] = { id: p.id, title: p.title, styleCode: p.styleCode || null, vendor: null, handle: p.handle || null, gsm: null, fit: null, composition: null, colourCount: 0 };
          } else if (!chatKnownHits[p.id].handle && p.handle) {
            chatKnownHits[p.id].handle = p.handle;
          }
        } else if (t.tool === 'get_quote' && t.input && t.input.productId) {
          // Item 2 — the config Ari ACTUALLY quoted, so a Configure
          // card/drawer never seeds a default that contradicts what the
          // customer was just told a price for. Last-write-wins per
          // productId, across the whole session (module-scope
          // _chatLastQuoteConfig, reset only per openKitRoom() — see there)
          // — never persisted, never sent to the server.
          var qCfgPrintLocations = (Array.isArray(t.input.printLocations) && t.input.printLocations.length) ? t.input.printLocations.slice() : null;
          var qCfgColour = t.input.colour || null;
          var qCfgQuantity = (typeof t.input.quantity === 'number' && t.input.quantity > 0) ? t.input.quantity : null;
          _chatLastQuoteConfig[t.input.productId] = {
            printLocations: qCfgPrintLocations,
            colour: qCfgColour,
            quantity: qCfgQuantity,
            // Feature 1 (2026-08 pre-build) — this turn's get_quote carried
            // EVERY field a line needs (colour + placements + qty), so
            // openChatConfigCard can open the card already fully set —
            // "confirm" framing — instead of asking the customer to
            // re-pick what Ari already established. False on a partial/
            // exploratory get_quote (e.g. pricing "a front print" before
            // colour's come up) — the existing inherited-seed behavior
            // still applies there, just without the confirm framing.
            configComplete: !!(qCfgColour && qCfgPrintLocations && qCfgQuantity)
          };
        }
      });

      var engagedIds = [], seenThisTurn = {};
      trace.forEach(function (t) {
        if (!t || !t.output || !t.output.product || !t.output.product.id) return;
        if (t.tool !== 'get_quote' && t.tool !== 'get_preview_config') return;
        var id = t.output.product.id;
        if (!seenThisTurn[id]) { seenThisTurn[id] = true; engagedIds.push(id); }
      });

      var toShow = [];
      engagedIds.forEach(function (id) {
        if (chatShownProductIds[id]) return; // already shown earlier in this thread
        var hit = chatKnownHits[id];
        if (!hit || !hit.handle) return; // no real handle known — never fabricate a card
        chatShownProductIds[id] = true;
        toShow.push(hit);
      });
      renderChatCards(toShow);
      return toShow; // handed to logChatTurn() for staff visibility — never re-fetched
    }

    // ===========================================================================
    // request_details form cards — Ari's tool for collecting structured
    // details (company/ABN/PO/attn/delivery address/size run) mid-conversation.
    // Contract (agent-chat.ts's new `request_details` tool): a trace entry
    // {tool:"request_details", input:{reason, fields:[...]}, output:{...}}.
    // The FIELD SPEC lives in input.fields, NOT output. Each field:
    // {key, type: text|email|tel|select|address|size_grid, label, required?,
    // prefill?, hint?, options?, sizes?}.
    //
    // Rendered as a native <form> (real <label for>, aria-required + a
    // visible * on required fields, aria-live error summary, every target
    // >=44px, natural tab order) — see buildFormField()/buildFormCard().
    //
    // On submit: (a) POST an additive `details` object to saveKitQuote
    // (submitDetailsForm(), rail section above — reuses the cached
    // quoteId/conversationId so it merges the same quote doc; silent-fail);
    // (b) push a plain-text SUMMARY as a synthetic user turn and auto-send it
    // (reuses sendChatMessage() verbatim by just setting chatInputEl.value —
    // so Ari's ack, any resulting product cards, and staff logging all go
    // through the exact same real pipeline, not a parallel one); (c) NEVER
    // sets a price client-side — a size_run rides in that synthetic turn,
    // which makes Ari re-call get_quote itself; renderTraceCards() (already
    // wired) picks up whatever it returns. The card then collapses to a
    // compact "✓ Details added" confirmation.
    // ===========================================================================

    // A stable-ish dedup key for a request_details call: prefer a real id if
    // the tool output ever carries one, else derive one from the field spec
    // itself (reason + ordered field keys) — good enough to stop an
    // identical re-ask from rendering a second card without inventing an id
    // the contract doesn't promise.
    function formKeyForEntry(t) {
      if (t.output && (t.output.formId || t.output.id)) return String(t.output.formId || t.output.id);
      var fields = (t.input && Array.isArray(t.input.fields)) ? t.input.fields : [];
      return 'rd:' + String((t.input && t.input.reason) || '') + ':' + fields.map(function (f) { return f && f.key; }).join(',');
    }

    // Build one field's markup + a read()/wire() controller. read(card)
    // returns {missing, value, display} — missing gates submission (required
    // + empty), value is what's written into the details object (skipped
    // entirely when null — never sends an empty optional field), display is
    // the human-readable fragment for the synthetic summary turn (null to
    // omit, e.g. an empty optional field).
    function buildFormField(field, fid) {
      var type = field.type;
      var required = !!field.required;
      var label = field.label || field.key || 'Details';
      var reqMark = required ? ' <span class="kr-form-req" aria-hidden="true">*</span>' : '';
      var reqAttrs = required ? ' required aria-required="true"' : '';
      var hintHtml = field.hint ? '<div class="kr-form-hint">' + escapeHtml(field.hint) + '</div>' : '';

      if (type === 'select') {
        var opts = Array.isArray(field.options) ? field.options : [];
        var optHtml = '<option value="">Choose…</option>' + opts.map(function (o) {
          var isObj = o && typeof o === 'object';
          var val = isObj ? (o.value != null ? o.value : o.label) : o;
          var lbl = isObj ? (o.label != null ? o.label : o.value) : o;
          var sel = (field.prefill != null && String(field.prefill) === String(val)) ? ' selected' : '';
          return '<option value="' + escapeHtml(val) + '"' + sel + '>' + escapeHtml(lbl) + '</option>';
        }).join('');
        var selectHtml = '<div class="kr-form-field">' +
          '<label for="' + fid + '">' + escapeHtml(label) + reqMark + '</label>' +
          '<select id="' + fid + '"' + reqAttrs + '>' + optHtml + '</select>' + hintHtml + '</div>';
        return { html: selectHtml, controller: {
          key: field.key, label: label,
          read: function (card) {
            var node = qs('#' + fid, card);
            var v = node ? node.value.trim() : '';
            if (!v) return { missing: required, value: null, display: null };
            return { missing: false, value: v, display: label + ': ' + v };
          }
        } };
      }

      if (type === 'address') {
        var pf = field.prefill || {};
        var subFields = [
          { k: 'address1', ph: 'Street address', val: pf.address1, full: true },
          { k: 'city', ph: 'Suburb', val: pf.city },
          { k: 'province', ph: 'State', val: pf.province },
          { k: 'zip', ph: 'Postcode', val: pf.zip }
        ];
        var subHtml = subFields.map(function (s, si) {
          return '<input type="text" class="kr-form-addr-part' + (s.full ? ' kr-form-addr-full' : '') + '" ' +
            'id="' + fid + '-' + si + '" placeholder="' + escapeHtml(s.ph) + '" ' +
            'aria-label="' + escapeHtml(label) + ' — ' + escapeHtml(s.ph) + '"' +
            (s.val ? ' value="' + escapeHtml(s.val) + '"' : '') + '>';
        }).join('');
        var addrHtml = '<div class="kr-form-field kr-form-field-address">' +
          '<label id="' + fid + '-lbl">' + escapeHtml(label) + reqMark + '</label>' +
          '<div class="kr-form-addr-grid" role="group" aria-labelledby="' + fid + '-lbl">' + subHtml + '</div>' +
          hintHtml + '</div>';
        return { html: addrHtml, controller: {
          key: field.key, label: label,
          read: function (card) {
            var parts = {}, any = false, hasCore = true;
            subFields.forEach(function (s, si) {
              var node = qs('#' + fid + '-' + si, card);
              var v = node ? node.value.trim() : '';
              if (v) { parts[s.k] = v; any = true; }
              else if (s.k === 'address1' || s.k === 'city') hasCore = false;
            });
            if (!any) return { missing: required, value: null, display: null };
            if (required && !hasCore) return { missing: true, value: null, display: null };
            var display = [parts.address1, parts.city, parts.province, parts.zip].filter(Boolean).join(', ');
            return { missing: false, value: parts, display: display ? ('Deliver ' + display) : null };
          }
        } };
      }

      if (type === 'size_grid') {
        var sizes = Array.isArray(field.sizes) ? field.sizes : [];
        var chipsHtml = sizes.map(function (s) {
          return '<div class="kr-form-size-chip" data-size="' + escapeHtml(s) + '">' +
            '<span class="kr-form-size-lbl">' + escapeHtml(s) + '</span>' +
            '<div class="kr-fqty">' +
              '<button type="button" class="kr-fqty-dec" aria-label="Decrease ' + escapeHtml(s) + '">−</button>' +
              '<span class="kr-fqty-n">0</span>' +
              '<button type="button" class="kr-fqty-inc" aria-label="Increase ' + escapeHtml(s) + '">+</button>' +
            '</div></div>';
        }).join('');
        var sizesHtml = '<div class="kr-form-field kr-form-field-sizes">' +
          '<div class="kr-form-sizes-lbl">' + escapeHtml(label) + reqMark + '</div>' +
          '<div class="kr-form-sizes" id="' + fid + '-grid">' + chipsHtml + '</div>' +
          '<div class="kr-form-size-total" id="' + fid + '-total">Total: 0 units</div>' +
          hintHtml + '</div>';
        return { html: sizesHtml, controller: {
          key: field.key, label: label,
          wire: function (card) {
            var gridEl = qs('#' + fid + '-grid', card);
            var totalEl = qs('#' + fid + '-total', card);
            if (!gridEl) return;
            function updateTotal() {
              var total = 0;
              Array.prototype.forEach.call(gridEl.querySelectorAll('.kr-fqty-n'), function (n) { total += parseInt(n.textContent, 10) || 0; });
              totalEl.textContent = 'Total: ' + total + ' unit' + (total === 1 ? '' : 's');
            }
            Array.prototype.forEach.call(gridEl.querySelectorAll('.kr-form-size-chip'), function (chip) {
              var nEl = qs('.kr-fqty-n', chip);
              qs('.kr-fqty-dec', chip).addEventListener('click', function () {
                nEl.textContent = String(Math.max(0, (parseInt(nEl.textContent, 10) || 0) - 1));
                updateTotal();
              });
              qs('.kr-fqty-inc', chip).addEventListener('click', function () {
                nEl.textContent = String(Math.min(99999, (parseInt(nEl.textContent, 10) || 0) + 1));
                updateTotal();
              });
            });
          },
          read: function (card) {
            var gridEl = qs('#' + fid + '-grid', card);
            var run = {}, any = false;
            if (gridEl) {
              Array.prototype.forEach.call(gridEl.querySelectorAll('.kr-form-size-chip'), function (chip) {
                var size = chip.getAttribute('data-size');
                var v = parseInt(qs('.kr-fqty-n', chip).textContent, 10) || 0;
                if (v > 0) { run[size] = v; any = true; }
              });
            }
            if (!any) return { missing: required, value: null, display: null };
            var display = 'Sizes ' + Object.keys(run).map(function (s) { return s + '×' + run[s]; }).join(' ');
            return { missing: false, value: run, display: display };
          }
        } };
      }

      // text / email / tel (default) — includes the ABN soft-format special
      // case: a non-blocking hint, never a hard stop (Ari still gets the
      // value even if it doesn't look like 11 digits).
      var inputType = (type === 'email' || type === 'tel') ? type : 'text';
      var inputModeAttr = type === 'tel' ? ' inputmode="tel"' : (type === 'email' ? ' inputmode="email"' : '');
      var isAbn = String(field.key || '').toLowerCase() === 'abn';
      var warnHtml = isAbn ? '<div class="kr-form-warn" id="' + fid + '-warn" hidden>That doesn’t look like a valid 11-digit ABN — we’ll still send it through.</div>' : '';
      // ABN gets a default format hint only when the caller didn't supply
      // its own — never render an empty hint div for a plain field with no hint.
      if (!hintHtml && isAbn) hintHtml = '<div class="kr-form-hint">ABN — 11 digits</div>';
      var textHtml = '<div class="kr-form-field">' +
        '<label for="' + fid + '">' + escapeHtml(label) + reqMark + '</label>' +
        '<input type="' + inputType + '" id="' + fid + '"' + inputModeAttr + reqAttrs +
          (field.prefill != null ? ' value="' + escapeHtml(field.prefill) + '"' : '') + '>' +
        hintHtml + warnHtml + '</div>';
      return { html: textHtml, controller: {
        key: field.key, label: label,
        wire: isAbn ? function (card) {
          var inputEl = qs('#' + fid, card), warnEl = qs('#' + fid + '-warn', card);
          if (!inputEl || !warnEl) return;
          inputEl.addEventListener('blur', function () {
            var digits = inputEl.value.replace(/\s+/g, '');
            warnEl.hidden = !digits || /^\d{11}$/.test(digits);
          });
        } : null,
        read: function (card) {
          var node = qs('#' + fid, card);
          var v = node ? node.value.trim() : '';
          if (!v) return { missing: required, value: null, display: null };
          return { missing: false, value: v, display: label + ': ' + v };
        }
      } };
    }

    // Build + append one form card, wire its submit flow.
    function buildFormCard(reason, fields) {
      FORM_UID_SEQ++;
      var uid = 'kr-rf' + FORM_UID_SEQ;
      var controllers = [];
      var fieldsHtml = fields.map(function (f, i) {
        var built = buildFormField(f, uid + '-' + i);
        controllers.push(built.controller);
        return built.html;
      }).join('');

      var card = el('form', 'kr-form-card',
        '<div class="kr-form-reason">' + escapeHtml(reason || 'A few details to help lock this in:') + '</div>' +
        '<div class="kr-form-err" aria-live="polite" hidden></div>' +
        '<div class="kr-form-fields">' + fieldsHtml + '</div>' +
        '<button type="submit" class="kr-form-submit">Send to Ari</button>');
      card.setAttribute('novalidate', 'novalidate'); // we own validation/UX via the error summary, not native popups

      threadEl.appendChild(card);
      scrollThreadToBottom();
      controllers.forEach(function (c) { if (c.wire) c.wire(card); });

      var errEl = qs('.kr-form-err', card);
      card.addEventListener('submit', function (e) {
        e.preventDefault(); // real <form> (Enter-to-submit works), but we never let it navigate/reload
        var missingLabels = [];
        var details = {};
        var summaryParts = [];
        controllers.forEach(function (c) {
          var r = c.read(card);
          if (r.missing) { missingLabels.push(c.label); return; }
          if (r.value !== null && r.value !== undefined) details[c.key] = r.value;
          if (r.display) summaryParts.push(r.display);
        });
        if (missingLabels.length) {
          errEl.textContent = 'Please fill in: ' + missingLabels.join(', ') + '.';
          errEl.hidden = false;
          return;
        }
        errEl.hidden = true;

        submitDetailsForm(details); // (a) additive save — silent-fail, never blocks

        var summary = summaryParts.length ? summaryParts.join(' · ') : 'Here are the details you asked for.';
        card.className = 'kr-form-card kr-form-done';
        card.innerHTML = '<div class="kr-form-done-check">✓ Details added</div>';

        // (b) synthetic USER turn, rendered/stored exactly like a typed
        // message — reuses sendChatMessage() verbatim so Ari's ack, any
        // resulting cards, and staff logging all go through the SAME real
        // pipeline. (c) never a price: a sizeRun just rides in this turn,
        // which makes Ari re-call get_quote itself if it needs to price it.
        chatInputEl.value = summary;
        sendChatMessage({ synthetic: true }); // softened bubble — the "✓ Details added" card right above already confirms this
      });
    }

    // FIX #17 (client half) — a returning customer's company/ABN/PO/contact/
    // delivery details (krBusinessDetails, rehydrated from the resumed
    // quote's `businessDetails` — see the resume handler above) pre-fill a
    // FRESH request_details form instead of making them retype everything.
    // Only fills a field the agent left blank (`f.prefill != null` already
    // wins — Ari's own live-session prefill, e.g. an email it just
    // extracted, always takes priority) and only when the shape matches
    // (address field <-> object, everything else <-> a scalar) — never
    // fabricates or coerces a field krBusinessDetails doesn't actually
    // carry. Returns the SAME fields array untouched when there's nothing
    // to rehydrate (fresh session, or an old quote saved before this
    // shipped) — byte-identical to pre-fix behaviour.
    function applyBusinessDetailsPrefill(fields) {
      if (!krBusinessDetails) return fields;
      return fields.map(function (f) {
        if (!f || !f.key || f.prefill != null) return f;
        var val = krBusinessDetails[f.key];
        if (val == null) return f;
        var isAddressField = f.type === 'address';
        if (isAddressField !== (typeof val === 'object')) return f; // shape mismatch — never guess
        var f2 = krCloneObj(f);
        f2.prefill = val;
        return f2;
      });
    }
    function renderRequestDetailsCards(trace) {
      if (!Array.isArray(trace)) return;
      trace.forEach(function (t) {
        if (!t || t.tool !== 'request_details' || !t.input || !Array.isArray(t.input.fields) || !t.input.fields.length) return;
        var formKey = formKeyForEntry(t);
        if (chatShownFormIds[formKey]) return; // already shown (or already answered) in this thread
        chatShownFormIds[formKey] = true;
        buildFormCard(t.input.reason, applyBusinessDetailsPrefill(t.input.fields));
      });
    }

    // ===========================================================================
    // update_quote — Ari (Bulk Desk) directly adding/editing/removing a rail
    // line, on an explicit customer instruction. Contract (agent-chat.ts's
    // update_quote execTool case): a trace entry {tool:'update_quote',
    // input:{action,productId,quantity?,sizes?,colour?,printLocations?,note?},
    // output:{ok,action,productId,...echoed fields}} — server-side is a PURE
    // NO-OP ACK (never a price, never a rail write); this is the ONLY place the
    // mutation + re-pricing actually happens, entirely client-side.
    //
    // Same trace→UI idiom as renderTraceCards/renderRequestDetailsCards above:
    // scan the trace, dedup, apply. Dedup key covers the exact call (action +
    // input) via chatAppliedQuoteActions — a trace can be re-sent/re-rendered
    // on a retry and must never double-apply.
    //
    // Client-side backstop: productId is resolved ONLY via chatKnownHits (the
    // same id->hit map renderTraceCards populates from search_products/
    // get_preview_config outputs). No known hit, or no real handle -> silently
    // skip — never fabricate a line from a hallucinated/unrecognised id.
    // ===========================================================================
    // FINDING #4 (defensive, P1 #6 follow-up, 2026-08) — the update_quote tool
    // contract (agent-chat.ts) has no per-colourway disambiguation today: Ari
    // can send an 'update'/'remove' with no colour at all. That's unambiguous
    // when the product has 0-1 colourways on the rail (the pre-multi-colourway
    // norm) but genuinely ambiguous with 2+ — silently falling back to
    // findLine(hit.id)'s "any" match would rewrite/remove whichever colourway
    // happened to be added first, the exact "wrong garment" risk this whole
    // pass exists to close. Real fix is a colour param on the tool contract
    // (out of this file's scope); this is the client-side backstop: never
    // guess, surface it instead.
    function countLinesForProduct(productId) {
      var n = 0;
      for (var i = 0; i < railLines.length; i++) { if (railLines[i].hit.id === productId) n++; }
      return n;
    }
    function ariColourAmbiguous(hit, out) {
      if (out.colour !== undefined) return false; // Ari named a colour — never ambiguous, exact match resolves it
      if (countLinesForProduct(hit.id) <= 1) return false; // 0 or 1 colourway on the rail — "any" is unambiguous
      console.warn('[kit-room] update_quote ' + out.action + ' for productId ' + hit.id +
        ' has no colour and ' + countLinesForProduct(hit.id) + ' colourways on the rail — refusing to guess which one.');
      toast('You have more than one colour of ' + (hit.title ? '"' + hit.title + '"' : 'that item') +
        ' — tell Ari which colour to ' + (out.action === 'remove' ? 'remove' : 'update') + '.');
      return true;
    }
    function applyUpdateQuoteActions(trace) {
      if (!Array.isArray(trace)) return;
      trace.forEach(function (t) {
        if (!t || t.tool !== 'update_quote' || !t.output || t.output.ok !== true) return;
        var dedupKey = 'uq:' + t.tool + ':' + JSON.stringify(t.input);
        if (chatAppliedQuoteActions[dedupKey]) return;
        chatAppliedQuoteActions[dedupKey] = true;

        var out = t.output;
        var hit = out.productId ? chatKnownHits[out.productId] : null;
        if (!hit || !hit.handle) return; // never fabricate a line for an id we can't resolve to a real product

        if (out.action === 'remove') {
          if (ariColourAmbiguous(hit, out)) return; // FINDING #4 — never silently remove "whichever one" with 2+ colourways
          // P1 fix #6 — exact (productId, colour) match when Ari named a colour (never
          // remove the WRONG colourway); falls back to "any" only when it didn't, which
          // is unambiguous in the overwhelmingly common single-colourway case and is the
          // same best-effort Ari made before multi-colourway lines were possible.
          var existing = (out.colour !== undefined) ? findLine(hit.id, out.colour) : findLine(hit.id);
          if (!existing) return; // nothing to remove — silent no-op
          applyAriRemove(existing, hit);
        } else if (out.action === 'add' || out.action === 'update') {
          if (out.action === 'update' && ariColourAmbiguous(hit, out)) return; // FINDING #4 — never silently rewrite "whichever one" with 2+ colourways; 'add' is always its own new/merge slot, never ambiguous
          applyAriUpsert(hit, out);
        }
      });
    }

    // Ari removed a line — full snapshot kept for Undo (a real, working undo,
    // not just a toast). Reuses the EXISTING removeLine() (same collapse
    // animation + source-card revert as a manual remove) rather than a
    // parallel removal path.
    function applyAriRemove(line, hit) {
      var snapshot = {
        hit: line.hit, quote: line.quote, preview: line.preview, qty: line.qty,
        printLocations: line.printLocations ? line.printLocations.slice() : [],
        colour: line.colour, sizeRun: line.sizeRun, card: line.card,
        artwork: line.artwork, ariAdded: line.ariAdded
      };
      removeLine(line);
      toast('Ari removed ' + (hit.title || 'that item') + ' from your quote.', {
        actionLabel: 'Undo',
        onAction: function () {
          // Exact (productId, colour) — P1 fix #6: an "any colourway" check would wrongly
          // treat a DIFFERENT colourway added since the removal as "already back" and
          // silently drop this Undo.
          if (findLine(snapshot.hit.id, snapshot.colour)) return; // already back (e.g. re-added since) — never duplicate
          railLines.push({
            hit: snapshot.hit, quote: snapshot.quote, preview: snapshot.preview, qty: snapshot.qty,
            printLocations: snapshot.printLocations, colour: snapshot.colour, sizeRun: snapshot.sizeRun,
            card: snapshot.card, requoting: false, requoteFailed: false, _debounceT: null,
            artwork: snapshot.artwork, ariAdded: snapshot.ariAdded, _entered: true
          });
          renderRail();
          railRev++; // FIX #12 — line restored (Undo)
          scheduleAutosave();
          toast((hit.title || 'Item') + ' restored to your quote.');
        }
      });
    }

    // Ari added or edited a line — resolves the live garment price via the
    // SAME fast product.js + computeClientQuote path Browse/Chat cards use
    // (loadCardFast/restoreRailFromEntries), never a client-side guess. Omitted
    // fields keep the existing line's current values (an update carrying only
    // `quantity` must not blank out colour/placements); a brand-new `add` (or
    // an update with no prior line) falls back to QUOTE_PRINT_LOCATIONS so it's
    // never an empty-placement line — same discipline as handleAddToRail's cfg
    // default. Upserts using the EXACT same line shape handleAddToRail/
    // restoreRailFromEntries push — an Ari-added line is a fully normal rail
    // line afterwards (see the ariAdded badge/clear in buildLineEl). Silent-
    // fail on any fetch/price error — never show the customer a wrong number.
    //
    // IDENTITY MODEL (P1 fix #6, 2026-08) — a rail line is (productId, colour), not
    // productId alone (see findLine()). This function branches on that:
    //  - action 'add', EXACT (productId, colour) match already on the rail -> MERGE:
    //    sum quantities (and per-size counts, via mergeSizeRuns, when either side has a
    //    size run) into the existing line instead of overwriting it. This is the actual
    //    fix for the reported bug: "add 30 Navy" after "add 50 Black" no longer touches
    //    the Black line — different colour = different identity = a genuinely new line;
    //    "add 20 more Black" after "add 50 Black" now correctly becomes ONE line at qty
    //    70 instead of silently overwriting to 20.
    //  - action 'add', NO exact match (a colour never seen for this product before, or
    //    a brand-new product) -> always a NEW line. A colourless add (out.colour
    //    undefined) is its OWN (productId, '') identity slot — it never silently reuses
    //    a line that has some OTHER named colour already, which would misattach units
    //    to the wrong colourway just as badly as the original bug.
    //  - action 'update' targets an EXACT (productId, colour) match when colour is
    //    given AND 2+ colourways already exist for this product (same resolution an
    //    'add' would use); with ≤1 colourway on the rail it uses the "any" match
    //    instead — even when Ari gave an explicit colour — so "make it navy" on the
    //    sole Red line RECOLOURS that line (keeping its qty/sizeRun/artwork) rather
    //    than exact-matching "Navy" (nothing yet), missing, and pushing a second line
    //    with QUOTE_QTY (P1 #6 defect fix, 2026-08 — see the ≤1-vs-2+ rule at
    //    findLine's contract banner). When colour is omitted altogether it falls back
    //    to ANY line for this productId regardless of colourway count — the
    //    update_quote tool contract (agent-chat.ts) has no multi-colourway
    //    disambiguation today. FINDING #4 (P1 #6 follow-up, 2026-08): a colourless
    //    update is only genuinely unambiguous when 0-1 colourways exist for this
    //    product — the caller (applyUpdateQuoteActions, via ariColourAmbiguous) now
    //    refuses and surfaces a "which colour?" toast BEFORE calling this function at
    //    all when 2+ colourways are on the rail, so the colourless "any" fallback below
    //    only ever runs in the genuinely unambiguous case. As before, 'update' REPLACES
    //    the fields it's given rather than summing — an explicit "set quantity to 40"
    //    must set it to 40, never add 40 on top; only an exact-identity 'add' sums. The
    //    exact-identity 'add' resolution above is completely UNCHANGED by any of this —
    //    a 2nd colourway add must still ADD, never overwrite.
    //  - print placements are deliberately NOT part of identity: a colourway is the
    //    same physical order line whether it currently prints front-only or
    //    front+back. A print-config-only change on an existing colourway is an edit of
    //    that line (matching the Configure drawer's own edit-in-place semantics for
    //    placements), not a reason to fork a duplicate line the customer never asked
    //    for — the same colourway is billed as one line either way.
    function applyAriUpsert(hit, out) {
      var isAdd = out.action === 'add';
      var explicitColour = (out.colour !== undefined) ? out.colour : undefined;
      // P1 #6 defect fix (2026-08) — 'update' with an explicit colour used to ALWAYS
      // exact-match, same as 'add'. That's right once 2+ colourways exist (never guess
      // which one Ari means), but with 0-1 colourways it broke the plain "change
      // colour" case: "make it navy" on the sole Red line found no exact "Navy" line,
      // fell through to the push-a-new-line branch below, and the new line got
      // QUOTE_QTY instead of the original's qty — a silent double-add + qty loss. The
      // ADD exact-identity resolution below is UNCHANGED (2+ colourway add must still
      // add, never overwrite) — only 'update' gets the ≤1-vs-2+ fallback, matching
      // findLine's contract banner and handleOpenConfigure/openChatConfigCard above.
      var existing;
      if (isAdd) {
        existing = (explicitColour !== undefined) ? findLine(hit.id, explicitColour) : null; // colourless add: its own slot, never "any"
      } else if (countLinesForProduct(hit.id) <= 1) {
        existing = findLine(hit.id); // 0-1 colourway — unambiguous; recolour/update that line in place, preserving its qty below
      } else {
        existing = (explicitColour !== undefined) ? findLine(hit.id, explicitColour) : findLine(hit.id); // 2+ colourways: ariColourAmbiguous already refused the colourless case before we got here
      }
      var colour = (explicitColour !== undefined) ? explicitColour : (existing ? existing.colour : null);
      var isMerge = isAdd && !!existing; // exact-identity add on top of an existing line -> sum, never overwrite
      var productFetch = fetch('/products/' + hit.handle + '.js')
        .then(function (r) { if (!r.ok) throw new Error('http ' + r.status); return r.json(); });
      // Item 3(c) — a colour Ari just set (or already had on this line)
      // should show the RIGHT garment photo on the RAIL thumbnail too, not
      // just the default front image product.js returns. Best-effort,
      // parallel with the price fetch above — a failure here never blocks
      // or breaks the price resolution (silent-fail via .catch(null)).
      var colourPreviewFetch = (colour && PB.previewUrl)
        ? fetchJSON(PB.previewUrl + '?productId=' + encodeURIComponent(hit.id) + '&colour=' + encodeURIComponent(colour)).catch(function () { return null; })
        : Promise.resolve(null);
      Promise.all([productFetch, colourPreviewFetch])
        .then(function (results) {
          var pj = results[0], colourPreviewData = results[1];
          var garmentUnit = (typeof pj.price_min === 'number' ? pj.price_min : 0) / 100;
          if (!(garmentUnit > 0)) throw new Error('no price on product.js');

          var printLocations = (out.printLocations && out.printLocations.length)
            ? out.printLocations.slice()
            : (existing && existing.printLocations && existing.printLocations.length)
              ? existing.printLocations.slice()
              : QUOTE_PRINT_LOCATIONS.slice();
          // P1 fix #6 — an exact-identity 'add' MERGES onto the existing line (sum
          // quantities / per-size counts) instead of the plain "explicit fields win,
          // else fall back to existing" replace semantics 'update' (and a fresh 'add')
          // still use below.
          var sizeRun, qty;
          if (isMerge) {
            if (out.sizes !== undefined) {
              sizeRun = mergeSizeRuns(existing.sizeRun, out.sizes);
              qty = Math.max(QTY_MIN, sizeRunTotal(sizeRun) || QTY_MIN);
            } else if (out.quantity !== undefined) {
              sizeRun = existing.sizeRun; // a bare-qty add never touches an existing size split
              qty = Math.max(QTY_MIN, (existing.qty || 0) + (Math.floor(out.quantity) || 0));
            } else {
              sizeRun = existing.sizeRun; qty = existing.qty; // add with neither qty nor sizes — no-op quantity-wise, still re-prices/refreshes other fields
            }
          } else {
            sizeRun = (out.sizes !== undefined) ? out.sizes : (existing ? existing.sizeRun : null);
            if (out.sizes !== undefined) qty = Math.max(QTY_MIN, sizeRunTotal(out.sizes) || QTY_MIN);
            else if (out.quantity !== undefined) qty = Math.max(QTY_MIN, Math.floor(out.quantity) || QTY_MIN);
            else qty = existing ? existing.qty : QUOTE_QTY;
          }

          var quote = computeClientQuote(garmentUnit, printLocations, qty);
          var fi = pj.featured_image;
          var frontImg = (typeof fi === 'string' ? fi : (fi && fi.src)) || (pj.images && pj.images[0]) || null;
          var colourImages = (colourPreviewData && colourPreviewData.preview && colourPreviewData.preview.images &&
            (colourPreviewData.preview.images.front || colourPreviewData.preview.images.back)) ? colourPreviewData.preview.images : null;
          var preview = colourImages
            ? { images: colourImages, availableColours: (existing && existing.preview && existing.preview.availableColours) || [] }
            : (existing && existing.preview) || { images: { front: frontImg }, availableColours: [] };

          if (existing) clearTimeout(existing._debounceT);
          var line = {
            hit: hit, quote: quote, preview: preview, qty: qty,
            printLocations: printLocations, colour: colour || null, sizeRun: sizeRun || null,
            card: existing ? existing.card : null,
            requoting: false, requoteFailed: false, _debounceT: null,
            artwork: existing ? existing.artwork : null,
            ariAdded: true, _entered: existing ? existing._entered : false
          };
          if (existing) {
            railLines[railLines.indexOf(existing)] = line;
          } else {
            railLines.push(line);
          }
          renderRail();
          railRev++; // FIX #12 — line added/updated/merged (Ari update_quote)
          scheduleAutosave();
          toast('Ari ' + (existing ? 'updated' : 'added') + ' ' + (hit.title || 'that item') + ' — ' + qty + ' units.');
        })
        .catch(function () { /* silent-fail — never show the customer a wrong number */ });
    }

    function setChatInputEnabled(enabled) {
      chatBusy = !enabled;
      chatSendBtn.disabled = !enabled;
      chatInputEl.disabled = !enabled;
    }

    // Ari has no other way to see "Your bulk order" — build a plain-text
    // snapshot of the CURRENT rail state, read from the exact same
    // `railLines`/quote fields renderRail() sums (never a second, divergent
    // computation), and inject it into the message Ari actually receives.
    // Rebuilt fresh on every send so it always reflects the rail AT SEND
    // TIME — never cached, never carried over from a prior turn.
    // PRICE INTEGRITY: these $ figures are client-computed (loadCardFast's
    // computeClientQuote), not a live get_quote call, so the server's
    // unblessed-price guard will strip them if Ari echoes one verbatim. This
    // context is for Ari's AWARENESS/continuity only — it must still call
    // get_quote itself to STATE an exact price back to the customer, which
    // the trailing note below reminds it of.
    function buildQuoteContext() {
      // What the customer currently has OPEN (drawer or chat card) but hasn’t
      // added yet — so Ari can resolve "this"/"is this a good choice?" to it
      // even when the quote itself is still empty.
      var openPart = '';
      if (krOpenConfig && krOpenConfig.hit) {
        var oc = krOpenConfig.hit;
        var ocName = (oc.vendor ? oc.vendor + ' ' : '') + (oc.title || 'Item');
        var ocBits = '';
        try {
          var ocCfg = krOpenConfig.ui.getConfig();
          var ocb = [zoneGroupLabel(ocCfg.printLocations)];
          if (ocCfg.colour) ocb.push(krTitleCase(ocCfg.colour));
          var ocsr = sizeRunSummary(ocCfg.sizeRun);
          if (ocsr) ocb.push('sizes ' + ocsr);
          if (ocCfg.qty) ocb.push(ocCfg.qty + ' units');
          ocBits = ' — ' + ocb.join(', ');
        } catch (e) {}
        // Reopening an EXISTING rail line to tweak it (Configure again from
        // the rail) is a different state than configuring a fresh product
        // that was never added — telling Ari "NOT yet added" for the former
        // is just wrong and makes it re-pitch/re-add something already on
        // the quote. Branch on whether this productId is already a line.
        var ocAlreadyOnQuote = !!findLine(oc.id);
        openPart = '[Right now the customer has this product OPEN on screen and is configuring it' +
          (ocAlreadyOnQuote ? ' (it is ALREADY on their quote — they reopened it to edit; do not describe it as unadded)' : ' (NOT yet added to the quote)') + ': ' +
          ocName + ' (styleCode ' + (oc.styleCode || '—') + ')' + ocBits + '. ' +
          'If they say "this", "it", "that one" or "is this a good choice?", they mean THIS — answer about it directly; never ask them to name a product you can already see.]';
      }
      // Vision/check_artwork awareness — an artwork uploaded via chat that
      // hasn't landed on a line yet (2+ lines, nothing open) still needs to
      // stay reachable so Ari can call check_artwork(imageUrl, productId)
      // once it learns which product it's for. Once it IS on a line, the
      // per-line artworkBit below already covers it — this note is
      // suppressed then so the same URL isn't mentioned twice.
      var unassignedArtworkNote = '';
      if (chatArtworkContext) {
        // Item 4 — l.artwork is now a map keyed by side; check every side's
        // uploadedUrl, not a single top-level one.
        var onALine = railLines.some(function (l) {
          return l.artwork && Object.keys(l.artwork).some(function (side) { return l.artwork[side].uploadedUrl === chatArtworkContext.uploadedUrl; });
        });
        if (!onALine) {
          unassignedArtworkNote = ' [The customer has also attached artwork you’ve been shown but that isn’t tied to a specific line yet: ' +
            chatArtworkContext.uploadedUrl + '. Once you know which product it’s for, call check_artwork with this as imageUrl + that productId for real print-readiness numbers — never state a size/DPI/price without it.]';
        }
      }
      if (!railLines.length) {
        var emptyBase = openPart ? ('[The customer’s quote is still empty.] ' + openPart) : '[The customer’s quote is currently empty.]';
        return emptyBase + unassignedArtworkNote;
      }
      var sub = 0, discount = 0, total = 0, totalQty = 0, topTierPct = 0;
      var lineDescs = railLines.map(function (l) {
        var q = l.quote;
        sub += q.subtotal; discount += q.discountAmount; total += q.total; totalQty += l.qty;
        var pct = q.appliedTier ? q.appliedTier.discountPercent : 0;
        if (pct > topTierPct) topTierPct = pct;
        var name = (l.hit.vendor ? l.hit.vendor + ' ' : '') + (l.hit.title || 'Item');
        // Real placements/colour/size run folded in — so Ari's own read of
        // the quote window can never contradict what the Configure drawer
        // actually shows the customer (no more implicit "front print" for
        // every line regardless of what was really picked).
        var configBits = [zoneGroupLabel(l.printLocations)];
        if (l.colour) configBits.push(krTitleCase(l.colour));
        var sr = sizeRunSummary(l.sizeRun);
        if (sr) configBits.push('sizes ' + sr);
        // Artwork attached to THIS line (per-line Configure upload, or a
        // chat-vision attachment storeArtworkOnActiveLine() resolved to this
        // line) — item 4: one real master URL PER SIDE now, each with its
        // own zone key, so Ari can call check_artwork (imageUrl + productId +
        // that exact zone) for print-readiness rather than ever guessing
        // DPI/size from the conversation, and never conflates a front file
        // with a back one on a 2-sided print.
        var artworkBit = (l.artwork && Object.keys(l.artwork).length)
          ? Object.keys(l.artwork).map(function (side) {
              // The REAL zone key (e.g. 'front-full') check_artwork's own
              // `zone` param expects — not our internal grouping key —
              // whichever of this line's actual placements maps to this side.
              var zoneKey = (l.printLocations || []).filter(function (loc) { return krArtworkSideKey(loc) === side; })[0] || side;
              return ' [' + (KR_ARTWORK_SIDE_LABELS[side] || side) + ' artwork attached — check_artwork imageUrl: ' + l.artwork[side].uploadedUrl + ', zone: ' + zoneKey + ']';
            }).join('')
          : '';
        return l.qty + '× ' + name + ' (styleCode ' + (l.hit.styleCode || '—') + ', ' + configBits.join(', ') + ')' + artworkBit + ' at ' +
          money(q.perUnit) + '/unit = ' + money(q.total);
      });
      var gst = total - total / 1.1;
      return '[Context — the customer’s current quote window: ' + lineDescs.join('; ') + '. ' +
        railLines.length + ' style' + (railLines.length === 1 ? '' : 's') + ', ' + totalQty + ' units; ' +
        'subtotal ' + money(sub) + (topTierPct ? ', ' + topTierPct + '% bulk discount −' + money(discount) : ', no bulk discount yet') +
        ', total ' + money(total) + ' inc GST (incl. ' + money(gst) + ' GST). ' +
        'Use this to stay aware of what they’ve already added — reference and build on it, don’t re-ask. ' +
        'These figures are for your awareness only — still call get_quote before stating any exact price back to them.]' +
        (openPart ? ' ' + openPart : '') + unassignedArtworkNote;
    }

    // Staff-visibility logging (bulkDeskLogTurn) — fire-and-forget: never
    // returned/awaited by the caller, so it can NEVER delay or break the
    // customer-facing chat. Wrapped in try/catch + its own .catch() so even
    // a synchronous throw (bad PB config, etc.) can't escape into the chat
    // flow. Never logs a turn with no real exchange (mirrors the CF's own
    // guard — belt-and-braces so a stray call never even leaves the browser).
    // assistantMessage MAY be '' — that's the one legitimate case (a
    // handed-off turn: agentChat returned {handedOff:true,reply:""} and the
    // customer's message still needs to persist to the staff-visible
    // thread). The CF itself already tolerates an empty assistantMessage
    // (index.ts ~76062, only skips when BOTH are empty); every OTHER caller
    // in this file always passes a real reply or ARI_ERROR_REPLY, so this
    // relaxation changes nothing for the normal (non-handoff) path.
    function logChatTurn(userMessage, assistantMessage, cards) {
      try {
        if (!userMessage) return;
        var payload = {
          conversationId: chatConversationId,
          // Populated the moment Save/Resume's "Email me this quote" (or a
          // resumed quote) learns a real identity — null until then.
          customerEmail: customerEmailCaptured,
          customerName: customerNameCaptured,
          userMessage: userMessage,
          assistantMessage: assistantMessage,
          cards: (cards || []).map(function (hit) {
            return { id: hit.id, title: hit.title || null, styleCode: hit.styleCode || null, handle: hit.handle || null };
          })
        };
        fetchJSON(bulkLogUrl(), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        }).then(function (data) {
          if (data && data.conversationId) {
            var learnedFirstId = !chatConversationId;
            chatConversationId = data.conversationId;
            if (learnedFirstId) startBulkDeskPoll();
          }
        }).catch(function () {}); // logging failure must stay invisible to the customer
      } catch (e) { /* same guarantee, for a synchronous throw */ }
    }

    // opts.synthetic — set by the deterministic sync-turn callers (config-card
    // confirm, request_details form submit) to soften the resulting bubble
    // (see appendUserBubble's `sync` param). Note chatSendBtn's click listener
    // below passes its click Event straight through as this function's first
    // arg — that's fine, a plain Event has no `.synthetic` property so
    // `opts && opts.synthetic` is falsy for every real click/Enter send.
    function sendChatMessage(opts) {
      var synthetic = !!(opts && opts.synthetic);
      var text = (chatInputEl.value || '').trim();
      // Vision — a file attached via the paperclip (handleArtworkFileChosen
      // below) sits in pendingArtwork until the NEXT send, which is the one
      // turn that carries it. An attach-only send (no typed text) is valid —
      // "here's my artwork" doesn't require a caption.
      var artwork = pendingArtwork;
      if ((!text && !artwork) || chatBusy) return; // ignore empty input; ignore a second send mid-flight
      chatInputEl.value = '';
      pendingArtwork = null;
      hidePendingChip();
      var sendText = text || 'Here’s the artwork I’m planning to use — what do you think?';
      if (artwork) {
        appendArtworkBubble(artwork.previewUrl, artwork.name, text); // ONE bubble: image + the customer's own note (if any) underneath
      } else {
        appendUserBubble(text, synthetic); // the rendered bubble is ALWAYS just what they typed
      }
      // Additive: a normal text-only turn keeps `content` a plain string,
      // byte-identical to pre-vision behavior. ONLY a turn with a fresh
      // attachment uses the array shape — every turn after this one goes
      // back to a string (pendingArtwork is already cleared above).
      // runAgent forwards `content` verbatim into client.messages.create
      // (typed `any`), so this image block reaches the vision-capable model
      // with zero backend change — verified against agentChat's source.
      var userContent = artwork
        ? [{ type: 'image', source: { type: 'url', url: artwork.previewUrl } }, { type: 'text', text: sendText }]
        : text;
      chatConvo.push({ role: 'user', content: userContent }); // messages stays the plain typed history — no bracketed context
      setChatInputEnabled(false);
      krBusy(true);
      var thinkingEl = appendThinkingBubble();

      // mode:'bulk' + context carries the CURRENT rail-state summary as its
      // own top-level field (the deployed agentChat maps this to
      // systemExtra — its live "quote window") rather than injected into a
      // user message. `messages` is now just the clean typed convo.
      var agentBody = { messages: chatConvo, maxTurns: AGENT_MAX_TURNS, mode: 'bulk', context: buildQuoteContext() };
      // Once a conversation exists, always send its id — this is how
      // agentChat's optional conversationId short-circuits to
      // {ok:true,handedOff:true,reply:""} once a human has taken over
      // (production-dashboard agent-chat.ts), so Ari never talks over a
      // specialist. Harmless/no-op while no handoff is active.
      if (chatConversationId) agentBody.conversationId = chatConversationId;
      fetchJSON(agentChatUrl(), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(agentBody)
      }).then(function (data) {
        if (thinkingEl.parentNode) thinkingEl.parentNode.removeChild(thinkingEl);
        if (data && data.handedOff === true) {
          // A specialist already owns this conversation — never render a
          // synthetic Ari reply. The takeover banner is (or will shortly be,
          // via the next poll tick) already showing; the customer's message
          // still needs to persist to the staff-visible thread though, so
          // log it with an empty assistantMessage (see logChatTurn's
          // relaxed guard above) rather than silently dropping it.
          logChatTurn(sendText, '', []);
          if (artwork) storeArtworkOnActiveLine(artwork, null); // a human took over — no trace this turn, but the upload itself is still real — keep it attached
          return;
        }
        var reply = (data && typeof data.reply === 'string' && data.reply.trim()) ? data.reply : ARI_ERROR_REPLY;
        appendAriBubble(reply);
        chatConvo.push({ role: 'assistant', content: reply });
        var cards = renderTraceCards(data && data.trace) || [];
        renderRequestDetailsCards(data && data.trace);
        applyUpdateQuoteActions(data && data.trace);
        logChatTurn(sendText, reply, cards); // fire-and-forget — never awaited
        // Ari has now actually SEEN this design (the vision turn just
        // round-tripped successfully) — attach it to whatever line (and, per
        // item 4, whichever zone) it's about, and keep it live in
        // buildQuoteContext for check_artwork.
        if (artwork) storeArtworkOnActiveLine(artwork, data && data.trace);
      }).catch(function () {
        if (thinkingEl.parentNode) thinkingEl.parentNode.removeChild(thinkingEl);
        // Honest failure — never fake a reply, and never push a synthetic
        // assistant turn: the customer's message stays as the last convo
        // entry so hitting send again naturally retries the same ask.
        // Nothing to log either — no real assistant exchange happened.
        appendAriBubble(ARI_ERROR_REPLY, true);
      }).then(function () {
        krBusy(false);
        setChatInputEnabled(true);
        try { chatInputEl.focus(); } catch (e) {}
      });
    }

    // ===========================================================================
    // Artwork attach (chat) — real R2 upload via krUploadArtwork() (module
    // scope, pure — see near buildArtworkPreviewBlob). Click OR drag/drop
    // onto the chat pane both funnel through handleArtworkFileChosen(); the
    // uploaded file then sits as pendingArtwork (composer chip) until the
    // next sendChatMessage() actually ships it to Ari.
    // ===========================================================================
    function showPendingChip(artwork) {
      if (!chatPendingEl) return;
      chatPendingThumbEl.src = artwork.previewUrl;
      chatPendingThumbEl.alt = artwork.name || 'Attached artwork';
      chatPendingNameEl.textContent = artwork.name || 'Artwork attached';
      chatPendingEl.hidden = false;
    }
    function hidePendingChip() {
      if (!chatPendingEl) return;
      chatPendingEl.hidden = true;
      chatPendingThumbEl.src = '';
    }
    function handleArtworkFileChosen(file) {
      if (!file || chatBusy) return;
      chatAttachBtn.disabled = true;
      chatAttachBtn.classList.add('kr-uploading');
      krUploadArtwork(file).then(function (artwork) {
        artwork.name = file.name;
        pendingArtwork = artwork;
        showPendingChip(artwork);
      }).catch(function (e) {
        // Honest failure — never a fake attach. Nothing lands in the
        // composer chip; the customer just tries again.
        toast((e && e.message) || 'That upload didn’t work — try again.');
      }).then(function () {
        chatAttachBtn.disabled = false;
        chatAttachBtn.classList.remove('kr-uploading');
        chatFileEl.value = ''; // allow re-choosing the exact same file
      });
    }
    // Attaches a chat-vision artwork to whatever rail line it's "about":
    // whichever line the customer currently has open in the Configure
    // drawer/chat card (krOpenConfig), falling back to the sole rail line
    // when there's NOTHING open at all and exactly one line exists. With 2+
    // lines and nothing open, there's no safe guess — it never silently
    // attaches to the wrong product; the upload's URL still stays live in
    // chatArtworkContext either way, so Ari can still reference/re-check it
    // via buildQuoteContext.
    // FINDING #2 (P1 #6 follow-up, 2026-08) — krOpenConfig now distinguishes
    // EDIT intent (krOpenConfig.line — the exact rail line Configure was
    // opened against, set by handleOpenConfigure's targetLine) from ADD
    // intent (krOpenConfig.line === null — Configure open for a product that
    // may or may not already have a colourway on the rail). EDIT intent
    // targets that exact line by object identity, colour-independent. ADD
    // intent only attaches to an EXISTING line if the colour currently
    // selected in the open drawer/card is an EXACT match for one — a
    // brand-new colourway that isn't a rail line yet has nowhere safe to
    // land, so it's left live in chatArtworkContext (never guessed onto a
    // DIFFERENT existing colourway or an unrelated sole rail line — that
    // sole-line fallback below is reserved for "nothing open at all").
    // Item 4 — WHICH SIDE of that line, in priority order: (1) THIS turn's
    // own check_artwork trace entry, if Ari called it with a zone for this
    // exact image (the strongest signal — Ari has actually reasoned about
    // placement); (2) the line's placement, if it only has ONE
    // (unambiguous); (3) never guess — the upload stays live in
    // chatArtworkContext for a later turn instead of landing on the wrong
    // zone. `trace` is the SAME turn's trace this upload was sent in (may be
    // null/absent — e.g. the human-handoff short-circuit has none).
    function storeArtworkOnActiveLine(artwork, trace) {
      var target = null;
      if (krOpenConfig && krOpenConfig.hit) {
        if (krOpenConfig.line) {
          // EDIT intent — exact identity already known; still validate it's
          // actually on the rail (defensive — could've been removed while open).
          target = (railLines.indexOf(krOpenConfig.line) > -1) ? krOpenConfig.line : null;
        } else {
          // ADD intent — only an EXACT (productId, live-selected colour)
          // match counts; never "any colourway" of this product.
          var liveColour = null;
          try { liveColour = krOpenConfig.ui.getConfig().colour; } catch (e) {}
          target = findLine(krOpenConfig.hit.id, liveColour);
        }
      } else if (railLines.length === 1) {
        // Nothing open at all — the original unambiguous case this fallback
        // was written for. Deliberately NOT reached when something IS open
        // but resolved no exact target — that would misattach to an
        // unrelated line.
        target = railLines[0];
      }
      chatArtworkContext = { uploadedUrl: artwork.uploadedUrl, productTitle: target ? (target.hit.title || null) : null };
      if (!target) return;
      var side = null;
      if (Array.isArray(trace)) {
        for (var i = trace.length - 1; i >= 0; i--) {
          var t = trace[i];
          if (t && t.tool === 'check_artwork' && t.input && t.input.zone &&
            (t.input.imageUrl === artwork.previewUrl || t.input.imageUrl === artwork.uploadedUrl)) {
            side = krArtworkSideKey(t.input.zone);
            break;
          }
        }
      }
      if (!side && target.printLocations && target.printLocations.length === 1) side = krArtworkSideKey(target.printLocations[0]);
      if (!side) return; // ambiguous which zone this is for — don't guess (still live in chatArtworkContext above)
      if (!target.artwork) target.artwork = {};
      target.artwork[side] = { printReadyUrl: artwork.printReadyUrl, uploadedUrl: artwork.uploadedUrl, previewUrl: artwork.previewUrl, name: artwork.name || null, mayHaveTransparency: !!artwork.mayHaveTransparency };
      renderRail();
      railRev++; // FIX #12 — artwork attached to a line
      scheduleAutosave();
    }
    chatAttachBtn.addEventListener('click', function () { if (!chatAttachBtn.disabled) chatFileEl.click(); });
    chatFileEl.addEventListener('change', function () {
      var f = chatFileEl.files && chatFileEl.files[0];
      if (f) handleArtworkFileChosen(f);
    });
    chatPendingXEl.addEventListener('click', function () { pendingArtwork = null; hidePendingChip(); });
    // Drop-anywhere-on-the-thread convenience — same upload path as the
    // paperclip. Purely additive: a plain click/type flow never touches this.
    chatEl.addEventListener('dragover', function (e) { e.preventDefault(); chatEl.classList.add('kr-chat-dragover'); });
    chatEl.addEventListener('dragleave', function (e) { if (e.target === chatEl) chatEl.classList.remove('kr-chat-dragover'); });
    chatEl.addEventListener('drop', function (e) {
      e.preventDefault();
      chatEl.classList.remove('kr-chat-dragover');
      var f = e.dataTransfer && e.dataTransfer.files && e.dataTransfer.files[0];
      if (f) handleArtworkFileChosen(f);
    });
    chatSendBtn.addEventListener('click', sendChatMessage);
    chatInputEl.addEventListener('keydown', function (e) {
      if (e.key === 'Enter') { e.preventDefault(); sendChatMessage(); }
    });
    // Human-handoff poll's focus/backoff listener — see onBulkPollVisibilityChange
    // above; removed in teardown(). The poll itself only ever starts once a
    // real chatConversationId exists (startBulkDeskPoll), so registering
    // this unconditionally here is inert for a customer who never chats.
    document.addEventListener('visibilitychange', onBulkPollVisibilityChange);

    // Toggle the canvas between Browse (chips+grid) and Chat — never both at
    // once. Switching TO Browse never touches the rail/compare state (same
    // guarantee loadRoomView already gives category switches); switching TO
    // Chat seeds the thread once (idempotent — chatSeeded guards it).
    function switchToBrowse() {
      chatEl.hidden = true;
      catsEl.hidden = false;
      canvasEl.hidden = false;
    }
    function switchToChat() {
      catsEl.hidden = true;
      canvasEl.hidden = true;
      chatEl.hidden = false;
      seedChatThread();
      try { chatInputEl.focus(); } catch (e) {}
    }

    // ===========================================================================
    // Room view switching — "Popular picks" (default) + real category chips.
    // Switching views clears compare (its card refs belong to the outgoing
    // grid) but NEVER touches the rail — the kit persists across categories.
    // ===========================================================================
    function loadRoomView(mode) {
      clearCompare();
      backToGrid();
      Array.prototype.forEach.call(root.querySelectorAll('.kr-chip'), function (chip) {
        chip.classList.toggle('kr-chip-on', chip.getAttribute('data-cat') === mode);
      });
      var grid = qs('#kr-grid', root);
      if (mode === 'popular') {
        qs('#kr-ghead', root).textContent = 'Popular picks';
        qs('#kr-gsub', root).textContent = 'Our best-sellers to get you started · priced at ' + _krGridQty + ' units, one front print · inc GST';
        loadPopularGrid(grid);
      } else {
        var cat = categoryByKey(mode);
        qs('#kr-ghead', root).textContent = cat ? cat.label : '';
        qs('#kr-gsub', root).textContent = 'Bulk-ready styles · priced at ' + QUOTE_QTY + ' units, one front print · inc GST';
        loadCategoryGrid(grid, cat ? cat.types : []);
      }
    }

    Array.prototype.forEach.call(root.querySelectorAll('.kr-chip'), function (chip) {
      chip.addEventListener('click', function () { loadRoomView(chip.getAttribute('data-cat')); });
    });

    // ---------- doors -> room ----------
    // "Browse" lands on the real Popular-picks grid; "Describe" (Chat) lands
    // on a real Ari conversation (agentChat CF) — see the Chat block above.
    // Both preload in the background regardless of which one is shown first
    // (loadRoomView('popular') always runs here), so flipping the segmented
    // toggle later never shows a cold Browse grid.
    function enterRoom(mode, clickedBtn) {
      var entry = qs('#kr-entry', root);
      var room = qs('#kr-room', root);
      if (entry.hidden) return; // already in the room

      function finishEnter() {
        if (!isOpen) return; // overlay closed during the 260ms step-inside delay
        entry.hidden = true;
        room.hidden = false;
        loadRoomView('popular');
        syncSegClasses(mode === 'describe' ? 'describe' : 'browse');
        if (mode === 'describe') switchToChat(); else switchToBrowse();
      }

      // §1 "step inside" — decorative only: press/recede the doors and fade
      // the hero copy up-out, THEN do the real view swap (finishEnter,
      // unchanged from before) slightly deferred so the exit motion has
      // somewhere to land. KR_RM/no-clicked-button (e.g. a future
      // programmatic opts.mode call with no click event) skip straight to
      // the instant swap — same end state either way.
      if (KR_RM || !clickedBtn) { finishEnter(); return; }

      Array.prototype.forEach.call(root.querySelectorAll('.kr-door'), function (d) {
        if (d === clickedBtn) d.classList.add('kr-door-pressed');
        else d.classList.add('kr-door-recede');
      });
      var entryInner = qs('.kr-entry-inner', root);
      if (entryInner) entryInner.classList.add('kr-entry-leaving');
      setTimeout(function () {
        finishEnter();
        var roomEl = qs('#kr-room', root);
        roomEl.classList.add('kr-room-entering');
        setTimeout(function () { roomEl.classList.remove('kr-room-entering'); }, 460);
      }, 260);
    }
    Array.prototype.forEach.call(root.querySelectorAll('.kr-door'), function (btn) {
      btn.addEventListener('click', function () { enterRoom(btn.getAttribute('data-kr-mode'), btn); });
    });

    // ---------- topbar segmented Chat|Browse toggle ----------
    // Lives in the persistent topbar (visible on both entry and room). On
    // the entry screen it's a shortcut into the room (same destination as
    // the matching door, minus the door's own press/recede choreography —
    // KR_RM/no-clickedBtn path in enterRoom() already handles that
    // gracefully). Once inside, it flips the canvas between the real Browse
    // grid and the real Ari chat thread — never both at once.
    function syncSegClasses(mode) {
      var segEl = qs('#kr-seg', root);
      if (!segEl) return;
      Array.prototype.forEach.call(segEl.querySelectorAll('button'), function (b) {
        var on = b.getAttribute('data-kr-seg-mode') === mode;
        b.classList.toggle('kr-seg-on', on);
        b.setAttribute('aria-selected', String(on));
      });
    }
    Array.prototype.forEach.call(qs('#kr-seg', root).querySelectorAll('button'), function (btn) {
      btn.addEventListener('click', function () {
        var mode = btn.getAttribute('data-kr-seg-mode');
        syncSegClasses(mode);
        if (qs('#kr-entry', root).hidden) {
          if (mode === 'describe') switchToChat(); else switchToBrowse();
        } else {
          enterRoom(mode);
        }
      });
    });

    var firstFocusable = focusables()[0];
    if (firstFocusable) { try { firstFocusable.focus(); } catch (e) {} }

    // ---------- Save/Resume: restore an existing quote on open ----------
    // An explicit ?quote=/opts.quote resume token (cross-device / the
    // emailed link) wins over a same-device localStorage snapshot — opening
    // a resume link should never get silently swapped for a different,
    // unrelated local quote. Both restores are silent (no entry-screen jump,
    // no rail UI change beyond the rail itself populating) — whichever
    // door/tab the shopper picks next just finds their kit already there.
    var resumeTokenParam = opts.quote || krQuoteTokenFromLocation();
    if (resumeTokenParam) {
      fetchJSON(getQuoteResumeUrl() + '?token=' + encodeURIComponent(resumeTokenParam), { method: 'GET' })
        .then(function (data) {
          if (!data || !Array.isArray(data.rail) || !data.rail.length) return;
          resumeToken = resumeTokenParam;
          // getKitQuote now also returns the Firestore doc id — cache it so
          // the NEXT autosave merges into this same quote instead of forking
          // a new one (closes the duplicate-quote gap on resume-then-edit).
          if (data.quoteId) quoteId = data.quoteId;
          if (data.quoteNumber) quoteNumber = data.quoteNumber;
          if (data.customer && data.customer.email) customerEmailCaptured = data.customer.email;
          if (data.customer && data.customer.name) customerNameCaptured = data.customer.name;
          // FIX #17 (client half) — a returning customer's company/ABN/PO/
          // contact/delivery details, captured once via Ari's request_details
          // form and now echoed back here as `businessDetails` (backend
          // contract — see applyBusinessDetailsPrefill() below for where this
          // actually pre-fills a fresh form). Old quotes saved before this
          // shipped simply have no businessDetails — krBusinessDetails stays
          // null and nothing is pre-filled, exactly like today.
          if (data.businessDetails && typeof data.businessDetails === 'object') krBusinessDetails = data.businessDetails;
          // FIX #12 (client half) — pick up the server's monotonic autosave
          // revision so the NEXT autosave from this session continues the
          // count instead of restarting at 0 (which the server would then
          // reject as stale against whatever it already has stored).
          if (typeof data.rev === 'number' && data.rev > railRev) railRev = data.rev;
          // R1 (2026-08-05): re-link this resumed session to its original /inbox
          // conversation so continuing the chat doesn't spawn a second thread (#8).
          if (data.conversationId) chatConversationId = data.conversationId;
          // getKitQuote is now a LOSSLESS round-trip — each rail item carries its real
          // handle, per-line sizeRun, and per-zone artwork (persisted by saveKitQuote).
          // printLocations is still a collapsed label (krPrintLocationsFromLabel
          // approximates it back to real zone keys).
          restoreRailFromEntries(data.rail.map(function (item) {
            return {
              hit: { id: item.productId, title: item.title, vendor: item.vendor, styleCode: null, handle: item.handle || null },
              qty: item.qty, printLocations: krPrintLocationsFromLabel(item.printLocations),
              colour: item.colour || null, sizeRun: item.sizeRun || null, artwork: item.artwork || null
            };
          }));
        })
        .catch(function () {
          // FIX #16 — was an empty .catch(){} that swallowed EVERY resume
          // failure (bad/expired/deleted token -> getKitQuote {ok:false}, a
          // 404, or a plain network error all land here via fetchJSON's own
          // `data.ok === false` rejection) and let the shopper fall straight
          // into what looks like a brand-new-visitor greeting with zero
          // explanation. Surface it honestly instead of pretending nothing
          // happened.
          showResumeFailedNotice();
        });
    } else {
      var savedLocal = krLoadLocalQuote();
      if (savedLocal && savedLocal.rail && savedLocal.rail.length) {
        quoteId = savedLocal.quoteId || null;
        resumeToken = savedLocal.resumeToken || null;
        // FIX #12 (client half) — same-device reload: carry forward the
        // revision this browser last successfully saved at (persisted
        // alongside the rail snapshot by persistLocalQuote()), else every
        // autosave after a page reload would restart at rev 0 and the
        // server would reject it as stale forever.
        if (typeof savedLocal.rev === 'number' && savedLocal.rev > railRev) railRev = savedLocal.rev;
        restoreRailFromEntries(savedLocal.rail); // each entry already {hit, qty} with a full hit (incl. handle)
      }
    }

    // A future landing-page CTA supplies a mode up front and should skip
    // straight to the room (per the eventual architecture). No such CTA
    // exists yet this slice, so opts.mode is normally undefined and the
    // two-door entry screen shows first.
    if (opts.mode) enterRoom(opts.mode);
  };

  // Module-scoped indirection so fillCard()/hydrateCard() (defined once,
  // outside any single openKitRoom() call) can reach the CURRENT overlay
  // instance's toast() without threading it through every function
  // signature. Reset per-open above; harmless no-op if called after close.
  var _toast = function () {};
  function toast(msg, opts) { _toast(msg, opts); }

  // Same indirection for the rail: fillCard()'s "Add to quote" handler
  // (module scope, defined once) needs to reach the CURRENT overlay
  // instance's rail-mutation function. Reset per-open below.
  var _addToRail = function () {};
  function addToRail(hit, quote, preview, card) { _addToRail(hit, quote, preview, card); }

  // Same indirection for compare (Slice 3): fillCard()'s ⇄ handler needs the
  // CURRENT overlay instance's compare-selection function.
  var _toggleCompare = function () {};
  function toggleCompare(hit, quote, preview, card, btn) { _toggleCompare(hit, quote, preview, card, btn); }

  // Same indirection for the Configure drawer: fillCard()'s `.kr-card-hit`
  // click needs the CURRENT overlay instance's drawer opener.
  var _openConfigure = function () {};
  function openConfigureDrawer(hit, quote, preview, card) { _openConfigure(hit, quote, preview, card); }

  // Same indirection for Shell B — the in-chat Ari config card. fillCard()'s
  // chat-context `.kr-card-hit`/`.kr-add` handlers need the CURRENT overlay
  // instance's chat thread/rail.
  var _openChatConfig = function () {};
  function openChatConfigCard(hit, quote, preview, card) { _openChatConfig(hit, quote, preview, card); }

  // Same indirection for the Ari orb "thinking" state (KIT-ROOM-MOTION-REVIEW.md
  // A1) — loadPopularGrid()/loadCategoryGrid() (module scope, shared across
  // opens) toggle it around their fetches; the real ref-counted implementation
  // lives inside openKitRoom() (needs `root` to find THIS instance's orb).
  var _krBusy = function () {};
  function krBusy(on) { _krBusy(on); }

  // ===========================================================================
  // PERF — Popular-picks prefetch. Root cause of the modal's slow first open:
  // loadPopularGrid()/seedChatThread() fire resolvePopularHit() (a
  // searchProducts POST) for all 6 POPULAR_STYLE_CODES the instant the room
  // opens, COLD — that's up to 6 cold Cloud Function round-trips standing
  // between the click and the grid painting, hence the shimmer. Both caches
  // (_krPopularHitCache per-code, _krPrintPricePromise) already dedupe by
  // design (see their own header notes) — calling them once, early, just
  // means loadPopularCard()'s Promise.all resolves against an
  // ALREADY-SETTLED promise the instant the shopper actually opens the room,
  // so zero NEW searchProducts calls fire and the grid can paint immediately
  // (loadCardFast's product.js fetch is a fast CDN hit either way).
  // Fires once, off the critical path, silent-fail (resolvePopularHit already
  // catches+self-uncaches on genuine network failures, so a failed prefetch
  // just leaves that one code cold — identical to today, never surfaced).
  // ===========================================================================
  function krPrefetchPopular() {
    if (krPrefetchPopular._done) return;
    krPrefetchPopular._done = true;
    try {
      POPULAR_STYLE_CODES.forEach(function (code) { resolvePopularHit(code).catch(function () {}); });
      getPrintPrices();
    } catch (e) {}
  }
  (function () {
    // Guarded to pages that actually mount the Bulk Desk doors (see
    // sections/bulk-landing.liquid's [data-kitroom-trigger] buttons) so this
    // never fires a background searchProducts burst if kit-room.js is ever
    // loaded somewhere unrelated.
    if (!document.querySelector('[data-kitroom-trigger]')) return;
    var EVENTS = ['scroll', 'pointermove', 'touchstart'];
    var OPTS = { passive: true };
    var fired = false;
    var fallbackTimer = null;
    function fire() {
      if (fired) return;
      fired = true;
      EVENTS.forEach(function (evt) { document.removeEventListener(evt, fire, OPTS); });
      clearTimeout(fallbackTimer);
      krPrefetchPopular();
    }
    // Whichever comes first: the visitor's first scroll/pointer/touch (a
    // strong "about to engage" signal — warms the cache well before they
    // reach the doors) or a short idle/timeout fallback, so a visitor who
    // opens the room immediately (e.g. straight off an anchor link, no
    // scroll/move first) still gets a head start rather than nothing.
    EVENTS.forEach(function (evt) { document.addEventListener(evt, fire, OPTS); });
    if (window.requestIdleCallback) {
      window.requestIdleCallback(fire, { timeout: 4000 });
    } else {
      fallbackTimer = setTimeout(fire, 2500);
    }
  })();

  // ===========================================================================
  // Delegated trigger — [data-kitroom-trigger] anywhere on the page opens the
  // room (optionally with data-kitroom-mode). No such element ships this
  // slice (the landing page is a later slice); this just makes the hook
  // ready for it, mirroring the [data-pcg-chat-trigger] convention
  // (assets/collection-grid.js:143-158).
  // ===========================================================================
  document.addEventListener('click', function (e) {
    var t = e.target;
    var trigger = null;
    while (t && t !== document.body) {
      if (t.hasAttribute && t.hasAttribute('data-kitroom-trigger')) { trigger = t; break; }
      t = t.parentNode;
    }
    if (!trigger) return;
    e.preventDefault();
    window.openKitRoom({ mode: trigger.getAttribute('data-kitroom-mode') || undefined });
  });
})();
